import { GameMap } from './Map';
import { Renderer } from './Renderer';
import { CameraController } from './CameraController';
import { GameState, Position, TerrainType, Unit, UnitType, FactionType, CampaignType, City, TrainingUnit, UnitCategory, CityLevel } from './types';
import { UnitManager } from './UnitManager';
import { UNIT_DEFINITIONS, getUnitsForFaction, isAirborneUnit, getTransportForAirborne, isTransportAircraft, isGroundTransport, canBeTransported, isTruck, isAircraft, isBomber } from './unitDefinitions';
import { getCityMaxUnits, getCityUpgradeCost, getCityUpgradeTime, canCityTrainUnit, getCityLevelName, getCityLevelDescription, getCityResourceGeneration } from './cityUtils';
import { SoundManager } from './SoundManager';
import { VisionSystem } from './VisionSystem';
// === LEGACY AI IMPORTS (Commented out for V3 migration) ===
// import { AIController } from './AIController';
// import { AIControllerWrapper } from './ai/AIControllerIntegration'; // Unused
// import { AIControllerUnified } from './ai/AIControllerUnified';
// import { GeneralAI } from './ai/GeneralAI';
// === END LEGACY AI IMPORTS ===

// V3 AI - Priority-Based CPU with Minimax Search
import { MinimaxAI, createMinimaxAI } from './ai/v3';
import { MultiplayerService } from './MultiplayerService';
import { MultiplayerLobby } from './MultiplayerLobby';
import { NotificationManager } from './NotificationManager';
import { NotificationEvent, NotificationType, NotificationMessage } from './NotificationTypes';
import { PopupManager } from './PopupManager';
import { debug } from './debug';
import AnalyticsService from './services/AnalyticsService';
import StatisticsService from './services/StatisticsService';
import OnlineUsersDisplay from './components/OnlineUsersDisplay';

class Game {
  private map!: GameMap;
  private renderer!: Renderer;
  private cameraController!: CameraController;
  private gameState!: GameState;
  private canvas!: HTMLCanvasElement;
  private unitManager!: UnitManager;
  private soundManager: SoundManager;
  private visionSystem!: VisionSystem;
  // === LEGACY AI TYPE (Commented out for V3 migration) ===
  // private aiController: AIController | AIControllerUnified | GeneralAI | null = null;
  // === END LEGACY AI TYPE ===
  // V3 AI controller - Priority-Based CPU with Minimax Search
  private aiController: MinimaxAI | null = null;
  private currentFaction: FactionType = 'United States Of America';
  private secondFaction: FactionType = '3rd Reich';
  private hasShownSecondBriefing: boolean = false;
  private selectedCampaign: CampaignType = CampaignType.NORMANDY;
  private pendingMultiplayerReconnection: any = null;
  private isLoadingMultiplayerSave: boolean = false;
  private availableMoves: Position[] | null = null;
  private enemiesInRange: Position[] | null = null;
  private isLoadingSavedGame: boolean = false;
  private lastFrameTime: number = 0;
  private keysPressed: Set<string> = new Set();
  private isInitialized: boolean = false;
  private lastTurnCities: Map<string, string | null> = new Map(); // Track city ownership changes
  private isAITurn: boolean = false; // Track if AI is currently executing
  private selectedGameMode: 'PvP' | 'PvE' | 'Online' = 'PvP'; // Store game mode selection before game init
  private humanPlayerFaction: FactionType | null = null; // Track human player's faction in PvE
  private currentSaveSlot: number = 1; // Track which save slot is being used
  private isGameOver: boolean = false; // Track if game has ended to prevent saving
  private isBoardingMode: boolean = false;
  private boardingUnit: Unit | null = null;
  private validBoardTargets: Position[] = [];
  private unitCycleIndex: number = -1; // For Tab key cycling through units
  private fogOfWar: boolean = true;
  private devFogDisabled: boolean = false; // Dev mode: allows player to see through fog (AI still uses fog)
  
  // Mouse/touch drag tracking
  private isDragging: boolean = false;
  private lastMousePosition: { x: number, y: number } = { x: 0, y: 0 };
  private multiplayerService: MultiplayerService | null = null;
  private isOnlineGame: boolean = false;
  private isMyTurn: boolean = true;
  private notificationManager: NotificationManager | null = null; // Only for multiplayer
  private popupManager: PopupManager;
  private analytics: AnalyticsService;
  private statistics: StatisticsService;
  private gameStartTime: number = 0;
  private onlineUsersDisplay: OnlineUsersDisplay;
  private debugSpriteMode: 'outline' | 'coloured' | 'filled' | 'silhouette' | 'borderless' | 'coloured-borderless' | 'coloured-faction' = 'outline';
  
  // Tutorial system
  private isTutorial: boolean = false;
  private tutorialStage: number = 0;
  private tutorialStep: number = 0; // Current step within a stage
  private tutorialOverlay: HTMLElement | null = null;
  // private tutorialObjective: string = ''; // Only used in old tutorial methods - removed
  // private tutorialActionCount: number = 0; // Only used in old tutorial methods - removed
  private tutorialEnemyUnits: string[] = []; // Track spawned tutorial enemy units
  private tutorialDimOverlay: HTMLElement | null = null;
  private tutorialDialog: HTMLElement | null = null;
  private tutorialHighlight: HTMLElement | null = null;
  // private tutorialStageData: any = {}; // Store stage-specific data for validation - unused
  private tutorialPollingInterval: number | null = null; // For checking order completion
  private tutorialValidationLogged: boolean = false; // Prevent spam logging
  // @ts-ignore - TypeScript incorrectly reports this as unused but it's used in multiple places
  private tutorialAIEnabled: boolean = false; // Enable full AI after combat tutorial
  // private tutorialEnemyAttackDefeated: boolean = false; // Track if defensive phase complete - unused
  // private tutorialAIScript: string = ''; // Never set - unused
  private tutorialUpgradeEnabled: boolean = false; // Allow U key to upgrade cities in tutorial

  constructor() {
    // Setup mobile viewport and prevent zooming
    this.setupMobileViewport();
    
    // Initialize sound manager early for menu sounds
    this.soundManager = new SoundManager();
    // Initialize popup manager
    this.popupManager = PopupManager.getInstance();
    // Initialize analytics
    this.analytics = AnalyticsService.getInstance();
    // Initialize statistics
    this.statistics = StatisticsService.getInstance();
    // Initialize online users display
    this.onlineUsersDisplay = new OnlineUsersDisplay();
    this.onlineUsersDisplay.initialize();
    
    // Load and apply saved sprite mode immediately
    const savedSpriteMode = localStorage.getItem('spriteMode') || 'outline';
    this.debugSpriteMode = savedSpriteMode as 'outline' | 'coloured' | 'filled' | 'silhouette' | 'borderless' | 'coloured-borderless' | 'coloured-faction';
    
    this.checkAnalyticsConsent();
    this.showMainMenu();
  }
  
  private checkAnalyticsConsent(): void {
    const consent = localStorage.getItem('analytics_consent');
    if (consent === null) {
      // First time user, show consent dialog
      this.showAnalyticsConsentDialog();
    }
  }
  
  private showAnalyticsConsentDialog(): void {
    const dialog = document.createElement('div');
    dialog.style.cssText = `
      position: fixed;
      bottom: 20px;
      left: 20px;
      right: 20px;
      background: rgba(0, 0, 0, 0.95);
      border: 2px solid #4CAF50;
      border-radius: 10px;
      padding: 20px;
      color: white;
      font-family: Arial, sans-serif;
      z-index: 50000;
      max-width: 500px;
      margin: 0 auto;
    `;
    
    dialog.innerHTML = `
      <h3 style="margin-bottom: 15px; color: #4CAF50;">Analytics & Privacy</h3>
      <p style="margin-bottom: 15px; font-size: 14px; line-height: 1.5;">
        We use Firebase Analytics to improve your gaming experience by collecting anonymous data about gameplay, 
        such as game duration, faction preferences, and geographic location (country only). 
        This helps us balance the game and understand player preferences.
      </p>
      <p style="margin-bottom: 20px; font-size: 12px; color: #aaa;">
        No personal information is collected.
      </p>
      <div style="display: flex; gap: 10px; justify-content: flex-end;">
        <button id="analytics-deny" style="
          padding: 10px 20px;
          background: #666;
          color: white;
          border: none;
          border-radius: 5px;
          cursor: pointer;
          font-size: 14px;
        ">Decline</button>
        <button id="analytics-accept" style="
          padding: 10px 20px;
          background: #4CAF50;
          color: white;
          border: none;
          border-radius: 5px;
          cursor: pointer;
          font-size: 14px;
        ">Accept</button>
      </div>
    `;
    
    document.body.appendChild(dialog);
    
    document.getElementById('analytics-accept')?.addEventListener('click', () => {
      this.analytics.grantConsent();
      dialog.remove();
    });
    
    document.getElementById('analytics-deny')?.addEventListener('click', () => {
      this.analytics.revokeConsent();
      dialog.remove();
    });
  }
  
  private showMainMenu(): void {
    // Check if mobile/small screen
    const isMobileScreen = window.innerWidth <= 768;
    const isSmallScreen = window.innerWidth <= 480;
    
    // Create main menu overlay
    const menuOverlay = document.createElement('div');
    menuOverlay.id = 'main-menu';
    menuOverlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
      z-index: 30000;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: ${isMobileScreen ? 'flex-start' : 'center'};
      color: white;
      font-family: Arial, sans-serif;
      transition: opacity 0.5s ease-in-out;
      padding: ${isSmallScreen ? '10px' : '20px'};
      overflow-y: auto;
    `;
    
    // Logo
    const logo = document.createElement('img');
    logo.src = '/icons/MainLogo.png';
    logo.alt = 'Frontlines WW2 Logo';
    logo.style.cssText = `
      width: auto;
      height: ${isSmallScreen ? '60px' : isMobileScreen ? '80px' : '150px'};
      margin-top: ${isMobileScreen ? '20px' : '0'};
      margin-bottom: ${isSmallScreen ? '10px' : '20px'};
      filter: drop-shadow(4px 4px 8px rgba(0,0,0,0.8));
    `;
    menuOverlay.appendChild(logo);
    
    // Title
    const title = document.createElement('h1');
    title.textContent = 'FRONTLINES';
    title.style.cssText = `
      font-size: ${isSmallScreen ? '32px' : isMobileScreen ? '48px' : '64px'};
      margin-bottom: ${isSmallScreen ? '10px' : '20px'};
      text-shadow: 4px 4px 8px rgba(0,0,0,0.8);
      letter-spacing: ${isSmallScreen ? '2px' : '4px'};
    `;
    menuOverlay.appendChild(title);
    
    // Subtitle
    const subtitle = document.createElement('p');
    subtitle.textContent = 'A Turn-Based Strategy Game';
    subtitle.style.cssText = `
      font-size: ${isSmallScreen ? '14px' : isMobileScreen ? '16px' : '20px'};
      margin-bottom: ${isSmallScreen ? '30px' : isMobileScreen ? '40px' : '60px'};
      color: #ccc;
    `;
    menuOverlay.appendChild(subtitle);
    
    // Main menu buttons section (always visible initially)
    const mainMenuSection = document.createElement('div');
    mainMenuSection.id = 'main-menu-section';
    mainMenuSection.style.cssText = `
      text-align: center;
      width: 100%;
      max-width: ${isMobileScreen ? '100%' : '400px'};
    `;
    
    // NEW GAME button
    const newGameButton = document.createElement('button');
    newGameButton.textContent = 'NEW GAME';
    newGameButton.style.cssText = `
      padding: ${isSmallScreen ? '12px 24px' : isMobileScreen ? '16px 32px' : '20px 40px'};
      font-size: ${isSmallScreen ? '18px' : isMobileScreen ? '20px' : '24px'};
      background: #4a4a4a;
      color: white;
      border: 2px solid #666;
      border-radius: 5px;
      cursor: pointer;
      transition: all 0.3s ease;
      margin-bottom: ${isSmallScreen ? '15px' : '20px'};
      min-width: ${isSmallScreen ? '250px' : '300px'};
      width: ${isMobileScreen ? '90%' : 'auto'};
      text-transform: uppercase;
      letter-spacing: ${isSmallScreen ? '1px' : '2px'};
      display: block;
      margin-left: auto;
      margin-right: auto;
    `;
    
    newGameButton.onmouseover = () => {
      this.soundManager.playMenuHover();
      newGameButton.style.background = '#5a5a5a';
      newGameButton.style.borderColor = '#888';
      newGameButton.style.transform = 'scale(1.05)';
    };
    
    newGameButton.onmouseout = () => {
      newGameButton.style.background = '#4a4a4a';
      newGameButton.style.borderColor = '#666';
      newGameButton.style.transform = 'scale(1)';
    };
    
    newGameButton.onclick = () => {
      this.soundManager.playMenuSelect();
      this.showMapSelection(menuOverlay);
    };
    
    mainMenuSection.appendChild(newGameButton);
    
    // Standard button style for all menu buttons
    const standardButtonStyle = `
      padding: ${isSmallScreen ? '10px 20px' : isMobileScreen ? '14px 28px' : '20px 40px'};
      font-size: ${isSmallScreen ? '14px' : isMobileScreen ? '16px' : '18px'};
      background: #4a4a4a;
      color: white;
      border: 2px solid #666;
      border-radius: 5px;
      cursor: pointer;
      transition: all 0.3s ease;
      min-width: ${isSmallScreen ? '250px' : '300px'};
      width: ${isMobileScreen ? '90%' : 'auto'};
      margin: ${isSmallScreen ? '8px 0' : '10px 0'};
      display: block;
      margin-left: auto;
      margin-right: auto;
    `;
    
    // Continue Saved Game button
    const continueButton = document.createElement('button');
    continueButton.textContent = 'Continue Saved Game';
    continueButton.style.cssText = standardButtonStyle;
    
    // Check if any save slot has data (including multiplayer)
    const hasAnySave = [1, 2, 3, 4, 5].some(slot => 
      localStorage.getItem(`frontlines_gamestate_slot${slot}`)
    ) || localStorage.getItem('frontlines_multiplayer_save');
    
    if (!hasAnySave) {
      continueButton.style.opacity = '0.5';
      continueButton.style.cursor = 'not-allowed';
      continueButton.title = 'No saved games available';
    } else {
      continueButton.onmouseover = () => {
        this.soundManager.playMenuHover();
        continueButton.style.background = '#5a5a5a';
        continueButton.style.borderColor = '#888';
        continueButton.style.transform = 'scale(1.05)';
      };
      continueButton.onmouseout = () => {
        continueButton.style.background = '#4a4a4a';
        continueButton.style.borderColor = '#666';
        continueButton.style.transform = 'scale(1)';
      };
      continueButton.onclick = () => {
        this.soundManager.playMenuSelect();
        this.showSaveSlotSelection(menuOverlay);
      };
    }
    
    mainMenuSection.appendChild(continueButton);
    
    // Game Guide button
    const guideButton = document.createElement('button');
    guideButton.textContent = 'Game Guide';
    guideButton.style.cssText = standardButtonStyle;
    
    guideButton.onmouseover = () => {
      this.soundManager.playMenuHover();
      guideButton.style.background = '#5a5a5a';
      guideButton.style.borderColor = '#888';
      guideButton.style.transform = 'scale(1.05)';
    };
    
    guideButton.onmouseout = () => {
      guideButton.style.background = '#4a4a4a';
      guideButton.style.borderColor = '#666';
      guideButton.style.transform = 'scale(1)';
    };
    
    guideButton.onclick = () => {
      this.soundManager.playMenuSelect();
      this.popupManager.showGuide();
    };
    
    mainMenuSection.appendChild(guideButton);
    
    // Tutorial button
    const tutorialButton = document.createElement('button');
    tutorialButton.textContent = 'Tutorial';
    tutorialButton.style.cssText = standardButtonStyle;
    
    tutorialButton.onmouseover = () => {
      this.soundManager.playMenuHover();
      tutorialButton.style.background = '#5a5a5a';
      tutorialButton.style.borderColor = '#888';
      tutorialButton.style.transform = 'scale(1.05)';
    };
    
    tutorialButton.onmouseout = () => {
      tutorialButton.style.background = '#4a4a4a';
      tutorialButton.style.borderColor = '#666';
      tutorialButton.style.transform = 'scale(1)';
    };
    
    tutorialButton.onclick = () => {
      this.soundManager.playMenuSelect();
      this.startTutorial(menuOverlay);
    };
    
    mainMenuSection.appendChild(tutorialButton);
    
    // Settings button
    const settingsButton = document.createElement('button');
    settingsButton.textContent = 'Settings';
    settingsButton.style.cssText = standardButtonStyle;
    
    settingsButton.onmouseover = () => {
      this.soundManager.playMenuHover();
      settingsButton.style.background = '#5a5a5a';
      settingsButton.style.borderColor = '#888';
      settingsButton.style.transform = 'scale(1.05)';
    };
    
    settingsButton.onmouseout = () => {
      settingsButton.style.background = '#4a4a4a';
      settingsButton.style.borderColor = '#666';
      settingsButton.style.transform = 'scale(1)';
    };
    
    settingsButton.onclick = () => {
      this.soundManager.playMenuSelect();
      this.showSettingsMenu();
    };

    mainMenuSection.appendChild(settingsButton);

    menuOverlay.appendChild(mainMenuSection);
    
    // Map selection (initially hidden)
    const mapSection = document.createElement('div');
    mapSection.id = 'map-selection-section';
    mapSection.style.cssText = `
      margin-bottom: ${isSmallScreen ? '20px' : '40px'};
      text-align: center;
      display: none;
      width: 100%;
      padding: ${isSmallScreen ? '10px' : '20px'};
    `;
    
    const mapLabel = document.createElement('h3');
    mapLabel.textContent = 'Select Campaign';
    mapLabel.style.cssText = `
      font-size: ${isSmallScreen ? '20px' : '24px'};
      margin-bottom: ${isSmallScreen ? '15px' : '20px'};
    `;
    mapSection.appendChild(mapLabel);
    
    const campaignContainer = document.createElement('div');
    campaignContainer.style.cssText = `
      display: flex;
      gap: ${isSmallScreen ? '10px' : '20px'};
      justify-content: center;
      flex-wrap: wrap;
      max-width: 100%;
    `;
    
    // Campaign button style
    const campaignButtonStyle = `
      padding: ${isSmallScreen ? '10px 20px' : isMobileScreen ? '15px 30px' : '20px 40px'};
      font-size: ${isSmallScreen ? '14px' : isMobileScreen ? '16px' : '18px'};
      background: #4a4a4a;
      color: white;
      border: 2px solid #666;
      border-radius: 5px;
      cursor: pointer;
      transition: all 0.3s ease;
      min-width: ${isSmallScreen ? '140px' : isMobileScreen ? '180px' : '250px'};
      width: ${isSmallScreen ? 'calc(50% - 10px)' : isMobileScreen ? '45%' : 'auto'};
    `;
    
    // Normandy Campaign button
    const normandyButton = document.createElement('button');
    normandyButton.textContent = 'Invasion of Normandy';
    normandyButton.style.cssText = campaignButtonStyle;
    normandyButton.onmouseover = () => {
      this.soundManager.playMenuHover();
      normandyButton.style.background = '#5a5a5a';
      normandyButton.style.borderColor = '#888';
      normandyButton.style.transform = 'scale(1.05)';
    };
    normandyButton.onmouseout = () => {
      normandyButton.style.background = '#4a4a4a';
      normandyButton.style.borderColor = '#666';
      normandyButton.style.transform = 'scale(1)';
    };
    campaignContainer.appendChild(normandyButton);
    
    // Tunisia Campaign button
    const tunisiaButton = document.createElement('button');
    tunisiaButton.textContent = 'Tunisia Campaign';
    tunisiaButton.style.cssText = campaignButtonStyle;
    tunisiaButton.onmouseover = () => {
      this.soundManager.playMenuHover();
      tunisiaButton.style.background = '#5a5a5a';
      tunisiaButton.style.borderColor = '#888';
      tunisiaButton.style.transform = 'scale(1.05)';
    };
    tunisiaButton.onmouseout = () => {
      tunisiaButton.style.background = '#4a4a4a';
      tunisiaButton.style.borderColor = '#666';
      tunisiaButton.style.transform = 'scale(1)';
    };
    campaignContainer.appendChild(tunisiaButton);
    
    // Stalingrad Campaign button
    const stalingradButton = document.createElement('button');
    stalingradButton.textContent = 'Siege of Stalingrad';
    stalingradButton.style.cssText = campaignButtonStyle;
    stalingradButton.onmouseover = () => {
      this.soundManager.playMenuHover();
      stalingradButton.style.background = '#5a5a5a';
      stalingradButton.style.borderColor = '#888';
      stalingradButton.style.transform = 'scale(1.05)';
    };
    stalingradButton.onmouseout = () => {
      stalingradButton.style.background = '#4a4a4a';
      stalingradButton.style.borderColor = '#666';
      stalingradButton.style.transform = 'scale(1)';
    };
    campaignContainer.appendChild(stalingradButton);
    
    // Okinawa Campaign button
    const okinawaButton = document.createElement('button');
    okinawaButton.textContent = 'Okinawa Assault';
    okinawaButton.style.cssText = campaignButtonStyle;
    okinawaButton.onmouseover = () => {
      this.soundManager.playMenuHover();
      okinawaButton.style.background = '#5a5a5a';
      okinawaButton.style.borderColor = '#888';
      okinawaButton.style.transform = 'scale(1.05)';
    };
    okinawaButton.onmouseout = () => {
      okinawaButton.style.background = '#4a4a4a';
      okinawaButton.style.borderColor = '#666';
      okinawaButton.style.transform = 'scale(1)';
    };
    campaignContainer.appendChild(okinawaButton);
    
    // Campobasso Campaign button
    const campobassoButton = document.createElement('button');
    campobassoButton.textContent = 'Campobasso Offensive';
    campobassoButton.style.cssText = campaignButtonStyle;
    campobassoButton.onmouseover = () => {
      this.soundManager.playMenuHover();
      campobassoButton.style.background = '#5a5a5a';
      campobassoButton.style.borderColor = '#888';
      campobassoButton.style.transform = 'scale(1.05)';
    };
    campobassoButton.onmouseout = () => {
      campobassoButton.style.background = '#4a4a4a';
      campobassoButton.style.borderColor = '#666';
      campobassoButton.style.transform = 'scale(1)';
    };
    campaignContainer.appendChild(campobassoButton);
    
    // France 1940 Campaign button
    const france1940Button = document.createElement('button');
    france1940Button.textContent = 'Battle of France';
    france1940Button.style.cssText = campaignButtonStyle;
    france1940Button.onmouseover = () => {
      this.soundManager.playMenuHover();
      france1940Button.style.background = '#5a5a5a';
      france1940Button.style.borderColor = '#888';
      france1940Button.style.transform = 'scale(1.05)';
    };
    france1940Button.onmouseout = () => {
      france1940Button.style.background = '#4a4a4a';
      france1940Button.style.borderColor = '#666';
      france1940Button.style.transform = 'scale(1)';
    };
    campaignContainer.appendChild(france1940Button);
    
    // Albania 1940 Campaign button
    const albania1940Button = document.createElement('button');
    albania1940Button.textContent = 'Greco-Italian War';
    albania1940Button.style.cssText = campaignButtonStyle;
    albania1940Button.onmouseover = () => {
      this.soundManager.playMenuHover();
      albania1940Button.style.background = '#5a5a5a';
      albania1940Button.style.borderColor = '#888';
      albania1940Button.style.transform = 'scale(1.05)';
    };
    albania1940Button.onmouseout = () => {
      albania1940Button.style.background = '#4a4a4a';
      albania1940Button.style.borderColor = '#666';
      albania1940Button.style.transform = 'scale(1)';
    };
    campaignContainer.appendChild(albania1940Button);
    
    mapSection.appendChild(campaignContainer);
    menuOverlay.appendChild(mapSection);
    
    // Game mode selection
    const gameModeSection = document.createElement('div');
    gameModeSection.id = 'game-mode-section';
    gameModeSection.style.cssText = `
      display: none;
      text-align: center;
      padding: ${isSmallScreen ? '10px' : '20px'};
      width: 100%;
    `;
    
    const gameModeLabel = document.createElement('h3');
    gameModeLabel.textContent = 'Select Game Mode';
    gameModeLabel.style.cssText = `
      font-size: ${isSmallScreen ? '20px' : '24px'};
      margin-bottom: ${isSmallScreen ? '20px' : '30px'};
    `;
    gameModeSection.appendChild(gameModeLabel);
    
    const gameModeContainer = document.createElement('div');
    gameModeContainer.style.cssText = `
      display: flex;
      gap: ${isSmallScreen ? '15px' : isMobileScreen ? '25px' : '40px'};
      justify-content: center;
      flex-wrap: wrap;
    `;
    
    // PvP Mode Button
    const pvpButton = document.createElement('div');
    pvpButton.style.cssText = `
      padding: ${isSmallScreen ? '20px 30px' : isMobileScreen ? '25px 40px' : '30px 50px'};
      background: #4a4a4a;
      border: 2px solid #666;
      border-radius: 10px;
      cursor: pointer;
      transition: all 0.3s ease;
      text-align: center;
      width: ${isSmallScreen ? '100%' : isMobileScreen ? 'calc(50% - 15px)' : 'auto'};
    `;
    pvpButton.innerHTML = `
      <h4 style="color: #fff; font-size: 22px; margin: 0 0 10px 0;">Player vs Player</h4>
      <p style="color: #ccc; font-size: 14px; margin: 0;">Local multiplayer - Pass the device</p>
    `;
    
    // PvE Mode Button
    const pveButton = document.createElement('div');
    pveButton.style.cssText = `
      padding: 30px 50px;
      background: #4a4a4a;
      border: 2px solid #666;
      border-radius: 10px;
      cursor: pointer;
      transition: all 0.3s ease;
      text-align: center;
    `;
    pveButton.innerHTML = `
      <h4 style="color: #fff; font-size: 22px; margin: 0 0 10px 0;">Player vs AI</h4>
      <p style="color: #ccc; font-size: 14px; margin: 0;">Single player against computer</p>
    `;
    
    // Online Mode Button
    const onlineButton = document.createElement('div');
    onlineButton.style.cssText = `
      padding: 30px 50px;
      background: #4a4a4a;
      border: 2px solid #666;
      border-radius: 10px;
      cursor: pointer;
      transition: all 0.3s ease;
      text-align: center;
    `;
    onlineButton.innerHTML = `
      <h4 style="color: #fff; font-size: 22px; margin: 0 0 10px 0;">Online PvP</h4>
      <p style="color: #ccc; font-size: 14px; margin: 0;">Play against others online</p>
    `;
    
    // Add hover effects
    [pvpButton, pveButton, onlineButton].forEach(button => {
      button.onmouseover = () => {
        this.soundManager.playMenuHover();
        button.style.background = '#5a5a5a';
        button.style.borderColor = '#888';
        button.style.transform = 'scale(1.05)';
      };
      button.onmouseout = () => {
        button.style.background = '#4a4a4a';
        button.style.borderColor = '#666';
        button.style.transform = 'scale(1)';
      };
    });
    
    gameModeContainer.appendChild(pvpButton);
    gameModeContainer.appendChild(pveButton);
    gameModeContainer.appendChild(onlineButton);
    gameModeSection.appendChild(gameModeContainer);
    menuOverlay.appendChild(gameModeSection);
    
    // Faction selection
    const factionSection = document.createElement('div');
    factionSection.id = 'faction-section';
    factionSection.style.cssText = `
      display: none;
      text-align: center;
    `;
    
    const factionLabel = document.createElement('h3');
    factionLabel.textContent = 'Select Starting Faction';
    factionLabel.style.cssText = `
      font-size: 24px;
      margin-bottom: 30px;
    `;
    factionSection.appendChild(factionLabel);
    
    const factionsContainer = document.createElement('div');
    factionsContainer.style.cssText = `
      display: flex;
      gap: 40px;
      justify-content: center;
    `;
    
    factionSection.appendChild(factionsContainer);
    menuOverlay.appendChild(factionSection);
    
    // Campaign button click handlers
    normandyButton.onclick = () => {
      this.soundManager.playMenuSelect();
      this.selectedCampaign = CampaignType.NORMANDY;
      this.analytics.trackMenuInteraction({ menuType: 'campaign', selection: 'normandy' });
      this.showGameModeSelection(menuOverlay);
    };
    
    tunisiaButton.onclick = () => {
      this.soundManager.playMenuSelect();
      this.selectedCampaign = CampaignType.TUNISIA;
      this.analytics.trackMenuInteraction({ menuType: 'campaign', selection: 'tunisia' });
      this.showGameModeSelection(menuOverlay);
    };
    
    stalingradButton.onclick = () => {
      this.soundManager.playMenuSelect();
      this.selectedCampaign = CampaignType.STALINGRAD;
      this.analytics.trackMenuInteraction({ menuType: 'campaign', selection: 'stalingrad' });
      this.showGameModeSelection(menuOverlay);
    };
    
    okinawaButton.onclick = () => {
      this.soundManager.playMenuSelect();
      this.selectedCampaign = CampaignType.OKINAWA;
      this.analytics.trackMenuInteraction({ menuType: 'campaign', selection: 'okinawa' });
      this.showGameModeSelection(menuOverlay);
    };
    
    campobassoButton.onclick = () => {
      this.soundManager.playMenuSelect();
      this.selectedCampaign = CampaignType.CAMPOBASSO;
      this.analytics.trackMenuInteraction({ menuType: 'campaign', selection: 'campobasso' });
      this.showGameModeSelection(menuOverlay);
    };
    
    france1940Button.onclick = () => {
      this.soundManager.playMenuSelect();
      this.selectedCampaign = CampaignType.FRANCE_1940;
      this.analytics.trackMenuInteraction({ menuType: 'campaign', selection: 'france_1940' });
      this.showGameModeSelection(menuOverlay);
    };
    
    albania1940Button.onclick = () => {
      this.soundManager.playMenuSelect();
      this.selectedCampaign = CampaignType.ALBANIA_1940;
      this.analytics.trackMenuInteraction({ menuType: 'campaign', selection: 'albania_1940' });
      this.showGameModeSelection(menuOverlay);
    };
    
    // Game mode button click handlers
    pvpButton.onclick = () => {
      this.soundManager.playMenuSelect();
      this.selectedGameMode = 'PvP';
      this.analytics.trackMenuInteraction({ menuType: 'main', selection: 'pvp' });
      gameModeSection.style.display = 'none';
      factionSection.style.display = 'block';
      // Update faction buttons for selected campaign
      this.updateFactionButtons(factionsContainer, this.selectedCampaign, menuOverlay);
    };
    
    pveButton.onclick = () => {
      this.soundManager.playMenuSelect();
      this.selectedGameMode = 'PvE';
      this.analytics.trackMenuInteraction({ menuType: 'main', selection: 'pve' });
      gameModeSection.style.display = 'none';
      factionSection.style.display = 'block';
      // Update faction buttons for selected campaign (PvE mode)
      this.updateFactionButtons(factionsContainer, this.selectedCampaign, menuOverlay);
    };
    
    onlineButton.onclick = () => {
      this.soundManager.playMenuSelect();
      this.selectedGameMode = 'Online';
      gameModeSection.style.display = 'none';
      factionSection.style.display = 'block';
      // Update faction buttons for selected campaign (Online mode)
      this.updateFactionButtonsForOnline(factionsContainer, this.selectedCampaign, menuOverlay);
    };
    
    document.body.appendChild(menuOverlay);
  }

  private showMapSelection(menuOverlay: HTMLElement): void {
    // Hide the main menu section (NEW GAME, Continue, Guide, Settings)
    const mainMenuSection = menuOverlay.querySelector('#main-menu-section') as HTMLElement;
    if (mainMenuSection) {
      mainMenuSection.style.display = 'none';
    }
    
    // Show the map selection section
    const mapSection = menuOverlay.querySelector('#map-selection-section') as HTMLElement;
    if (mapSection) {
      mapSection.style.display = 'block';
    }
  }
  
  private showGameModeSelection(menuOverlay: HTMLElement): void {
    // Hide the map selection section
    const mapSection = menuOverlay.querySelector('#map-selection-section') as HTMLElement;
    if (mapSection) {
      mapSection.style.display = 'none';
    }
    
    // Show the game mode selection section
    const gameModeSection = menuOverlay.querySelector('#game-mode-section') as HTMLElement;
    if (gameModeSection) {
      gameModeSection.style.display = 'block';
    }
  }
  
  private createFactionButton(name: string, flagPath: string, color: string, onClick: () => void): HTMLElement {
    const isMobileScreen = window.innerWidth <= 768;
    const isSmallScreen = window.innerWidth <= 480;
    
    const button = document.createElement('div');
    button.style.cssText = `
      padding: ${isSmallScreen ? '12px' : isMobileScreen ? '16px' : '20px'};
      background: rgba(255, 255, 255, 0.05);
      border: 2px solid #444;
      border-radius: 10px;
      cursor: pointer;
      transition: all 0.3s ease;
      text-align: center;
      width: ${isSmallScreen ? '100%' : isMobileScreen ? 'calc(50% - 10px)' : 'auto'};
    `;
    
    button.innerHTML = `
      <img src="${flagPath}" alt="${name} Flag" 
           style="width: ${isSmallScreen ? '100px' : isMobileScreen ? '120px' : '150px'}; height: auto; margin-bottom: ${isSmallScreen ? '8px' : '15px'}; border: 2px solid #666;">
      <h4 style="color: ${color}; font-size: ${isSmallScreen ? '14px' : isMobileScreen ? '16px' : '18px'}; margin: 0;">${name}</h4>
    `;
    
    button.onmouseover = () => {
      this.soundManager.playMenuHover();
      button.style.background = 'rgba(255, 255, 255, 0.1)';
      button.style.borderColor = '#666';
      button.style.transform = 'scale(1.05)';
    };
    
    button.onmouseout = () => {
      button.style.background = 'rgba(255, 255, 255, 0.05)';
      button.style.borderColor = '#444';
      button.style.transform = 'scale(1)';
    };
    
    button.onclick = onClick;
    
    return button;
  }
  
  private updateFactionButtons(container: HTMLElement, campaign: CampaignType, menuOverlay: HTMLElement): void {
    // Clear existing buttons
    container.innerHTML = '';
    
    if (campaign === CampaignType.NORMANDY) {
      // USA faction button
      const usaButton = this.createFactionButton(
        'United States Of America',
        'flags/USA.jpg',
        '#4B5320',
        () => this.startGame('United States Of America', menuOverlay)
      );
      container.appendChild(usaButton);
      
      // Germany faction button
      const germanyButton = this.createFactionButton(
        '3rd Reich',
        'flags/Reich.jpg',
        '#708090',
        () => this.startGame('3rd Reich', menuOverlay)
      );
      container.appendChild(germanyButton);
    } else if (campaign === CampaignType.TUNISIA) {
      // UK faction button
      const ukButton = this.createFactionButton(
        'United Kingdom',
        'flags/Brits.jpg',
        '#003F87',
        () => this.startGame('United Kingdom', menuOverlay)
      );
      container.appendChild(ukButton);
      
      // Afrika Korps (still 3rd Reich) faction button
      const germanyButton = this.createFactionButton(
        '3rd Reich',
        'flags/Reich.jpg',
        '#708090',
        () => this.startGame('3rd Reich', menuOverlay)
      );
      container.appendChild(germanyButton);
    } else if (campaign === CampaignType.STALINGRAD) {
      // USSR faction button
      const ussrButton = this.createFactionButton(
        'USSR',
        'flags/Soviet.jpg',
        '#CC0000',
        () => this.startGame('USSR', menuOverlay)
      );
      container.appendChild(ussrButton);
      
      // Germany faction button
      const germanyButton = this.createFactionButton(
        '3rd Reich',
        'flags/Reich.jpg',
        '#708090',
        () => this.startGame('3rd Reich', menuOverlay)
      );
      container.appendChild(germanyButton);
    } else if (campaign === CampaignType.OKINAWA) {
      // USA faction button (Marines)
      const usaButton = this.createFactionButton(
        'United States Of America',
        'flags/USA.jpg',
        '#4B5320',
        () => this.startGame('United States Of America', menuOverlay)
      );
      container.appendChild(usaButton);
      
      // Imperial Japan faction button
      const japanButton = this.createFactionButton(
        'Imperial Japan',
        'flags/Japs.jpg',
        '#DC143C',
        () => this.startGame('Imperial Japan', menuOverlay)
      );
      container.appendChild(japanButton);
    } else if (campaign === CampaignType.CAMPOBASSO) {
      // Canada faction button
      const canadaButton = this.createFactionButton(
        'Canada',
        'flags/Canadians.jpg',
        '#D2691E',
        () => this.startGame('Canada', menuOverlay)
      );
      container.appendChild(canadaButton);
      
      // Fascist Italy faction button
      const italyButton = this.createFactionButton(
        'Fascist Italy',
        'flags/Italians.jpg',
        '#009246',
        () => this.startGame('Fascist Italy', menuOverlay)
      );
      container.appendChild(italyButton);
    } else if (campaign === CampaignType.FRANCE_1940) {
      // France faction button
      const franceButton = this.createFactionButton(
        'France',
        'flags/French.jpg',
        '#7A9BB8',
        () => this.startGame('France', menuOverlay)
      );
      container.appendChild(franceButton);
      
      // 3rd Reich faction button
      const wehrmachtButton = this.createFactionButton(
        '3rd Reich',
        'flags/Reich.jpg',
        '#708090',
        () => this.startGame('3rd Reich', menuOverlay)
      );
      container.appendChild(wehrmachtButton);
    } else if (campaign === CampaignType.ALBANIA_1940) {
      // Greece faction button
      const greeceButton = this.createFactionButton(
        'Greece',
        'flags/Greeks.jpg',
        '#0072CE',
        () => this.startGame('Greece', menuOverlay)
      );
      container.appendChild(greeceButton);
      
      // Italy faction button
      const italyButton = this.createFactionButton(
        'Fascist Italy',
        'flags/Italians.jpg',
        '#009246',
        () => this.startGame('Fascist Italy', menuOverlay)
      );
      container.appendChild(italyButton);
    }
  }
  
  private startGame(startingFaction: FactionType, menuOverlay: HTMLElement): void {
    // Play selection sound
    this.soundManager.playMenuSelect();
    
    // Set starting faction
    this.currentFaction = startingFaction;
    
    // Store the second faction
    if (this.selectedCampaign === CampaignType.NORMANDY) {
      this.secondFaction = startingFaction === 'United States Of America' ? '3rd Reich' : 'United States Of America';
    } else if (this.selectedCampaign === CampaignType.TUNISIA) {
      this.secondFaction = startingFaction === 'United Kingdom' ? '3rd Reich' : 'United Kingdom';
    } else if (this.selectedCampaign === CampaignType.STALINGRAD) {
      this.secondFaction = startingFaction === 'USSR' ? '3rd Reich' : 'USSR';
    } else if (this.selectedCampaign === CampaignType.OKINAWA) {
      this.secondFaction = startingFaction === 'United States Of America' ? 'Imperial Japan' : 'United States Of America';
    } else if (this.selectedCampaign === CampaignType.CAMPOBASSO) {
      this.secondFaction = startingFaction === 'Canada' ? 'Fascist Italy' : 'Canada';
    } else if (this.selectedCampaign === CampaignType.FRANCE_1940) {
      this.secondFaction = startingFaction === 'France' ? '3rd Reich' : 'France';
    } else if (this.selectedCampaign === CampaignType.ALBANIA_1940) {
      this.secondFaction = startingFaction === 'Greece' ? 'Fascist Italy' : 'Greece';
    }
    
    // In PvE mode, set the AI-controlled faction
    if (this.selectedGameMode === 'PvE') {
      // Will be set when game state is initialized
      this.humanPlayerFaction = startingFaction; // Remember the human player's faction
      this.hasShownSecondBriefing = true; // Skip second faction briefing in PvE
    } else {
      this.hasShownSecondBriefing = false;
    }
    
    // Show save slot selection for new game (skip for multiplayer reconnections)
    if (!this.isLoadingMultiplayerSave) {
      this.showSaveSlotSelectionForNewGame(startingFaction, menuOverlay);
    } else {
      // For multiplayer reconnections, skip slot selection and briefing
      menuOverlay.style.transition = 'opacity 0.5s ease-out';
      menuOverlay.style.opacity = '0';
      
      setTimeout(() => {
        menuOverlay.remove();
        this.initializeGame();
      }, 500);
    }
  }

  private showFactionBriefing(faction: FactionType, onReady?: () => void): void {
    // Play war drums for dramatic effect
    this.soundManager.playWarDrums();
    
    // Detect mobile
    const isMobile = window.innerWidth <= 812 || window.innerHeight <= 812;
    
    // Create briefing overlay
    const briefingOverlay = document.createElement('div');
    briefingOverlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: #000;
      z-index: 25000;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      transition: opacity 1s ease-in-out;
      overflow-y: auto;
    `;
    
    // Create content container with mobile-responsive styling
    const content = document.createElement('div');
    content.style.cssText = `
      max-width: ${isMobile ? '90%' : '800px'};
      max-height: ${isMobile ? '85vh' : '90vh'};
      padding: ${isMobile ? '20px' : '40px'};
      text-align: center;
      color: white;
      font-family: 'Georgia', serif;
      opacity: 0;
      transition: opacity 1s ease-in-out;
      overflow-y: auto;
      display: flex;
      flex-direction: column;
    `;
    
    // Faction-specific briefing content
    if (faction === 'United States Of America' && this.selectedCampaign === CampaignType.NORMANDY) {
      content.innerHTML = `
        <h2 style="font-size: 36px; margin-bottom: 30px; color: #4B5320;">US ARMY COMMAND BRIEFING</h2>
        <p style="font-size: 18px; margin-bottom: 20px; font-style: italic; color: #ddd;">
          Date: June 6, 1944<br/>
          Location: Normandy, France<br/>
          Operation: Overlord
        </p>
        <div style="text-align: left; font-size: 16px; line-height: 1.8; color: #ccc;">
          <p style="margin-bottom: 20px;">
            <strong>SITUATION:</strong> American forces have successfully landed on the Normandy beaches 
            and established initial beachheads. The town of Coutances has been secured as a forward 
            operating base.
          </p>
          <p style="margin-bottom: 20px;">
            <strong>ENEMY FORCES:</strong> German Wehrmacht units control the majority of inland cities 
            and strategic positions throughout Normandy. Enemy strength and disposition unknown.
          </p>
          <p style="margin-bottom: 30px; font-weight: bold; color: #4B5320;">
            <strong>MISSION:</strong> Advance inland from current positions. Secure all major population 
            centers in the Normandy region. Establish control over the entire area of operations.
          </p>
        </div>
      `;
    } else if (faction === '3rd Reich' && this.selectedCampaign === CampaignType.NORMANDY) {
      content.innerHTML = `
        <h2 style="font-size: 36px; margin-bottom: 30px; color: #708090;">WEHRMACHT COMMAND BRIEFING</h2>
        <p style="font-size: 18px; margin-bottom: 20px; font-style: italic; color: #ddd;">
          Date: 6 June 1944<br/>
          Location: Normandy, France<br/>
          Sector: Western Front
        </p>
        <div style="text-align: left; font-size: 16px; line-height: 1.8; color: #ccc;">
          <p style="margin-bottom: 20px;">
            <strong>SITUATION:</strong> Enemy forces have initiated amphibious landings along the 
            Normandy coast. Initial reports indicate the establishment of several beachheads. 
            Our forces maintain control of Bayeux and most inland positions.
          </p>
          <p style="margin-bottom: 20px;">
            <strong>ENEMY FORCES:</strong> Allied invasion force of unknown strength has secured 
            coastal positions including the town of Coutances. Further enemy intentions unclear.
          </p>
          <p style="margin-bottom: 30px; font-weight: bold; color: #708090;">
            <strong>MISSION:</strong> Contain enemy advance from coastal positions. Maintain control 
            of all strategic cities and prevent enemy breakout into the French interior.
          </p>
        </div>
      `;
    } else if (faction === '3rd Reich' && this.selectedCampaign === CampaignType.TUNISIA) {
      content.innerHTML = `
        <h2 style="font-size: 36px; margin-bottom: 30px; color: #708090;">AFRIKA KORPS COMMAND BRIEFING</h2>
        <p style="font-size: 18px; margin-bottom: 20px; font-style: italic; color: #ddd;">
          Date: November 12, 1942<br/>
          Location: Tunisia, North Africa<br/>
          Commander: Rommel
        </p>
        <div style="text-align: left; font-size: 16px; line-height: 1.8; color: #ccc;">
          <p style="margin-bottom: 20px;">
            <strong>SITUATION:</strong> Following tactical withdrawal from El Alamein, Afrika Korps 
            has established defensive positions at Gabès. Supply situation critical. Enemy forces 
            approaching from multiple directions.
          </p>
          <p style="margin-bottom: 20px;">
            <strong>ENEMY FORCES:</strong> British 8th Army advancing from west through Tébessa. 
            Montgomery's forces are well-supplied and reinforced. American landings reported in 
            Morocco and Algeria.
          </p>
          <p style="margin-bottom: 30px; font-weight: bold; color: #708090;">
            <strong>MISSION:</strong> Hold Tunisian ports at all costs. Prevent enemy forces from 
            linking. Conduct fighting withdrawal if necessary. Preserve Afrika Korps as effective 
            fighting force.
          </p>
        </div>
      `;
    } else if (faction === 'United Kingdom') {
      content.innerHTML = `
        <h2 style="font-size: 36px; margin-bottom: 30px; color: #003F87;">8TH ARMY COMMAND BRIEFING</h2>
        <p style="font-size: 18px; margin-bottom: 20px; font-style: italic; color: #ddd;">
          Date: November 12, 1942<br/>
          Location: Tunisia, North Africa<br/>
          Operation: Supercharge Follow-up
        </p>
        <div style="text-align: left; font-size: 16px; line-height: 1.8; color: #ccc;">
          <p style="margin-bottom: 20px;">
            <strong>SITUATION:</strong> Following our victory at El Alamein, Rommel's Afrika Korps 
            is in full retreat westward. American forces have landed in Morocco and Algeria as part 
            of Operation Torch. We have secured Tébessa as our forward operating base.
          </p>
          <p style="margin-bottom: 20px;">
            <strong>ENEMY FORCES:</strong> Afrika Korps estimated at division strength has occupied 
            defensive positions around Gabès. Enemy morale remains high despite recent defeats. 
            Expect skillful defensive operations.
          </p>
          <p style="margin-bottom: 30px; font-weight: bold; color: #003F87;">
            <strong>MISSION:</strong> Advance eastward and capture all enemy-held cities. Prevent 
            Afrika Korps withdrawal to Italian mainland. Effect junction with American forces 
            advancing from the west.
          </p>
        </div>
      `;
    } else if (faction === 'USSR') {
      content.innerHTML = `
        <h2 style="font-size: 36px; margin-bottom: 30px; color: #CC0000;">RED ARMY COMMAND BRIEFING</h2>
        <p style="font-size: 18px; margin-bottom: 20px; font-style: italic; color: #ddd;">
          Date: December 15, 1942<br/>
          Location: Stalingrad Region<br/>
          Operation: Winter Storm Counter-Offensive
        </p>
        <div style="text-align: left; font-size: 16px; line-height: 1.8; color: #ccc;">
          <p style="margin-bottom: 20px;">
            <strong>SITUATION:</strong> German 6th Army continues assault on Stalingrad. 
            Enemy forces have extended supply lines across frozen steppes. Winter conditions 
            favor defenders. Volkhov secured as staging area for counter-offensive operations.
          </p>
          <p style="margin-bottom: 20px;">
            <strong>ENEMY FORCES:</strong> Wehrmacht units control strategic towns across region. 
            Enemy maintains air superiority from Gumrak airfield. German forces experienced but 
            suffering from extended supply lines and harsh winter conditions.
          </p>
          <p style="margin-bottom: 30px; font-weight: bold; color: #CC0000;">
            <strong>MISSION:</strong> Hold Stalingrad at all costs. Launch counter-offensive to 
            encircle enemy forces. Exploit numerical superiority and winter conditions. Not one 
            step back, Comrades!
          </p>
        </div>
      `;
    } else if (faction === '3rd Reich' && this.selectedCampaign === CampaignType.STALINGRAD) {
      content.innerHTML = `
        <h2 style="font-size: 36px; margin-bottom: 30px; color: #708090;">6TH ARMY COMMAND BRIEFING</h2>
        <p style="font-size: 18px; margin-bottom: 20px; font-style: italic; color: #ddd;">
          Date: December 15, 1942<br/>
          Location: Stalingrad Region<br/>
          Operation: Fall Blau - Phase III
        </p>
        <div style="text-align: left; font-size: 16px; line-height: 1.8; color: #ccc;">
          <p style="margin-bottom: 20px;">
            <strong>SITUATION:</strong> 6th Army advancing on Stalingrad as ordered. Winter 
            conditions deteriorating. Supply lines stretched but operational. Luftwaffe maintains 
            air bridge from Gumrak. Soviet resistance stiffening.
          </p>
          <p style="margin-bottom: 20px;">
            <strong>ENEMY FORCES:</strong> Red Army forces of unknown strength defending western 
            approaches. Intelligence reports massive Soviet buildup beyond Volga. Enemy morale 
            high despite recent defeats.
          </p>
          <p style="margin-bottom: 30px; font-weight: bold; color: #708090;">
            <strong>MISSION:</strong> Capture Stalingrad before full winter sets in. Secure all 
            strategic towns to protect flanks. Maintain supply lines at all costs. The Führer 
            demands victory!
          </p>
        </div>
      `;
    } else if (faction === 'United States Of America' && this.selectedCampaign === CampaignType.OKINAWA) {
      content.innerHTML = `
        <h2 style="font-size: 36px; margin-bottom: 30px; color: #4B5320;">US MARINES COMMAND BRIEFING</h2>
        <p style="font-size: 18px; margin-bottom: 20px; font-style: italic; color: #ddd;">
          Date: April 1, 1945<br/>
          Location: Okinawa, Ryukyu Islands<br/>
          Operation: Iceberg
        </p>
        <div style="text-align: left; font-size: 16px; line-height: 1.8; color: #ccc;">
          <p style="margin-bottom: 20px;">
            <strong>SITUATION:</strong> Largest amphibious assault in Pacific War. Okinawa is the 
            final stepping stone before Operation Downfall - the invasion of mainland Japan. 
            Japanese 32nd Army has turned the island into a fortress with elaborate cave systems 
            and defensive positions. Hagushi Beaches secured with lighter resistance than expected.
          </p>
          <p style="margin-bottom: 20px;">
            <strong>ENEMY FORCES:</strong> General Ushijima commands 130,000 defenders including 
            regular army, naval forces, and civilian militia. Enemy has adopted defense-in-depth 
            tactics centered on Shuri Castle. Coral ridges and cave networks provide natural 
            fortifications. Expect fanatical resistance and kamikaze attacks.
          </p>
          <p style="margin-bottom: 30px; font-weight: bold; color: #4B5320;">
            <strong>MISSION:</strong> Secure Kadena and Yontan airfields. Assault the Shuri Line. 
            Eliminate all Japanese resistance on Okinawa. The eyes of the world are upon us!
          </p>
        </div>
      `;
    } else if (faction === 'Imperial Japan' && this.selectedCampaign === CampaignType.OKINAWA) {
      content.innerHTML = `
        <h2 style="font-size: 36px; margin-bottom: 30px; color: #DC143C;">IMPERIAL JAPANESE ARMY BRIEFING</h2>
        <p style="font-size: 18px; margin-bottom: 20px; font-style: italic; color: #ddd;">
          Date: April 1, 1945<br/>
          Location: Okinawa 32nd Army HQ<br/>
          Commander: General Ushijima
        </p>
        <div style="text-align: left; font-size: 16px; line-height: 1.8; color: #ccc;">
          <p style="margin-bottom: 20px;">
            <strong>SITUATION:</strong> American invasion force has landed on Hagushi Beaches. 
            Main defensive line established around Shuri Castle. Defense-in-depth strategy 
            implemented with coral ridges and cave positions. No naval support or air cover 
            available. This is the final battle for the homeland.
          </p>
          <p style="margin-bottom: 20px;">
            <strong>ENEMY FORCES:</strong> Combined US Army and Marine forces in overwhelming 
            numbers with massive naval gunfire support. Enemy possesses unlimited ammunition 
            and supplies. They underestimate our determination.
          </p>
          <p style="margin-bottom: 30px; font-weight: bold; color: #DC143C;">
            <strong>MISSION:</strong> Hold the Shuri Line at all costs. Bleed the enemy white. 
            Every day we resist brings honor to the Emperor and delays their mainland assault!
          </p>
        </div>
      `;
    } else if (faction === 'Canada' && this.selectedCampaign === CampaignType.CAMPOBASSO) {
      content.innerHTML = `
        <h2 style="font-size: 36px; margin-bottom: 30px; color: #D2691E;">1ST CANADIAN DIVISION BRIEFING</h2>
        <p style="font-size: 18px; margin-bottom: 20px; font-style: italic; color: #ddd;">
          Date: October 1, 1943<br/>
          Location: Vinchiaturo Staging Area<br/>
          Operation: Molise Campaign
        </p>
        <div style="text-align: left; font-size: 16px; line-height: 1.8; color: #ccc;">
          <p style="margin-bottom: 20px;">
            <strong>SITUATION:</strong> Following the Italian armistice, German forces have 
            withdrawn to defensive positions in the Apennine Mountains. Italian garrison forces 
            remain in Campobasso, their intentions unclear. October rains have turned valleys 
            into mudfields. Vinchiaturo secured as forward operating base.
          </p>
          <p style="margin-bottom: 20px;">
            <strong>ENEMY FORCES:</strong> Italian mountain troops control the ridgelines and 
            Campobasso stronghold. These are not the demoralized forces from Sicily - expect 
            skilled mountain warfare specialists defending their homeland. Terrain heavily favors 
            the defender.
          </p>
          <p style="margin-bottom: 30px; font-weight: bold; color: #D2691E;">
            <strong>MISSION:</strong> Advance north through the mountains and capture Campobasso. 
            Secure all strategic ridgelines and valley towns. This is Canada's first major 
            independent operation - make the Dominion proud!
          </p>
        </div>
      `;
    } else if (faction === 'Fascist Italy' && this.selectedCampaign === CampaignType.CAMPOBASSO) {
      content.innerHTML = `
        <h2 style="font-size: 36px; margin-bottom: 30px; color: #009246;">ITALIAN ARMY COMMAND BRIEFING</h2>
        <p style="font-size: 18px; margin-bottom: 20px; font-style: italic; color: #ddd;">
          Date: October 1, 1943<br/>
          Location: Campobasso HQ, Molise<br/>
          Commanding: Local Defense Forces
        </p>
        <div style="text-align: left; font-size: 16px; line-height: 1.8; color: #ccc;">
          <p style="margin-bottom: 20px;">
            <strong>SITUATION:</strong> Political chaos reigns. Some units have sided with the 
            Allies, others with the Germans. We stand alone, defending our homes in the mountains 
            of Molise. Canadian forces have occupied Vinchiaturo and threaten our positions. 
            Autumn rains provide some protection.
          </p>
          <p style="margin-bottom: 20px;">
            <strong>ENEMY FORCES:</strong> Canadian 1st Division - fresh troops eager to prove 
            themselves. They have superior equipment and supplies but lack experience in mountain 
            warfare. The terrain is our greatest ally.
          </p>
          <p style="margin-bottom: 30px; font-weight: bold; color: #009246;">
            <strong>MISSION:</strong> Hold Campobasso and the mountain passes. Use knowledge of 
            local terrain to maximum advantage. This is not about politics - this is about 
            defending our homeland! Per l'onore d'Italia!
          </p>
        </div>
      `;
    } else if (faction === 'France' && this.selectedCampaign === CampaignType.FRANCE_1940) {
      content.innerHTML = `
        <h2 style="font-size: 36px; margin-bottom: 30px; color: #7A9BB8;">FRENCH ARMY COMMAND BRIEFING</h2>
        <p style="font-size: 18px; margin-bottom: 20px; font-style: italic; color: #ddd;">
          Date: May 10, 1940<br/>
          Location: French High Command, Paris<br/>
          Alert Status: Maximum
        </p>
        <div style="text-align: left; font-size: 16px; line-height: 1.8; color: #ccc;">
          <p style="margin-bottom: 20px;">
            <strong>SITUATION:</strong> German forces have initiated offensive operations through 
            Belgium and the Ardennes. Our forces control Paris and major cities across northern 
            France. The Maginot Line holds firm but enemy movements suggest unexpected attack vectors.
          </p>
          <p style="margin-bottom: 20px;">
            <strong>ENEMY FORCES:</strong> Wehrmacht forces advancing through the Ardennes at Sedan. 
            This was considered impassable for armor. Enemy panzer divisions moving with unprecedented 
            speed. Luftwaffe achieving local air superiority.
          </p>
          <p style="margin-bottom: 30px; font-weight: bold; color: #7A9BB8;">
            <strong>MISSION:</strong> Defend the homeland! Hold all strategic cities and prevent 
            German breakthrough. Rally our forces for counterattack. The Republic depends on you. 
            Pour la France!
          </p>
        </div>
      `;
    } else if (faction === 'Greece' && this.selectedCampaign === CampaignType.ALBANIA_1940) {
      content.innerHTML = `
        <h2 style="font-size: 36px; margin-bottom: 30px; color: #0072CE;">HELLENIC ARMY HIGH COMMAND</h2>
        <p style="font-size: 18px; margin-bottom: 20px; font-style: italic; color: #ddd;">
          Date: October 28, 1940<br/>
          Location: Ioannina, Northern Greece<br/>
          Alert Status: WAR DECLARED
        </p>
        <div style="text-align: left; font-size: 16px; line-height: 1.8; color: #ccc;">
          <p style="margin-bottom: 20px;">
            <strong>SITUATION:</strong> At 05:30 this morning, Mussolini's forces crossed our border 
            from Albania without provocation. We control Ioannina and key defensive positions along 
            the frontier. The Italian ultimatum has been rejected - "OXI!" rings through the streets.
          </p>
          <p style="margin-bottom: 20px;">
            <strong>ENEMY FORCES:</strong> Italian divisions advancing from Albania, expecting easy victory. 
            Their forces are numerous but poorly prepared for mountain warfare. Winter approaches and 
            they underestimate Greek resolve. This is their fatal mistake.
          </p>
          <p style="margin-bottom: 30px; font-weight: bold; color: #0072CE;">
            <strong>MISSION:</strong> Defend Hellas! Stop the invasion, then counter-attack into Albania. 
            Show the world that David can defeat Goliath. Our Evzones know these mountains - use terrain 
            to our advantage. For Greece and freedom! Ζήτω η Ελλάς!
          </p>
        </div>
      `;
    } else if (faction === 'Fascist Italy' && this.selectedCampaign === CampaignType.ALBANIA_1940) {
      content.innerHTML = `
        <h2 style="font-size: 36px; margin-bottom: 30px; color: #009246;">ITALIAN EXPEDITIONARY FORCE</h2>
        <p style="font-size: 18px; margin-bottom: 20px; font-style: italic; color: #ddd;">
          Date: October 28, 1940<br/>
          Location: Tirana, Albania<br/>
          Operation: Emergenza G (Emergency Greece)
        </p>
        <div style="text-align: left; font-size: 16px; line-height: 1.8; color: #ccc;">
          <p style="margin-bottom: 20px;">
            <strong>SITUATION:</strong> Il Duce has ordered immediate invasion of Greece. We control 
            Albania and must advance quickly before winter sets in. Greek forces are minimal - this 
            should be a swift victory to match German successes.
          </p>
          <p style="margin-bottom: 20px;">
            <strong>ENEMY FORCES:</strong> Greek army is small and poorly equipped. They have one outpost 
            at Ioannina blocking our advance. Intelligence suggests low morale and outdated equipment. 
            However, terrain favors defense - speed is essential.
          </p>
          <p style="margin-bottom: 30px; font-weight: bold; color: #009246;">
            <strong>MISSION:</strong> Conquer Greece within weeks! Capture all Greek cities and secure 
            the Balkans for the Axis. Our Alpini excel in mountains - use them wisely. Show Berlin that 
            Rome still knows how to conquer! Avanti Savoia!
          </p>
        </div>
      `;
    } else if (faction === '3rd Reich' && this.selectedCampaign === CampaignType.FRANCE_1940) {
      content.innerHTML = `
        <h2 style="font-size: 36px; margin-bottom: 30px; color: #708090;">WEHRMACHT COMMAND BRIEFING</h2>
        <p style="font-size: 18px; margin-bottom: 20px; font-style: italic; color: #ddd;">
          Date: May 10, 1940<br/>
          Location: German High Command<br/>
          Operation: Fall Gelb (Case Yellow)
        </p>
        <div style="text-align: left; font-size: 16px; line-height: 1.8; color: #ccc;">
          <p style="margin-bottom: 20px;">
            <strong>SITUATION:</strong> Operation Fall Gelb has commenced. Our forces have secured 
            Charleville as a staging point for the Ardennes offensive. French forces hold defensive 
            positions throughout northern France. Speed is essential.
          </p>
          <p style="margin-bottom: 20px;">
            <strong>ENEMY FORCES:</strong> French armies positioned along expected invasion routes. 
            The enemy expects us through Belgium. They consider the Ardennes impassable for our 
            panzer divisions. This miscalculation will be their downfall.
          </p>
          <p style="margin-bottom: 30px; font-weight: bold; color: #708090;">
            <strong>MISSION:</strong> Execute Manstein Plan. Break through at Sedan and advance 
            rapidly to cut off Allied forces. Capture all strategic cities before enemy can 
            reorganize. Victory through Blitzkrieg!
          </p>
        </div>
      `;
    }
    
    briefingOverlay.appendChild(content);
    document.body.appendChild(briefingOverlay);
    
    // Fade in overlay
    requestAnimationFrame(() => {
      briefingOverlay.style.opacity = '1';
      setTimeout(() => {
        content.style.opacity = '1';
        // Call callback after briefing is visible
        if (onReady) {
          onReady();
        }
      }, 500);
    });
    
    // Add continue button after delay
    setTimeout(() => {
      const continueBtn = document.createElement('button');
      continueBtn.textContent = 'Begin Campaign';
      const isMobile = window.innerWidth <= 812 || window.innerHeight <= 812;
      continueBtn.style.cssText = `
        margin-top: ${isMobile ? '20px' : '40px'};
        padding: ${isMobile ? '12px 30px' : '15px 40px'};
        font-size: ${isMobile ? '16px' : '20px'};
        background: #4a4a4a;
        color: white;
        border: 2px solid #666;
        border-radius: 5px;
        cursor: pointer;
        opacity: 0;
        transition: all 0.3s ease;
        flex-shrink: 0;
      `;
      
      continueBtn.onmouseover = () => {
        this.soundManager.playMenuHover();
        continueBtn.style.background = '#5a5a5a';
        continueBtn.style.borderColor = '#888';
      };
      
      continueBtn.onmouseout = () => {
        continueBtn.style.background = '#4a4a4a';
        continueBtn.style.borderColor = '#666';
      };
      
      continueBtn.onclick = () => {
        this.soundManager.playMenuSelect();
        briefingOverlay.style.opacity = '0';
        setTimeout(() => {
          briefingOverlay.remove();
          // Initialize game after briefing
          this.initializeGame();
        }, 1000);
      };
      
      content.appendChild(continueBtn);
      
      // Fade in button
      setTimeout(() => {
        continueBtn.style.opacity = '1';
      }, 100);
    }, 3000);
  }
  
  private initOnlineGame(menuOverlay: HTMLElement): void {
    // Show faction briefing for online games too
    // Show briefing for the player's faction, not the current turn faction
    const briefingFaction = this.humanPlayerFaction || this.currentFaction;
    this.showFactionBriefing(briefingFaction, () => {
      // Remove menu overlay after briefing
      menuOverlay.remove();
      
      // Initialize game components
      this.initializeGame();
      
      // If host, wait for units to be created then start the game
      if (this.multiplayerService!.isHost()) {
        // Give the game time to create all units
        setTimeout(() => {
          debug.log('Host: Units in gameState:', this.gameState.units.size);
          debug.log('Host: Units in UnitManager:', this.unitManager.getAllUnits().length);
          const allUnits = this.unitManager.getAllUnits();
          allUnits.forEach(unit => {
            debug.log(`  Unit: ${unit.name} (${unit.faction}) at (${unit.position.x}, ${unit.position.y})`);
          });
          
          // Start the game with the complete gameState including map
          const completeGameState = this.buildCompleteGameState();
          debug.log('Host: Sending game state with', completeGameState.units.size, 'units to Firebase');
          
          this.multiplayerService!.startGame(completeGameState).then(() => {
            debug.log('Host: Game started in Firebase, setting up sync');
            // Set up sync AFTER game is started in Firebase
            this.setupOnlineSync();
          }).catch(error => {
            debug.error('Failed to start game:', error);
          });
          
          // Update UI
          this.updateOnlineUI();
        }, 1000); // Increased delay to ensure units are created
      } else {
        // Guest: Listen for room status to become 'playing'
        // This ensures the host has sent the initial game state
        debug.log('Guest: Waiting for host to start the game...');
        
        // Set up listener for room updates
        this.multiplayerService!.onRoomUpdate((room) => {
          if (room.status === 'playing' && !this.initialSyncComplete) {
            debug.log('Guest: Room is now playing, setting up sync');
            this.setupOnlineSync();
            this.updateOnlineUI();
          }
        });
        
        // Also set up sync immediately in case room is already playing (reconnection scenario)
        const currentRoom = this.multiplayerService!.getCurrentRoom();
        if (currentRoom && currentRoom.status === 'playing') {
          debug.log('Guest: Room already playing, setting up sync immediately');
          this.setupOnlineSync();
          this.updateOnlineUI();
        }
      }
    });
  }
  
  private initialSyncComplete = false;
  
  private lastProcessedTurn = -1;  // Track which turn we've already processed
  private lastGameStateTimestamp = 0;  // Track when we last sent a game state to Firebase
  
  private setupOnlineSync(): void {
    if (!this.multiplayerService) return;
    
    // Listen for game state updates
    this.multiplayerService.onGameStateUpdate((gameState) => {
      
      // gameState.units is already a Map because deserializeGameState converts it
      const hasUnits = gameState.units && gameState.units.size > 0;
      
      if (hasUnits) {
        const receivedTimestamp = gameState.stateTimestamp || 0;
        debug.log(`[FIREBASE RECV] Timestamp: ${receivedTimestamp}, Units: ${gameState.units.size}, Last sent: ${this.lastGameStateTimestamp}`);
        
        // Check if this is an old state
        if (receivedTimestamp < this.lastGameStateTimestamp) {
          debug.warn(`[FIREBASE RECV] Received OLDER state (${receivedTimestamp} < ${this.lastGameStateTimestamp}), may overwrite newer data!`);
        }
        
        // Log unit IDs in received state
        const receivedUnitIds = Array.from(gameState.units.keys());
        debug.log(`[FIREBASE RECV] Unit IDs in received state:`, receivedUnitIds);
        
        // Log detailed unit info
        gameState.units.forEach((unit: Unit) => {
          debug.log(`  [FIREBASE RECV] Unit: ${unit.name} (${unit.faction}) at (${unit.position.x}, ${unit.position.y})`);
        });
        
        // Check if guest's faction has units
        if (!this.multiplayerService!.isHost()) {
          const myFaction = this.humanPlayerFaction;
          const myUnits = (Array.from(gameState.units.values()) as Unit[]).filter((u: Unit) => u.faction === myFaction);
          debug.log(`[GUEST] My faction is ${myFaction}, I have ${myUnits.length} units`);
          if (myUnits.length === 0) {
            debug.error(`[GUEST] ERROR: No units for my faction ${myFaction}!`);
          }
        }
        
        this.initialSyncComplete = true;  // Mark as complete when we get a valid state
      } else if (!this.initialSyncComplete) {
        // Only skip empty states during initialization
        debug.log('Received empty game state during initialization, skipping');
        return;
      }
      
      // Update local game state from Firebase
      // gameState.units and resources are already Maps from deserializeGameState
      this.gameState.map = gameState.map;
      
      this.gameState.units = gameState.units || new Map();
      this.gameState.resources = gameState.resources || new Map();
      this.gameState.battleStats = gameState.battleStats || new Map();
      this.gameState.currentFaction = gameState.currentFaction;
      this.gameState.turn = gameState.turn;
      
      // Update currentFaction to track whose turn it is
      this.currentFaction = gameState.currentFaction;
      
      // CRITICAL: Update the actual map tiles and cities from the synced state
      // This ensures city captures and capture progress are reflected
      if (gameState.map) {
        // Update each tile in the map
        for (let y = 0; y < gameState.map.length; y++) {
          for (let x = 0; x < gameState.map[y].length; x++) {
            const syncedTile = gameState.map[y][x];
            const localTile = this.map.getTile(x, y);
            if (localTile && syncedTile) {
              // Update the tile properties
              localTile.terrain = syncedTile.terrain;
              
              // Update mine if present
              if (syncedTile.mine) {
                localTile.mine = syncedTile.mine;
              } else {
                localTile.mine = undefined;
              }
              
              // Update city if present
              if (syncedTile.city) {
                // Update the cities Map in GameMap (this is the source of truth)
                const cities = this.map.getCities();
                if (cities.has(syncedTile.city.name)) {
                  const mapCity = cities.get(syncedTile.city.name)!;
                  
                  // Debug logging for training queue sync
                  if (syncedTile.city.name && mapCity.trainingQueue && mapCity.trainingQueue.length > 0) {
                    debug.log(`Syncing city ${syncedTile.city.name}: Local queue has ${mapCity.trainingQueue.length} units, synced queue has ${syncedTile.city.trainingQueue?.length || 0} units`);
                  }
                  
                  // Log capture progress changes
                  if (mapCity.captureProgress !== syncedTile.city.captureProgress) {
                    debug.log(`City ${mapCity.name} captureProgress changing from`, mapCity.captureProgress, 'to', syncedTile.city.captureProgress);
                  }
                  
                  // Full sync from Firebase - no protection needed since we process AFTER sync
                  mapCity.faction = syncedTile.city.faction;
                  mapCity.captureProgress = syncedTile.city.captureProgress;
                  mapCity.trainingQueue = syncedTile.city.trainingQueue || [];
                  // Clean up any placeholder queue IDs when syncing
                  mapCity.unitsProduced = (syncedTile.city.unitsProduced || []).filter((id: string) => !id.startsWith('queue_'));
                  mapCity.level = syncedTile.city.level || CityLevel.OUTPOST;
                  mapCity.maxUnits = getCityMaxUnits(mapCity.level);
                  mapCity.upgradeInProgress = syncedTile.city.upgradeInProgress;
                  
                  // Keep the tile's city reference pointing to the same object in the cities Map
                  // Don't replace it with syncedTile.city
                  localTile.city = mapCity;
                }
              }
            }
          }
        }
      }
      
      // Update turn counter UI
      const turnCounterElement = document.getElementById('turn-counter');
      if (turnCounterElement) {
        turnCounterElement.textContent = `Turn ${this.gameState.turn}`;
      }
      
      // Update resources display
      this.updateResourceDisplay();
      
      // Check if turn changed
      const wasMyTurn = this.isMyTurn;
      // In online games, check against humanPlayerFaction, not currentFaction
      this.isMyTurn = this.isOnlineGame 
        ? (gameState.currentFaction === this.humanPlayerFaction)
        : (gameState.currentFaction === this.currentFaction);
      
      // Handle turn change
      if (wasMyTurn !== this.isMyTurn) {
        if (this.isMyTurn) {
          // Check if we've already processed this turn to prevent duplicates
          if (this.lastProcessedTurn === this.gameState.turn) {
            debug.log(`Already processed turn ${this.gameState.turn}, skipping duplicate processing`);
            return;
          }
          
          // It's now our turn - process immediately
          debug.log(`Turn changed to mine (${this.humanPlayerFaction}), processing turn start actions`);
          
          // Mark this turn as processed
          this.lastProcessedTurn = this.gameState.turn;
          
          // Process immediately - no delay needed
          debug.log(`Processing turn start actions for ${this.humanPlayerFaction} at turn ${this.gameState.turn}`);
          
          // CRITICAL: Reset movement for the faction whose turn it is (should be our faction)
          const allUnits = this.unitManager.getAllUnits();
          const factionWhoseTurnItIs = this.gameState.currentFaction;
          debug.log(`Resetting movement for ${factionWhoseTurnItIs} units at start of turn ${this.gameState.turn}`);
          let resetCount = 0;
          allUnits.forEach(unit => {
            if (unit.faction === factionWhoseTurnItIs) {
              const oldMovement = unit.movementRemaining;
              unit.movementRemaining = unit.stats.movement;
              unit.hasAttacked = false;
              (unit as any).hunkerChangedThisTurn = false;
              // Reset engineer/medic one-per-turn actions
              (unit as any).hasPlacedMine = false;
              (unit as any).hasHealed = false;
              // Reset transport/towing one-per-turn actions
              unit.hasBoarded = false;
              unit.hasTowed = false;
              resetCount++;
              debug.log(`  Reset ${unit.name}: movement ${oldMovement} -> ${unit.movementRemaining}, hasBoarded: ${unit.hasBoarded}, hasTowed: ${unit.hasTowed}`);
              
              // Also update in gameState.units to ensure sync
              if (this.gameState.units.has(unit.id)) {
                const gameStateUnit = this.gameState.units.get(unit.id)!;
                gameStateUnit.movementRemaining = unit.movementRemaining;
                gameStateUnit.hasAttacked = false;
                gameStateUnit.hasBoarded = false;
                gameStateUnit.hasTowed = false;
                (gameStateUnit as any).hunkerChangedThisTurn = false;
                (gameStateUnit as any).hasPlacedMine = false;
                (gameStateUnit as any).hasHealed = false;
              }
            }
          });
          debug.log(`Reset movement for ${resetCount} ${factionWhoseTurnItIs} units`);
          
          // Clear notification tracking for new turn
          this.processedTrainingThisTurn = new Set();
          
          // Process city captures at the START of our turn
          this.processCityCaptures(this.currentFaction);
          
          // Check for any captures that can start
          this.checkForNewCaptures();
          
          // Process city upgrades at the START of our turn
          this.processCityUpgrades(this.currentFaction);
          
          // Process training queues at the START of our turn
          debug.log(`About to call processTrainingQueues for ${this.currentFaction} at turn ${this.gameState.turn}`);
          this.processTrainingQueues(this.currentFaction);
          
          // CRITICAL: Sync IMMEDIATELY after processing, BEFORE any notifications
          // Notifications trigger Firebase updates that can remove units!
          if (this.multiplayerService) {
            debug.log('Syncing state IMMEDIATELY after processing training and captures');
            const completeGameState = this.buildCompleteGameState();
            this.multiplayerService.updateGameState(
              completeGameState,
              this.gameState.currentFaction,
              this.gameState.turn
            );
          }
          
          // Update notification manager faction if needed
          if (this.notificationManager) {
            this.notificationManager.setViewerFaction(this.currentFaction);
          }
          
          // NOW send queued notifications after sync is complete
          if (this.pendingNotifications && this.pendingNotifications.length > 0) {
            debug.log(`[SENDING QUEUED NOTIFICATIONS] ${this.pendingNotifications.length} notifications`);
            this.pendingNotifications.forEach((notification, index) => {
              debug.log(`  [QUEUED ${index+1}] Type: ${notification.type}, Data:`, notification.data);
              this.sendNotification(notification);
            });
            this.pendingNotifications = [];
          }
          
          this.hideEnemyTurnIndicator();
          this.showNotification("It's your turn!");
          // Update turn state UI
          this.updateTurnState();
        } else {
          // It's now enemy's turn
          debug.log(`Turn changed to opponent (${this.gameState.currentFaction}), ensuring their units have movement`);
          
          // CRITICAL: Reset movement for opponent's units when their turn starts
          // This ensures that even after reloading, the opponent gets their movement
          const allUnits = this.unitManager.getAllUnits();
          const factionWhoseTurnItIs = this.gameState.currentFaction;
          debug.log(`Resetting movement for opponent ${factionWhoseTurnItIs} units at start of turn ${this.gameState.turn}`);
          let resetCount = 0;
          allUnits.forEach(unit => {
            if (unit.faction === factionWhoseTurnItIs) {
              const oldMovement = unit.movementRemaining;
              unit.movementRemaining = unit.stats.movement;
              unit.hasAttacked = false;
              (unit as any).hunkerChangedThisTurn = false;
              // Reset engineer/medic one-per-turn actions
              (unit as any).hasPlacedMine = false;
              (unit as any).hasHealed = false;
              // Reset transport/towing one-per-turn actions
              unit.hasBoarded = false;
              unit.hasTowed = false;
              resetCount++;
              debug.log(`  Reset opponent unit ${unit.name}: movement ${oldMovement} -> ${unit.movementRemaining}, hasBoarded: ${unit.hasBoarded}, hasTowed: ${unit.hasTowed}`);
              
              // Also update in gameState.units to ensure sync
              if (this.gameState.units.has(unit.id)) {
                const gameStateUnit = this.gameState.units.get(unit.id)!;
                gameStateUnit.movementRemaining = unit.movementRemaining;
                gameStateUnit.hasAttacked = false;
                gameStateUnit.hasBoarded = false;
                gameStateUnit.hasTowed = false;
                (gameStateUnit as any).hunkerChangedThisTurn = false;
                (gameStateUnit as any).hasPlacedMine = false;
                (gameStateUnit as any).hasHealed = false;
              }
            }
          });
          debug.log(`Reset movement for ${resetCount} opponent ${factionWhoseTurnItIs} units`);
          
          this.showEnemyTurnIndicator();
          // Update turn state UI
          this.updateTurnState();
        }
      }
      
      // Intelligently refresh units - only update what has changed
      const currentUnitIds = new Set(this.unitManager.getAllUnits().map(u => u.id));
      const newUnitIds = new Set(this.gameState.units.keys());
      
      debug.log(`[UNIT SYNC] Current units in UnitManager: ${currentUnitIds.size}, New units from Firebase: ${newUnitIds.size}`);
      
      // Log significant changes
      const unitsToRemove = Array.from(currentUnitIds).filter(id => !newUnitIds.has(id));
      const unitsToAdd = Array.from(newUnitIds).filter(id => !currentUnitIds.has(id));
      
      if (unitsToRemove.length > 0) {
        debug.warn(`Removing ${unitsToRemove.length} units from sync:`, unitsToRemove);
      }
      if (unitsToAdd.length > 0) {
        debug.log(`Adding ${unitsToAdd.length} units from sync:`, unitsToAdd);
      }
      
      // Remove units that no longer exist
      currentUnitIds.forEach(id => {
        if (!newUnitIds.has(id)) {
          const unit = this.unitManager.getUnit(id);
          if (unit) {
            debug.warn(`[FIREBASE SYNC] Removing unit ${unit.name} (${id}) at position ${unit.position.x},${unit.position.y}`);
            debug.warn(`  Reason: Unit exists locally but not in Firebase sync`);
            debug.warn(`  Current turn: ${this.gameState.turn}, Current faction: ${this.gameState.currentFaction}`);
            debug.warn(`  Unit faction: ${unit.faction}, Unit type: ${unit.type}`);
            console.trace('  Removal triggered from:');
            this.unitManager.removeUnit(id);
          }
        }
      });
      
      // Add or update units
      this.gameState.units.forEach(unit => {
        const existingUnit = this.unitManager.getUnit(unit.id);
        if (!existingUnit) {
          // New unit, add it
          debug.log(`Adding new unit ${unit.name} (${unit.id}) at position ${unit.position.x},${unit.position.y}`);
          this.unitManager.addExistingUnit(unit);
        } else {
          // Check if position actually changed (for remote moves)
          const positionChanged = existingUnit.position.x !== unit.position.x || 
                                 existingUnit.position.y !== unit.position.y;
          
          // Store display position before updating
          const oldDisplayPos = existingUnit.displayPosition;
          const oldTargetPos = existingUnit.targetPosition;
          const oldAnimStartTime = existingUnit.animationStartTime;
          
          // Store hunker flags before updating (preserve if not in synced data)
          const oldIsHunkered = (existingUnit as any).isHunkered;
          const oldHunkerChangedThisTurn = (existingUnit as any).hunkerChangedThisTurn;
          
          // Store transport data before updating
          const oldCarryingUnit = existingUnit.carryingUnit;
          const oldCarryingUnitData = existingUnit.carryingUnitData;
          const oldCarryingUnits = existingUnit.carryingUnits;
          const oldCarryingUnitsData = existingUnit.carryingUnitsData;
          const oldIsTransport = existingUnit.isTransport;
          
          // Update all properties from Firebase
          Object.assign(existingUnit, unit);
          
          // Restore animation properties (they don't sync through Firebase)
          existingUnit.displayPosition = oldDisplayPos;
          existingUnit.targetPosition = oldTargetPos;
          existingUnit.animationStartTime = oldAnimStartTime;
          
          // Restore transport data if present in synced data, otherwise keep old
          if (unit.carryingUnit !== undefined) {
            existingUnit.carryingUnit = unit.carryingUnit;
          } else if (oldCarryingUnit !== undefined) {
            existingUnit.carryingUnit = oldCarryingUnit;
          }
          
          if (unit.carryingUnitData !== undefined) {
            existingUnit.carryingUnitData = unit.carryingUnitData;
          } else if (oldCarryingUnitData !== undefined) {
            existingUnit.carryingUnitData = oldCarryingUnitData;
          }
          
          if (unit.carryingUnits !== undefined) {
            existingUnit.carryingUnits = unit.carryingUnits;
          } else if (oldCarryingUnits !== undefined) {
            existingUnit.carryingUnits = oldCarryingUnits;
          }
          
          if (unit.carryingUnitsData !== undefined) {
            existingUnit.carryingUnitsData = unit.carryingUnitsData;
          } else if (oldCarryingUnitsData !== undefined) {
            existingUnit.carryingUnitsData = oldCarryingUnitsData;
          }
          
          if (unit.isTransport !== undefined) {
            existingUnit.isTransport = unit.isTransport;
          } else if (oldIsTransport !== undefined) {
            existingUnit.isTransport = oldIsTransport;
          }
          
          // Restore hunker flags if they weren't included in the sync
          if ((unit as any).isHunkered === undefined) {
            (existingUnit as any).isHunkered = oldIsHunkered;
          }
          if ((unit as any).hunkerChangedThisTurn === undefined) {
            (existingUnit as any).hunkerChangedThisTurn = oldHunkerChangedThisTurn;
          }
          
          // If this is a remote move (position changed), update visual immediately
          if (positionChanged) {
            debug.log(`Remote unit ${unit.id} moved to (${unit.position.x},${unit.position.y}), updating visual position`);
            existingUnit.displayPosition = { x: unit.position.x, y: unit.position.y };
            existingUnit.targetPosition = undefined;
            existingUnit.animationStartTime = undefined;
          }
          // If position didn't change, this is our own move echoing back - keep the animation
        }
      });
      
      // Update turn display
      this.updateTurnState();
      
      // Update turn counter
      const turnCounter = document.getElementById('turn-counter');
      if (turnCounter) {
        turnCounter.textContent = `Turn ${this.gameState.turn}`;
      }
      
      // Update city count to reflect any captures
      this.updateCityCount();
      
      // Check victory conditions in case game is over
      this.checkVictoryConditions();
    });
    
    // We no longer use individual moves - everything syncs through game state updates
    
    // Listen for disconnections
    this.multiplayerService.onPlayerDisconnect(() => {
      this.showNotification(`Opponent disconnected. Waiting for reconnection...`);
    });
    
    // Listen for room updates
    this.multiplayerService.onRoomUpdate((room) => {
      // Check if game ended
      if (room.status === 'finished' && room.winner) {
        this.handleOnlineGameEnd(room.winner);
      }
    });
    
    // Listen for notifications from other players
    if (this.notificationManager) {
      this.multiplayerService.onNotification((notification) => {
        debug.log(`[FIREBASE NOTIFICATION RECEIVED] Type: ${notification.type}, Faction: ${notification.faction}`);
        // Process ALL notifications from Firebase (including our own)
        // This is the only place online game notifications are shown
        const event: NotificationEvent = {
          type: notification.type as any,
          faction: notification.faction,
          targetFaction: notification.targetFaction,
          data: notification.data,
          timestamp: notification.timestamp
        };
        
        // Show the notification - this is the ONLY place online notifications are displayed
        // Check if this event is from the viewer's faction (not current turn faction)
        this.notificationManager!.processEvent(event, event.faction === this.humanPlayerFaction);
      });
    }
  }
  
  private updateTurnState(): void {
    // Update UI to show whose turn it is
    // No visual changes needed - the turn indicator handles this
  }
  
  private updateOnlineUI(): void {
    // Add online game indicator
    const statusBar = document.getElementById('status-bar');
    if (statusBar) {
      const onlineIndicator = document.createElement('div');
      onlineIndicator.id = 'online-indicator';
      onlineIndicator.style.cssText = `
        position: absolute;
        top: 10px;
        right: 10px;
        padding: 5px 10px;
        background: #4CAF50;
        color: white;
        border-radius: 5px;
        font-size: 14px;
      `;
      onlineIndicator.textContent = '🌐 Online Match';
      statusBar.appendChild(onlineIndicator);
    }
    
    // Update turn indicator for online games
    this.updateTurnState();
    
    // Show enemy turn indicator if it's not our turn
    if (!this.isMyTurn) {
      this.showEnemyTurnIndicator();
    }
  }
  
  private showEnemyTurnIndicator(): void {
    // Remove any existing indicator
    const existingIndicator = document.getElementById('enemy-turn-indicator');
    if (existingIndicator) {
      existingIndicator.remove();
    }
    
    // Create enemy turn indicator (same style as AI turn indicator)
    const indicator = document.createElement('div');
    indicator.id = 'enemy-turn-indicator';
    indicator.style.cssText = `
      position: fixed;
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      background: rgba(180, 30, 30, 0.9);
      color: white;
      padding: 15px 30px;
      border-radius: 5px;
      font-size: 20px;
      font-weight: bold;
      z-index: 10000;
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.5);
    `;
    indicator.textContent = 'ENEMY TURN';
    document.body.appendChild(indicator);
  }
  
  private hideEnemyTurnIndicator(): void {
    const indicator = document.getElementById('enemy-turn-indicator');
    if (indicator) {
      indicator.remove();
    }
  }
  
  private handleOnlineGameEnd(winner: FactionType): void {
    // Check if the game ended due to surrender
    const room = this.multiplayerService?.getCurrentRoom();
    if (room && (room as any).surrendered) {
      const surrenderedPlayer = (room as any).surrendered;
      const didWeSurrender = (surrenderedPlayer === 'host' && this.multiplayerService?.isHost()) ||
                            (surrenderedPlayer === 'guest' && !this.multiplayerService?.isHost());
      
      if (!didWeSurrender) {
        // The opponent surrendered, show a special message
        this.showNotification('Your opponent has surrendered!');
      }
    }
    
    // Use the same detailed victory screen as offline games
    this.showGameOverScreen(winner);
    
    // Clean up multiplayer service after showing the screen
    if (this.multiplayerService) {
      this.multiplayerService.cleanup();
    }
  }
  
  private initializeGame(): void {
    if (this.isInitialized) return;
    this.isInitialized = true;
    // Get canvas
    const canvas = document.getElementById('game-canvas') as HTMLCanvasElement;
    if (!canvas) throw new Error('Canvas not found');
    this.canvas = canvas;

    // Initialize game components with selected campaign map
    this.map = new GameMap(this.selectedCampaign);
    this.renderer = new Renderer(canvas);
    
    // Apply the sprite mode that was loaded in constructor
    this.renderer.setSpriteMode(this.debugSpriteMode);
    
    this.unitManager = new UnitManager();
    // Sound manager already initialized in constructor
    this.visionSystem = new VisionSystem();
    
    // Initialize notification manager for all game modes
    this.notificationManager = new NotificationManager(this.currentFaction, this.soundManager);
    
    // Update date display based on campaign
    const dateDisplay = document.getElementById('date-display');
    if (dateDisplay) {
      dateDisplay.textContent = this.selectedCampaign === CampaignType.TUNISIA ? 'November 1942' : 
                                this.selectedCampaign === CampaignType.STALINGRAD ? 'November 1942' :
                                this.selectedCampaign === CampaignType.OKINAWA ? 'April 1945' :
                                this.selectedCampaign === CampaignType.CAMPOBASSO ? 'October 1943' :
                                this.selectedCampaign === CampaignType.FRANCE_1940 ? 'May 1940' :
                                'June 1944';
    }
    
    // Initialize camera zoomed in on faction's starting city
    const tileSize = 64; // Must match Renderer's tileSize
    // Detect mobile and set appropriate initial zoom
    const isMobile = window.innerWidth <= 812 || window.innerHeight <= 812;
    const isLandscape = window.innerWidth > window.innerHeight;
    const startZoom = isMobile ? (isLandscape ? 0.8 : 0.6) : 1.0; // Better mobile defaults
    
    // Find starting city for current faction
    const cities = this.map.getCities();
    let startingCity = null;
    
    // In online games, center on the player's own faction's city, not whoever's turn it is
    const cameraFaction = this.isOnlineGame ? (this.humanPlayerFaction || this.currentFaction) : this.currentFaction;
    
    for (const city of cities.values()) {
      if (city.faction === cameraFaction) {
        startingCity = city;
        break;
      }
    }
    
    let cameraX = 0;
    let cameraY = 0;
    
    if (startingCity && startingCity.tiles.length > 0) {
      // Center on the starting city
      const centerTile = startingCity.tiles[Math.floor(startingCity.tiles.length / 2)];
      cameraX = canvas.width / 2 - (centerTile.x * tileSize * startZoom);
      cameraY = canvas.height / 2 - (centerTile.y * tileSize * startZoom);
    } else {
      // Fallback to map center if no starting city
      const mapCenterX = this.map.getWidth() / 2;
      const mapCenterY = this.map.getHeight() / 2;
      cameraX = canvas.width / 2 - (mapCenterX * tileSize * startZoom);
      cameraY = canvas.height / 2 - (mapCenterY * tileSize * startZoom);
    }
    
    this.cameraController = new CameraController({ x: cameraX, y: cameraY, zoom: startZoom });
    
    // Set map dimensions for boundary checking
    this.cameraController.setMapSize(this.map.getWidth(), this.map.getHeight());
    
    this.gameState = {
      map: this.map.getTiles(),
      camera: this.cameraController.getCamera(),
      selectedTile: null,
      units: new Map(),
      selectedUnit: null,
      currentFaction: this.currentFaction,
      resources: new Map([
        ['United States Of America', 0],
        ['3rd Reich', 0],
        ['United Kingdom', 0],
        ['USSR', 0],
        ['Imperial Japan', 0],
        ['Canada', 0],
        ['Fascist Italy', 0],
        ['France', 0],
        ['Greece', 0]
      ]),
      turn: 1,
      gameMode: this.selectedGameMode,
      aiControlledFaction: this.selectedGameMode === 'PvE' ? this.secondFaction : undefined,
      battleStats: new Map()
    };
    
    // Initialize battle stats for each faction in this campaign
    this.initializeBattleStats();
    
    // If loading a saved game, restore the full state now that map is initialized
    let wasLoadingSavedGame = false;
    if (this.isLoadingSavedGame) {
      wasLoadingSavedGame = true;
      this.isLoadingSavedGame = false;
      if (!this.loadGameState()) {
        debug.error('Failed to load saved game state');
        // Fall back to creating new game
        this.createStartingUnits();
        this.initializeCityTracking();
        wasLoadingSavedGame = false;
      }
    } else if (this.isLoadingMultiplayerSave) {
      // Loading a multiplayer save - load the local state first
      wasLoadingSavedGame = true;
      this.isLoadingMultiplayerSave = false;
      
      debug.log('Loading multiplayer save from local storage');
      if (!this.loadGameState(99)) {
        debug.error('Failed to load multiplayer saved game state');
        // Fall back to creating new game
        this.createStartingUnits();
        this.initializeCityTracking();
        wasLoadingSavedGame = false;
      }
      // Turn and currentFaction will be updated from Firebase in pendingMultiplayerReconnection
    } else {
      // Create initial units for new game
      // Both host and guest create the same starting units locally
      // This ensures both players have units immediately without sync delays
      debug.log('Creating starting units for all factions');
      this.createStartingUnits();
      debug.log('After createStartingUnits, units in gameState:', this.gameState.units.size);
      debug.log('After createStartingUnits, units in unitManager:', this.unitManager.getAllUnits().length);
      
      // List units per faction
      const unitsByFaction = new Map<string, number>();
      this.unitManager.getAllUnits().forEach(unit => {
        unitsByFaction.set(unit.faction, (unitsByFaction.get(unit.faction) || 0) + 1);
      });
      unitsByFaction.forEach((count, faction) => {
        debug.log(`  ${faction}: ${count} units`);
      });
      
      // Initialize city ownership tracking
      this.initializeCityTracking();
    }
    
    // Track game start in analytics
    if (!wasLoadingSavedGame) {
      this.analytics.trackGameStart({
        campaign: this.selectedCampaign,
        gameMode: this.gameState.gameMode || 'PvP',
        playerFaction: this.humanPlayerFaction || this.currentFaction,
        opponentFaction: this.secondFaction,
        mapWidth: this.map.getWidth(),
        mapHeight: this.map.getHeight(),
        aiDifficulty: this.gameState.gameMode === 'PvE' ? 'normal' : undefined
      });
    }
    
    // === LEGACY AI INITIALIZATION (Commented out for V3 migration) ===
    // Initialize AI controller if in PvE mode (AFTER game state is created) - including tutorial mode
    // if (this.gameState.gameMode === 'PvE' && this.gameState.aiControlledFaction) {
    //   // Use GeneralAI - the new strategic brain that coordinates all tactics
    //   this.aiController = new GeneralAI(
    //     this.gameState.aiControlledFaction,
    //     this.selectedCampaign,
    //     this.gameState,
    //     this.map,
    //     this.unitManager,
    //     this.visionSystem
    //   );
    //   console.log('[Main] GeneralAI initialized - Strategic Brain Active');
    //
    //   // Enable AI production - GeneralAI sets tutorial objectives internally, but
    //   // production should still work in regular PvE games
    //   if (!this.isTutorial) {
    //     this.aiController.enableTutorialProduction();
    //     console.log('[Main] AI production enabled for PvE mode');
    //   }
    //
    //   // Restore AI state if we loaded from a save
    //   if ((this as any).pendingAIState) {
    //     debug.log('Restoring AI state from saved game');
    //     this.aiController.restoreAIState((this as any).pendingAIState);
    //     delete (this as any).pendingAIState;
    //   }
    //
    //   // If we loaded a save during AI's turn, resume AI execution
    //   // Check if current faction is AI-controlled or if isAITurn flag was saved
    //   const isCurrentFactionAI = (this.gameState.gameMode === 'PvE' &&
    //                               this.currentFaction === this.gameState.aiControlledFaction) ||
    //                              this.isAITurn;
    //
    //   // Also check for tutorial mode
    //   const isTutorialAI = this.isTutorial && this.currentFaction !== this.humanPlayerFaction;
    //
    //   debug.log(`AI turn check - wasLoading: ${wasLoadingSavedGame}, currentFaction: ${this.currentFaction}, aiFaction: ${this.gameState.aiControlledFaction}, isAITurn: ${this.isAITurn}, isCurrentFactionAI: ${isCurrentFactionAI}, isTutorialAI: ${isTutorialAI}`);
    //
    //   if (wasLoadingSavedGame && (isCurrentFactionAI || isTutorialAI)) {
    //     debug.log('Resuming AI turn after loading saved game');
    //     // Use a timeout to let the game fully initialize first
    //     setTimeout(() => {
    //       // Both tutorial and regular PvE now use the same AI controller
    //       this.executeAITurn();
    //     }, 500);
    //   }
    // }
    // === END LEGACY AI INITIALIZATION ===

    // V3 AI Initialization - Priority-Based CPU with Minimax Search
    if (this.gameState.gameMode === 'PvE' && this.gameState.aiControlledFaction) {
      const aiFaction = this.gameState.aiControlledFaction;
      const humanFaction = this.humanPlayerFaction || this.currentFaction;

      this.aiController = createMinimaxAI(
        this.gameState,
        this.unitManager,
        this.map,
        aiFaction,
        humanFaction,
        this.selectedCampaign
      );
      console.log(`[V3 AI] MinimaxAI initialized for ${aiFaction} vs ${humanFaction}, campaign: ${this.selectedCampaign}`);
    }
    

    // Set up event listeners
    this.setupEventListeners();
    
    // Ensure sprite mode is applied (already loaded in constructor)
    if (this.renderer) {
      this.renderer.setSpriteMode(this.debugSpriteMode);
    }
    
    // Hide online players display when game starts
    const onlineDisplay = document.getElementById('online-users-display');
    if (onlineDisplay) {
      onlineDisplay.style.display = 'none';
    }
    
    // Initialize faction display
    this.updateFactionDisplay();
    this.updateResourceDisplay();
    this.updateCityCount();
    this.updateTurnCounter();
    
    // Track game start for statistics
    if (!wasLoadingSavedGame) {
      this.gameStartTime = Date.now();
      const opponentFaction = this.selectedGameMode === 'PvP' ? this.secondFaction : 
                             this.gameState.aiControlledFaction || this.secondFaction;
      this.statistics.trackGameStart({
        campaign: this.selectedCampaign,
        playerFaction: this.humanPlayerFaction || this.currentFaction,
        opponentFaction: opponentFaction,
        gameMode: this.selectedGameMode
      });
    }
    
    // Handle pending multiplayer reconnection
    if (this.pendingMultiplayerReconnection) {
      debug.log('Handling multiplayer reconnection...');
      debug.log('Current state - isOnlineGame:', this.isOnlineGame, 'multiplayerService:', !!this.multiplayerService);
      debug.log('humanPlayerFaction:', this.humanPlayerFaction, 'currentFaction:', this.currentFaction);
      
      // IMPORTANT: Only update turn-related fields from Firebase, keep local game state
      const room = this.pendingMultiplayerReconnection.room;
      if (room.gameState) {
        debug.log('Syncing turn information from Firebase...');
        const deserializedState = this.multiplayerService!.deserializeGameState(room.gameState);
        
        // ONLY sync turn-related information from Firebase
        // This prevents the "time rewind" exploit while keeping local game state intact
        const firebaseTurn = deserializedState.turn;
        const firebaseCurrentFaction = deserializedState.currentFaction;
        
        debug.log(`Firebase says: Turn ${firebaseTurn}, Current faction: ${firebaseCurrentFaction}`);
        debug.log(`Local save had: Turn ${this.gameState.turn}, Current faction: ${this.gameState.currentFaction}`);
        
        // Update ONLY the turn information to match Firebase
        this.gameState.turn = firebaseTurn;
        this.gameState.currentFaction = firebaseCurrentFaction;
        this.currentFaction = firebaseCurrentFaction;
        
        debug.log(`Synced to Firebase turn state: Turn ${this.gameState.turn}, Current faction: ${this.gameState.currentFaction}`);
        debug.log(`Keeping local units (${this.gameState.units.size}), resources, and cities`);
      } else {
        debug.error('No game state in Firebase room!');
      }
      
      // NOW set up the normal online sync (this handles all the listeners)
      this.setupOnlineSync();
      
      // Check whose turn it is based on the actual current faction from the game state
      // Use humanPlayerFaction to determine if it's OUR turn
      // This should match the logic in the sync handler
      this.isMyTurn = this.isOnlineGame 
        ? (this.gameState.currentFaction === this.humanPlayerFaction)
        : (this.gameState.currentFaction === this.currentFaction);
      
      debug.log(`Reconnected: Current turn is ${this.gameState.currentFaction}, we are ${this.humanPlayerFaction}, isMyTurn: ${this.isMyTurn}`);
      
      // Update the UI to reflect the current turn
      if (this.isMyTurn) {
        this.hideEnemyTurnIndicator();
        // Only reset movement if units haven't moved yet this turn
        // (The game state already has the correct movement values)
      } else {
        this.showEnemyTurnIndicator();
      }
      
      // Update turn state UI
      this.updateTurnState();
      
      // Clear the pending reconnection
      this.pendingMultiplayerReconnection = null;
      
      debug.log('Multiplayer reconnection complete - using standard sync');
      debug.log('Final state - isMyTurn:', this.isMyTurn, 'multiplayerService:', !!this.multiplayerService);
    }
    
    // Start game loop
    this.gameLoop();
  }
  
  private initializeCityTracking(): void {
    const cities = this.map.getCities();
    cities.forEach((city, name) => {
      this.lastTurnCities.set(name, city.faction);
    });
  }
  
  // Currently unused - city ownership changes are handled in the attack logic
  // private checkCityOwnershipChanges(): void {
  //   const cities = this.map.getCities();
  //   
  //   cities.forEach((city, name) => {
  //     const previousOwner = this.lastTurnCities.get(name);
  //     const currentOwner = city.faction;
  //     
  //     if (previousOwner !== currentOwner) {
  //       if (currentOwner === this.currentFaction) {
  //         // We captured a city
  //         this.notifications.push(`You have captured ${name}!`);
  //       } else if (previousOwner === this.currentFaction && currentOwner !== null) {
  //         // We lost a city
  //         this.notifications.push(`You have lost ${name} to the enemy!`);
  //       }
  //     }
  //     
  //     // Update tracking
  //     this.lastTurnCities.set(name, currentOwner);
  //   });
  // }
  
  private showNotification(message: string, type?: NotificationType): void {
    if (this.notificationManager) {
      // For custom messages without a specific type, display them directly
      if (!type) {
        // Determine type based on message content for proper coloring
        let inferredType = NotificationType.TURN_START; // Default
        const lowerMessage = message.toLowerCase();
        
        // Transport-related messages should use transport colors
        if (lowerMessage.includes('transport') || lowerMessage.includes('board') || 
            lowerMessage.includes('disembark') || lowerMessage.includes('carrying')) {
          inferredType = NotificationType.UNIT_BOARDED;
        }
        // Error/warning messages
        else if (lowerMessage.includes('cannot') || lowerMessage.includes('no ') || 
                 lowerMessage.includes('already') || lowerMessage.includes('full')) {
          inferredType = NotificationType.UNIT_DAMAGED; // Use orange for warnings
        }
        // Turn-related messages
        else if (lowerMessage.includes('turn')) {
          inferredType = NotificationType.TURN_START;
        }
        
        // Create a custom notification message directly
        const customMessage: NotificationMessage = {
          text: message,
          type: inferredType,
          priority: 'normal',
          duration: 3000,
          sound: undefined
        };
        this.notificationManager.addNotification(customMessage);
      } else {
        // Use the event system for typed notifications
        const event: NotificationEvent = {
          type: type,
          faction: this.currentFaction,
          data: { message },
          timestamp: Date.now()
        };
        this.notificationManager.processEvent(event, true);
      }
    }
  }
  
  private sendNotification(event: NotificationEvent): void {
    debug.log(`[SEND NOTIFICATION] Type: ${event.type}, Faction: ${event.faction}, Data:`, event.data);
    
    // In PvE mode, filter out AI notifications that the player shouldn't see
    if (this.selectedGameMode === 'PvE' && this.humanPlayerFaction) {
      // Don't show notifications for AI's losses or AI's city captures
      const isAIEvent = event.faction !== this.humanPlayerFaction;
      const shouldFilter = isAIEvent && (
        event.type === NotificationType.UNIT_DESTROYED ||
        event.type === NotificationType.CITY_LOST ||
        event.type === NotificationType.UNIT_TRAINING_COMPLETE ||
        event.type === NotificationType.CITY_CAPTURED
      );
      
      if (shouldFilter) {
        debug.log(`[PVE FILTER] Blocking AI notification: ${event.type}`);
        return;
      }
    }
    
    // For offline games, show notification directly
    // For online games, only send to Firebase (will be shown when received)
    if (!this.isOnlineGame && this.notificationManager) {
      debug.log(`[LOCAL NOTIFICATION] Showing notification locally (offline game)`);
      this.notificationManager.processEvent(event, this.currentFaction === event.faction);
    }
    
    // Sync to Firebase for online games
    if (this.isOnlineGame && this.multiplayerService) {
      debug.log(`[FIREBASE NOTIFICATION] Sending to Firebase (will be shown when received)`);
      // Firebase doesn't allow undefined values, so only include targetFaction if it exists
      const notification: any = {
        type: event.type,
        faction: event.faction,
        data: event.data,
        timestamp: event.timestamp
      };
      
      if (event.targetFaction !== undefined) {
        notification.targetFaction = event.targetFaction;
      }
      
      this.multiplayerService.sendNotification(notification);
    }
  }
  

  private setupMobileViewport(): void {
    // Detect if mobile device
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                     ('ontouchstart' in window) ||
                     (navigator.maxTouchPoints > 0);
    
    if (isMobile) {
      // Prevent double-tap zoom
      let lastTouchEnd = 0;
      document.addEventListener('touchend', (e) => {
        const now = Date.now();
        if (now - lastTouchEnd <= 300) {
          e.preventDefault();
        }
        lastTouchEnd = now;
      }, { passive: false });
      
      // Prevent pinch zoom on document
      document.addEventListener('gesturestart', (e) => e.preventDefault());
      document.addEventListener('gesturechange', (e) => e.preventDefault());
      document.addEventListener('gestureend', (e) => e.preventDefault());
      
      // Prevent scrolling
      document.body.addEventListener('touchmove', (e) => {
        if (e.target === document.body) {
          e.preventDefault();
        }
      }, { passive: false });
      
      // Force viewport settings
      const viewport = document.querySelector('meta[name="viewport"]');
      if (viewport) {
        viewport.setAttribute('content', 
          'width=device-width, initial-scale=1.0, maximum-scale=1.0, minimum-scale=1.0, user-scalable=no, viewport-fit=cover');
      }
      
      // Add mobile class for CSS
      document.body.classList.add('mobile');
    }
  }

  private setupEventListeners(): void {
    // Mouse controls
    this.canvas.addEventListener('mousedown', (e) => this.handleMouseDown(e));
    this.canvas.addEventListener('mousemove', (e) => this.handleMouseMove(e));
    this.canvas.addEventListener('mouseup', () => this.handleMouseUp());
    this.canvas.addEventListener('mouseleave', () => this.handleMouseUp());
    
    // Mouse wheel zoom
    this.canvas.addEventListener('wheel', (e) => this.handleWheel(e));

    // Touch controls for mobile
    this.setupTouchControls();
    
    // Mobile panel toggles
    this.setupMobilePanelToggles();
    
    // Keyboard controls for zoom and movement
    document.addEventListener('keydown', (e) => this.handleKeyDown(e));
    document.addEventListener('keyup', (e) => this.handleKeyUp(e));
    
    // Click for tile selection
    this.canvas.addEventListener('click', (e) => this.handleClick(e));
    
    // End turn button
    const endTurnBtn = document.getElementById('end-turn-btn');
    if (endTurnBtn) {
      endTurnBtn.addEventListener('click', () => this.endTurn());
    }
    
    // Resign button (show for all game modes)
    const resignBtn = document.getElementById('resign-btn');
    if (resignBtn) {
      resignBtn.style.display = 'inline-block';
      resignBtn.addEventListener('click', () => this.handleResign());
    }
    
    // Track when the user leaves the page
    window.addEventListener('beforeunload', () => {
      this.analytics.trackSessionEnd();
    });
  }

  private handleMouseDown(e: MouseEvent): void {
    if (e.button === 0) { // Left click
      this.cameraController.startDrag(e.clientX, e.clientY);
    }
  }

  private handleMouseMove(e: MouseEvent): void {
    this.cameraController.updateDrag(e.clientX, e.clientY);
  }

  private handleMouseUp(): void {
    this.cameraController.endDrag();
  }
  
  private handleWheel(e: WheelEvent): void {
    e.preventDefault();
    
    // Smooth zoom based on scroll intensity
    // Normalize delta across browsers
    const delta = e.deltaY > 0 ? 1 : -1;
    
    // Pass mouse coordinates for zoom centering
    this.cameraController.zoomByDelta(delta, e.clientX, e.clientY);
  }

  // Touch event handling for mobile
  private touches: Map<number, Touch> = new Map();
  private lastTouchDistance: number = 0;
  private touchStartTime: number = 0;
  private isTouchPanning: boolean = false;
  private pinchZoomAccumulator: number = 0; // Accumulate small pinch changes
  private lastPinchTime: number = 0;

  private setupTouchControls(): void {
    // Prevent default touch behaviors on the whole document
    document.addEventListener('touchstart', (e) => {
      if (e.target === this.canvas || this.canvas.contains(e.target as Node)) {
        e.preventDefault();
      }
    }, { passive: false });
    
    document.addEventListener('touchmove', (e) => {
      if (e.target === this.canvas || this.canvas.contains(e.target as Node)) {
        e.preventDefault();
      }
    }, { passive: false });
    
    // Prevent context menu on long press
    this.canvas.addEventListener('contextmenu', (e) => e.preventDefault());
    
    // Canvas-specific touch handlers
    this.canvas.addEventListener('touchstart', (e) => this.handleTouchStart(e), { passive: false });
    this.canvas.addEventListener('touchmove', (e) => this.handleTouchMove(e), { passive: false });
    this.canvas.addEventListener('touchend', (e) => this.handleTouchEnd(e), { passive: false });
    this.canvas.addEventListener('touchcancel', (e) => this.handleTouchEnd(e), { passive: false });
  }

  private handleTouchStart(e: TouchEvent): void {
    e.preventDefault();
    
    // Store all active touches
    for (let i = 0; i < e.touches.length; i++) {
      const touch = e.touches[i];
      this.touches.set(touch.identifier, touch);
    }

    if (e.touches.length === 1) {
      // Single touch - prepare for tap or pan
      this.touchStartTime = Date.now();
      this.isTouchPanning = false;
      const touch = e.touches[0];
      this.isDragging = true;
      this.lastMousePosition = { x: touch.clientX, y: touch.clientY };
      this.cameraController.startDrag(touch.clientX, touch.clientY);
    } else if (e.touches.length === 2) {
      // Two touches - prepare for pinch zoom
      this.isDragging = false;
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      this.lastTouchDistance = this.getTouchDistance(touch1, touch2);
    }
  }

  private handleTouchMove(e: TouchEvent): void {
    e.preventDefault();

    // Update stored touches
    for (let i = 0; i < e.touches.length; i++) {
      const touch = e.touches[i];
      this.touches.set(touch.identifier, touch);
    }

    if (e.touches.length === 1 && this.isDragging) {
      // Single touch drag - pan the camera
      const touch = e.touches[0];
      const deltaX = touch.clientX - this.lastMousePosition.x;
      const deltaY = touch.clientY - this.lastMousePosition.y;
      
      // Mark as panning if moved significantly
      if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
        this.isTouchPanning = true;
      }

      this.cameraController.updateDrag(touch.clientX, touch.clientY);
      this.lastMousePosition = { x: touch.clientX, y: touch.clientY };
    } else if (e.touches.length === 2) {
      // Two touch pinch - zoom
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const currentDistance = this.getTouchDistance(touch1, touch2);
      
      if (this.lastTouchDistance > 0) {
        const scale = currentDistance / this.lastTouchDistance;
        const centerX = (touch1.clientX + touch2.clientX) / 2;
        const centerY = (touch1.clientY + touch2.clientY) / 2;
        
        // Accumulate pinch changes for smoother zooming
        const deltaScale = (scale - 1) * 2; // Increase sensitivity
        this.pinchZoomAccumulator += deltaScale;
        
        // Apply zoom when accumulated enough or after time threshold
        const now = Date.now();
        if (Math.abs(this.pinchZoomAccumulator) > 0.15 || (now - this.lastPinchTime) > 100) {
          if (this.pinchZoomAccumulator > 0) {
            this.cameraController.zoomByDelta(-1, centerX, centerY);
          } else if (this.pinchZoomAccumulator < 0) {
            this.cameraController.zoomByDelta(1, centerX, centerY);
          }
          this.pinchZoomAccumulator = 0;
          this.lastPinchTime = now;
        }
      }
      
      this.lastTouchDistance = currentDistance;
    }
  }

  private handleTouchEnd(e: TouchEvent): void {
    e.preventDefault();

    // Remove ended touches
    for (let i = 0; i < e.changedTouches.length; i++) {
      const touch = e.changedTouches[i];
      this.touches.delete(touch.identifier);
    }

    if (e.touches.length === 0) {
      // All touches ended
      if (!this.isTouchPanning && Date.now() - this.touchStartTime < 300) {
        // It was a tap - simulate click
        const touch = e.changedTouches[0];
        const clickEvent = new MouseEvent('click', {
          clientX: touch.clientX,
          clientY: touch.clientY,
          bubbles: true
        });
        this.canvas.dispatchEvent(clickEvent);
      }
      
      this.isDragging = false;
      this.isTouchPanning = false;
      this.lastTouchDistance = 0;
      this.pinchZoomAccumulator = 0;
      this.cameraController.endDrag();
    } else if (e.touches.length === 1) {
      // Went from 2+ touches to 1 - restart single touch handling
      const touch = e.touches[0];
      this.isDragging = true;
      this.lastMousePosition = { x: touch.clientX, y: touch.clientY };
      this.lastTouchDistance = 0;
      this.cameraController.startDrag(touch.clientX, touch.clientY);
    }
  }

  private getTouchDistance(touch1: Touch, touch2: Touch): number {
    const dx = touch2.clientX - touch1.clientX;
    const dy = touch2.clientY - touch1.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  private setupMobilePanelToggles(): void {
    const leftPanelToggle = document.getElementById('left-panel-toggle');
    const rightPanelToggle = document.getElementById('right-panel-toggle');
    const leftPanel = document.getElementById('left-panel');
    const rightPanel = document.getElementById('side-panel');
    const overlay = document.getElementById('panel-overlay');
    
    // Detect if we're on a touch device
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    
    // Function to close all panels
    const closeAllPanels = () => {
      leftPanel?.classList.remove('active');
      rightPanel?.classList.remove('active');
      overlay?.classList.remove('active');
    };
    
    // Left panel toggle
    leftPanelToggle?.addEventListener('click', (e) => {
      e.stopPropagation();
      const isActive = leftPanel?.classList.contains('active');
      closeAllPanels();
      if (!isActive) {
        leftPanel?.classList.add('active');
        overlay?.classList.add('active');
        // Prevent accidental taps while panel is animating
        if (isTouchDevice) {
          setTimeout(() => {
            leftPanel?.focus();
          }, 300);
        }
      }
    });
    
    // Right panel toggle
    rightPanelToggle?.addEventListener('click', (e) => {
      e.stopPropagation();
      const isActive = rightPanel?.classList.contains('active');
      closeAllPanels();
      if (!isActive) {
        rightPanel?.classList.add('active');
        overlay?.classList.add('active');
        // Prevent accidental taps while panel is animating
        if (isTouchDevice) {
          setTimeout(() => {
            rightPanel?.focus();
          }, 300);
        }
      }
    });
    
    // Close buttons
    const closeButtons = document.querySelectorAll('.panel-close');
    closeButtons.forEach(button => {
      button.addEventListener('click', closeAllPanels);
    });
    
    // Overlay click to close
    overlay?.addEventListener('click', closeAllPanels);
  }

  private async loadMultiplayerSave(savedState: any): Promise<void> {
    if (!savedState.roomCode) {
      this.popupManager.showAlert('No room code found in save. Loading as local game.');
      // Fall back to local loading
      const tempSlot = 99;
      localStorage.setItem(`frontlines_gamestate_slot${tempSlot}`, JSON.stringify(savedState));
      this.loadGameState(tempSlot);
      return;
    }
    
    try {
      // Initialize multiplayer service if not already done
      if (!this.multiplayerService) {
        this.multiplayerService = new MultiplayerService();
        // Wait a bit for authentication to complete
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      
      // Reconnect to the Firebase room
      const room = await this.multiplayerService.reconnectToRoom(
        savedState.roomCode,
        savedState.currentFaction
      );
      
      debug.log('Reconnected to room:', room.id);
      
      // Set up multiplayer state
      this.isOnlineGame = true;
      this.selectedCampaign = room.campaign;
      
      // Determine our faction and the opponent's faction based on the saved state
      // Use playerFaction if available (new saves), otherwise fall back to determining from room
      const playerFaction = savedState.playerFaction || 
        (room.host.faction === savedState.currentFaction ? room.host.faction : room.guest?.faction);
      
      // Determine if we're the host or guest based on our faction
      const isHost = room.host.faction === playerFaction;
      
      // Set up our player faction and opponent
      this.humanPlayerFaction = playerFaction;
      if (isHost) {
        this.secondFaction = room.guest!.faction;
      } else {
        this.secondFaction = room.host.faction;
      }
      
      // currentFaction will be set when we load the game state (it's whose turn it is)
      // We'll use humanPlayerFaction to determine if it's our turn
      
      // Store the room in a property so we can set up listeners after game starts
      this.pendingMultiplayerReconnection = {
        savedState,
        room
      };
      
      // Load the saved state to localStorage temporarily  
      const tempSlot = 99;
      localStorage.setItem(`frontlines_gamestate_slot${tempSlot}`, JSON.stringify(savedState));
      
      // Mark that we're loading a multiplayer save
      this.isLoadingMultiplayerSave = true;
      
      // Find the menu overlay - it has id 'main-menu'
      const menuOverlay = document.getElementById('main-menu');
      
      if (menuOverlay) {
        // Now actually start the game with the loaded state
        // Use playerFaction (our faction) not currentFaction (whose turn it is)
        this.startGame(playerFaction, menuOverlay);
      } else {
        debug.error('Could not find menu overlay - attempting direct initialization');
        // Try to start the game anyway
        this.initializeGame();
      }
      
    } catch (e: any) {
      debug.error('Failed to reconnect to multiplayer game:', e);
      
      // Fall back to local loading
      const shouldLoadLocal = await this.popupManager.showConfirm(`Failed to reconnect to multiplayer room: ${e.message}\n\nLoad as local game instead?`);
      if (shouldLoadLocal) {
        const tempSlot = 99;
        localStorage.setItem(`frontlines_gamestate_slot${tempSlot}`, JSON.stringify(savedState));
        this.loadGameState(tempSlot);
      }
    }
  }
  
  private getOpponentFaction(faction: FactionType): FactionType {
    // Determine opponent based on campaign
    switch (this.selectedCampaign) {
      case CampaignType.NORMANDY:
        return faction === 'United States Of America' ? '3rd Reich' : 'United States Of America';
      case CampaignType.TUNISIA:
        return faction === 'United Kingdom' ? '3rd Reich' : 'United Kingdom';
      case CampaignType.STALINGRAD:
        return faction === 'USSR' ? '3rd Reich' : 'USSR';
      case CampaignType.OKINAWA:
        return faction === 'United States Of America' ? 'Imperial Japan' : 'United States Of America';
      case CampaignType.CAMPOBASSO:
        return faction === 'Canada' ? 'Fascist Italy' : 'Canada';
      case CampaignType.FRANCE_1940:
        return faction === 'France' ? '3rd Reich' : 'France';
      case CampaignType.ALBANIA_1940:
        return faction === 'Greece' ? 'Fascist Italy' : 'Greece';
      default:
        return '3rd Reich'; // Fallback
    }
  }
  
  private async handleResign(): Promise<void> {
    // Confirm resignation
    const confirmed = await this.popupManager.showConfirm("Are you sure you want to resign? This will end the game and your opponent will win.");
    if (!confirmed) return;
    
    // For online games
    if (this.isOnlineGame && this.multiplayerService) {
      // Ensure humanPlayerFaction is set for multiplayer games
      if (!this.humanPlayerFaction) {
        // Determine our faction based on whether we're host or guest
        if (this.multiplayerService.isHost()) {
          this.humanPlayerFaction = this.multiplayerService.getCurrentRoom()?.host.faction || this.currentFaction;
        } else {
          this.humanPlayerFaction = this.multiplayerService.getCurrentRoom()?.guest?.faction || this.currentFaction;
        }
      }
      
      // Use the surrender method which properly marks the game as surrendered
      await this.multiplayerService.surrenderGame();
      
      // The opponent will be notified via Firebase and will see the victory screen
      // We'll see the defeat screen when the room update comes back
      debug.log('Surrendered the game');
    } else {
      // For offline games (PvP and PvE)
      const checkFaction = this.humanPlayerFaction || this.currentFaction;
      const winner = this.getOpponentFaction(checkFaction);
      
      // Show defeat screen
      this.showGameOverScreen(winner);
    }
  }
  
  private handleKeyDown(e: KeyboardEvent): void {
    // Tab key to cycle through units with actions remaining
    if (e.key === 'Tab') {
      e.preventDefault();
      this.cycleToNextActionableUnit();
      return;
    }

    // DEV MODE: F key to toggle fog of war visibility for player (AI still uses fog)
    if (e.key === 'f' || e.key === 'F') {
      e.preventDefault();
      this.devFogDisabled = !this.devFogDisabled;
      console.log(`[DEV] Fog of war visibility: ${this.devFogDisabled ? 'DISABLED (full map visible)' : 'ENABLED (normal)'}`);
      // Clear vision cache to force recalculation
      this.visionSystem.clearCache();
      return;
    }
    
    // Tutorial: U key to upgrade city
    if (this.tutorialUpgradeEnabled && (e.key === 'u' || e.key === 'U')) {
      e.preventDefault();
      const selectedTile = this.gameState.selectedTile;
      if (selectedTile) {
        const tile = this.map.getTile(selectedTile.x, selectedTile.y);
        if (tile?.city && tile.city.faction === this.humanPlayerFaction && !tile.city.upgradeInProgress) {
          // Determine next upgrade level
          let targetLevel = CityLevel.FACTORY;
          if (tile.city.level === CityLevel.FACTORY) {
            targetLevel = CityLevel.AIRBASE;
          } else if (tile.city.level === CityLevel.AIRBASE) {
            return; // Already max level
          }
          
          // Start the upgrade
          if (this.startCityUpgrade(tile.city, targetLevel)) {
            this.tutorialUpgradeEnabled = false; // Disable after use
          }
        }
      }
    }
    
    // Zoom controls - continuous when held
    if (e.key === '+' || e.key === '=') {
      e.preventDefault();
      if (!this.keysPressed.has('zoom_in')) {
        this.keysPressed.add('zoom_in');
        this.updateCameraControls();
      }
    } else if (e.key === '-' || e.key === '_') {
      e.preventDefault();
      if (!this.keysPressed.has('zoom_out')) {
        this.keysPressed.add('zoom_out');
        this.updateCameraControls();
      }
    }
    
    // Track key presses for smooth movement
    if (e.key === 'ArrowUp' || e.key === 'w' || e.key === 'W' ||
        e.key === 'ArrowDown' || e.key === 's' || e.key === 'S' ||
        e.key === 'ArrowLeft' || e.key === 'a' || e.key === 'A' ||
        e.key === 'ArrowRight' || e.key === 'd' || e.key === 'D') {
      e.preventDefault();
      this.keysPressed.add(e.key.toLowerCase());
      this.updateCameraControls();
    }
  }

  private createStartingUnits(): void {
    // Find starting cities
    const cities = this.map.getCities();
    debug.log('Creating starting units for campaign:', this.selectedCampaign);
    debug.log('Cities:', Array.from(cities.values()).map(c => ({ name: c.name, faction: c.faction })));
    cities.forEach(city => {
      if (city.faction === 'United States Of America') {
        // Create US starting unit
        const centerTile = city.tiles[Math.floor(city.tiles.length / 2)];
        const unitType = this.selectedCampaign === CampaignType.OKINAWA ? 
          UnitType.MARINE_SQUAD : 
          UnitType.RIFLE_SQUAD;
        const unit = this.unitManager.createUnit(unitType, 'United States Of America', centerTile);
        unit.hometown = city.name;
        // Add to gameState
        this.gameState.units.set(unit.id, unit);
        // Track this unit as produced by the city
        city.unitsProduced.push(unit.id);
        // Track unit creation in battle statistics
        const usStats = this.gameState.battleStats?.get('United States Of America');
        if (usStats) {
          usStats.unitsCreated++;
        }
      } else if (city.faction === '3rd Reich') {
        // In tutorial mode, don't create enemy units initially - they'll be spawned later
        if (!this.isTutorial) {
          // Create German starting unit - different for each campaign
          const centerTile = city.tiles[Math.floor(city.tiles.length / 2)];
          const unitType = this.selectedCampaign === CampaignType.NORMANDY ? 
            UnitType.PANZERGRENADIER_SQUAD : 
            this.selectedCampaign === CampaignType.STALINGRAD ?
            UnitType.GRENADIER_SQUAD_1942 : 
            this.selectedCampaign === CampaignType.TUTORIAL ?
            UnitType.PANZERGRENADIER_SQUAD :
            this.selectedCampaign === CampaignType.FRANCE_1940 ?
            UnitType.WEHRMACHT_INFANTRY_1940 :
            UnitType.SCHUTZEN_SQUAD;
          const unit = this.unitManager.createUnit(unitType, '3rd Reich', centerTile);
          unit.hometown = city.name;
          // Add to gameState
          this.gameState.units.set(unit.id, unit);
          // Track this unit as produced by the city
          city.unitsProduced.push(unit.id);
          // Track unit creation in battle statistics
          const gerStats = this.gameState.battleStats?.get('3rd Reich');
          if (gerStats) {
            gerStats.unitsCreated++;
          }
        }
      } else if (city.faction === 'United Kingdom') {
        // Create UK starting unit
        const centerTile = city.tiles[Math.floor(city.tiles.length / 2)];
        const unit = this.unitManager.createUnit(UnitType.INFANTRY_SECTION, 'United Kingdom', centerTile);
        unit.hometown = city.name;
        // Add to gameState
        this.gameState.units.set(unit.id, unit);
        // Track this unit as produced by the city
        city.unitsProduced.push(unit.id);
        // Track unit creation in battle statistics
        const ukStats = this.gameState.battleStats?.get('United Kingdom');
        if (ukStats) {
          ukStats.unitsCreated++;
        }
      } else if (city.faction === 'USSR') {
        // Create USSR starting unit
        const centerTile = city.tiles[Math.floor(city.tiles.length / 2)];
        const unit = this.unitManager.createUnit(UnitType.RED_ARMY_SQUAD, 'USSR', centerTile);
        unit.hometown = city.name;
        // Add to gameState
        this.gameState.units.set(unit.id, unit);
        // Track this unit as produced by the city
        city.unitsProduced.push(unit.id);
        // Track unit creation in battle statistics
        const ussrStats = this.gameState.battleStats?.get('USSR');
        if (ussrStats) {
          ussrStats.unitsCreated++;
        }
      } else if (city.faction === 'Imperial Japan') {
        // Create Imperial Japan starting unit
        const centerTile = city.tiles[Math.floor(city.tiles.length / 2)];
        const unit = this.unitManager.createUnit(UnitType.IJA_SQUAD, 'Imperial Japan', centerTile);
        unit.hometown = city.name;
        // Add to gameState
        this.gameState.units.set(unit.id, unit);
        // Track this unit as produced by the city
        city.unitsProduced.push(unit.id);
        // Track unit creation in battle statistics
        const japanStats = this.gameState.battleStats?.get('Imperial Japan');
        if (japanStats) {
          japanStats.unitsCreated++;
        }
      } else if (city.faction === 'Canada') {
        // Create Canada starting unit
        const centerTile = city.tiles[Math.floor(city.tiles.length / 2)];
        debug.log('Creating Canadian unit for city:', city.name, 'at position:', centerTile);
        const unit = this.unitManager.createUnit(UnitType.CANADIAN_INFANTRY_SECTION, 'Canada', centerTile);
        debug.log('Created unit:', unit.id, 'faction:', unit.faction, 'type:', unit.type);
        unit.hometown = city.name;
        // Add to gameState
        this.gameState.units.set(unit.id, unit);
        // Track this unit as produced by the city
        city.unitsProduced.push(unit.id);
        // Track unit creation in battle statistics
        const canadaStats = this.gameState.battleStats?.get('Canada');
        if (canadaStats) {
          canadaStats.unitsCreated++;
        }
      } else if (city.faction === 'France') {
        // Create France starting unit  
        debug.log('Creating French unit for city:', city.name, 'faction:', city.faction);
        const centerTile = city.tiles[Math.floor(city.tiles.length / 2)];
        const unit = this.unitManager.createUnit(UnitType.FRENCH_INFANTRY_SQUAD, 'France', centerTile);
        debug.log('Created French unit:', unit.id, 'faction:', unit.faction, 'type:', unit.type);
        unit.hometown = city.name;
        // Add to gameState
        this.gameState.units.set(unit.id, unit);
        // Track this unit as produced by the city
        city.unitsProduced.push(unit.id);
        // Track unit creation in battle statistics
        const franceStats = this.gameState.battleStats?.get('France');
        if (franceStats) {
          franceStats.unitsCreated++;
        }
      } else if (city.faction === 'Fascist Italy') {
        // Create Fascist Italy starting unit
        const centerTile = city.tiles[Math.floor(city.tiles.length / 2)];
        debug.log('Creating Italian unit for city:', city.name, 'at position:', centerTile);
        // Use different units for Albania 1940 campaign
        const unitType = this.selectedCampaign === CampaignType.ALBANIA_1940 ?
          UnitType.LIBYAN_COLONIAL_TROOPS :
          UnitType.BERSAGLIERI_SQUAD;
        const unit = this.unitManager.createUnit(unitType, 'Fascist Italy', centerTile);
        debug.log('Created unit:', unit.id, 'faction:', unit.faction, 'type:', unit.type);
        unit.hometown = city.name;
        // Add to gameState
        this.gameState.units.set(unit.id, unit);
        // Track this unit as produced by the city
        city.unitsProduced.push(unit.id);
        // Track unit creation in battle statistics
        const italyStats = this.gameState.battleStats?.get('Fascist Italy');
        if (italyStats) {
          italyStats.unitsCreated++;
        }
      } else if (city.faction === 'Greece') {
        // Create Greece starting unit
        const centerTile = city.tiles[Math.floor(city.tiles.length / 2)];
        const unit = this.unitManager.createUnit(UnitType.NATIONAL_GUARD_BATTALION, 'Greece', centerTile);
        unit.hometown = city.name;
        // Add to gameState
        this.gameState.units.set(unit.id, unit);
        // Track this unit as produced by the city
        city.unitsProduced.push(unit.id);
        // Track unit creation in battle statistics
        const greeceStats = this.gameState.battleStats?.get('Greece');
        if (greeceStats) {
          greeceStats.unitsCreated++;
        }
      }
    });
    
    
  }

  private handleClick(e: MouseEvent): void {
    // Prevent clicks during AI turn
    if (this.isAITurn) return;
    
    const camera = this.cameraController.getCamera();
    const tilePos = this.renderer.screenToTile(e.clientX, e.clientY, camera);
    
    const tile = this.map.getTile(tilePos.x, tilePos.y);
    if (!tile) return;
    
    // Handle mine placement mode
    if (this.isPlacingMine && this.minePlacementTiles) {
      const isValidPlacement = this.minePlacementTiles.some(
        pos => pos.x === tilePos.x && pos.y === tilePos.y
      );
      
      if (isValidPlacement && this.minePlacementEngineer) {
        this.placeMine(this.minePlacementEngineer, tilePos);
        return;
      }
    }
    
    // Handle healing/repair mode
    if (this.isHealingMode && this.healTargetTiles) {
      const isValidTarget = this.healTargetTiles.some(
        pos => pos.x === tilePos.x && pos.y === tilePos.y
      );
      
      if (isValidTarget && this.healingUnit) {
        const targetUnit = this.unitManager.getUnitAt(tilePos);
        if (targetUnit) {
          this.showHealConfirmation(this.healingUnit, targetUnit);
          return;
        }
      }
    }
    
    // Handle boarding mode
    if (this.isBoardingMode && this.validBoardTargets) {
      const isValidTarget = this.validBoardTargets.some(
        pos => pos.x === tilePos.x && pos.y === tilePos.y
      );
      
      if (isValidTarget && this.boardingUnit) {
        const transport = this.unitManager.getUnitAt(tilePos);
        if (transport && isGroundTransport(transport.type)) {
          this.boardTransport(this.boardingUnit, transport);
          this.isBoardingMode = false;
          this.boardingUnit = null;
          this.validBoardTargets = [];
          return;
        }
      }
    }
    
    // Check if there's a unit at this position
    const unit = this.unitManager.getUnitAt(tilePos);
    
    // First, check if we should attack with a selected unit
    if (this.gameState.selectedUnit && unit) {
      const selectedUnit = this.unitManager.getUnit(this.gameState.selectedUnit);
      if (selectedUnit && selectedUnit.faction === this.currentFaction && 
          unit.faction !== selectedUnit.faction && !selectedUnit.hasAttacked) {
        // Check if enemy is in range
        const isEnemyInRange = this.enemiesInRange?.some(pos => pos.x === tilePos.x && pos.y === tilePos.y);
        if (isEnemyInRange) {
          // Attack the enemy
          this.attackUnit(selectedUnit, unit);
          return; // Exit early to prevent unit selection
        }
      }
    }
    
    if (unit && unit.stats.health > 0) {
      // Play click sound
      this.soundManager.playClick();
      
      // Select the unit (only if alive)
      this.gameState.selectedUnit = unit.id;
      this.gameState.selectedTile = tilePos;
      
      // Close build menu if open
      const buildMenu = document.getElementById('build-menu');
      if (buildMenu) buildMenu.remove();
      
      this.updateUnitInfo(unit);
      this.updateTileInfo(tile);
      
      // Only show movement and attack options for own units
      // In online games, check against humanPlayerFaction, not currentFaction
      const controllingFaction = this.isOnlineGame ? this.humanPlayerFaction : this.currentFaction;
      if (unit.faction === controllingFaction) {
        this.calculateAvailableMoves(unit);
        // Always calculate enemies in range, even with 0 movement
        if (!unit.hasAttacked) {
          this.calculateEnemiesInRange(unit);
        } else {
          this.enemiesInRange = null;
        }
      } else {
        // Enemy unit - just show info, no moves
        this.availableMoves = null;
        this.enemiesInRange = null;
      }
    } else if (this.gameState.selectedUnit) {
      // Try to move or attack with selected unit
      const selectedUnit = this.unitManager.getUnit(this.gameState.selectedUnit);
      if (selectedUnit && selectedUnit.faction === this.currentFaction) {
        // Check if clicked on enemy in range
        const targetUnit = this.unitManager.getUnitAt(tilePos);
        const isEnemyInRange = targetUnit && targetUnit.faction !== selectedUnit.faction && 
          this.enemiesInRange?.some(pos => pos.x === tilePos.x && pos.y === tilePos.y);
        
        if (isEnemyInRange && targetUnit && !selectedUnit.hasAttacked) {
          // Attack the enemy
          this.attackUnit(selectedUnit, targetUnit);
        } else {
          // Check if it's a valid move
          const isValidMove = this.availableMoves?.some(
            move => move.x === tilePos.x && move.y === tilePos.y
          );
          
          if (isValidMove) {
            this.moveUnit(selectedUnit, tilePos);
          } else {
            // Clicked outside available moves - deselect unit
            this.soundManager.playTerrainSelect();
            
            this.gameState.selectedUnit = null;
            this.availableMoves = null;
            this.enemiesInRange = null;
            this.updateUnitInfo(null);
            this.gameState.selectedTile = tilePos;
            this.updateTileInfo(tile);
          }
        }
      } else {
        // Can't move enemy units - deselect
        this.gameState.selectedUnit = null;
        this.availableMoves = null;
        this.enemiesInRange = null;
        this.updateUnitInfo(null);
        this.gameState.selectedTile = tilePos;
        this.updateTileInfo(tile);
      }
    } else if (tile.city && tile.city.faction === this.currentFaction) {
      // Check if it's your turn in online games
      if (this.isOnlineGame && !this.isMyTurn) {
        this.soundManager.playError();
        this.showNotification("You can only build units on your turn!");
        // Still select the tile to show info
        this.gameState.selectedTile = tilePos;
        this.updateTileInfo(tile);
      } else {
        // Play menu open sound for city selection
        this.soundManager.playMenuOpen();
        
        // Show build menu for owned city
        this.gameState.selectedTile = tilePos;
        this.updateTileInfo(tile);
        this.showUnitBuildMenu(tile.city);
      }
    } else {
      // Just select the tile - play terrain select sound
      this.soundManager.playTerrainSelect();
      
      this.gameState.selectedTile = tilePos;
      this.gameState.selectedUnit = null;
      this.availableMoves = null;
      this.enemiesInRange = null;
      
      // Close any open menus
      const buildMenu = document.getElementById('build-menu');
      if (buildMenu) buildMenu.remove();
      
      this.updateTileInfo(tile);
      this.updateUnitInfo(null);
    }
  }

  private updateTileInfo(tile: any): void {
    const tileDetails = document.getElementById('tile-details');
    if (!tileDetails) return;
    
    // Handle null tile (clear display)
    if (!tile) {
      tileDetails.innerHTML = 'No tile selected';
      return;
    }
    
    const terrainNames: Record<TerrainType, string> = {
      // Urban (special)
      [TerrainType.URBAN]: 'Urban',
      
      // Normandy Campaign
      [TerrainType.OPEN_FIELDS]: 'Open Fields',
      [TerrainType.BOCAGE]: 'Bocage',
      [TerrainType.DENSE_WOODS]: 'Dense Woods',
      
      // Tunisia Campaign
      [TerrainType.DESERT_PLAINS]: 'Desert Plains',
      [TerrainType.ROCKY_HIGHLANDS]: 'Rocky Highlands',
      [TerrainType.SHIFTING_DUNES]: 'Shifting Dunes',
      
      // Stalingrad Campaign
      [TerrainType.FROZEN_STEPPE]: 'Frozen Steppe',
      [TerrainType.SNOW_DRIFTS]: 'Snow Drifts',
      [TerrainType.FROZEN_MARSHES]: 'Frozen Marshes',
      
      // Okinawa Campaign
      [TerrainType.RICE_PADDIES]: 'Rice Paddies',
      [TerrainType.CORAL_RIDGES]: 'Coral Ridges',
      [TerrainType.DENSE_JUNGLE]: 'Dense Jungle',
      
      // Campobasso Campaign
      [TerrainType.VALLEY_FLOORS]: 'Valley Floors',
      [TerrainType.WOODED_HILLS]: 'Wooded Hills',
      [TerrainType.MOUNTAIN_RIDGES]: 'Mountain Ridges',
      
      // Battle of France Campaign
      [TerrainType.FRENCH_COUNTRYSIDE]: 'French Countryside',
      [TerrainType.ROLLING_HILLS]: 'Rolling Hills',
      [TerrainType.DENSE_FORESTS]: 'Dense Forests',
      
      // Albania 1940 Campaign
      [TerrainType.COASTAL_LOWLANDS]: 'Coastal Lowlands',
      [TerrainType.ALBANIAN_HIGHLANDS]: 'Albanian Highlands',
      [TerrainType.PINDUS_MOUNTAINS]: 'Pindus Mountains'
    };
    
    const terrainEffects: Record<TerrainType, { movement: number, defense: string, attack: string, description: string }> = {
      // Urban (special)
      [TerrainType.URBAN]: { 
        movement: 1, 
        defense: '+40%', 
        attack: '-15%', 
        description: 'Buildings and rubble create strongpoints perfect for determined defenders.' 
      },
      
      // Normandy Campaign - Easy terrain
      [TerrainType.OPEN_FIELDS]: { 
        movement: 1, 
        defense: '0%', 
        attack: '0%', 
        description: 'The pastoral farmlands and meadows of northern France - flat agricultural land with good visibility.' 
      },
      // Normandy - Rough terrain
      [TerrainType.BOCAGE]: { 
        movement: 2, 
        defense: '+20%', 
        attack: '0%', 
        description: 'The infamous hedgerow country - ancient field boundaries with thick hedges and earthen banks.' 
      },
      // Normandy - Challenging terrain
      [TerrainType.DENSE_WOODS]: { 
        movement: 3, 
        defense: '+20%', 
        attack: '-10%', 
        description: 'The thick Norman forests like those around Falaise - difficult to navigate and coordinate forces.' 
      },
      
      // Tunisia Campaign - Easy terrain
      [TerrainType.DESERT_PLAINS]: {
        movement: 1,
        defense: '0%',
        attack: '0%',
        description: 'Hard-packed desert floor and flat expanses - ideal for mechanized movement.'
      },
      // Tunisia - Rough terrain
      [TerrainType.ROCKY_HIGHLANDS]: {
        movement: 2,
        defense: '+20%',
        attack: '0%',
        description: 'The djebels and stony ridges that dominate the Tunisian landscape.'
      },
      // Tunisia - Challenging terrain
      [TerrainType.SHIFTING_DUNES]: {
        movement: 3,
        defense: '+20%',
        attack: '-10%',
        description: 'Deep sand seas and soft sand that exhausts troops and bogs down vehicles.'
      },
      
      // Stalingrad Campaign - Easy terrain
      [TerrainType.FROZEN_STEPPE]: {
        movement: 1,
        defense: '0%',
        attack: '0%',
        description: 'The flat, frozen grasslands of southern Russia - cold but traversable.'
      },
      // Stalingrad - Rough terrain
      [TerrainType.SNOW_DRIFTS]: {
        movement: 2,
        defense: '+20%',
        attack: '0%',
        description: 'Areas with deeper snow accumulation and wind-carved snowbanks providing natural cover.'
      },
      // Stalingrad - Challenging terrain
      [TerrainType.FROZEN_MARSHES]: {
        movement: 3,
        defense: '+20%',
        attack: '-10%',
        description: 'The treacherous wetlands along the Volga, frozen but unstable and difficult to cross.'
      },
      
      // Okinawa Campaign - Easy terrain
      [TerrainType.RICE_PADDIES]: {
        movement: 1,
        defense: '0%',
        attack: '0%',
        description: 'The cultivated flatlands and agricultural terraces of central Okinawa.'
      },
      // Okinawa - Rough terrain
      [TerrainType.CORAL_RIDGES]: {
        movement: 2,
        defense: '+20%',
        attack: '0%',
        description: 'The distinctive limestone ridgelines running north-south across the island.'
      },
      // Okinawa - Challenging terrain
      [TerrainType.DENSE_JUNGLE]: {
        movement: 3,
        defense: '+20%',
        attack: '-10%',
        description: 'The thick subtropical vegetation and tangled undergrowth of Okinawa\'s interior.'
      },
      
      // Campobasso Campaign - Easy terrain
      [TerrainType.VALLEY_FLOORS]: {
        movement: 1,
        defense: '0%',
        attack: '0%',
        description: 'The relatively flat river valleys cutting through the Apennines.'
      },
      // Campobasso - Rough terrain
      [TerrainType.WOODED_HILLS]: {
        movement: 2,
        defense: '+20%',
        attack: '0%',
        description: 'The chestnut groves and forested slopes characteristic of the region.'
      },
      // Campobasso - Challenging terrain
      [TerrainType.MOUNTAIN_RIDGES]: {
        movement: 3,
        defense: '+20%',
        attack: '-10%',
        description: 'The steep, rocky peaks of the Apennine range - exhausting climbs under fire.'
      },
      
      // Battle of France Campaign - Easy terrain
      [TerrainType.FRENCH_COUNTRYSIDE]: {
        movement: 1,
        defense: '0%',
        attack: '0%',
        description: 'The gentle rolling farmland and open fields of northern France - ideal for mechanized warfare.'
      },
      // Battle of France - Rough terrain  
      [TerrainType.ROLLING_HILLS]: {
        movement: 2,
        defense: '+20%',
        attack: '0%',
        description: 'The undulating terrain and scattered woodlands that characterize the French landscape.'
      },
      // Battle of France - Challenging terrain
      [TerrainType.DENSE_FORESTS]: {
        movement: 3,
        defense: '+20%',
        attack: '-10%',
        description: 'The thick Ardennes forests and deep woodlands - difficult terrain for coordinated attacks.'
      },
      
      // Albania 1940 - Easy terrain
      [TerrainType.COASTAL_LOWLANDS]: {
        movement: 1,
        defense: '0%',
        attack: '0%',
        description: 'The narrow coastal plains and valleys along the Adriatic - passable but exposed terrain.'
      },
      // Albania 1940 - Rough terrain
      [TerrainType.ALBANIAN_HIGHLANDS]: {
        movement: 2,
        defense: '+20%',
        attack: '0%',
        description: 'The rugged Albanian highlands - difficult terrain that favors the defender.'
      },
      // Albania 1940 - Challenging terrain
      [TerrainType.PINDUS_MOUNTAINS]: {
        movement: 3,
        defense: '+20%',
        attack: '-10%',
        description: 'The forbidding Pindus mountain range - nearly impassable terrain where armies struggled.'
      }
    };
      
      const effects = terrainEffects[tile.terrain as TerrainType];
      
      // Determine terrain difficulty
      let terrainDifficulty = 'Easy';
      const terrainType = tile.terrain as TerrainType;
      
      // Easy terrains (movement cost 1, no defense bonus)
      if (terrainType === TerrainType.OPEN_FIELDS || 
          terrainType === TerrainType.DESERT_PLAINS || 
          terrainType === TerrainType.FROZEN_STEPPE || 
          terrainType === TerrainType.RICE_PADDIES || 
          terrainType === TerrainType.VALLEY_FLOORS ||
          terrainType === TerrainType.FRENCH_COUNTRYSIDE ||
          terrainType === TerrainType.COASTAL_LOWLANDS) {
        terrainDifficulty = 'Easy';
      }
      // Rough terrains (movement cost 2, some defense bonus)
      else if (terrainType === TerrainType.BOCAGE || 
               terrainType === TerrainType.ROCKY_HIGHLANDS || 
               terrainType === TerrainType.SNOW_DRIFTS || 
               terrainType === TerrainType.CORAL_RIDGES || 
               terrainType === TerrainType.WOODED_HILLS ||
               terrainType === TerrainType.ROLLING_HILLS ||
               terrainType === TerrainType.ALBANIAN_HIGHLANDS) {
        terrainDifficulty = 'Rough';
      }
      // Challenging terrains (movement cost 3, high defense bonus)
      else if (terrainType === TerrainType.DENSE_WOODS || 
               terrainType === TerrainType.SHIFTING_DUNES || 
               terrainType === TerrainType.FROZEN_MARSHES || 
               terrainType === TerrainType.DENSE_JUNGLE || 
               terrainType === TerrainType.MOUNTAIN_RIDGES ||
               terrainType === TerrainType.DENSE_FORESTS ||
               terrainType === TerrainType.PINDUS_MOUNTAINS) {
        terrainDifficulty = 'Challenging';
      }
      // Urban is special
      else if (terrainType === TerrainType.URBAN) {
        terrainDifficulty = 'Urban';
      }
      
      let content = `
        <div class="stat-line">
          <span>Type:</span>
          <span class="stat-value">${terrainNames[tile.terrain as TerrainType]}</span>
        </div>
        <div class="stat-line">
          <span>Terrain Difficulty:</span>
          <span class="stat-value" style="color: ${
            terrainDifficulty === 'Easy' ? '#66ff66' : 
            terrainDifficulty === 'Rough' ? '#ffaa00' : 
            terrainDifficulty === 'Challenging' ? '#ff6666' : 
            '#cccccc'
          }">${terrainDifficulty}</span>
        </div>
        <div class="stat-line">
          <span>Position:</span>
          <span class="stat-value">${tile.x}, ${tile.y}</span>
        </div>
        <div class="stat-line">
          <span>Movement Cost:</span>
          <span class="stat-value">${effects.movement}</span>
        </div>
        <div class="stat-line">
          <span>Defense Bonus:</span>
          <span class="stat-value">${effects.defense}</span>
        </div>
        <div class="stat-line">
          <span>Attack Modifier:</span>
          <span class="stat-value">${effects.attack}</span>
        </div>
        <div style="margin-top: 10px; font-size: 12px; line-height: 1.4; color: #c0c0c0; font-style: italic;">
          ${effects.description}
        </div>
      `;
      
      if (tile.city) {
        const isOwnCity = tile.city.faction === this.currentFaction;
        const cityDefenseBonus = '+40%';
        const cityLevel = tile.city.level || CityLevel.OUTPOST;
        const cityLevelName = getCityLevelName(cityLevel);
        
        // Update max units based on level
        tile.city.maxUnits = getCityMaxUnits(cityLevel);
        
        content += `
          <div class="stat-line" style="margin-top: 10px;">
            <span>City:</span>
            <span class="stat-value">${tile.city.name}</span>
          </div>
          <div class="stat-line">
            <span>Level:</span>
            <span class="stat-value">${cityLevelName}</span>
          </div>
          <div class="stat-line">
            <span>Resources/Turn:</span>
            <span class="stat-value">+${getCityResourceGeneration(cityLevel)}</span>
          </div>`;
          
        // Show upgrade status if upgrading
        if (tile.city.upgradeInProgress) {
          content += `
          <div class="stat-line" style="color: #f0a030;">
            <span>Upgrading to:</span>
            <span class="stat-value">${getCityLevelName(tile.city.upgradeInProgress.toLevel)} (${tile.city.upgradeInProgress.turnsRemaining} turns)</span>
          </div>`;
        }
          
        // Only show unit capacity and training queue for own cities
        if (isOwnCity) {
          content += `
          <div class="stat-line">
            <span>Unit Capacity:</span>
            <span class="stat-value">${tile.city.unitsProduced.filter((id: string) => !id.startsWith('queue_')).length}/${tile.city.maxUnits}</span>
          </div>
          ${(() => {
            const unitsInCity = this.getUnitsInCity(tile.city);
            const friendlyUnits = unitsInCity.filter(u => u.faction === tile.city.faction);
            const enemyUnits = unitsInCity.filter(u => u.faction !== tile.city.faction);
            
            if (enemyUnits.length > 0 && friendlyUnits.length > 0) {
              return '<div class="stat-line" style="color: #ff6666;"><span>Status:</span><span class="stat-value">CONTESTED</span></div>';
            } else if (enemyUnits.length > 0) {
              return '<div class="stat-line" style="color: #ffaa00;"><span>Status:</span><span class="stat-value">OCCUPIED</span></div>';
            }
            return '';
          })()}
          ${tile.city.trainingQueue && tile.city.trainingQueue.length > 0 ? `
          <div class="stat-line" style="margin-top: 10px;">
            <span>Training Queue:</span>
          </div>
          ${tile.city.trainingQueue.map((unit: TrainingUnit) => `
            <div style="margin-left: 10px; font-size: 11px; color: #c0c0c0;">
              ${UNIT_DEFINITIONS[unit.type].name} - ${unit.turnsRemaining} turn${unit.turnsRemaining > 1 ? 's' : ''}
            </div>
          `).join('')}
          ` : ''}`;
        }
        
        content += `
          <div class="stat-line">
            <span>Control:</span>
            <span class="stat-value">${tile.city.faction || 'Neutral'}</span>
          </div>
          ${tile.city.captureProgress ? `
          <div class="stat-line" style="color: #ffaa00;">
            <span>Capture in Progress:</span>
            <span class="stat-value">${tile.city.captureProgress.faction}</span>
          </div>
          ` : ''}
          <div class="stat-line">
            <span>City Defense:</span>
            <span class="stat-value">${cityDefenseBonus}</span>
          </div>
        `;
      }
      
      tileDetails.innerHTML = content;
  }

  private updateFactionDisplay(): void {
    const factionName = document.getElementById('faction-name');
    const factionFlag = document.getElementById('faction-flag') as HTMLImageElement;
    
    // In online games, always show the player's own faction, not the current turn's faction
    const displayFaction = this.isOnlineGame ? (this.humanPlayerFaction || this.currentFaction) : this.currentFaction;
    
    if (factionName) {
      factionName.textContent = displayFaction;
      // Set faction color with black outline for readability
      if (displayFaction === 'United States Of America') {
        factionName.style.color = '#4B5320'; // Olive green
      } else if (displayFaction === 'United Kingdom') {
        factionName.style.color = '#003F87'; // Dark blue
      } else if (displayFaction === 'USSR') {
        factionName.style.color = '#CC0000'; // Soviet red
      } else if (displayFaction === 'Imperial Japan') {
        factionName.style.color = '#DC143C'; // Crimson red
      } else if (displayFaction === 'Canada') {
        factionName.style.color = '#D2691E'; // Autumn orange
      } else if (displayFaction === 'Fascist Italy') {
        factionName.style.color = '#009246'; // Italian green
      } else if (displayFaction === 'France') {
        factionName.style.color = '#7A9BB8'; // Muted pastel blue
      } else if (displayFaction === 'Greece') {
        factionName.style.color = '#0072CE'; // Greek blue
      } else {
        factionName.style.color = '#708090'; // Slate gray
      }
      factionName.style.textShadow = '1px 1px 2px black, -1px -1px 2px black, 1px -1px 2px black, -1px 1px 2px black';
    }
    
    if (factionFlag) {
      if (displayFaction === 'United States Of America') {
        factionFlag.src = '/flags/USA.jpg';
        factionFlag.alt = 'USA Flag';
      } else if (displayFaction === 'United Kingdom') {
        factionFlag.src = '/flags/Brits.jpg';
        factionFlag.alt = 'UK Flag';
      } else if (displayFaction === 'USSR') {
        factionFlag.src = '/flags/Soviet.jpg';
        factionFlag.alt = 'USSR Flag';
      } else if (displayFaction === '3rd Reich') {
        factionFlag.src = '/flags/Reich.jpg';
        factionFlag.alt = 'Reich Flag';
      } else if (displayFaction === 'Imperial Japan') {
        factionFlag.src = '/flags/Japs.jpg';
        factionFlag.alt = 'Japan Flag';
      } else if (displayFaction === 'Canada') {
        factionFlag.src = '/flags/Canadians.jpg';
        factionFlag.alt = 'Canada Flag';
      } else if (displayFaction === 'Fascist Italy') {
        factionFlag.src = '/flags/Italians.jpg';
        factionFlag.alt = 'Italy Flag';
      } else if (displayFaction === 'France') {
        factionFlag.src = '/flags/French.jpg';
        factionFlag.alt = 'France Flag';
      } else if (displayFaction === 'Greece') {
        factionFlag.src = '/flags/Greeks.jpg';
        factionFlag.alt = 'Greece Flag';
      }
    }
  }

  private moveUnit(unit: Unit, targetPos: Position): void {
    // Check if it's my turn in online games
    if (this.isOnlineGame && !this.isMyTurn) {
      this.soundManager.playError();
      this.showNotification("It's not your turn!");
      return;
    }
    
    // Check if target is in available moves
    const isValidMove = this.availableMoves?.some(
      move => move.x === targetPos.x && move.y === targetPos.y
    );
    
    if (!isValidMove) {
      if (unit.faction === 'United States Of America') {
        debug.log(`DEBUG: US unit move to ${targetPos.x},${targetPos.y} not in available moves`);
        debug.log('Available moves are:', this.availableMoves?.slice(0, 10));
      }
      // Play error sound for invalid move
      this.soundManager.playError();
      return;
    }
    
    // Calculate actual path cost using BFS
    const pathCost = this.calculatePathCost(unit.position, targetPos, unit);
    if (unit.faction === 'United States Of America') {
      debug.log(`DEBUG: US unit path cost from ${unit.position.x},${unit.position.y} to ${targetPos.x},${targetPos.y}: ${pathCost}, movement remaining: ${unit.movementRemaining}`);
    }
    
    if (pathCost > 0 && pathCost <= unit.movementRemaining) {
      // For vehicles, check if there's a mine in the straight line path
      const isVehicle = unit.category === UnitCategory.ARMOR || 
                        unit.category === UnitCategory.RECONNAISSANCE ||
                        unit.category === UnitCategory.TRANSPORT;
      
      let actualTarget = targetPos;
      let mineEncountered = false;
      
      if (isVehicle) {
        // Simple check: trace a line from current position to target
        const mineTile = this.findMineInPath(unit.position, targetPos);
        if (mineTile) {
          actualTarget = mineTile;
          mineEncountered = true;
        }
      }
      
      // Recalculate path cost if we're stopping at a mine
      const actualPathCost = mineEncountered ? 
        this.calculatePathCost(unit.position, actualTarget, unit) : 
        pathCost;
      
      // Store the old position before moving
      const oldPosition = { x: unit.position.x, y: unit.position.y };
      
      if (this.unitManager.moveUnit(unit.id, actualTarget, actualPathCost)) {
        // Reset unit cycle index since available units changed
        this.unitCycleIndex = -1;
        
        // Get the updated unit from unitManager after the move
        const movedUnit = this.unitManager.getUnit(unit.id);
        if (movedUnit) {
          unit = movedUnit; // Update our local reference
        }
        
        // Also update in gameState.units
        if (this.gameState.units.has(unit.id)) {
          const gameStateUnit = this.gameState.units.get(unit.id)!;
          gameStateUnit.position = actualTarget;
          gameStateUnit.movementRemaining = unit.movementRemaining;
        }
        
        // Clear vision cache since unit positions changed
        this.visionSystem.clearCache();
        
        // Play movement sound
        this.soundManager.playMove();
        
        // For mine encounters, delay detonation until after movement animation
        if (mineEncountered) {
          // Remove all movement for vehicles that hit mines
          unit.movementRemaining = 0;
          // Also update in gameState.units
          if (this.gameState.units.has(unit.id)) {
            this.gameState.units.get(unit.id)!.movementRemaining = 0;
          }
          this.availableMoves = null;
          
          // Remove the mine immediately to prevent double detonation
          const mineTile = this.map.getTile(actualTarget.x, actualTarget.y);
          const mineOwner = mineTile?.mine?.owner;
          if (mineTile && mineTile.mine) {
            mineTile.mine = undefined;
          }
          
          // Delay mine detonation effects by animation duration (300ms default)
          setTimeout(() => {
            // Pass the mine owner since we removed the mine already
            this.applyMineDetonation(unit, actualTarget, mineOwner);
            
            // CRITICAL: Sync with Firebase AFTER applying mine damage
            if (this.isOnlineGame && this.multiplayerService) {
              const completeGameState = this.buildCompleteGameState();
              this.multiplayerService.updateGameState(
                completeGameState,
                this.gameState.currentFaction,
                this.gameState.turn
              );
            }
            
            // Send notification AFTER syncing to prevent race conditions
            const message = `${unit.name} detonated an anti-tank mine at (${actualTarget.x}, ${actualTarget.y})!`;
            this.sendNotification({
              type: NotificationType.MINE_DETONATED,
              faction: unit.faction,
              targetFaction: mineOwner,
              data: { 
                unitName: unit.name,
                position: actualTarget,
                message: message
              },
              timestamp: Date.now()
            });
          }, 300);
        } else {
          // Normal mine check at destination (no mine in path)
          this.checkMineDetonation(unit, actualTarget);
        }
        
        // Save game state after move
        this.saveGameState();
        
        // Update tutorial progress if in tutorial mode
        // DISABLED: Now handled by the guided tutorial system
        // if (this.isTutorial && unit.faction === this.humanPlayerFaction) {
        //   this.updateTutorialProgress(actualPathCost);
        // }
        
        // Update available moves if unit still has movement
        const updatedUnit = this.unitManager.getUnit(unit.id);
        
        // Sync with Firebase in online games - just update the full game state
        // Don't send individual moves as they can cause race conditions
        if (this.isOnlineGame && this.multiplayerService) {
          const completeGameState = this.buildCompleteGameState();
          this.multiplayerService.updateGameState(
            completeGameState,
            this.gameState.currentFaction,
            this.gameState.turn
          );
        }
        if (updatedUnit) {
          if (updatedUnit.movementRemaining > 0) {
            this.calculateAvailableMoves(updatedUnit);
          } else {
            this.availableMoves = null;
          }
          
          // Recalculate enemies in range if unit hasn't attacked
          if (!updatedUnit.hasAttacked) {
            this.calculateEnemiesInRange(updatedUnit);
          } else {
            this.enemiesInRange = null;
          }
          
          this.updateUnitInfo(updatedUnit); // Update unit stats display
          
          // Check if unit moved into a city and start capture immediately
          this.checkAndStartCapture(updatedUnit);
          
          // Check if unit moved OUT of a city - this might allow enemies to start capturing
          const oldTile = this.map.getTile(oldPosition.x, oldPosition.y);
          if (oldTile?.city) {
            // Unit left a city, check if any remaining units can now start capturing
            this.checkForNewCapturesInCity(oldTile.city);
          }
          
          // Keep unit selected even with 0 movement if it can still attack
          if (updatedUnit.movementRemaining === 0 && updatedUnit.hasAttacked) {
            this.gameState.selectedUnit = null;
            this.availableMoves = null;
            this.enemiesInRange = null;
            this.updateUnitInfo(null);
          }
        }
      }
    }
  }

  private findMineInPath(start: Position, end: Position): Position | null {
    // Use a simple line-drawing algorithm to check tiles between start and end
    const dx = Math.abs(end.x - start.x);
    const dy = Math.abs(end.y - start.y);
    const sx = start.x < end.x ? 1 : -1;
    const sy = start.y < end.y ? 1 : -1;
    
    let x = start.x;
    let y = start.y;
    
    // For diagonal movement, check all tiles along the path
    if (dx > 0 && dy > 0) {
      // Moving diagonally - check each step
      while (x !== end.x || y !== end.y) {
        if (x !== end.x) x += sx;
        if (y !== end.y) y += sy;
        
        const tile = this.map.getTile(x, y);
        if (tile?.mine) {
          return { x, y };
        }
      }
    } else if (dx > 0) {
      // Moving horizontally
      while (x !== end.x) {
        x += sx;
        const tile = this.map.getTile(x, y);
        if (tile?.mine) {
          return { x, y };
        }
      }
    } else if (dy > 0) {
      // Moving vertically
      while (y !== end.y) {
        y += sy;
        const tile = this.map.getTile(x, y);
        if (tile?.mine) {
          return { x, y };
        }
      }
    }
    
    return null; // No mine found in path
  }
  
  private calculatePathCost(start: Position, end: Position, unit?: Unit): number {
    const queue: { pos: Position, cost: number }[] = [];
    const visited = new Map<string, number>();
    
    queue.push({ pos: start, cost: 0 });
    visited.set(`${start.x},${start.y}`, 0);
    
    // Use Dijkstra's algorithm with proper priority queue (same as calculateAvailableMoves)
    while (queue.length > 0) {
      // Sort queue by cost to process lowest cost nodes first
      queue.sort((a, b) => a.cost - b.cost);
      const { pos, cost } = queue.shift()!;
      
      // Found target
      if (pos.x === end.x && pos.y === end.y) {
        return cost;
      }
      
      // Check all adjacent tiles (8 directions)
      const directions = [
        { dx: 0, dy: -1 },   // up
        { dx: 1, dy: -1 },   // up-right
        { dx: 1, dy: 0 },    // right
        { dx: 1, dy: 1 },    // down-right
        { dx: 0, dy: 1 },    // down
        { dx: -1, dy: 1 },   // down-left
        { dx: -1, dy: 0 },   // left
        { dx: -1, dy: -1 }   // up-left
      ];
      
      for (const { dx, dy } of directions) {
        const newX = pos.x + dx;
        const newY = pos.y + dy;
        const newKey = `${newX},${newY}`;
        
        const tile = this.map.getTile(newX, newY);
        if (tile) {
          const terrainCost = this.getTerrainCost(tile.terrain, unit);
          const totalCost = cost + terrainCost;
          
          // Only process if we found a better path or haven't visited
          if (!visited.has(newKey) || visited.get(newKey)! > totalCost) {
            visited.set(newKey, totalCost);
            queue.push({ pos: { x: newX, y: newY }, cost: totalCost });
          }
        }
      }
    }
    
    return -1; // No path found
  }

  private updateUnitInfo(unit: Unit | null): void {
    const unitDetails = document.getElementById('unit-details');
    if (!unitDetails) return;
    
    if (!unit) {
      unitDetails.innerHTML = 'No unit selected';
      return;
    }
    
    // Determine which faction the player controls
    const controllingFaction = this.isOnlineGame ? this.humanPlayerFaction : this.currentFaction;
    
    {
      // const definition = UNIT_DEFINITIONS[unit.type];
      const squadComposition = this.getSquadComposition(unit.type);
      
      // Get current terrain
      const currentTile = this.map.getTile(unit.position.x, unit.position.y);
      const terrainType = currentTile?.terrain || TerrainType.OPEN_FIELDS;
      const terrainModifiers = this.getTerrainModifiers(terrainType, currentTile?.city, unit.faction, unit);
      
      let content = `
        <div class="stat-line">
          <span>Unit:</span>
          <span class="stat-value">${unit.name}</span>
        </div>
        <div class="stat-line">
          <span>Faction:</span>
          <span class="stat-value">${unit.faction === 'United States Of America' ? 'USA' : 
                                    unit.faction === 'United Kingdom' ? 'UK' : 
                                    unit.faction === 'USSR' ? 'USSR' : 
                                    unit.faction === 'Imperial Japan' ? 'Japan' : 
                                    unit.faction === 'Canada' ? 'Canada' :
                                    unit.faction === 'Fascist Italy' ? 'Italy' :
                                    unit.faction === 'France' ? 'France' :
                                    unit.faction === 'Greece' ? 'Greece' :
                                    unit.faction === '3rd Reich' ? '3rd Reich' :
                                    unit.faction}</span>
        </div>
        ${unit.isVeteran ? `
        <div class="stat-line" style="color: #ffd700; font-weight: bold;">
          <span>Status:</span>
          <span class="stat-value">⭐ VETERAN</span>
        </div>
        ` : ''}
        ${!unit.isVeteran && (unit.killCount || 0) > 0 ? `
        <div class="stat-line" style="color: #88ccff;">
          <span>Kills:</span>
          <span class="stat-value">${unit.killCount}/3 to veteran</span>
        </div>
        ` : ''}
        ${(unit as any).isHunkered ? `
        <div class="stat-line" style="color: #ffaa00;">
          <span>Status:</span>
          <span class="stat-value">🛡️ HUNKERED DOWN</span>
        </div>
        ` : ''}
        ${unit.shellshocked && unit.shellshocked.turnsRemaining > 0 ? `
        <div class="stat-line" style="color: #ff4444; font-weight: bold;">
          <span>Status:</span>
          <span class="stat-value">💥 SHELLSHOCKED (${unit.shellshocked.turnsRemaining} turns)</span>
        </div>
        <div class="stat-line" style="color: #ff6666; font-size: 0.9em;">
          <span style="margin-left: 20px;">-50% Movement, -25% Attack/Defense</span>
        </div>
        ` : ''}
        <div class="stat-line">
          <span>Health:</span>
          <span class="stat-value">${unit.stats.health}/${unit.stats.maxHealth}</span>
        </div>
        <div class="stat-line">
          <span>Movement:</span>
          <span class="stat-value">${unit.movementRemaining}/${unit.stats.movement}</span>
        </div>
        <div class="stat-line">
          <span>Attack:</span>
          <span class="stat-value">${unit.stats.attack} ${terrainModifiers.attack !== '0%' ? `<span style="color: ${terrainModifiers.attack.startsWith('+') ? '#66ff66' : '#ff6666'}">(${terrainModifiers.attack})</span>` : ''}</span>
        </div>
        <div class="stat-line">
          <span>Defense:</span>
          <span class="stat-value">${unit.stats.defense}${(unit as any).isHunkered ? ' <span style="color: #66ff66">(+2 Hunkered)</span>' : ''} ${terrainModifiers.defense !== '0%' ? `<span style="color: ${terrainModifiers.defense.startsWith('+') ? '#66ff66' : '#ff6666'}">(${terrainModifiers.defense})</span>` : ''}</span>
        </div>
        <div class="stat-line">
          <span>Range:</span>
          <span class="stat-value">${unit.stats.range}</span>
        </div>
        ${unit.carryingUnit ? `
        <div class="stat-line" style="color: #88ccff;">
          <span>Carrying:</span>
          <span class="stat-value">${UNIT_DEFINITIONS[unit.carryingUnit].name}</span>
        </div>
        ` : ''}
        ${unit.carryingUnits && unit.carryingUnits.length > 0 ? `
        <div class="stat-line" style="color: #88ccff;">
          <span>Carrying (${unit.carryingUnits.length}/2):</span>
          <span class="stat-value">${unit.carryingUnits.map(u => UNIT_DEFINITIONS[u].name).join(', ')}</span>
        </div>
        ` : ''}
        ${unit.towingUnit ? `
        <div class="stat-line" style="color: #ffaa00;">
          <span>Towing:</span>
          <span class="stat-value">${UNIT_DEFINITIONS[unit.towingUnit].name}</span>
        </div>
        ` : ''}
        ${unit.isTransport && unit.carryingUnit && isTransportAircraft(unit.type) ? `
        <button id="deploy-btn" style="
          width: 100%;
          padding: 10px 20px;
          margin-top: 10px;
          background: linear-gradient(135deg, #5a5a5a 0%, #4a4a4a 100%);
          color: #ffffff;
          border: 2px solid #6a6a6a;
          border-radius: 5px;
          cursor: pointer;
          font-weight: bold;
          font-size: 14px;
          transition: all 0.3s ease;
        ">DEPLOY PARATROOPERS</button>
        ` : ''}
        ${unit.isTransport && ((unit.carryingUnit && isGroundTransport(unit.type)) || (unit.carryingUnits && unit.carryingUnits.length > 0)) ? `
        <button id="disembark-btn" style="
          width: 100%;
          padding: 10px 20px;
          margin-top: 10px;
          background: linear-gradient(135deg, #4a6a4a 0%, #3a5a3a 100%);
          color: #ffffff;
          border: 2px solid #5a7a5a;
          border-radius: 5px;
          cursor: pointer;
          font-weight: bold;
          font-size: 14px;
          transition: all 0.3s ease;
        ">DISEMBARK TROOPS</button>
        ` : ''}
        ${unit.category === UnitCategory.ENGINEERS && unit.faction === controllingFaction && !(unit as any).hasPlacedMine && !(unit as any).isHunkered ? `
        <button id="mine-btn" style="
          width: 100%;
          padding: 10px 20px;
          margin-top: 10px;
          background: linear-gradient(135deg, #5a5a5a 0%, #4a4a4a 100%);
          color: #ffffff;
          border: 2px solid #6a6a6a;
          border-radius: 5px;
          cursor: pointer;
          font-weight: bold;
          font-size: 14px;
          transition: all 0.3s ease;
        ">PLACE MINE (5 RES)</button>
        ` : ''}
        ${unit.category === UnitCategory.ENGINEERS && unit.faction === controllingFaction && !(unit as any).hasHealed && !(unit as any).isHunkered ? `
        <button id="repair-btn" style="
          width: 100%;
          padding: 10px 20px;
          margin-top: 10px;
          background: linear-gradient(135deg, #5a5a5a 0%, #4a4a4a 100%);
          color: #ffffff;
          border: 2px solid #6a6a6a;
          border-radius: 5px;
          cursor: pointer;
          font-weight: bold;
          font-size: 14px;
          transition: all 0.3s ease;
        ">REPAIR</button>
        ` : ''}
        ${unit.category === UnitCategory.MEDICS && unit.faction === controllingFaction && !(unit as any).hasHealed && !(unit as any).isHunkered ? `
        <button id="heal-btn" style="
          width: 100%;
          padding: 10px 20px;
          margin-top: 10px;
          background: linear-gradient(135deg, #5a5a5a 0%, #4a4a4a 100%);
          color: #ffffff;
          border: 2px solid #6a6a6a;
          border-radius: 5px;
          cursor: pointer;
          font-weight: bold;
          font-size: 14px;
          transition: all 0.3s ease;
        ">HEAL</button>
        ` : ''}
        ${(unit.category === UnitCategory.INFANTRY || unit.category === UnitCategory.MEDICS || unit.category === UnitCategory.ENGINEERS) && 
          unit.faction === controllingFaction ? `
        <button id="hunker-btn" style="
          width: 100%;
          padding: 10px 20px;
          margin-top: 10px;
          background: linear-gradient(135deg, #5a5a5a 0%, #4a4a4a 100%);
          color: #ffffff;
          border: 2px solid #6a6a6a;
          border-radius: 5px;
          cursor: pointer;
          font-weight: bold;
          font-size: 14px;
          transition: all 0.3s ease;
        ">${(unit as any).isHunkered ? 'STAND UP' : 'HUNKER DOWN'}</button>
        ` : ''}
        ${(unit.category === UnitCategory.INFANTRY || 
          unit.category === UnitCategory.ENGINEERS || 
          unit.category === UnitCategory.MEDICS) && 
          unit.faction === controllingFaction && 
          !(unit as any).isHunkered ? `
        <button id="board-btn" style="
          width: 100%;
          padding: 10px 20px;
          margin-top: 10px;
          background: linear-gradient(135deg, #4a5a4a 0%, #3a4a3a 100%);
          color: #ffffff;
          border: 2px solid #5a6a5a;
          border-radius: 5px;
          cursor: pointer;
          font-weight: bold;
          font-size: 14px;
          transition: all 0.3s ease;
        ">BOARD TRANSPORT</button>
        ` : ''}
        ${isGroundTransport(unit.type) && unit.faction === controllingFaction && unit.towingUnit ? `
        <button id="untow-btn" style="
          width: 100%;
          padding: 10px 20px;
          margin-top: 10px;
          background: linear-gradient(135deg, #6a4a4a 0%, #5a3a3a 100%);
          color: #ffffff;
          border: 2px solid #7a5a5a;
          border-radius: 5px;
          cursor: pointer;
          font-weight: bold;
          font-size: 14px;
          transition: all 0.3s ease;
        ">UNTOW ARTILLERY</button>
        ` : ''}
        ${isGroundTransport(unit.type) && unit.faction === controllingFaction && !unit.towingUnit ? `
        <button id="tow-btn" style="
          width: 100%;
          padding: 10px 20px;
          margin-top: 10px;
          background: linear-gradient(135deg, #5a5a4a 0%, #4a4a3a 100%);
          color: #ffffff;
          border: 2px solid #6a6a5a;
          border-radius: 5px;
          cursor: pointer;
          font-weight: bold;
          font-size: 14px;
          transition: all 0.3s ease;
        ">TOW ARTILLERY</button>
        ` : ''}
        <div style="margin-top: 10px; font-size: 11px; line-height: 1.3; color: #c0c0c0;">
          <strong>Squad Composition:</strong><br/>
          ${squadComposition}
        </div>
      `;
      
      unitDetails.innerHTML = content;
      
      // Add event listener for deploy button if present
      const deployBtn = document.getElementById('deploy-btn');
      if (deployBtn) {
        deployBtn.addEventListener('click', () => {
          this.deployParatroopers(unit);
        });
      }
      
      // Add event listener for disembark button if present
      const disembarkBtn = document.getElementById('disembark-btn');
      if (disembarkBtn) {
        disembarkBtn.addEventListener('click', () => {
          this.disembarkTroops(unit);
        });
      }
      
      // Add event listener for mine button if present
      const mineBtn = document.getElementById('mine-btn');
      if (mineBtn) {
        mineBtn.addEventListener('click', () => {
          this.startMinePlacement(unit);
        });
      }
      
      // Add event listener for repair button if present
      const repairBtn = document.getElementById('repair-btn');
      if (repairBtn) {
        repairBtn.addEventListener('click', () => {
          this.startHealingMode(unit, 'repair');
        });
      }
      
      // Add event listener for heal button if present
      const healBtn = document.getElementById('heal-btn');
      if (healBtn) {
        healBtn.addEventListener('click', () => {
          this.startHealingMode(unit, 'heal');
        });
      }
      
      // Add event listener for hunker button if present
      const hunkerBtn = document.getElementById('hunker-btn');
      if (hunkerBtn) {
        hunkerBtn.addEventListener('click', () => {
          this.toggleHunker(unit);
        });
      }
      
      // Add event listener for board button if present
      const boardBtn = document.getElementById('board-btn');
      if (boardBtn) {
        boardBtn.addEventListener('click', () => {
          this.startBoardingMode(unit);
        });
      }
      
      // Add event listener for tow button if present
      const towBtn = document.getElementById('tow-btn');
      if (towBtn) {
        towBtn.addEventListener('click', () => {
          this.startTowingMode(unit);
        });
      }
      
      // Add event listener for untow button if present
      const untowBtn = document.getElementById('untow-btn');
      if (untowBtn) {
        untowBtn.addEventListener('click', () => {
          this.untowArtillery(unit);
          this.updateUnitInfo(unit);
        });
      }
    }
  }
  
  private startTowingMode(transport: Unit): void {
    // Check if it's your turn in online games
    if (this.isOnlineGame && !this.isMyTurn) {
      this.soundManager.playError();
      this.showNotification("You can only tow units on your turn!");
      return;
    }
    
    // Check if transport can tow
    if (!isGroundTransport(transport.type)) {
      this.showNotification('Only transport vehicles can tow!');
      return;
    }
    
    if (transport.towingUnit) {
      this.showNotification('Transport is already towing a unit!');
      return;
    }
    
    // Find adjacent artillery that can be towed
    const offsets = [
      { x: 0, y: -1 }, // North
      { x: 1, y: 0 },  // East
      { x: 0, y: 1 },  // South
      { x: -1, y: 0 }  // West
    ];
    
    let artilleryFound = false;
    for (const offset of offsets) {
      const checkPos = {
        x: transport.position.x + offset.x,
        y: transport.position.y + offset.y
      };
      
      const unit = this.unitManager.getUnitAt(checkPos);
      if (unit && unit.faction === transport.faction && 
          unit.category === UnitCategory.ARTILLERY &&
          UNIT_DEFINITIONS[unit.type].movement === 2) {
        // Found a valid artillery to tow
        this.towArtillery(transport, unit);
        this.updateUnitInfo(transport);
        artilleryFound = true;
        break;
      }
    }
    
    if (!artilleryFound) {
      this.showNotification('No towable artillery adjacent to transport!');
    }
  }
  
  private startBoardingMode(unit: Unit): void {
    // Toggle off if already in boarding mode
    if (this.isBoardingMode) {
      this.isBoardingMode = false;
      this.boardingUnit = null;
      this.validBoardTargets = [];
      this.updateUnitInfo(unit);
      return;
    }
    
    // Check if it's your turn in online games
    if (this.isOnlineGame && !this.isMyTurn) {
      this.soundManager.playError();
      this.showNotification("You can only board units on your turn!");
      return;
    }
    
    // Check if unit can board
    if (!canBeTransported(unit.type)) {
      this.showNotification('This unit cannot board transports!');
      return;
    }
    
    // Enter board selection mode (like mine placement)
    this.boardingUnit = unit;
    this.isBoardingMode = true;
    
    // Find valid transport targets
    this.validBoardTargets = [];
    const offsets = [
      { x: 0, y: -1 }, // North
      { x: 1, y: 0 },  // East
      { x: 0, y: 1 },  // South
      { x: -1, y: 0 }, // West
      { x: -1, y: -1 }, // Northwest
      { x: 1, y: -1 },  // Northeast
      { x: 1, y: 1 },   // Southeast
      { x: -1, y: 1 },  // Southwest
    ];
    
    // Debug logging
    console.log(`[BOARD MODE] Checking for transports adjacent to ${unit.name} at (${unit.position.x}, ${unit.position.y})`);
    
    for (const offset of offsets) {
      const checkPos = {
        x: unit.position.x + offset.x,
        y: unit.position.y + offset.y
      };
      
      const adjacentUnit = this.unitManager.getUnitAt(checkPos);
      if (adjacentUnit) {
        console.log(`[BOARD MODE] Found unit at (${checkPos.x}, ${checkPos.y}): ${adjacentUnit.name}, Type: ${adjacentUnit.type}, Faction: ${adjacentUnit.faction}, Is transport: ${isGroundTransport(adjacentUnit.type)}`);
        
        if (adjacentUnit.faction === unit.faction && isGroundTransport(adjacentUnit.type)) {
          // Check capacity based on transport type
          let hasCapacity = false;
          if (isTruck(adjacentUnit.type)) {
            // Trucks can carry 2 units
            hasCapacity = !adjacentUnit.carryingUnits || adjacentUnit.carryingUnits.length < 2;
            console.log(`[BOARD MODE] Truck capacity: ${adjacentUnit.carryingUnits?.length || 0}/2, has capacity: ${hasCapacity}`);
          } else {
            // Halftracks can carry 1 unit
            hasCapacity = !adjacentUnit.carryingUnit;
            console.log(`[BOARD MODE] Halftrack carrying: ${adjacentUnit.carryingUnit ? 'occupied' : 'empty'}, has capacity: ${hasCapacity}`);
          }
          
          if (hasCapacity) {
            this.validBoardTargets.push(checkPos);
            console.log(`[BOARD MODE] Added valid transport at (${checkPos.x}, ${checkPos.y})`);
          }
        }
      }
    }
    
    console.log(`[BOARD MODE] Found ${this.validBoardTargets.length} valid transport targets`);
    
    if (this.validBoardTargets.length === 0) {
      this.showNotification('No empty transports adjacent to this unit!');
      this.isBoardingMode = false;
      this.boardingUnit = null;
      return;
    }
    
    // Visual feedback - use showNotification for instruction messages
    this.showNotification('Select a transport to board (Click Board again to cancel)');
  }
  
  private boardTransport(unit: Unit, transport: Unit): void {
    debug.log(`[BOARDING START] Unit ${unit.name} (${unit.id}) boarding ${transport.name} (${transport.id})`);
    debug.log(`[BOARDING] Transport type: ${transport.type}, Is truck: ${isTruck(transport.type)}`);
    
    // Check if unit has already boarded/disembarked this turn
    if (unit.hasBoarded) {
      this.showNotification('Unit can only board/disembark once per turn!');
      return;
    }
    
    // Note: Removed transport.hasBoarded check - vehicles should be able to have multiple units board them
    // Only the individual boarding unit should be restricted to once per turn
    
    // Check if this is a truck (can carry 2 units) or halftrack/aircraft (1 unit)
    if (isTruck(transport.type)) {
      // Initialize carryingUnits array if not present
      if (!transport.carryingUnits) {
        transport.carryingUnits = [];
        transport.carryingUnitsData = [];
      }
      
      debug.log(`[BOARDING] Truck capacity: ${transport.carryingUnits.length}/2`);
      
      // Check capacity (trucks can carry 2 units)
      if (transport.carryingUnits.length >= 2) {
        this.showNotification('Transport is at full capacity!');
        return;
      }
      
      // Add unit to the truck - preserve both type and full data
      transport.carryingUnits.push(unit.type);
      transport.carryingUnitsData!.push({...unit}); // Clone the unit data
      debug.log(`[BOARDING] Added to truck, now carrying ${transport.carryingUnits.length} units`);
    } else {
      // Halftrack or aircraft - single unit capacity
      debug.log(`[BOARDING] Halftrack/aircraft currently carrying: ${transport.carryingUnit || 'nothing'}`);
      
      if (transport.carryingUnit) {
        this.showNotification('Transport already carrying a unit!');
        return;
      }
      // Preserve both type and full data (BEFORE setting hasBoarded)
      transport.carryingUnit = unit.type;
      transport.carryingUnitData = {...unit}; // Clone the unit data
      debug.log(`[BOARDING] Added to halftrack/aircraft, now carrying: ${transport.carryingUnit}`);
    }
    
    transport.isTransport = true;
    
    // Mark the boarding unit as having used their boarding action this turn
    // Note: We don't mark the transport since it should be able to have multiple units board
    unit.hasBoarded = true;
    
    // Remove the infantry unit from the map (but data is preserved in transport)
    debug.log(`[BOARDING REMOVE] Removing unit ${unit.name} (${unit.id}) from map`);
    this.unitManager.removeUnit(unit.id);
    
    // Also ensure it's removed from gameState.units immediately
    this.gameState.units.delete(unit.id);
    
    // Play boarding sound
    this.soundManager.playMove();
    
    // CRITICAL: Sync with Firebase BEFORE sending notification
    // This prevents the notification from triggering a sync that re-adds the unit
    if (this.isOnlineGame && this.multiplayerService) {
      debug.log(`[BOARDING SYNC] Syncing state after removing boarded unit from map`);
      const completeGameState = this.buildCompleteGameState();
      this.multiplayerService.updateGameState(
        completeGameState,
        this.gameState.currentFaction,
        this.gameState.turn
      );
    }
    
    // Send notification AFTER syncing
    this.sendNotification({
      type: NotificationType.UNIT_BOARDED,
      faction: this.currentFaction,
      data: { unitName: unit.name, transportName: UNIT_DEFINITIONS[transport.type].name },
      timestamp: Date.now()
    });
    this.updateUnitInfo(transport);
  }
  
  private towArtillery(transport: Unit, artillery: Unit): void {
    // Check if transport can tow (trucks and halftracks)
    if (!isGroundTransport(transport.type)) {
      this.showNotification('Only transport vehicles can tow artillery!');
      return;
    }
    
    // Check if transport has already towed/untowed this turn
    if (transport.hasTowed) {
      this.showNotification('Transport can only tow/untow once per turn!');
      return;
    }
    
    // Check if artillery can be towed (movement 2 artillery)
    const artilleryDef = UNIT_DEFINITIONS[artillery.type];
    if (artillery.category !== UnitCategory.ARTILLERY || artilleryDef.movement !== 2) {
      this.showNotification('This unit cannot be towed!');
      return;
    }
    
    // Check if transport is already towing
    if (transport.towingUnit) {
      this.showNotification('Transport is already towing a unit!');
      return;
    }
    
    // Check if they belong to the same faction
    if (transport.faction !== artillery.faction) {
      this.showNotification('You can only tow your own artillery!');
      return;
    }
    
    // Check if transport is adjacent to artillery
    const distance = Math.abs(transport.position.x - artillery.position.x) + 
                    Math.abs(transport.position.y - artillery.position.y);
    if (distance > 1) {
      this.showNotification('Transport must be adjacent to artillery to tow it!');
      return;
    }
    
    // Store artillery data in transport (like boarding)
    transport.towingUnit = artillery.type;
    transport.towingUnitData = {...artillery}; // Clone the artillery data
    transport.hasTowed = true; // Mark that towing action was used this turn
    
    // Remove the artillery unit from the map (like boarding)
    this.unitManager.removeUnit(artillery.id);
    this.gameState.units.delete(artillery.id);
    
    // Play sound
    this.soundManager.playMove();
    
    // Sync with Firebase if online
    if (this.isOnlineGame && this.multiplayerService) {
      const completeGameState = this.buildCompleteGameState();
      this.multiplayerService.updateGameState(
        completeGameState,
        this.gameState.currentFaction,
        this.gameState.turn
      );
    }
    
    // Send notification
    this.sendNotification({
      type: NotificationType.UNIT_BOARDED,
      faction: this.currentFaction,
      data: { 
        unitName: UNIT_DEFINITIONS[artillery.type].name, 
        transportName: UNIT_DEFINITIONS[transport.type].name 
      },
      timestamp: Date.now()
    });
    
    this.showNotification(`${UNIT_DEFINITIONS[transport.type].name} is now towing ${UNIT_DEFINITIONS[artillery.type].name}`);
  }
  
  private untowArtillery(transport: Unit): void {
    if (!transport.towingUnit || !transport.towingUnitData) {
      this.showNotification('Transport is not towing anything!');
      return;
    }
    
    // Check if transport has already towed/untowed this turn
    if (transport.hasTowed) {
      this.showNotification('Transport can only tow/untow once per turn!');
      return;
    }
    
    // Check if it's your turn in online games
    if (this.isOnlineGame && !this.isMyTurn) {
      this.soundManager.playError();
      this.showNotification("You can only untow units on your turn!");
      return;
    }
    
    // Find an empty adjacent tile to place the artillery
    const offsets = [
      { x: 0, y: -1 }, // North
      { x: 1, y: 0 },  // East
      { x: 0, y: 1 },  // South
      { x: -1, y: 0 }, // West
      { x: -1, y: -1 }, // Northwest
      { x: 1, y: -1 },  // Northeast
      { x: 1, y: 1 },   // Southeast
      { x: -1, y: 1 },  // Southwest
    ];
    
    let emptyTile: Position | null = null;
    for (const offset of offsets) {
      const checkPos = {
        x: transport.position.x + offset.x,
        y: transport.position.y + offset.y
      };
      
      // Check if tile is empty and valid
      if (checkPos.x >= 0 && checkPos.x < this.map.getWidth() &&
          checkPos.y >= 0 && checkPos.y < this.map.getHeight()) {
        const existingUnit = this.unitManager.getUnitAt(checkPos);
        if (!existingUnit) {
          emptyTile = checkPos;
          break;
        }
      }
    }
    
    if (!emptyTile) {
      this.showNotification('No empty tiles to untow artillery!');
      return;
    }
    
    // Restore the artillery unit from stored data
    const restoredArtillery = {...transport.towingUnitData};
    restoredArtillery.position = emptyTile;
    restoredArtillery.displayPosition = emptyTile; // Update display position
    restoredArtillery.targetPosition = emptyTile; // Update target position
    restoredArtillery.movementRemaining = 0; // Can't move after being untowed
    
    // Add artillery back to the game
    this.unitManager.addExistingUnit(restoredArtillery);
    this.gameState.units.set(restoredArtillery.id, restoredArtillery);
    
    // Clear towing data from transport
    transport.towingUnit = undefined;
    transport.towingUnitData = undefined;
    transport.hasTowed = true; // Mark that untowing action was used this turn
    
    // Play sound
    this.soundManager.playMove();
    
    // Sync with Firebase if online
    if (this.isOnlineGame && this.multiplayerService) {
      const completeGameState = this.buildCompleteGameState();
      this.multiplayerService.updateGameState(
        completeGameState,
        this.gameState.currentFaction,
        this.gameState.turn
      );
    }
    
    // Send notification
    this.showNotification(`${UNIT_DEFINITIONS[restoredArtillery.type].name} has been untowed`);
  }
  
  private disembarkTroops(transport: Unit): void {
    if (!transport.isTransport || !isGroundTransport(transport.type)) {
      return;
    }
    
    // Check if it's your turn in online games
    if (this.isOnlineGame && !this.isMyTurn) {
      this.soundManager.playError();
      this.showNotification("You can only disembark units on your turn!");
      return;
    }
    
    // Note: Removed transport.hasBoarded check for disembarking
    // Transports should be able to disembark multiple units in one turn if they have them
    
    // Check if transport has any units
    const hasTruckUnits = isTruck(transport.type) && transport.carryingUnits && transport.carryingUnits.length > 0;
    const hasHalftrackUnit = !isTruck(transport.type) && transport.carryingUnit;
    
    if (!hasTruckUnits && !hasHalftrackUnit) {
      this.showNotification('No units to disembark!');
      return;
    }
    
    // Check if transport belongs to current player
    if (transport.faction !== this.currentFaction) {
      this.showNotification('You can only disembark your own units!');
      return;
    }
    
    // Find empty adjacent tiles (including diagonals for disembarking)
    const offsets = [
      { x: 0, y: -1 }, // North
      { x: 1, y: 0 },  // East
      { x: 0, y: 1 },  // South
      { x: -1, y: 0 }, // West
      { x: -1, y: -1 }, // Northwest
      { x: 1, y: -1 },  // Northeast
      { x: 1, y: 1 },   // Southeast
      { x: -1, y: 1 },  // Southwest
    ];
    
    const emptyTiles: Position[] = [];
    for (const offset of offsets) {
      const checkPos = {
        x: transport.position.x + offset.x,
        y: transport.position.y + offset.y
      };
      
      // Check if tile is empty and valid
      if (checkPos.x >= 0 && checkPos.x < this.map.getWidth() &&
          checkPos.y >= 0 && checkPos.y < this.map.getHeight()) {
        const existingUnit = this.unitManager.getUnitAt(checkPos);
        if (!existingUnit) {
          emptyTiles.push(checkPos);
        }
      }
    }
    
    // Determine units to disembark
    const unitsToDisembark: UnitType[] = [];
    if (isTruck(transport.type) && transport.carryingUnits) {
      unitsToDisembark.push(...transport.carryingUnits);
    } else if (transport.carryingUnit) {
      unitsToDisembark.push(transport.carryingUnit);
    }
    
    if (emptyTiles.length < unitsToDisembark.length) {
      this.showNotification(`Need ${unitsToDisembark.length} empty tiles to disembark all units!`);
      return;
    }
    
    // Store notifications to send after sync
    const disembarkNotifications: any[] = [];
    
    // Disembark all units
    if (transport.carryingUnitsData && transport.carryingUnitsData.length > 0) {
      // Truck with preserved unit data
      for (let i = 0; i < transport.carryingUnitsData.length && i < emptyTiles.length; i++) {
        const restoredUnit = transport.carryingUnitsData[i];
        const position = emptyTiles[i];
        
        // Create the unit at the empty tile
        const unit = this.unitManager.createUnit(
          restoredUnit.type,
          restoredUnit.faction,
          position
        );
        
        // Restore preserved attributes
        unit.name = restoredUnit.name;
        unit.stats = {...restoredUnit.stats};
        unit.hometown = restoredUnit.hometown;
        unit.level = restoredUnit.level;
        unit.movementRemaining = 0; // Can't move after disembarking
        unit.hasAttacked = true; // Can't attack after disembarking
        // Don't set hasBoarded on the disembarked unit - only the transport has the restriction
        
        // Add to gameState
        this.gameState.units.set(unit.id, unit)
        
        // Queue notification for this unit
        disembarkNotifications.push({
          type: NotificationType.UNIT_DISEMBARKED,
          faction: this.currentFaction,
          data: { unitName: unit.name, position: position },
          timestamp: Date.now()
        });
      }
      transport.carryingUnitsData = undefined;
      transport.carryingUnits = undefined;
    } else if (transport.carryingUnitData) {
      // Single unit with preserved data
      const restoredUnit = transport.carryingUnitData;
      const position = emptyTiles[0];
      
      const unit = this.unitManager.createUnit(
        restoredUnit.type,
        restoredUnit.faction,
        position
      );
      
      // Restore preserved attributes
      unit.name = restoredUnit.name;
      unit.stats = {...restoredUnit.stats};
      unit.hometown = restoredUnit.hometown;
      unit.level = restoredUnit.level;
      unit.movementRemaining = 0;
      unit.hasAttacked = true; // Can't attack after disembarking
      // Don't set hasBoarded on the disembarked unit - only the transport has the restriction
      
      // Add to gameState
      this.gameState.units.set(unit.id, unit);
      
      // Queue notification
      disembarkNotifications.push({
        type: NotificationType.UNIT_DISEMBARKED,
        faction: this.currentFaction,
        data: { unitName: unit.name, position: position },
        timestamp: Date.now()
      });
      transport.carryingUnitData = undefined;
      transport.carryingUnit = undefined;
    } else {
      // Fallback for old saves without preserved data
      for (let i = 0; i < unitsToDisembark.length; i++) {
        const unitType = unitsToDisembark[i];
        const position = emptyTiles[i];
        
        const unit = this.unitManager.createUnit(
          unitType,
          transport.faction,
          position
        );
        unit.movementRemaining = 0;
        unit.hasAttacked = true; // Can't attack after disembarking
        // Don't set hasBoarded on the disembarked unit - only the transport has the restriction
        
        // Add to gameState
        this.gameState.units.set(unit.id, unit);
        
        // Queue notification
        disembarkNotifications.push({
          type: NotificationType.UNIT_DISEMBARKED,
          faction: this.currentFaction,
          data: { unitName: UNIT_DEFINITIONS[unit.type].name, position: position },
          timestamp: Date.now()
        });
      }
      
      // Clear the old-style transport status
      if (isTruck(transport.type)) {
        transport.carryingUnits = undefined;
      } else {
        transport.carryingUnit = undefined;
      }
    }
    
    // Clear transport flag if no units left
    if (!transport.carryingUnit && !transport.carryingUnits) {
      transport.isTransport = false;
    }
    
    // Note: Removed transport.hasBoarded marking for disembarking
    // Transport restrictions only apply to individual units boarding, not transport capacity
    
    // Update the transport in gameState.units to ensure proper sync
    if (this.gameState.units.has(transport.id)) {
      const gameStateTransport = this.gameState.units.get(transport.id)!;
      gameStateTransport.carryingUnit = undefined;
      gameStateTransport.carryingUnits = undefined;
      gameStateTransport.carryingUnitData = undefined;
      gameStateTransport.carryingUnitsData = undefined;
      gameStateTransport.isTransport = false;
    }
    
    // CRITICAL: Sync with Firebase BEFORE sending notifications
    // This prevents notifications from triggering a sync that overwrites our changes
    if (this.isOnlineGame && this.multiplayerService) {
      debug.log(`[DISEMBARK SYNC] Syncing state after creating disembarked units`);
      const completeGameState = this.buildCompleteGameState();
      this.multiplayerService.updateGameState(
        completeGameState,
        this.gameState.currentFaction,
        this.gameState.turn
      );
    }
    
    // Now send all queued notifications AFTER syncing
    disembarkNotifications.forEach(notification => {
      this.sendNotification(notification);
    });
    
    // Play sound
    this.soundManager.playMove();
    
    // Update display
    this.updateUnitInfo(transport);
  }
  
  private deployParatroopers(transport: Unit): void {
    if (!transport.isTransport || (!transport.carryingUnit && !transport.carryingUnitData)) {
      return;
    }
    
    // Check if it's your turn in online games
    if (this.isOnlineGame && !this.isMyTurn) {
      this.soundManager.playError();
      this.showNotification("You can only deploy paratroopers on your turn!");
      return;
    }
    
    // Check if transport belongs to current player
    if (transport.faction !== this.currentFaction) {
      this.showNotification('You can only deploy your own units!');
      return;
    }
    
    // Check if there's a unit already at this position
    const existingUnit = this.unitManager.getUnitAt(transport.position);
    if (existingUnit && existingUnit.id !== transport.id) {
      this.showNotification('Cannot deploy - tile is occupied!');
      return;
    }
    
    let airborneUnit: Unit;
    
    if (transport.carryingUnitData) {
      // Use preserved unit data
      const restoredUnit = transport.carryingUnitData;
      airborneUnit = this.unitManager.createUnit(
        restoredUnit.type,
        restoredUnit.faction,
        transport.position
      );
      
      // Restore preserved attributes
      airborneUnit.name = restoredUnit.name;
      airborneUnit.stats = {...restoredUnit.stats};
      airborneUnit.hometown = restoredUnit.hometown;
      airborneUnit.level = restoredUnit.level;
      
      // Add to gameState
      this.gameState.units.set(airborneUnit.id, airborneUnit);
    } else {
      // Fallback for old saves
      airborneUnit = this.unitManager.createUnit(
        transport.carryingUnit!,
        transport.faction,
        transport.position
      );
      
      // Copy hometown if present
      if (transport.hometown) {
        airborneUnit.hometown = transport.hometown;
      }
      
      // Add to gameState
      this.gameState.units.set(airborneUnit.id, airborneUnit);
    }
    
    // Set movement to 0 (deployed units can't move same turn)
    airborneUnit.movementRemaining = 0;
    
    // Remove the transport aircraft
    this.unitManager.removeUnit(transport.id);
    
    // Update selected unit to the new airborne unit
    this.gameState.selectedUnit = airborneUnit.id;
    
    // Update UI
    this.updateUnitInfo(airborneUnit);
    
    // CRITICAL: Sync with Firebase BEFORE sending notification
    // This prevents the notification from triggering a sync that might re-add units
    if (this.isOnlineGame && this.multiplayerService) {
      debug.log(`[PARADROP SYNC] Syncing state after deploying paratroopers`);
      const completeGameState = this.buildCompleteGameState();
      this.multiplayerService.updateGameState(
        completeGameState,
        this.gameState.currentFaction,
        this.gameState.turn
      );
    }
    
    // Send notification AFTER syncing
    this.sendNotification({
      type: NotificationType.PARATROOPERS_DEPLOYED,
      faction: this.currentFaction,
      data: { unitName: airborneUnit.name },
      timestamp: Date.now()
    });
    
    // Play sound effect
    this.soundManager.playDeploySound();
    
    // Save game state
    this.saveGameState();
  }
  
  private isPlacingMine: boolean = false;
  private minePlacementEngineer: Unit | null = null;
  
  private startMinePlacement(engineer: Unit): void {
    // Toggle mine placement mode
    if (this.isPlacingMine && this.minePlacementEngineer?.id === engineer.id) {
      this.cancelMinePlacement();
      return;
    }
    
    // Check if engineer belongs to current player
    if (engineer.faction !== this.currentFaction) {
      this.showNotification('You can only use your own engineers!');
      return;
    }
    
    // Check resources
    const resources = this.gameState.resources.get(this.currentFaction) || 0;
    if (resources < 5) {
      this.showNotification('Not enough resources! Need 5 resources to place a mine.');
      return;
    }
    
    // Enter mine placement mode
    this.isPlacingMine = true;
    this.minePlacementEngineer = engineer;
    
    // Clear movement highlights
    this.availableMoves = null;
    this.enemiesInRange = null;
    
    // Update button text to Cancel
    const mineBtn = document.getElementById('mine-btn') as HTMLButtonElement;
    if (mineBtn) {
      mineBtn.textContent = 'CANCEL PLACEMENT';
      mineBtn.style.background = 'linear-gradient(135deg, #8a4a4a 0%, #6a3a3a 100%)';
    }
    
    // Show mine placement UI
    this.showMinePlacementUI(engineer);
  }
  
  private cancelMinePlacement(): void {
    this.isPlacingMine = false;
    this.minePlacementTiles = null; // Clear the yellow tiles
    
    // Clear placement UI
    document.querySelectorAll('.mine-placement-tile').forEach(el => el.remove());
    
    // Reset button
    const mineBtn = document.getElementById('mine-btn') as HTMLButtonElement;
    if (mineBtn) {
      mineBtn.textContent = 'PLACE MINE (5 RES)';
      mineBtn.style.background = 'linear-gradient(135deg, #5a5a5a 0%, #4a4a4a 100%)';
    }
    
    // Restore movement highlights if engineer is selected
    if (this.minePlacementEngineer && this.gameState.selectedUnit === this.minePlacementEngineer.id) {
      this.calculateAvailableMoves(this.minePlacementEngineer);
      if (!this.minePlacementEngineer.hasAttacked) {
        this.calculateEnemiesInRange(this.minePlacementEngineer);
      }
    }
    
    this.minePlacementEngineer = null;
  }
  
  private showMinePlacementUI(engineer: Unit): void {
    // Clear any existing mine placement UI
    document.querySelectorAll('.mine-placement-tile').forEach(el => el.remove());
    
    // Get valid tiles for mine placement (1 tile radius including diagonals)
    const validTiles: Position[] = [];
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        if (dx === 0 && dy === 0) continue; // Skip engineer's own tile
        
        const x = engineer.position.x + dx;
        const y = engineer.position.y + dy;
        
        // Check if tile is within map bounds
        const tile = this.map.getTile(x, y);
        if (!tile) continue;
        
        // Check if tile already has a mine
        if (tile.mine) continue;
        
        // Check if tile has a unit on it
        const unitAtTile = this.unitManager.getUnitAt({ x, y });
        if (unitAtTile) continue;
        
        validTiles.push({ x, y });
      }
    }
    
    // Use the renderer to highlight tiles in yellow through game state
    this.minePlacementTiles = validTiles;
  }
  
  private minePlacementTiles: Position[] | null = null;
  
  private placeMine(engineer: Unit, position: Position): void {
    // Check if it's your turn in online games
    if (this.isOnlineGame && !this.isMyTurn) {
      this.soundManager.playError();
      this.showNotification("You can only place mines on your turn!");
      return;
    }
    
    // Double-check that no unit has moved to this tile
    const unitAtTile = this.unitManager.getUnitAt(position);
    if (unitAtTile) {
      this.showNotification('Cannot place mine - tile is now occupied!');
      return;
    }
    
    // Deduct resources
    const currentResources = this.gameState.resources.get(this.currentFaction) || 0;
    this.gameState.resources.set(this.currentFaction, currentResources - 5);
    
    // Place mine on tile
    const tile = this.map.getTile(position.x, position.y);
    if (tile) {
      tile.mine = {
        owner: engineer.faction,
        id: `mine_${Date.now()}_${Math.random()}`
      };
      
      // Track mine placement in battle statistics
      const factionStats = this.gameState.battleStats?.get(engineer.faction);
      if (factionStats) {
        factionStats.minesPlaced++;
      }
    }
    
    // Mark engineer as having placed mine this turn
    (engineer as any).hasPlacedMine = true;
    
    // Play placement sound
    this.soundManager.playClick();
    
    // Exit mine placement mode
    this.cancelMinePlacement();
    
    // Update resource display
    this.updateResourceDisplay();
    
    // Update unit info to hide mine button
    this.updateUnitInfo(engineer);
    
    // Save game state
    this.saveGameState();
    
    // CRITICAL: Sync with Firebase BEFORE sending notification
    // This ensures the mine is in the state when the notification triggers
    if (this.isOnlineGame && this.multiplayerService) {
      debug.log(`[MINE SYNC] Syncing state after placing mine at (${position.x},${position.y})`);
      const completeGameState = this.buildCompleteGameState();
      this.multiplayerService.updateGameState(
        completeGameState,
        this.gameState.currentFaction,
        this.gameState.turn
      );
    }
    
    // Send notification AFTER syncing
    this.sendNotification({
      type: NotificationType.MINE_PLACED,
      faction: this.currentFaction,
      data: { position: position },
      timestamp: Date.now()
    });
  }
  
  private isHealingMode: boolean = false;
  private healingModeType: 'heal' | 'repair' | null = null;
  private healingUnit: Unit | null = null;
  private healTargetTiles: Position[] | null = null;
  
  private startHealingMode(healer: Unit, mode: 'heal' | 'repair'): void {
    // Toggle healing mode
    if (this.isHealingMode && this.healingUnit?.id === healer.id) {
      this.cancelHealingMode();
      return;
    }
    
    // Check if it's your turn in online games
    if (this.isOnlineGame && !this.isMyTurn) {
      this.soundManager.playError();
      this.showNotification(`You can only ${mode} units on your turn!`);
      return;
    }
    
    // Check if healer belongs to current player
    if (healer.faction !== this.currentFaction) {
      this.showNotification(`You can only use your own ${mode === 'heal' ? 'medics' : 'engineers'}!`);
      return;
    }
    
    // Enter healing mode
    this.isHealingMode = true;
    this.healingModeType = mode;
    this.healingUnit = healer;
    
    // Clear movement highlights
    this.availableMoves = null;
    this.enemiesInRange = null;
    
    // Update button text to Cancel
    const btnId = mode === 'heal' ? 'heal-btn' : 'repair-btn';
    const btn = document.getElementById(btnId) as HTMLButtonElement;
    if (btn) {
      btn.textContent = 'CANCEL';
      btn.style.background = 'linear-gradient(135deg, #8a4a4a 0%, #6a3a3a 100%)';
    }
    
    // Show valid heal/repair targets
    this.showHealTargets(healer, mode);
  }
  
  private cancelHealingMode(): void {
    this.isHealingMode = false;
    this.healTargetTiles = null;
    
    // Reset button
    const btnId = this.healingModeType === 'heal' ? 'heal-btn' : 'repair-btn';
    const btn = document.getElementById(btnId) as HTMLButtonElement;
    if (btn) {
      btn.textContent = this.healingModeType === 'heal' ? 'HEAL' : 'REPAIR';
      btn.style.background = 'linear-gradient(135deg, #5a5a5a 0%, #4a4a4a 100%)';
    }
    
    // Restore movement highlights if healer is selected
    if (this.healingUnit && this.gameState.selectedUnit === this.healingUnit.id) {
      this.calculateAvailableMoves(this.healingUnit);
      if (!this.healingUnit.hasAttacked) {
        this.calculateEnemiesInRange(this.healingUnit);
      }
    }
    
    this.healingModeType = null;
    this.healingUnit = null;
  }
  
  private showHealTargets(healer: Unit, mode: 'heal' | 'repair'): void {
    const validTargets: Position[] = [];
    
    // Get units in 1-tile radius (including diagonals)
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        const x = healer.position.x + dx;
        const y = healer.position.y + dy;
        
        const unit = this.unitManager.getUnitAt({ x, y });
        if (!unit) continue;
        
        // Check if unit belongs to same faction
        if (unit.faction !== healer.faction) continue;
        
        // Check if unit is damaged
        if (unit.stats.health >= unit.stats.maxHealth) continue;
        
        // Check unit type based on mode
        if (mode === 'heal') {
          // Medics can heal infantry (including engineers and other medics)
          if (unit.category === UnitCategory.INFANTRY || 
              unit.category === UnitCategory.ENGINEERS || 
              unit.category === UnitCategory.MEDICS) {
            validTargets.push({ x, y });
          }
        } else {
          // Engineers can repair armor, recon, and artillery (but not aircraft)
          if (unit.category === UnitCategory.ARMOR || 
              unit.category === UnitCategory.RECONNAISSANCE || 
              unit.category === UnitCategory.ARTILLERY) {
            validTargets.push({ x, y });
          }
        }
      }
    }
    
    // Highlight valid targets in green
    this.healTargetTiles = validTargets;
  }
  
  private showHealConfirmation(healer: Unit, target: Unit): void {
    // Calculate healing amount (75% of lost health)
    const lostHealth = target.stats.maxHealth - target.stats.health;
    const healAmount = Math.floor(lostHealth * 0.75);
    
    // Calculate cost (50% of unit's original cost)
    const unitDef = UNIT_DEFINITIONS[target.type];
    const healCost = Math.floor(unitDef.cost * 0.5);
    
    const currentResources = this.gameState.resources.get(this.currentFaction) || 0;
    
    // Create confirmation dialog
    const confirmDialog = document.createElement('div');
    confirmDialog.id = 'heal-confirm';
    confirmDialog.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: #2a2a2a;
      border: 2px solid #6a6a6a;
      padding: 20px;
      border-radius: 5px;
      z-index: 10000;
      min-width: 300px;
      text-align: center;
    `;
    
    const mode = this.healingModeType === 'heal' ? 'Heal' : 'Repair';
    
    confirmDialog.innerHTML = `
      <h3 style="color: #d4af37; margin-bottom: 15px;">${mode} ${target.name}</h3>
      <p style="color: #e0e0e0; margin-bottom: 10px;">Current Health: ${target.stats.health}/${target.stats.maxHealth}</p>
      <p style="color: #4CAF50; margin-bottom: 10px;">Will ${mode}: +${healAmount} HP</p>
      <p style="color: #e0e0e0; margin-bottom: 10px;">New Health: ${target.stats.health + healAmount}/${target.stats.maxHealth}</p>
      <p style="color: ${currentResources >= healCost ? '#e0e0e0' : '#ff6666'}; margin-bottom: 15px;">
        Cost: ${healCost} resources (Current: ${currentResources})
      </p>
      <div style="display: flex; gap: 10px; justify-content: center;">
        <button id="confirm-heal" style="
          padding: 8px 16px;
          background: ${currentResources >= healCost ? '#4CAF50' : '#666'};
          color: white;
          border: none;
          cursor: ${currentResources >= healCost ? 'pointer' : 'not-allowed'};
          border-radius: 3px;
        " ${currentResources >= healCost ? '' : 'disabled'}>Confirm</button>
        <button id="cancel-heal" style="
          padding: 8px 16px;
          background: #8b4513;
          color: white;
          border: none;
          cursor: pointer;
          border-radius: 3px;
        ">Cancel</button>
      </div>
    `;
    
    document.body.appendChild(confirmDialog);
    
    // Add event listeners
    const confirmBtn = document.getElementById('confirm-heal');
    const cancelBtn = document.getElementById('cancel-heal');
    
    if (confirmBtn && currentResources >= healCost) {
      confirmBtn.addEventListener('click', () => {
        this.performHeal(healer, target, healAmount, healCost);
        confirmDialog.remove();
      });
    }
    
    if (cancelBtn) {
      cancelBtn.addEventListener('click', () => {
        confirmDialog.remove();
      });
    }
  }
  
  private performHeal(healer: Unit, target: Unit, healAmount: number, cost: number): void {
    // Deduct resources and track spending
    const currentResources = this.gameState.resources.get(this.currentFaction) || 0;
    this.gameState.resources.set(this.currentFaction, currentResources - cost);
    
    // Check if target is a vehicle or infantry
    const isVehicle = target.category === UnitCategory.ARMOR || 
                      target.category === UnitCategory.RECONNAISSANCE ||
                      target.category === UnitCategory.ARTILLERY ||
                      target.category === UnitCategory.TRANSPORT;
    
    // Track healing/repair and resource spending in battle statistics
    const factionStats = this.gameState.battleStats?.get(this.currentFaction);
    if (factionStats) {
      if (isVehicle) {
        factionStats.unitsRepaired++;
      } else {
        factionStats.unitsHealed++;
      }
      factionStats.resourcesSpent += cost;
    }
    
    // Heal the target
    target.stats.health = Math.min(target.stats.health + healAmount, target.stats.maxHealth);
    
    // Mark healer as having healed this turn
    (healer as any).hasHealed = true;
    
    // Play sound
    this.soundManager.playClick();
    
    // Exit healing mode
    this.cancelHealingMode();
    
    // Update displays
    this.updateResourceDisplay();
    this.updateUnitInfo(healer);
    
    // Show success message
    const action = this.healingModeType === 'heal' ? 'healed' : 'repaired';
    this.sendNotification({
      type: NotificationType.UNIT_HEALED,
      faction: this.currentFaction,
      data: { healerName: healer.name, action: action, targetName: target.name, healAmount: healAmount },
      timestamp: Date.now()
    });
    
    // Save game state
    this.saveGameState();
  }
  
  private toggleHunker(unit: Unit): void {
    // Check if it's your turn in online games
    if (this.isOnlineGame && !this.isMyTurn) {
      this.soundManager.playError();
      this.showNotification("You can only change stance on your turn!");
      return;
    }
    
    // Check if unit belongs to current player
    if (unit.faction !== this.currentFaction) {
      this.showNotification('You can only control your own units!');
      return;
    }
    
    // Check if unit has already changed hunker status this turn
    if ((unit as any).hunkerChangedThisTurn) {
      this.showNotification('Unit has already changed stance this turn!');
      return;
    }
    
    if ((unit as any).isHunkered) {
      // Standing up
      (unit as any).isHunkered = false;
      (unit as any).hunkerChangedThisTurn = true;
      
      // Remove defense bonus
      unit.stats.defense -= 2;
      
      // Use all movement for standing up
      unit.movementRemaining = 0;
      
      // Clear movement highlights since they can't move
      this.availableMoves = null;
      
      // Notification will be sent after Firebase sync
    } else {
      // Hunkering down
      (unit as any).isHunkered = true;
      (unit as any).hunkerChangedThisTurn = true;
      
      // Add defense bonus
      unit.stats.defense += 2;
      
      // Remove all movement
      unit.movementRemaining = 0;
      
      // Clear movement highlights
      this.availableMoves = null;
      // Don't clear enemiesInRange and don't set hasAttacked - hunkered units can still attack!
      // Recalculate enemies in range to ensure it's shown
      if (!unit.hasAttacked) {
        this.calculateEnemiesInRange(unit);
      }
      
      // Notification will be sent after Firebase sync
    }
    
    // Update the unit in gameState.units to include hunker flags
    if (this.gameState.units.has(unit.id)) {
      const gameStateUnit = this.gameState.units.get(unit.id)!;
      (gameStateUnit as any).isHunkered = (unit as any).isHunkered;
      (gameStateUnit as any).hunkerChangedThisTurn = (unit as any).hunkerChangedThisTurn;
      // Also update the defense stat
      gameStateUnit.stats.defense = unit.stats.defense;
    }
    
    // Update UI
    this.updateUnitInfo(unit);
    
    // Save game state
    this.saveGameState();
    
    // CRITICAL: Sync with Firebase BEFORE sending notification
    // This ensures the hunker state is synced when the notification triggers
    if (this.isOnlineGame && this.multiplayerService) {
      debug.log(`[HUNKER SYNC] Syncing state after ${(unit as any).isHunkered ? 'hunkering' : 'standing up'} ${unit.name}`);
      const completeGameState = this.buildCompleteGameState();
      this.multiplayerService.updateGameState(
        completeGameState,
        this.gameState.currentFaction,
        this.gameState.turn
      );
    }
    
    // Send notification AFTER syncing
    const notificationType = (unit as any).isHunkered ? NotificationType.UNIT_HUNKERED : NotificationType.UNIT_STOOD_UP;
    this.sendNotification({
      type: notificationType,
      faction: this.currentFaction,
      data: { unitName: unit.name },
      timestamp: Date.now()
    });
  }
  
  private processHunkeredUnits(): void {
    // Called at the start of each turn to heal hunkered units
    for (const unit of this.unitManager.getAllUnits()) {
      if (unit.faction === this.currentFaction && (unit as any).isHunkered) {
        // Heal 1 HP per turn while hunkered
        if (unit.stats.health < unit.stats.maxHealth) {
          unit.stats.health = Math.min(unit.stats.health + 1, unit.stats.maxHealth);
          
          // Add healing animation
          unit.damageText = {
            amount: -1, // Negative for healing
            startTime: Date.now(),
            duration: 1500
          };
        }
      }
    }
  }
  
  private processShellshockedUnits(): void {
    // Called at the start of each faction's turn to reduce shellshocked duration and check for panic
    for (const unit of this.unitManager.getAllUnits()) {
      if (unit.faction === this.currentFaction && unit.shellshocked && unit.shellshocked.turnsRemaining > 0) {
        // First check for panic retreat (10% chance when outnumbered)
        if (Math.random() < 0.1) {
          const shouldPanic = this.evaluatePanicConditions(unit);
          if (shouldPanic) {
            this.executePanicRetreat(unit);
            // Continue to next unit after panic
            continue;
          }
        }
        
        // Reduce shellshocked duration
        unit.shellshocked.turnsRemaining--;
        
        // Remove shellshocked status if duration has expired
        if (unit.shellshocked.turnsRemaining <= 0) {
          unit.shellshocked = undefined;
          debug.log(`${unit.name} has recovered from being shellshocked!`);
          
          // Show notification that unit recovered
          if (this.gameState.gameMode !== 'PvE' || this.currentFaction === this.humanPlayerFaction) {
            this.showNotification(`${unit.name} has recovered from shellshock!`);
          }
        } else {
          debug.log(`${unit.name} is still shellshocked for ${unit.shellshocked.turnsRemaining} more turn(s)`);
        }
      }
    }
  }
  
  private evaluatePanicConditions(unit: Unit): boolean {
    // Count nearby friendly and enemy forces within 5 tiles
    const evaluationRange = 5;
    let friendlyPower = 0;
    let enemyPower = 0;
    
    // Calculate combat power for each unit type
    const getUnitPower = (u: Unit): number => {
      // Base power from attack and defense stats
      let power = u.stats.attack + u.stats.defense;
      
      // Multiply by health percentage
      power *= (u.stats.health / u.stats.maxHealth);
      
      // Bonus for tanks and artillery
      const unitDef = UNIT_DEFINITIONS[u.type];
      if (unitDef?.category === UnitCategory.ARMOR) power *= 1.5;
      if (unitDef?.category === UnitCategory.ARTILLERY) power *= 1.3;
      
      return power;
    };
    
    // Count friendly units
    const friendlyUnits = this.unitManager.getUnitsForFaction(unit.faction);
    for (const friendly of friendlyUnits) {
      if (friendly.id === unit.id) continue; // Don't count self
      const distance = Math.abs(friendly.position.x - unit.position.x) + 
                      Math.abs(friendly.position.y - unit.position.y);
      if (distance <= evaluationRange) {
        friendlyPower += getUnitPower(friendly);
      }
    }
    
    // Count enemy units
    const allUnits = this.unitManager.getAllUnits();
    for (const enemy of allUnits) {
      if (enemy.faction === unit.faction) continue;
      const distance = Math.abs(enemy.position.x - unit.position.x) + 
                      Math.abs(enemy.position.y - unit.position.y);
      if (distance <= evaluationRange) {
        enemyPower += getUnitPower(enemy);
      }
    }
    
    // Add the panicking unit's own power to friendly side
    friendlyPower += getUnitPower(unit);
    
    debug.log(`Panic evaluation for ${unit.name}: Friendly power ${friendlyPower.toFixed(1)} vs Enemy power ${enemyPower.toFixed(1)}`);
    
    // Panic if outnumbered/outgunned by 20% or more
    return enemyPower > friendlyPower * 1.2;
  }
  
  private executePanicRetreat(unit: Unit): void {
    debug.log(`⚠️ ${unit.name} is PANICKING and retreating!`);
    
    // Show panic notification
    const message = `${unit.name} panics and breaks formation!`;
    this.showNotification(message);
    
    // Find nearest enemy
    let nearestEnemy: Unit | null = null;
    let minDistance = Infinity;
    
    const allUnits = this.unitManager.getAllUnits();
    for (const enemy of allUnits) {
      if (enemy.faction === unit.faction) continue;
      const distance = Math.abs(enemy.position.x - unit.position.x) + 
                      Math.abs(enemy.position.y - unit.position.y);
      
      // Check if in attack range
      if (distance <= unit.stats.range && distance < minDistance) {
        minDistance = distance;
        nearestEnemy = enemy;
      }
    }
    
    // Attack nearest enemy if possible (panic fire)
    if (nearestEnemy && !unit.hasAttacked) {
      debug.log(`  - Panic firing at ${nearestEnemy.name}`);
      // Mark unit as panicking for the attack (affects accuracy/damage)
      (unit as any).isPanicking = true;
      this.performAttack(unit, nearestEnemy);
      (unit as any).isPanicking = false;
      unit.hasAttacked = true;
    }
    
    // Now flee in opposite direction from enemies
    const enemies: Position[] = [];
    for (const enemy of allUnits) {
      if (enemy.faction !== unit.faction) {
        enemies.push(enemy.position);
      }
    }
    
    if (enemies.length > 0 && unit.movementRemaining > 0) {
      // Calculate average enemy position
      const avgEnemyPos = enemies.reduce((acc, pos) => ({
        x: acc.x + pos.x / enemies.length,
        y: acc.y + pos.y / enemies.length
      }), { x: 0, y: 0 });
      
      // Calculate flee direction (opposite of enemy direction)
      const fleeDirection = {
        x: unit.position.x - avgEnemyPos.x,
        y: unit.position.y - avgEnemyPos.y
      };
      
      // Normalize direction
      const magnitude = Math.sqrt(fleeDirection.x ** 2 + fleeDirection.y ** 2);
      if (magnitude > 0) {
        fleeDirection.x /= magnitude;
        fleeDirection.y /= magnitude;
      }
      
      // Use ALL movement points to flee
      let currentPos = { ...unit.position };
      let totalMoved = 0;
      
      while (totalMoved < unit.movementRemaining) {
        // Try to move in the flee direction
        const candidates = [
          { x: Math.round(currentPos.x + fleeDirection.x), y: Math.round(currentPos.y + fleeDirection.y) },
          { x: currentPos.x + (fleeDirection.x > 0 ? 1 : -1), y: currentPos.y },
          { x: currentPos.x, y: currentPos.y + (fleeDirection.y > 0 ? 1 : -1) },
          { x: currentPos.x + 1, y: currentPos.y + 1 },
          { x: currentPos.x - 1, y: currentPos.y - 1 },
          { x: currentPos.x + 1, y: currentPos.y - 1 },
          { x: currentPos.x - 1, y: currentPos.y + 1 }
        ];
        
        let moved = false;
        for (const candidate of candidates) {
          const tile = this.map.getTile(candidate.x, candidate.y);
          if (tile && !this.unitManager.getUnitAt(candidate)) {
            const terrainCost = this.getTerrainCost(tile.terrain);
            if (totalMoved + terrainCost <= unit.movementRemaining) {
              currentPos = candidate;
              totalMoved += terrainCost;
              moved = true;
              break;
            }
          }
        }
        
        if (!moved) break; // Can't move further
      }
      
      // Actually move the unit
      if (currentPos.x !== unit.position.x || currentPos.y !== unit.position.y) {
        debug.log(`  - Fleeing from (${unit.position.x},${unit.position.y}) to (${currentPos.x},${currentPos.y})`);
        const distance = Math.abs(currentPos.x - unit.position.x) + Math.abs(currentPos.y - unit.position.y);
        this.unitManager.moveUnit(unit.id, currentPos, distance);
        unit.movementRemaining = 0; // Used all movement in panic
      }
    }
    
    // Mark unit as having moved and attacked (spent for the turn)
    unit.movementRemaining = 0;
    unit.hasAttacked = true;
  }
  

  private applyMineDetonation(unit: Unit, position: Position, mineOwner?: FactionType): void {
    // This is called for mines that were already removed (delayed detonation)
    if (!mineOwner) return;
    
    // All mines use a standardized attack value of 9
    const mineDamage = 9;
    
    // Check if this is a friendly mine (unit faction same as mine owner)
    const isFriendlyMine = mineOwner === unit.faction;
    
    // If mine belongs to enemy, queue notification for them
    if (!isFriendlyMine && !this.isOnlineGame) {
      // Store notification for the mine owner to see on their turn (offline only)
      if (!this.gameState.pendingNotifications) {
        this.gameState.pendingNotifications = new Map();
      }
      const ownerNotifications = this.gameState.pendingNotifications.get(mineOwner) || [];
      ownerNotifications.push(`Enemy ${unit.name} detonated your mine at (${position.x}, ${position.y})!`);
      this.gameState.pendingNotifications.set(mineOwner, ownerNotifications);
    }
    
    // Apply damage with animation
    unit.stats.health -= mineDamage;
    
    // Also update in gameState.units
    if (this.gameState.units.has(unit.id)) {
      this.gameState.units.get(unit.id)!.stats.health = unit.stats.health;
    }
    
    // Add damage text animation
    unit.damageText = {
      amount: mineDamage,
      startTime: Date.now(),
      duration: 1500
    };
    
    // Add attack animation to show the mine explosion
    unit.attackAnimation = {
      type: 'defender', // Use defender animation for being hit
      startTime: Date.now(),
      duration: 500
    };
    
    // Play explosion sound
    this.soundManager.playUnitDestroyed();
    
    // Check if unit died
    if (unit.stats.health <= 0) {
      // Add death animation
      unit.deathAnimation = {
        startTime: Date.now(),
        duration: 1000,
        startY: unit.displayPosition?.y || unit.position.y
      };
      
      // Delay unit removal to allow death animation to play
      setTimeout(() => {
        this.unitManager.removeUnit(unit.id);
        
        // CRITICAL: Also remove from gameState.units to prevent ghost units
        this.gameState.units.delete(unit.id);
        
        // Clear selections if this was the selected unit
        if (this.gameState.selectedUnit === unit.id) {
          this.gameState.selectedUnit = null;
          this.availableMoves = null;
          this.enemiesInRange = null;
          this.updateUnitInfo(null);
        }
        
        // Sync with Firebase if online to ensure unit removal is propagated
        if (this.isOnlineGame && this.multiplayerService) {
          const completeGameState = this.buildCompleteGameState();
          this.multiplayerService.updateGameState(
            completeGameState,
            this.gameState.currentFaction,
            this.gameState.turn
          );
        }
      }, 1000);
      
      // Show death notification immediately
      this.sendNotification({
        type: NotificationType.MINE_DESTROYED_UNIT,
        faction: mineOwner,
        targetFaction: unit.faction,
        data: { 
          unitName: unit.name,
          position: position
        },
        timestamp: Date.now()
      });
      
      // Queue notification for mine owner (offline only)
      if (!isFriendlyMine && !this.isOnlineGame) {
        if (!this.gameState.pendingNotifications) {
          this.gameState.pendingNotifications = new Map();
        }
        const ownerNotifications = this.gameState.pendingNotifications.get(mineOwner) || [];
        ownerNotifications.push(`Your mine destroyed enemy ${unit.name}!`);
        this.gameState.pendingNotifications.set(mineOwner, ownerNotifications);
      }
    }
  }

  private checkMineDetonation(unit: Unit, position: Position): void {
    const tile = this.map.getTile(position.x, position.y);
    if (!tile || !tile.mine) return;
    
    // Check if unit is a vehicle (tanks, reconnaissance vehicles, or transports)
    const isVehicle = unit.category === UnitCategory.ARMOR || 
                      unit.category === UnitCategory.RECONNAISSANCE ||
                      unit.category === UnitCategory.TRANSPORT;
    
    if (!isVehicle) {
      // Infantry don't trigger anti-tank mines
      return;
    }
    
    // All mines use a standardized attack value of 9
    const mineDamage = 9;
    
    // Show detonation notification immediately to the unit owner (current player)
    const message = `${unit.name} detonated an anti-tank mine at (${position.x}, ${position.y})!`;
    
    // Check if this is a friendly mine (unit faction same as mine owner)
    const isFriendlyMine = tile.mine.owner === unit.faction;
    
    // If mine belongs to enemy, queue notification for them
    if (!isFriendlyMine && !this.isOnlineGame) {
      // Store notification for the mine owner to see on their turn (offline only)
      if (!this.gameState.pendingNotifications) {
        this.gameState.pendingNotifications = new Map();
      }
      const ownerNotifications = this.gameState.pendingNotifications.get(tile.mine.owner) || [];
      ownerNotifications.push(`Enemy ${unit.name} detonated your mine at (${position.x}, ${position.y})!`);
      this.gameState.pendingNotifications.set(tile.mine.owner, ownerNotifications);
    }
    
    // Apply damage with animation
    unit.stats.health -= mineDamage;
    
    // Also update in gameState.units
    if (this.gameState.units.has(unit.id)) {
      this.gameState.units.get(unit.id)!.stats.health = unit.stats.health;
    }
    
    // Add damage text animation
    unit.damageText = {
      amount: mineDamage,
      startTime: Date.now(),
      duration: 1500
    };
    
    // Add attack animation to show the mine explosion
    unit.attackAnimation = {
      type: 'defender', // Use defender animation for being hit
      startTime: Date.now(),
      duration: 500
    };
    
    // Play explosion sound
    this.soundManager.playUnitDestroyed();
    
    // Check if unit died
    if (unit.stats.health <= 0) {
      // Add death animation
      unit.deathAnimation = {
        startTime: Date.now(),
        duration: 1000,
        startY: unit.displayPosition?.y || unit.position.y
      };
      
      // Delay unit removal to allow death animation to play
      setTimeout(() => {
        this.unitManager.removeUnit(unit.id);
        
        // CRITICAL: Also remove from gameState.units to prevent ghost units
        this.gameState.units.delete(unit.id);
        
        // Clear selections if this was the selected unit
        if (this.gameState.selectedUnit === unit.id) {
          this.gameState.selectedUnit = null;
          this.availableMoves = null;
          this.enemiesInRange = null;
          this.updateUnitInfo(null);
        }
        
        // Sync with Firebase if online to ensure unit removal is propagated
        if (this.isOnlineGame && this.multiplayerService) {
          const completeGameState = this.buildCompleteGameState();
          this.multiplayerService.updateGameState(
            completeGameState,
            this.gameState.currentFaction,
            this.gameState.turn
          );
        }
      }, 1000);
      
      // Show death notification immediately
      this.sendNotification({
        type: NotificationType.MINE_DESTROYED_UNIT,
        faction: unit.faction,
        targetFaction: tile.mine.owner,
        data: { unitName: unit.name },
        timestamp: Date.now()
      });
      
      // Also notify mine owner if different (offline only)
      if (!isFriendlyMine && !this.isOnlineGame) {
        if (!this.gameState.pendingNotifications) {
          this.gameState.pendingNotifications = new Map();
        }
        const ownerNotifications = this.gameState.pendingNotifications.get(tile.mine.owner) || [];
        ownerNotifications.push(`Your mine destroyed enemy ${unit.name}!`);
        this.gameState.pendingNotifications.set(tile.mine.owner, ownerNotifications);
      }
    }
    
    // Save mine owner before removing
    const mineOwner = tile.mine.owner;
    
    // Track mine detonation in battle statistics
    const mineOwnerStats = this.gameState.battleStats?.get(tile.mine!.owner);
    if (mineOwnerStats) {
      mineOwnerStats.minesDetonated++;
    }
    
    // Remove the mine after detonation
    tile.mine = undefined;
    
    // Sync with Firebase after mine detonation
    if (this.isOnlineGame && this.multiplayerService) {
      const completeGameState = this.buildCompleteGameState();
      this.multiplayerService.updateGameState(
        completeGameState,
        this.gameState.currentFaction,
        this.gameState.turn
      );
    }
    
    // Send notification AFTER syncing to prevent race conditions
    this.sendNotification({
      type: NotificationType.MINE_DETONATED,
      faction: unit.faction,
      targetFaction: mineOwner,
      data: { 
        unitName: unit.name,
        position: position,
        message: message
      },
      timestamp: Date.now()
    });
    
    // Map will be redrawn on next frame automatically via gameLoop
  }

  private showUnitBuildMenu(city: any): void {
    const buildMenu = document.createElement('div');
    buildMenu.id = 'build-menu';
    buildMenu.style.cssText = `
      position: absolute;
      bottom: 100px;
      left: 50%;
      transform: translateX(-50%);
      background: #3a3a3a;
      border: 2px solid #6a6a6a;
      padding: 20px;
      border-radius: 5px;
      max-width: 600px;
      max-height: 400px;
      overflow-y: auto;
    `;
    
    const resources = this.gameState.resources.get(this.currentFaction) || 0;
    
    // Update city max units based on level
    city.maxUnits = getCityMaxUnits(city.level || CityLevel.OUTPOST);
    
    // Count actual spawned units (not placeholders) and units in training
    const actualUnits = city.unitsProduced.filter((id: string) => !id.startsWith('queue_'));
    const unitsInTraining = (city.trainingQueue || []).length;
    const slotsUsed = actualUnits.length + unitsInTraining;
    const slotsAvailable = city.maxUnits - slotsUsed;
    
    // Check if city is upgrading
    const isUpgrading = city.upgradeInProgress != null;
    
    const cityLevel = city.level || CityLevel.OUTPOST;
    const cityLevelName = getCityLevelName(cityLevel);
    const cityLevelDesc = getCityLevelDescription(cityLevel);
    
    buildMenu.innerHTML = `
      <h3 style="color: #d4af37; margin-bottom: 10px;">Build Unit - ${city.name}</h3>
      <div style="background: #2a2a2a; padding: 10px; margin-bottom: 10px; border-radius: 3px;">
        <p style="color: #f0f0f0; margin-bottom: 5px; font-weight: bold;">City Level: ${cityLevelName}</p>
        <p style="color: #c0c0c0; font-size: 12px; margin-bottom: 5px;">${cityLevelDesc}</p>
        <p style="color: #7FBF7F; font-size: 12px;">Generates +${getCityResourceGeneration(cityLevel)} resources per turn</p>
      </div>
      <p style="color: #e0e0e0; margin-bottom: 5px;">Resources: ${resources}</p>
      <p style="color: #e0e0e0; margin-bottom: 15px;">Unit Slots: ${slotsUsed}/${city.maxUnits}</p>
      ${slotsAvailable === 0 ? '<p style="color: #ff6666; margin-bottom: 15px;">City at unit capacity!</p>' : ''}
      ${isUpgrading ? `<p style="color: #f0a030; margin-bottom: 15px;">Upgrading to ${getCityLevelName(city.upgradeInProgress.toLevel)} (${city.upgradeInProgress.turnsRemaining} turns remaining)</p>` : ''}
      ${!isUpgrading && cityLevel < CityLevel.AIRBASE ? '<div id="upgrade-section" style="margin-bottom: 15px;"></div>' : ''}
      <div id="unit-list"></div>
      <button onclick="document.getElementById('build-menu').remove()" style="
        margin-top: 15px;
        padding: 8px 16px;
        background: #8b4513;
        color: white;
        border: none;
        cursor: pointer;
      ">Cancel</button>
    `;
    
    const unitList = buildMenu.querySelector('#unit-list');
    const upgradeSection = buildMenu.querySelector('#upgrade-section');
    
    // Add upgrade button if not at max level, not currently upgrading, and not being captured
    const isBeingCaptured = city.captureProgress && city.captureProgress.faction !== this.currentFaction;
    if (upgradeSection && !isUpgrading && !isBeingCaptured && cityLevel < CityLevel.AIRBASE) {
      const nextLevel = cityLevel === CityLevel.OUTPOST ? CityLevel.FACTORY : CityLevel.AIRBASE;
      const upgradeCost = getCityUpgradeCost(nextLevel);
      const upgradeTime = getCityUpgradeTime(nextLevel);
      const canAffordUpgrade = resources >= upgradeCost;
      
      const upgradeButton = document.createElement('button');
      upgradeButton.style.cssText = `
        width: 100%;
        padding: 12px;
        background: ${canAffordUpgrade ? '#2a5a2a' : '#3a3a3a'};
        color: ${canAffordUpgrade ? '#90ee90' : '#999999'};
        border: 2px solid ${canAffordUpgrade ? '#4a7a4a' : '#5a5a5a'};
        cursor: ${canAffordUpgrade ? 'pointer' : 'not-allowed'};
        border-radius: 3px;
        font-weight: bold;
      `;
      upgradeButton.innerHTML = `
        Upgrade to ${getCityLevelName(nextLevel)} - Cost: ${upgradeCost} (${upgradeTime} turns)
      `;
      
      if (canAffordUpgrade) {
        upgradeButton.onclick = () => this.upgradeCity(city, nextLevel);
      }
      
      upgradeSection.appendChild(upgradeButton);
    }
    
    // Filter units based on city level
    const allUnits = getUnitsForFaction(this.currentFaction, this.selectedCampaign);
    const availableUnits = allUnits.filter(unitType => canCityTrainUnit(city, unitType));
    
    // Sort units by cost (lowest to highest)
    const sortedUnits = availableUnits.sort((a, b) => {
      return UNIT_DEFINITIONS[a].cost - UNIT_DEFINITIONS[b].cost;
    });
    
    sortedUnits.forEach(unitType => {
      const definition = UNIT_DEFINITIONS[unitType];
      const canAfford = resources >= definition.cost && slotsAvailable > 0;
      
      const unitButton = document.createElement('div');
      unitButton.style.cssText = `
        padding: 10px;
        margin: 5px 0;
        background: ${canAfford ? '#4a4a4a' : '#2a2a2a'};
        border: 1px solid #6a6a6a;
        cursor: ${canAfford ? 'pointer' : 'not-allowed'};
        opacity: ${canAfford ? '1' : '0.5'};
        border-radius: 3px;
      `;
      
      // Format category name nicely for display
      const categoryDisplay = this.formatUnitCategory(definition.category);

      unitButton.innerHTML = `
        <div style="display: flex; justify-content: space-between; align-items: baseline;">
          <div>
            <span style="color: #f0f0f0; font-weight: bold;">${definition.name}</span>
            <span style="color: #888; font-size: 11px; margin-left: 8px;">Level ${definition.level} ${categoryDisplay}</span>
          </div>
          <span style="color: #d4af37;">Cost: ${definition.cost}</span>
        </div>
        <div style="color: #c0c0c0; font-size: 12px; margin-top: 5px;">
          ATK: ${definition.attack} | DEF: ${definition.defense} | RNG: ${definition.range} | HP: ${definition.maxHealth} | MOV: ${definition.movement} | Training: ${definition.trainingTime} turn${definition.trainingTime > 1 ? 's' : ''}
        </div>
      `;
      
      if (canAfford && !isUpgrading) {
        unitButton.onclick = () => this.buildUnit(unitType, city);
      } else if (isUpgrading) {
        unitButton.style.opacity = '0.3';
        unitButton.style.cursor = 'not-allowed';
        unitButton.title = 'Cannot train units while city is upgrading';
      }
      
      unitList?.appendChild(unitButton);
    });
    
    document.body.appendChild(buildMenu);
  }

  private calculateEnemiesInRange(unit: Unit): void {
    // Hunkered units cannot attack
    if ((unit as any).isHunkered) {
      this.enemiesInRange = [];
      return;
    }
    
    const enemies: Position[] = [];
    const allUnits = this.unitManager.getAllUnits();
    
    for (const enemy of allUnits) {
      if (enemy.faction !== unit.faction) {
        // Check if bombers are trying to target aircraft (not allowed)
        if (isBomber(unit.type as UnitType) && 
            isAircraft(enemy.type as UnitType)) { // Bombers cannot attack aircraft
          continue; // Skip this enemy
        }
        
        // Calculate distance (Manhattan distance for now)
        const dx = Math.abs(enemy.position.x - unit.position.x);
        const dy = Math.abs(enemy.position.y - unit.position.y);
        const distance = Math.max(dx, dy); // Chebyshev distance for attack range
        
        if (distance <= unit.stats.range) {
          enemies.push(enemy.position);
        }
      }
    }
    
    this.enemiesInRange = enemies;
  }

  private getEnemiesInRangeForUnit(unit: Unit): Position[] {
    // Helper method to check if a unit has enemies in range without side effects
    const enemies: Position[] = [];
    const range = unit.stats.range || 1;
    
    // Check all positions within range
    for (let dx = -range; dx <= range; dx++) {
      for (let dy = -range; dy <= range; dy++) {
        if (dx === 0 && dy === 0) continue;
        
        const targetX = unit.position.x + dx;
        const targetY = unit.position.y + dy;
        
        // Check if there's an enemy unit at this position
        const targetUnit = this.unitManager.getUnitAt({ x: targetX, y: targetY });
        if (targetUnit && targetUnit.faction !== unit.faction && targetUnit.stats.health > 0) {
          // Check visibility in fog of war if enabled
          if (this.fogOfWar && this.visionSystem) {
            // Use isPositionVisible instead of private hasLineOfSight
            const isVisible = this.visionSystem.isPositionVisible(targetX, targetY);
            if (isVisible) {
              enemies.push({ x: targetX, y: targetY });
            }
          } else {
            enemies.push({ x: targetX, y: targetY });
          }
        }
      }
    }
    
    return enemies;
  }

  private cycleToNextActionableUnit(): void {
    // Determine which faction the player controls
    const controllingFaction = this.isOnlineGame ? this.humanPlayerFaction : 
                              this.selectedGameMode === 'PvE' ? this.humanPlayerFaction : 
                              this.currentFaction;
    
    if (!controllingFaction) return;
    
    // Get all units for the controlling faction
    const allUnits = this.unitManager.getUnitsForFaction(controllingFaction);
    
    // Filter to only units that have actions remaining
    const actionableUnits = allUnits.filter(unit => {
      // Skip boarded or hunkered units
      if ((unit as any).boardedTransportId || (unit as any).isHunkered) {
        return false;
      }
      
      // Check if unit has movement
      const hasMovement = unit.movementRemaining > 0;
      
      // Check if unit can attack (hasn't attacked AND has enemies in range)
      let canActuallyAttack = false;
      if (!unit.hasAttacked) {
        const enemiesInRange = this.getEnemiesInRangeForUnit(unit);
        canActuallyAttack = enemiesInRange.length > 0;
      }
      
      // Only include units that can move OR can actually attack something
      return hasMovement || canActuallyAttack;
    });
    
    if (actionableUnits.length === 0) {
      // No units with actions remaining
      this.showNotification('No units with actions remaining', NotificationType.UNIT_CYCLE);
      return;
    }
    
    // Sort units by position for consistent cycling order (top-left to bottom-right)
    actionableUnits.sort((a, b) => {
      if (a.position.y !== b.position.y) {
        return a.position.y - b.position.y;
      }
      return a.position.x - b.position.x;
    });
    
    // Find the current selected unit's index
    let currentIndex = -1;
    if (this.gameState.selectedUnit) {
      currentIndex = actionableUnits.findIndex(u => u.id === this.gameState.selectedUnit);
    }
    
    // Move to next unit (wrap around)
    this.unitCycleIndex = (currentIndex + 1) % actionableUnits.length;
    const nextUnit = actionableUnits[this.unitCycleIndex];
    
    // Select the unit
    this.gameState.selectedUnit = nextUnit.id;
    this.gameState.selectedTile = nextUnit.position;
    
    // Center camera on the unit with smooth animation
    const tileSize = 64;
    this.cameraController.smoothCenterOn(
      nextUnit.position.x, 
      nextUnit.position.y, 
      tileSize, 
      this.canvas.width, 
      this.canvas.height
    );
    
    // Update UI
    this.updateUnitInfo(nextUnit);
    const tile = this.map.getTile(nextUnit.position.x, nextUnit.position.y);
    if (tile) {
      this.updateTileInfo(tile);
    }
    
    // Calculate available moves and enemies in range
    this.calculateAvailableMoves(nextUnit);
    if (!nextUnit.hasAttacked) {
      this.calculateEnemiesInRange(nextUnit);
    } else {
      this.enemiesInRange = null;
    }
    
    // Play sound feedback
    this.soundManager.playMenuHover();
    
    // Show a brief notification with unit info
    const movementText = nextUnit.movementRemaining > 0 ? `${nextUnit.movementRemaining} move` : 'No move';
    const enemyCount = this.enemiesInRange ? this.enemiesInRange.length : 0;
    const attackText = !nextUnit.hasAttacked ? 
      (enemyCount > 0 ? `${enemyCount} target${enemyCount > 1 ? 's' : ''}` : 'No targets') : 
      'Already attacked';
    this.showNotification(`${nextUnit.name}: ${movementText}, ${attackText}`, NotificationType.UNIT_CYCLE);
  }

  private calculateAvailableMoves(unit: Unit): void {
    // Dead units cannot move
    if (unit.stats.health <= 0) {
      this.availableMoves = [];
      return;
    }
    
    // Hunkered units cannot move at all
    if ((unit as any).isHunkered) {
      this.availableMoves = [];
      return;
    }
    
    const moves: Position[] = [];
    const visited = new Map<string, number>();
    const queue: { pos: Position, cost: number }[] = [];
    
    // Apply shellshocked movement penalty
    let effectiveMovement = unit.movementRemaining;
    if (unit.shellshocked && unit.shellshocked.turnsRemaining > 0) {
      effectiveMovement = Math.floor(effectiveMovement * 0.5); // 50% movement when shellshocked
      debug.log(`${unit.name} is shellshocked - movement reduced from ${unit.movementRemaining} to ${effectiveMovement}`);
    }
    
    // Debug logging for American starting unit
    if (unit.faction === 'United States Of America' && unit.position.x === 18 && unit.position.y === 51) {
      debug.log(`DEBUG: Calculating moves for US starting unit at ${unit.position.x},${unit.position.y} with ${effectiveMovement} movement`);
    }
    
    // Start from unit's current position with 0 cost
    queue.push({ pos: unit.position, cost: 0 });
    visited.set(`${unit.position.x},${unit.position.y}`, 0);
    
    // Use Dijkstra's algorithm with proper priority queue (sort by cost)
    while (queue.length > 0) {
      // Sort queue by cost to process lowest cost nodes first
      queue.sort((a, b) => a.cost - b.cost);
      const { pos, cost } = queue.shift()!;
      
      // Skip if we've already found a better path to this position
      const key = `${pos.x},${pos.y}`;
      if (visited.has(key) && visited.get(key)! < cost) {
        continue;
      }
      
      // Check all adjacent tiles (8 directions)
      const directions = [
        { dx: 0, dy: -1 },   // up
        { dx: 1, dy: -1 },   // up-right
        { dx: 1, dy: 0 },    // right
        { dx: 1, dy: 1 },    // down-right
        { dx: 0, dy: 1 },    // down
        { dx: -1, dy: 1 },   // down-left
        { dx: -1, dy: 0 },   // left
        { dx: -1, dy: -1 }   // up-left
      ];
      
      for (const { dx, dy } of directions) {
        const newX = pos.x + dx;
        const newY = pos.y + dy;
        const newKey = `${newX},${newY}`;
        
        const tile = this.map.getTile(newX, newY);
        if (tile && !this.unitManager.getUnitAt({ x: newX, y: newY })) {
          const terrainCost = this.getTerrainCost(tile.terrain, unit);
          const totalCost = cost + terrainCost;
          
          // Only process if we can reach this tile and haven't found a better path
          if (totalCost <= effectiveMovement && (!visited.has(newKey) || visited.get(newKey)! > totalCost)) {
            visited.set(newKey, totalCost);
            queue.push({ pos: { x: newX, y: newY }, cost: totalCost });
            
            // Add to moves if not the starting position and not already added
            if (!(newX === unit.position.x && newY === unit.position.y) && !moves.some(m => m.x === newX && m.y === newY)) {
              moves.push({ x: newX, y: newY });
            }
          }
        }
      }
    }
    
    // Debug logging for American starting unit
    if (unit.faction === 'United States Of America' && unit.position.x === 18 && unit.position.y === 51) {
      debug.log(`DEBUG: Found ${moves.length} available moves for US starting unit`);
      debug.log('Available moves:', moves.slice(0, 10)); // Show first 10 moves
    }
    this.availableMoves = moves;
  }

  private getTerrainCost(terrain: TerrainType, unit?: Unit): number {
    // Aircraft ignore terrain movement penalties
    if (unit && isAircraft(unit.type as UnitType)) {
      return 1;
    }
    
    // Simplified terrain system: Easy = 1, Rough = 2, Challenging = 3
    const costs: Record<TerrainType, number> = {
      // Urban (special)
      [TerrainType.URBAN]: 1,
      
      // Easy terrain (1 movement cost)
      [TerrainType.OPEN_FIELDS]: 1,      // Normandy
      [TerrainType.DESERT_PLAINS]: 1,    // Tunisia
      [TerrainType.FROZEN_STEPPE]: 1,    // Stalingrad
      [TerrainType.RICE_PADDIES]: 1,     // Okinawa
      [TerrainType.VALLEY_FLOORS]: 1,    // Campobasso
      [TerrainType.FRENCH_COUNTRYSIDE]: 1, // Battle of France
      [TerrainType.COASTAL_LOWLANDS]: 1,   // Albania 1940
      
      // Rough terrain (2 movement cost)
      [TerrainType.BOCAGE]: 2,           // Normandy
      [TerrainType.ROCKY_HIGHLANDS]: 2,  // Tunisia
      [TerrainType.SNOW_DRIFTS]: 2,      // Stalingrad
      [TerrainType.CORAL_RIDGES]: 2,     // Okinawa
      [TerrainType.WOODED_HILLS]: 2,     // Campobasso
      [TerrainType.ROLLING_HILLS]: 2,    // Battle of France
      [TerrainType.ALBANIAN_HIGHLANDS]: 2, // Albania 1940
      
      // Challenging terrain (3 movement cost)
      [TerrainType.DENSE_WOODS]: 3,      // Normandy
      [TerrainType.SHIFTING_DUNES]: 3,   // Tunisia
      [TerrainType.FROZEN_MARSHES]: 3,   // Stalingrad
      [TerrainType.DENSE_JUNGLE]: 3,     // Okinawa
      [TerrainType.MOUNTAIN_RIDGES]: 3,  // Campobasso
      [TerrainType.DENSE_FORESTS]: 3,    // Battle of France
      [TerrainType.PINDUS_MOUNTAINS]: 3  // Albania 1940
    };
    return costs[terrain] || 1;
  }


  private getTerrainModifiers(terrain: TerrainType, city?: City, unitFaction?: string, unit?: Unit): { attack: string, defense: string } {
    // Aircraft get no terrain modifiers
    if (unit && isAircraft(unit.type as UnitType)) {
      return { attack: '0%', defense: '0%' };
    }
    
    // Simplified terrain system
    const modifiers: Record<TerrainType, { attack: string, defense: string }> = {
      // Urban (special - handled separately below)
      [TerrainType.URBAN]: { attack: '-15%', defense: '+40%' },
      
      // Easy terrain (0% attack, 0% defense)
      [TerrainType.OPEN_FIELDS]: { attack: '0%', defense: '0%' },      // Normandy
      [TerrainType.DESERT_PLAINS]: { attack: '0%', defense: '0%' },    // Tunisia
      [TerrainType.FROZEN_STEPPE]: { attack: '0%', defense: '0%' },    // Stalingrad
      [TerrainType.RICE_PADDIES]: { attack: '0%', defense: '0%' },     // Okinawa
      [TerrainType.VALLEY_FLOORS]: { attack: '0%', defense: '0%' },    // Campobasso
      [TerrainType.FRENCH_COUNTRYSIDE]: { attack: '0%', defense: '0%' }, // Battle of France
      [TerrainType.COASTAL_LOWLANDS]: { attack: '0%', defense: '0%' },  // Albania 1940
      
      // Rough terrain (0% attack, +20% defense)
      [TerrainType.BOCAGE]: { attack: '0%', defense: '+20%' },           // Normandy
      [TerrainType.ROCKY_HIGHLANDS]: { attack: '0%', defense: '+20%' },  // Tunisia
      [TerrainType.SNOW_DRIFTS]: { attack: '0%', defense: '+20%' },      // Stalingrad
      [TerrainType.CORAL_RIDGES]: { attack: '0%', defense: '+20%' },     // Okinawa
      [TerrainType.WOODED_HILLS]: { attack: '0%', defense: '+20%' },     // Campobasso
      [TerrainType.ROLLING_HILLS]: { attack: '0%', defense: '+20%' },    // Battle of France
      [TerrainType.ALBANIAN_HIGHLANDS]: { attack: '0%', defense: '+20%' }, // Albania 1940
      
      // Challenging terrain (-10% attack, +20% defense)
      [TerrainType.DENSE_WOODS]: { attack: '-10%', defense: '+20%' },      // Normandy
      [TerrainType.SHIFTING_DUNES]: { attack: '-10%', defense: '+20%' },   // Tunisia
      [TerrainType.FROZEN_MARSHES]: { attack: '-10%', defense: '+20%' },   // Stalingrad
      [TerrainType.DENSE_JUNGLE]: { attack: '-10%', defense: '+20%' },     // Okinawa
      [TerrainType.MOUNTAIN_RIDGES]: { attack: '-10%', defense: '+20%' },  // Campobasso
      [TerrainType.DENSE_FORESTS]: { attack: '-10%', defense: '+20%' },    // Battle of France
      [TerrainType.PINDUS_MOUNTAINS]: { attack: '-10%', defense: '+20%' } // Albania 1940
    };
    
    // Special handling for urban terrain based on city ownership
    if (terrain === TerrainType.URBAN && city) {
      if (city.faction === unitFaction) {
        // Friendly city - defense bonus scales with city level
        const cityLevel = city.level || CityLevel.OUTPOST;
        let defenseBonus = '+30%'; // Base for Level 1 (Outpost)
        
        if (cityLevel === CityLevel.FACTORY) {
          defenseBonus = '+40%'; // Level 2 (Factory)
        } else if (cityLevel === CityLevel.AIRBASE) {
          defenseBonus = '+50%'; // Level 3 (Airbase)
        }
        
        return { attack: '0%', defense: defenseBonus };
      } else if (city.faction === null) {
        // Neutral city - no bonuses
        return { attack: '0%', defense: '0%' };
      } else {
        // Enemy city - attack penalty scales with city level
        const cityLevel = city.level || CityLevel.OUTPOST;
        let attackPenalty = '-10%'; // Base for Level 1 (Outpost)
        
        if (cityLevel === CityLevel.FACTORY) {
          attackPenalty = '-15%'; // Level 2 (Factory)
        } else if (cityLevel === CityLevel.AIRBASE) {
          attackPenalty = '-20%'; // Level 3 (Airbase)
        }
        
        return { attack: attackPenalty, defense: '0%' };
      }
    }
    
    return modifiers[terrain];
  }


  private performAttack(attacker: Unit, defender: Unit, isCounterattack: boolean = false): number {
    // Get terrain modifiers
    const attackerTile = this.map.getTile(attacker.position.x, attacker.position.y);
    const defenderTile = this.map.getTile(defender.position.x, defender.position.y);
    
    const attackerModifiers = this.getTerrainModifiers(
      attackerTile?.terrain || TerrainType.OPEN_FIELDS,
      attackerTile?.city,
      attacker.faction,
      attacker
    );
    
    const defenderModifiers = this.getTerrainModifiers(
      defenderTile?.terrain || TerrainType.OPEN_FIELDS,
      defenderTile?.city,
      defender.faction,
      defender
    );
    
    // Calculate modified stats with randomness
    // Random factor: 50% to 150% of base stat (much more random)
    const attackRandom = 0.5 + Math.random() * 1.0;
    const defenseRandom = 0.5 + Math.random() * 1.0;
    
    // Check for critical hit (attacker rolls high, defender rolls low)
    const isCritical = attackRandom >= 1.35 && defenseRandom <= 0.65;
    
    // Apply Shellshocked penalties if defender is shellshocked
    let shellshockedPenalty = 1.0;
    if (defender.shellshocked && defender.shellshocked.turnsRemaining > 0) {
      shellshockedPenalty = 0.75; // 25% reduction to defense when shellshocked
      debug.log(`  - Defender is SHELLSHOCKED! Defense reduced by 25%`);
    }
    
    // Apply Shellshocked penalty to attacker if applicable
    let attackerShellshockedPenalty = 1.0;
    if (attacker.shellshocked && attacker.shellshocked.turnsRemaining > 0) {
      attackerShellshockedPenalty = 0.75; // 25% reduction to attack when shellshocked
      debug.log(`  - Attacker is SHELLSHOCKED! Attack reduced by 25%`);
    }
    
    // Apply panic penalty if unit is panic firing
    let panicPenalty = 1.0;
    if ((attacker as any).isPanicking) {
      panicPenalty = 0.5; // 50% reduction to attack when panic firing
      debug.log(`  - PANIC FIRE! Attack severely reduced (-50%)`);
    }
    
    const modifiedAttack = attacker.stats.attack * attackRandom * attackerShellshockedPenalty * panicPenalty * (1 + parseFloat(attackerModifiers.attack) / 100);
    const modifiedDefense = defender.stats.defense * defenseRandom * shellshockedPenalty * (1 + parseFloat(defenderModifiers.defense) / 100);
    
    // Calculate damage (new formula for higher damage)
    const baseDamage = modifiedAttack * 3;
    const damageReduction = modifiedDefense / (modifiedDefense + 20);
    let finalDamage = baseDamage * (1 - damageReduction);
    
    // Apply critical hit bonus
    if (isCritical) {
      finalDamage *= 1.5; // 50% bonus damage on critical hits
      debug.log(`*** CRITICAL HIT! Attack roll: ${(attackRandom * 100).toFixed(0)}% vs Defense roll: ${(defenseRandom * 100).toFixed(0)}% ***`);
    }
    
    // Ensure minimum damage (at least 50% of attacker's base attack)
    finalDamage = Math.max(finalDamage, attacker.stats.attack * 0.5);
    
    const roundedDamage = Math.round(finalDamage);
    
    // Track battle statistics
    const attackerStats = this.gameState.battleStats?.get(attacker.faction);
    if (attackerStats) {
      attackerStats.damageDealt += roundedDamage;
    }
    const defenderStats = this.gameState.battleStats?.get(defender.faction);
    if (defenderStats) {
      defenderStats.damageTaken += roundedDamage;
    }
    
    // Console logging for damage calculation
    debug.log(`=== ${isCounterattack ? 'COUNTERATTACK' : 'ATTACK'} CALCULATION ===`);
    debug.log(`Attacker: ${attacker.name} (${attacker.faction})`);
    debug.log(`  - Base Attack: ${attacker.stats.attack}`);
    debug.log(`  - Terrain: ${attackerTile?.terrain || 'plains'} (${attackerModifiers.attack})`);
    debug.log(`  - Random Factor: ${(attackRandom * 100).toFixed(1)}%`);
    debug.log(`  - Modified Attack: ${modifiedAttack.toFixed(2)}`);
    debug.log(`Defender: ${defender.name} (${defender.faction})`);
    debug.log(`  - Base Defense: ${defender.stats.defense}`);
    debug.log(`  - Terrain: ${defenderTile?.terrain || 'plains'} (${defenderModifiers.defense})`);
    debug.log(`  - Random Factor: ${(defenseRandom * 100).toFixed(1)}%`);
    debug.log(`  - Modified Defense: ${modifiedDefense.toFixed(2)}`);
    debug.log(`Damage Calculation:`);
    debug.log(`  - Base Damage: ${modifiedAttack.toFixed(2)} × 3 = ${baseDamage.toFixed(2)}`);
    debug.log(`  - Damage Reduction: ${modifiedDefense.toFixed(2)} / (${modifiedDefense.toFixed(2)} + 20) = ${(damageReduction * 100).toFixed(1)}%`);
    debug.log(`  - Damage After Reduction: ${baseDamage.toFixed(2)} × ${(1 - damageReduction).toFixed(3)} = ${(baseDamage * (1 - damageReduction)).toFixed(2)}`);
    debug.log(`  - Minimum Damage Check: ${attacker.stats.attack * 0.5} (50% of base attack)`);
    debug.log(`  - Final Damage: ${roundedDamage}`);
    debug.log(`================================`);
    
    // Apply damage (pass attacker ID for veteran tracking)
    console.log(`[COMBAT] ${attacker.name} deals ${roundedDamage} damage to ${defender.name} (health: ${defender.stats.health})`);
    const result = this.unitManager.damageUnit(defender.id, roundedDamage, attacker.faction, attacker.id);
    console.log(`[COMBAT] After damage: ${defender.name} health = ${defender.stats.health}, destroyed = ${result.destroyed}`);
    
    // Track kills in battle statistics
    if (result.destroyed) {
      const attackerStats = this.gameState.battleStats?.get(attacker.faction);
      if (attackerStats) {
        attackerStats.enemiesKilled++;
      }
      const defenderStats = this.gameState.battleStats?.get(defender.faction);
      if (defenderStats) {
        defenderStats.unitsLost++;
      }
    }
    
    // Add damage text to defender (show CRITICAL! if it was a critical hit)
    defender.damageText = {
      amount: roundedDamage,
      startTime: Date.now(),
      duration: 2000 // 2 seconds
    };
    // Store critical hit and panic fire info separately if needed
    (defender as any).lastHitCritical = isCritical;
    (defender as any).lastHitPanicFire = (attacker as any).isPanicking || false;
    
    // Apply Shellshocked status on critical hit if defender survives and is not a veteran
    if (isCritical && !result.destroyed) {
      if (!defender.isVeteran) {
        defender.shellshocked = {
          turnsRemaining: 3,
          appliedTurn: this.gameState.turn
        };
        debug.log(`*** ${defender.name} is now SHELLSHOCKED for 3 turns! ***`);
        
        // Show shellshocked notification
        this.showNotification(`CRITICAL HIT! ${defender.name} is shellshocked!`);
      } else {
        // Veterans are immune to shellshock but still take critical damage
        debug.log(`*** CRITICAL HIT on veteran ${defender.name} - immune to shellshock! ***`);
        this.showNotification(`CRITICAL HIT! ${defender.name} (veteran) resists shellshock!`);
      }
    }
    
    // Play destruction sound and start death animation if unit was destroyed
    if (result.destroyed) {
      this.soundManager.playUnitDestroyed();
      // Add death animation
      defender.deathAnimation = {
        startTime: Date.now(),
        duration: 1000, // 1 second for backflip
        startY: defender.displayPosition ? defender.displayPosition.y : defender.position.y
      };
      
      // Track unit destruction for notifications (store for the unit's owner to see later)
      if (!this.gameState.pendingNotifications) {
        this.gameState.pendingNotifications = new Map();
      }
      
      const defenderFaction = defender.faction;
      if (!this.gameState.pendingNotifications.has(defenderFaction)) {
        this.gameState.pendingNotifications.set(defenderFaction, []);
      }
      
      this.gameState.pendingNotifications.get(defenderFaction)!.push(`The ${defender.name} has been destroyed!`);
      
      // Check if attacker was promoted to veteran
      if (result.attackerPromoted) {
        // Play special sound for veteran promotion
        this.soundManager.playMenuSelect(); // Use a special sound
        
        // Show veteran promotion notification
        this.sendNotification({
          type: NotificationType.UNIT_VETERAN_PROMOTION,
          faction: attacker.faction,
          data: { 
            unitName: attacker.name,
            killCount: attacker.killCount || 3
          },
          timestamp: Date.now()
        });
        
        // Add veteran promotion animation
        attacker.veteranPromotionAnimation = {
          startTime: Date.now(),
          duration: 2000 // 2 seconds
        };
      }
    }
    
    // Set animation for visual feedback
    if (!isCounterattack) {
      // Attacker jumps and flashes white
      attacker.attackAnimation = {
        type: 'attacker',
        startTime: Date.now(),
        duration: 500
      };
      
      // Defender vibrates and flashes red
      if (!result.destroyed) {
        defender.attackAnimation = {
          type: 'defender',
          startTime: Date.now(),
          duration: 500
        };
      }
    }
    
    // Give resource reward if unit was destroyed
    if (result.destroyed && result.reward) {
      const currentResources = this.gameState.resources.get(attacker.faction) || 0;
      this.gameState.resources.set(attacker.faction, currentResources + result.reward);
      this.updateResourceDisplay();
    }
    
    return roundedDamage;
  }
  
  private attackUnit(attacker: Unit, defender: Unit): void {
    // Check if it's my turn in online games
    if (this.isOnlineGame && !this.isMyTurn) {
      this.soundManager.playError();
      this.showNotification("It's not your turn!");
      return;
    }
    
    // Check if bombers are trying to attack aircraft (not allowed)
    if (isBomber(attacker.type as UnitType) && 
        isAircraft(defender.type as UnitType)) { // Bombers cannot attack aircraft
      debug.log(`Bombers cannot attack aircraft!`);
      return;
    }
    
    // Play attack sound
    this.soundManager.playAttack();
    
    // Perform the attack
    this.performAttack(attacker, defender);
    
    // Mark attacker as having attacked
    attacker.hasAttacked = true;
    
    // Reset unit cycle index since available units changed
    this.unitCycleIndex = -1;
    
    // Update UI
    this.updateCombatUI();
    
    // Update tutorial progress if in tutorial mode and human player attacked
    // DISABLED: Now handled by the guided tutorial system
    // if (this.isTutorial && attacker.faction === this.humanPlayerFaction && this.tutorialStage === 4) {
    //   this.updateTutorialProgress();
    // }
    
    // Save game state after attack
    this.saveGameState();
    
    // Sync attack with Firebase in online games
    if (this.isOnlineGame && this.multiplayerService) {
      // Just update the full game state - don't send individual moves
      const completeGameState = this.buildCompleteGameState();
      this.multiplayerService.updateGameState(
        completeGameState,
        this.gameState.currentFaction,
        this.gameState.turn
      );
    }
  }
  
  private updateCombatUI(): void {
    // Clear selection if selected unit was destroyed
    if (this.gameState.selectedUnit) {
      const selectedUnit = this.unitManager.getUnit(this.gameState.selectedUnit);
      if (!selectedUnit) {
        this.gameState.selectedUnit = null;
        this.availableMoves = null;
        this.enemiesInRange = null;
        this.updateUnitInfo(null);
      } else {
        this.updateUnitInfo(selectedUnit);
        // Recalculate available moves if unit has movement remaining
        if (selectedUnit.movementRemaining > 0) {
          this.calculateAvailableMoves(selectedUnit);
        } else {
          this.availableMoves = null;
        }
        // Recalculate enemies in range if unit still exists and hasn't attacked
        if (!selectedUnit.hasAttacked) {
          this.calculateEnemiesInRange(selectedUnit);
        } else {
          this.enemiesInRange = null;
        }
      }
    }
  }

  /**
   * Format UnitCategory enum value to display string
   */
  private formatUnitCategory(category: UnitCategory): string {
    switch (category) {
      case UnitCategory.INFANTRY: return 'Infantry';
      case UnitCategory.ARMOR: return 'Armour';
      case UnitCategory.ARTILLERY: return 'Artillery';
      case UnitCategory.RECONNAISSANCE: return 'Recon';
      case UnitCategory.AIRCRAFT: return 'Aircraft';
      case UnitCategory.FIGHTERS: return 'Fighter';
      case UnitCategory.BOMBERS: return 'Bomber';
      case UnitCategory.TRANSPORT_AIRCRAFT: return 'Transport Aircraft';
      case UnitCategory.ENGINEERS: return 'Engineer';
      case UnitCategory.MEDICS: return 'Medic';
      case UnitCategory.TRANSPORT: return 'Transport';
      case UnitCategory.HALFTRACK: return 'Halftrack';
      default: return 'Unknown';
    }
  }

  private getSquadComposition(type: UnitType): string {
    switch(type) {
      // US Units
      case UnitType.RIFLE_SQUAD:
        return '• 1x Squad Leader (M1 Carbine)<br/>• 7x Riflemen (M1 Garand)<br/>• 1x BAR Gunner<br/>• 1x Assistant (M1 Garand)<br/>• 1x Scout (M1 Garand)<br/>• 1x Grenadier (M1 w/ rifle grenades)<br/>(12 men total)';
      case UnitType.RANGER_SQUAD:
        return '• 1x Squad Leader (Thompson SMG)<br/>• 5x Rangers (M1 Garand)<br/>• 2x Rangers (Thompson SMG)<br/>• 1x BAR Gunner (M1918A2 BAR)<br/>• 1x Demolitions Expert (M1 Garand + Composition C2)<br/>• Satchel charges & Bangalore torpedoes<br/>(10 elite volunteers)';
      case UnitType.AIRBORNE_SQUAD:
        return '• 1x Squad Leader (Thompson SMG)<br/>• 7x Paratroopers (M1A1 Carbine)<br/>• 1x Machine Gunner (M1919A6)<br/>• 1x Assistant Gunner<br/>• 1x Sniper (M1903 Springfield)<br/>(11 men)';
      case UnitType.SHERMAN_SECTION:
        return '• 1x M4A3 Sherman tank<br/>• 75mm M3 gun<br/>• .50 cal + 2x .30 cal MGs<br/>• 5 crew members';
      case UnitType.TANK_DESTROYER_SECTION:
        return '• 1x M10 Wolverine TD<br/>• 3-inch (76.2mm) gun<br/>• Open-topped turret<br/>• 5 crew members';
      case UnitType.US_ARTILLERY_SECTION:
        return '• 2x M101A1 105mm howitzers<br/>• HE/WP/Smoke shells<br/>• 6 men per gun (12 total)<br/>• 2x 2½-ton trucks';
      case UnitType.ENGINEER_SQUAD:
        return '• 1x Squad Leader (M1 Carbine)<br/>• 6x Engineers (M1 Garand)<br/>• 1x Flamethrower Operator (M2-2 flamethrower)<br/>• 1x Demolitions Specialist (M1 Garand + TNT/Composition B)<br/>• 1x Mine Detector Operator (M1 Garand + SCR-625 detector)<br/>• Bangalore torpedoes & satchel charges<br/>(10 men)';
      
      // German Units
      case UnitType.PANZERGRENADIER_SQUAD:
        return '• 1x Squad Leader (MP40)<br/>• 1x MG42 Gunner<br/>• 1x Assistant Gunner (Kar 98k)<br/>• 6x Panzergrenadiers (Kar 98k)<br/>• 1x Panzerfaust Gunner<br/>(10 men)';
      case UnitType.WAFFEN_SS_SQUAD:
        return '• 1x Squad Leader (StG 44)<br/>• 4x Assault Riflemen (StG 44)<br/>• 1x MG42 Gunner<br/>• 1x Assistant Gunner<br/>• 3x Riflemen (Kar 98k)<br/>(10 men)';
      case UnitType.FALLSCHIRMJAGER_SQUAD:
        return '• 1x Squad Leader (MP40)<br/>• 2x Automatic Riflemen (FG 42)<br/>• 1x MG34 Gunner<br/>• 1x Assistant Gunner<br/>• 5x Riflemen (Kar 98k)<br/>(10 men)';
      case UnitType.PANZER_IV_ZUG:
        return '• 1x Panzer IV Ausf. H<br/>• 75mm KwK 40 L/48 gun<br/>• Schürzen side armor<br/>• 5 crew members';
      case UnitType.TIGER_I_SECTION:
        return '• 1x Tiger I Ausf. E<br/>• 88mm KwK 36 L/56 gun<br/>• 100mm frontal armor<br/>• 5 crew members';
      case UnitType.STUG_III_SECTION:
        return '• 1x StuG III Ausf. G<br/>• 75mm StuK 40 L/48 gun<br/>• Low profile (2.16m)<br/>• 4 crew members';
      case UnitType.GER_ARTILLERY_SECTION:
        return '• 2x 7.5cm Pak 40 anti-tank guns<br/>• AP/HE rounds<br/>• 6 men per gun (12 total)<br/>• 2x Sd.Kfz. 7 half-tracks';
      case UnitType.PIONEER_SQUAD:
        return '• 1x Squad Leader (MP40)<br/>• 6x Pioneers (Kar 98k)<br/>• 1x Flamethrower Operator (Flammenwerfer 35)<br/>• 1x Demolitions Expert (Kar 98k + Sprengbüchse/Geballte Ladung)<br/>• 1x Mine Layer (Kar 98k + S-mines/Teller mines)<br/>• Smoke grenades & wire cutters<br/>(10 men)';
      
      // Eastern Wehrmacht Units (Stalingrad 1942)
      case UnitType.GRENADIER_SQUAD_1942:
        return '• 1x Squad Leader (MP40)<br/>• 1x MG34 Gunner<br/>• 1x Assistant Gunner (Kar 98k)<br/>• 7x Grenadiers (Kar 98k)<br/>(10 men)';
      case UnitType.STURM_ABTEILUNG_SQUAD:
        return '• 1x Squad Leader (MP40)<br/>• 2x Assault Troops (MP40)<br/>• 1x MG34 Gunner<br/>• 1x Assistant Gunner<br/>• 5x Riflemen (Kar 98k)<br/>(10 elite assault troops)';
      case UnitType.FALLSCHIRMJAGER_SQUAD_1942:
        return '• 1x Squad Leader (MP40)<br/>• 3x Assault Troops (MP40)<br/>• 1x MG34 Gunner<br/>• 1x Assistant Gunner<br/>• 4x Riflemen (Kar 98k)<br/>(10 paratroopers)';
      case UnitType.PANZERGRENADIER_SQUAD_1942:
        return '• 1x Squad Leader (MP40)<br/>• 1x MG34 Gunner<br/>• 1x Assistant Gunner (Kar 98k)<br/>• 6x Panzergrenadiers (Kar 98k)<br/>• 1x Anti-tank Specialist (Panzerfaust/AT grenades)<br/>(10 mechanized infantry)';
      case UnitType.STURMPIONIERE:
        return '• 1x Squad Leader (MP40)<br/>• 6x Pioneers (Kar 98k)<br/>• 1x Flamethrower Operator (Flammenwerfer 35)<br/>• 1x Demolitions Expert (Kar 98k + charges)<br/>• 1x Assault Engineer (MP40)<br/>(10 assault engineers)';
      case UnitType.SANITATER_SQUAD_1942:
        return '• 1x Medical Officer<br/>• 3x Sanitäter (medics)<br/>• 2x Stretcher bearers<br/>• Medical supplies and morphine<br/>(6 medical personnel)';
      case UnitType.PANZER_IV_AUSF_F2:
        return '• 1x Panzer IV Ausf. F2<br/>• 75mm KwK 40 L/43 gun<br/>• Improved armor<br/>• 5 crew members';
      case UnitType.PANZER_III_AUSF_L:
        return '• 1x Panzer III Ausf. L<br/>• 50mm KwK 39 L/60 gun<br/>• Face-hardened armor<br/>• 5 crew members';
      case UnitType.STUG_III_AUSF_F:
        return '• 1x StuG III Ausf. F<br/>• 75mm StuK 40 L/43 gun<br/>• Low profile assault gun<br/>• 4 crew members';
      case UnitType.PAK_38_BATTERY:
        return '• 2x 5cm Pak 38 anti-tank guns<br/>• AP/HE rounds<br/>• 5 men per gun (10 total)<br/>• 2x Sd.Kfz. 10 half-tracks';
      case UnitType.SDKFZ_250_1:
        return '• 1x Sd.Kfz. 250/1 Neu<br/>• Light armored personnel carrier<br/>• MG34 mount<br/>• 6 passengers + 2 crew';
      case UnitType.JU_87_D_STUKA:
        return '• 1x Ju 87 B Stuka<br/>• Dive bomber configuration<br/>• 250kg/500kg bombs<br/>• 2 crew members';
      case UnitType.HE_111_H6:
        return '• 1x He 111 H-6<br/>• Medium bomber<br/>• 2000kg bomb load<br/>• 5 crew members';
      case UnitType.OPEL_BLITZ_1942:
        return '• 1x Opel Blitz 3.6-36S<br/>• 3.5-ton cargo truck<br/>• Canvas-covered cargo bed<br/>• 2 crew + cargo space';
      case UnitType.SDKFZ_251_1942:
        return '• 1x Sd.Kfz. 251/1 Ausf. C<br/>• Armored personnel carrier<br/>• MG34 mount<br/>• 10 passengers + 2 crew';
      
      // UK Units
      case UnitType.INFANTRY_SECTION:
        return '• 1x Section Leader (Sten gun)<br/>• 1x Bren Gunner<br/>• 1x Bren Assistant<br/>• 7x Riflemen (Lee-Enfield No.4 Mk I)<br/>(10 men)';
      case UnitType.GUARDS_INFANTRY:
        return '• 1x Section Leader (Thompson SMG)<br/>• 2x Bren Gunners<br/>• 2x Bren Assistants<br/>• 5x Guardsmen (Lee-Enfield)<br/>(10 elite troops)';
      case UnitType.COMMANDO_SECTION:
        return '• 1x Section Leader (Thompson SMG)<br/>• 4x Commandos (Thompson SMG)<br/>• 2x Commandos (Lee-Enfield + Fairbairn-Sykes knife)<br/>• 1x Sniper (Lee-Enfield No.4 Mk I(T))<br/>• 1x Demolitions Expert<br/>(9 special forces)';
      case UnitType.SAPPER_SECTION:
        return '• 1x Section Leader (Sten gun)<br/>• 6x Sappers (Lee-Enfield)<br/>• 1x Flamethrower Operator (Lifebuoy flamethrower)<br/>• 1x Demolitions Expert<br/>• 1x Mine Detector Operator<br/>(10 men)';
      case UnitType.CRUSADER_TANK:
        return '• 1x Crusader Mk III<br/>• 6-pounder (57mm) gun<br/>• 51mm armor<br/>• 5 crew members';
      case UnitType.MATILDA_II:
        return '• 1x Matilda II Infantry Tank<br/>• 2-pounder (40mm) gun<br/>• 78mm armor<br/>• 4 crew members';
      case UnitType.SIX_POUNDER_AT:
        return '• 1x 6-pounder (57mm) anti-tank gun<br/>• AP/HE rounds<br/>• 6 crew members<br/>• 1x Universal Carrier';
      case UnitType.TWENTY_FIVE_POUNDER:
        return '• 2x 25-pounder gun-howitzers<br/>• HE/AP/Smoke shells<br/>• 6 men per gun (12 total)<br/>• 2x Morris Quad tractors';
      
      // Afrika Korps Units
      case UnitType.SCHUTZEN_SQUAD:
        return '• 1x Squad Leader (MP40)<br/>• 1x MG34 Gunner<br/>• 1x Assistant Gunner<br/>• 7x Riflemen (Kar 98k)<br/>(10 men)';
      case UnitType.AFRIKA_GRENADIERS:
        return '• 1x Squad Leader (MP40)<br/>• 1x MG34 Gunner<br/>• 1x Assistant Gunner<br/>• 5x Riflemen (Kar 98k)<br/>• 2x Rifle Grenadiers<br/>(10 desert veterans)';
      case UnitType.AK_PIONEER_SQUAD:
        return '• 1x Squad Leader (MP40)<br/>• 7x Pioneers (Kar 98k)<br/>• 1x Demolitions Expert<br/>• 1x Mine Detection/Laying Specialist<br/>(10 men)';
      case UnitType.AK_FALLSCHIRMJAGER:
        return '• 1x Squad Leader (MP40)<br/>• 2x Machine Gunners (MG34)<br/>• 2x Assistant Gunners<br/>• 5x Riflemen (Kar 98k)<br/>(10 elite paratroopers)';
      case UnitType.PANZER_III:
        return '• 1x Panzer III Ausf. L<br/>• 50mm KwK 39 L/60 gun<br/>• Additional armor plates<br/>• 5 crew members';
      case UnitType.PANZER_II:
        return '• 1x Panzer II Ausf. F<br/>• 20mm KwK 30 autocannon<br/>• Light reconnaissance tank<br/>• 3 crew members';
      case UnitType.EIGHTY_EIGHT_FLAK:
        return '• 1x 88mm Flak 36<br/>• Dual-purpose AA/AT gun<br/>• 8 crew members<br/>• 1x Sd.Kfz. 7 half-track';
      case UnitType.PAK_38:
        return '• 1x 50mm Pak 38 anti-tank gun<br/>• AP/HE rounds<br/>• 6 crew members<br/>• 1x Sd.Kfz. 10 half-track';
      
      // Soviet Units
      case UnitType.RED_ARMY_SQUAD:
        return '• 1x Squad Leader (PPSh-41)<br/>• 7x Riflemen (Mosin-Nagant)<br/>• 1x DP-28 Gunner<br/>• 1x Assistant Gunner<br/>(10 men)';
      case UnitType.CONSCRIPT_SQUAD:
        return '• 1x Junior Sergeant (Mosin-Nagant)<br/>• 9x Conscripts (Mosin-Nagant)<br/>• Minimal training<br/>(10 men)';
      case UnitType.SHOCK_TROOPS:
        return '• 1x Squad Leader (PPS-43)<br/>• 6x Shock Troops (PPSh-41)<br/>• 3x Assault Troops (F-1 grenades)<br/>• Body armor<br/>(10 assault specialists)';
      case UnitType.T34_76:
        return '• 1x T-34/76 Model 1943<br/>• 76.2mm F-34 gun<br/>• Sloped armor<br/>• 4 crew members';
      case UnitType.KV1_HEAVY_TANK:
        return '• 1x KV-1 Model 1942<br/>• 76.2mm ZiS-5 gun<br/>• 90mm armor<br/>• 5 crew members';
      case UnitType.T70_LIGHT_TANK:
        return '• 2x T-70 light tanks<br/>• 45mm L/46 guns<br/>• 2 crew per tank<br/>• Fast recon tanks';
      case UnitType.BA64_ARMORED_CAR:
        return '• 1x BA-64B armored car<br/>• 7.62mm DT machine gun<br/>• 2 crew members<br/>• Scout vehicle';
      case UnitType.ZIS3_AT_GUN:
        return '• 1x 76.2mm ZiS-3 divisional gun<br/>• AP/HE rounds<br/>• 6 crew members<br/>• 1x GAZ truck';
      case UnitType.SOVIET_SAPPER_SQUAD:
        return '• 1x Squad Leader (PPSh-41)<br/>• 7x Sappers (Mosin-Nagant)<br/>• 2x Demolition Experts<br/>• Mine clearing equipment<br/>(10 engineers)';
      case UnitType.KATYUSHA_BATTERY:
        return '• 2x BM-13 Katyusha rocket launchers<br/>• 132mm rockets (16 per vehicle)<br/>• 3 crew per vehicle<br/>• "Stalin\'s Organ"';
      
      // US Marine Units (Okinawa)
      case UnitType.MARINE_SQUAD:
        return '• 1x Squad Leader (M1 Carbine)<br/>• 7x Marines (M1 Garand)<br/>• 1x BAR Gunner (M1918A2 BAR)<br/>• 1x Assistant BAR Gunner<br/>• 1x Flamethrower Operator (M2-2)<br/>• 1x Corpsman (M1 Carbine)<br/>(12 Marines total)';
      case UnitType.MARINE_RAIDERS:
        return '• 1x Squad Leader (Thompson SMG)<br/>• 6x Raiders (M1 Garand)<br/>• 1x BAR Gunner (M1918A2 BAR)<br/>• 1x Demolitions Expert<br/>• 1x Sniper (M1903 Springfield)<br/>• "Gung Ho!" spirit<br/>(10 elite Marines)';
      case UnitType.MARINE_FLAME_TEAM:
        return '• 1x Team Leader (M1 Carbine)<br/>• 2x Flamethrower Operators (M2-2)<br/>• 4x Riflemen (M1 Garand)<br/>• 1x Demolitions Expert<br/>• Specialized bunker clearing<br/>(8 Marines)';
      case UnitType.MARINE_ENGINEERS:
        return '• 1x Squad Leader (M1 Carbine)<br/>• 6x Engineers (M1 Garand)<br/>• 1x Flamethrower Operator (M2-2)<br/>• 1x Demolitions Specialist<br/>• 1x Mine Detector Operator<br/>• Bangalore torpedoes & satchel charges<br/>(10 Marines)';
      case UnitType.M4A3_SHERMAN:
        return '• 1x M4A3 Sherman tank<br/>• 75mm M3 gun<br/>• .50 cal + 2x .30 cal MGs<br/>• 5 crew members<br/>• Marine variant';
      case UnitType.M3_STUART:
        return '• 1x M3 Stuart light tank<br/>• 37mm M6 gun<br/>• .30 cal machine gun<br/>• 4 crew members<br/>• Fast reconnaissance';
      case UnitType.M7_PRIEST:
        return '• 1x M7 Priest SPG<br/>• 105mm M2A1 howitzer<br/>• .50 cal AA machine gun<br/>• 7 crew members<br/>• Self-propelled artillery';
      case UnitType.PACK_HOWITZER:
        return '• 1x 75mm M1A1 Pack Howitzer<br/>• Lightweight design<br/>• HE/Smoke shells<br/>• 6 crew members<br/>• Can be man-portable';
      
      // Imperial Japan Units (Okinawa)
      case UnitType.IJA_SQUAD:
        return '• 1x Squad Leader (Type 14 Nambu)<br/>• 7x Riflemen (Type 38 Arisaka)<br/>• 1x Type 99 LMG Gunner<br/>• 1x Assistant Gunner<br/>• "No retreat" discipline<br/>(10 soldiers)';
      case UnitType.SNLF_SQUAD:
        return '• 1x Squad Leader (Type 100 SMG)<br/>• 6x Naval Infantry (Type 38 Arisaka)<br/>• 1x Type 99 LMG Gunner<br/>• 1x Assistant Gunner<br/>• 1x Grenadier (Type 89 launcher)<br/>• Elite naval landing force<br/>(10 soldiers)';
      case UnitType.ASSAULT_PIONEERS:
        return '• 1x Squad Leader (Type 100 SMG)<br/>• 5x Assault Troops (Type 38 Arisaka)<br/>• 2x Demolition Experts<br/>• 1x Flamethrower Operator (Type 93)<br/>• Close combat specialists<br/>(9 soldiers)';
      case UnitType.CAVE_DEFENSE_SQUAD:
        return '• 1x Squad Leader (Type 14 Nambu)<br/>• 6x Riflemen (Type 38 Arisaka)<br/>• 1x Type 99 LMG Gunner<br/>• 1x Assistant Gunner<br/>• 1x Grenadier (Type 89 launcher)<br/>• Tunnel warfare specialists<br/>(10 soldiers)';
      case UnitType.TYPE_95_HA_GO:
        return '• 1x Type 95 Ha-Go light tank<br/>• 37mm Type 94 gun<br/>• 7.7mm Type 97 MG<br/>• 3 crew members<br/>• Fast reconnaissance tank';
      case UnitType.TYPE_97_CHI_HA:
        return '• 1x Type 97 Chi-Ha tank<br/>• 47mm Type 1 gun<br/>• 7.7mm Type 97 MG<br/>• 4 crew members<br/>• Standard medium tank';
      case UnitType.TYPE_4_CHI_TO:
        return '• 1x Type 4 Chi-To heavy tank<br/>• 75mm Type 5 gun<br/>• 7.7mm Type 97 MG<br/>• 5 crew members<br/>• Experimental design';
      case UnitType.TYPE_92_JYU_SOKOSHA:
        return '• 1x Type 92 Jyu-Sokosha armored car<br/>• 6.5mm Type 91 machine gun<br/>• 3 crew members<br/>• Reconnaissance and patrol<br/>• Light armor protection';
      case UnitType.TYPE_92_BATTALION_GUN:
        return '• 1x Type 92 70mm battalion gun<br/>• HE/AP shells<br/>• 6 crew members<br/>• Direct fire support<br/>• Can be dismantled';
      case UnitType.TYPE_89_MORTAR:
        return '• 1x Type 89 50mm grenade launcher<br/>• HE grenades<br/>• 3 crew members<br/>• "Knee mortar" nickname<br/>• Lightweight indirect fire';
      case UnitType.TYPE_1_47MM_AT_GUN:
        return '• 1x Type 1 47mm anti-tank gun<br/>• AP/HE rounds<br/>• 6 crew members<br/>• Japan\'s best AT gun 1945<br/>• Effective vs light armor';
      case UnitType.TYPE_96_150MM_HOWITZER:
        return '• 1x Type 96 150mm howitzer<br/>• HE/Smoke shells<br/>• 8 crew members<br/>• Long-range bombardment<br/>• Heavy artillery support';
      
      // Canadian Units
      case UnitType.CANADIAN_INFANTRY_SECTION:
        return '• 1x Section Leader (Sten Mk II)<br/>• 6x Riflemen (Lee-Enfield No.4 Mk I)<br/>• 1x Bren Gunner<br/>• 1x Assistant Gunner<br/>• 1x PIAT Gunner<br/>(10 men)';
      case UnitType.PPCLI:
        return '• 1x Section Leader (Thompson SMG)<br/>• 7x Riflemen (Lee-Enfield No.4 Mk I)<br/>• 1x Bren Gunner<br/>• 1x Assistant Gunner<br/>• Princess Patricia\'s Canadian Light Infantry<br/>(10 elite infantry)';
      case UnitType.HIGHLAND_REGIMENT:
        return '• 1x Section Leader (Sten Mk II)<br/>• 6x Highlanders (Lee-Enfield No.4 Mk I)<br/>• 1x Bren Gunner<br/>• 1x Assistant Gunner<br/>• 1x Piper (bagpipes + Lee-Enfield)<br/>• Elite mountain troops<br/>(10 men)';
      case UnitType.COMBAT_ENGINEERS:
        return '• 1x Section Leader (Sten Mk II)<br/>• 5x Engineers (Lee-Enfield No.4 Mk I)<br/>• 1x Flamethrower Operator (Wasp II)<br/>• 1x Demolitions Expert<br/>• 1x Mine Detector Operator<br/>• Bailey bridges & explosives<br/>(9 men)';
      case UnitType.RAM_TANK:
        return '• 1x Ram Mk II tank<br/>• 6-pounder (57mm) gun<br/>• .30 cal coaxial MG<br/>• 5 crew members<br/>• Canadian-built cruiser tank';
      case UnitType.SEXTON_SPG:
        return '• 1x Sexton self-propelled gun<br/>• 25-pounder (87.6mm) gun-howitzer<br/>• .50 cal AA machine gun<br/>• 6 crew members<br/>• Mobile artillery support';
      case UnitType.TWENTY_FIVE_POUNDER_BATTERY:
        return '• 2x 25-pounder gun-howitzers<br/>• HE/AP/Smoke shells<br/>• 6 men per gun (12 total)<br/>• Quad gun tractors<br/>• Commonwealth standard artillery';
      case UnitType.RECONNAISSANCE_PATROL:
        return '• 1x Universal Carrier<br/>• 1x Patrol Leader (Thompson SMG)<br/>• 2x Scouts (Lee-Enfield No.4 Mk I)<br/>• 1x Driver (Sten Mk II)<br/>• .303 Bren gun mount<br/>• Extended vision equipment<br/>(4 men + vehicle)';
      case UnitType.GRIZZLY_CRUISER:
        return '• 1x Grizzly I cruiser tank<br/>• 75mm M3 L/40 gun<br/>• .30 cal coaxial MG<br/>• .50 cal AA machine gun<br/>• 5 crew members<br/>• CDP winter tracks';
      case UnitType.SHERMAN_FIREFLY:
        return '• 1x Sherman Firefly Mk IC<br/>• 17-pounder (76.2mm) gun<br/>• .30 cal coaxial MG<br/>• 5 crew members<br/>• Tank killer variant';
      
      // Italian Units
      case UnitType.BERSAGLIERI_SQUAD:
        return '• 1x Squad Leader (Beretta MAB 38)<br/>• 7x Bersaglieri (Carcano M91/38)<br/>• 1x Breda 30 LMG Gunner<br/>• 1x Assistant Gunner<br/>• Elite light infantry<br/>(10 men)';
      case UnitType.ALPINI_MOUNTAIN_TROOPS:
        return '• 1x Squad Leader (Beretta MAB 38)<br/>• 6x Alpini (Carcano M91/38)<br/>• 1x Breda 30 LMG Gunner<br/>• 1x Assistant Gunner<br/>• 1x Mountain Guide<br/>• Mountain warfare specialists<br/>(10 men)';
      case UnitType.FOLGORE_PARATROOPERS:
        return '• 1x Squad Leader (Beretta MAB 38)<br/>• 5x Paratroopers (Carcano M91/38)<br/>• 2x Assault Troops (FNAB-43 SMG)<br/>• 1x Breda 30 LMG Gunner<br/>• Elite airborne forces<br/>(9 men)';
      case UnitType.GENIO_ENGINEERS:
        return '• 1x Squad Leader (Beretta M1934)<br/>• 5x Engineers (Carcano M91/38)<br/>• 1x Flamethrower Operator (Lanciafiamme Mod.35)<br/>• 1x Demolitions Expert<br/>• 1x Mine Layer<br/>• Combat engineers<br/>(9 men)';
      case UnitType.M15_42_TANK:
        return '• 1x M15/42 medium tank<br/>• 47mm L/40 gun<br/>• 8mm Breda 38 coaxial MG<br/>• 4 crew members<br/>• Best Italian tank 1943';
      case UnitType.SEMOVENTE_75_18:
        return '• 1x Semovente 75/18 assault gun<br/>• 75mm L/18 howitzer<br/>• Low profile design<br/>• 3 crew members<br/>• Infantry support vehicle';
      case UnitType.CANNONE_75_32:
        return '• 1x Cannone da 75/32 modello 37<br/>• 75mm field gun<br/>• HE/AP shells<br/>• 6 crew members<br/>• Effective anti-tank gun';
      case UnitType.OBICE_149_19:
        return '• 2x Obice da 149/19 howitzers<br/>• 149mm heavy artillery<br/>• HE/Smoke shells<br/>• 8 men per gun (16 total)<br/>• Fiat-SPA TM40 tractors<br/>• Long-range bombardment';
      case UnitType.ARDITI_SCOUTS:
        return '• 1x Lancia 1ZM armored car<br/>• 1x Patrol Leader (Beretta MAB 38)<br/>• 2x Arditi scouts (Carcano M91 cavalry)<br/>• 1x Driver/Gunner<br/>• Twin 8mm Breda MGs<br/>• Elite reconnaissance unit<br/>(4 men + vehicle)';
      case UnitType.P26_40_HEAVY_TANK:
        return '• 1x P26/40 heavy tank<br/>• 75mm L/34 gun<br/>• 8mm Breda 38 coaxial MG<br/>• 4 crew members<br/>• Italy\'s heaviest tank';
      
      // Canadian Aircraft
      case UnitType.SPITFIRE_FIGHTER:
        return '• 1x Spitfire Mk IX<br/>• 2x 20mm Hispano cannons<br/>• 2x .50 cal Browning MGs<br/>• Single-seat fighter<br/>• RCAF markings';
      case UnitType.MOSQUITO_FIGHTER_BOMBER:
        return '• 1x de Havilland Mosquito FB.VI<br/>• 4x 20mm Hispano cannons<br/>• 4x .303 Browning MGs<br/>• 2,000 lb bomb load<br/>• 2 crew members<br/>• "Wooden Wonder"';
      
      // Italian Aircraft
      case UnitType.MACCHI_C205:
        return '• 1x Macchi C.205 Veltro<br/>• 2x 12.7mm Breda-SAFAT MGs<br/>• 2x 20mm MG 151/20 cannons<br/>• Single-seat fighter<br/>• "Greyhound" - Italy\'s best fighter';
      case UnitType.SM79_BOMBER:
        return '• 1x Savoia-Marchetti SM.79 Sparviero<br/>• 3x 12.7mm Breda-SAFAT MGs<br/>• 1,250 kg bomb load<br/>• 5 crew members<br/>• "Sparrowhawk" tri-motor bomber';
      
      // New Elite Units
      case UnitType.HEAVY_WEAPONS_SQUAD:
        return '• 1x Squad Leader (M1 Carbine)<br/>• 2x Machine Gun Teams (.30 cal M1919A4)<br/>• 2x Bazooka Teams (M1A1 Bazooka)<br/>• 2x Ammo Bearers<br/>• Heavy firepower support<br/>(10 men)';
      case UnitType.GEBIRGSJAGER_SQUAD:
        return '• 1x Squad Leader (MP40)<br/>• 1x MG42 Gunner<br/>• 1x Assistant Gunner<br/>• 5x Mountain Troops (Kar 98k)<br/>• 2x Ski Troops<br/>• Edelweiss insignia<br/>(10 mountain specialists)';
      case UnitType.BRANDENBURG_COMMANDOS:
        return '• 1x Team Leader (MP40)<br/>• 4x Commandos (StG 44)<br/>• 2x Snipers (Kar 98k w/ scope)<br/>• 1x Demolitions Expert<br/>• Foreign language specialists<br/>(8 special forces)';
      case UnitType.SAN_MARCO_MARINES:
        return '• 1x Squad Leader (Beretta MAB 38)<br/>• 6x Marines (Carcano M91/38)<br/>• 1x Breda 30 LMG Gunner<br/>• 1x Assistant Gunner<br/>• Naval assault specialists<br/>(9 naval infantry)';
        
      // New Airborne Units
      case UnitType.RED_DEVILS_PARACHUTE:
        return '• 1x Section Leader (Sten Mk V)<br/>• 5x Paratroopers (Lee-Enfield No.4)<br/>• 1x Bren Gunner<br/>• 1x PIAT Anti-tank<br/>• 2x Gammon bomb carriers<br/>• Red beret troops<br/>(9 airborne soldiers)';
      case UnitType.VDV_GUARDS_AIRBORNE:
        return '• 1x Squad Leader (PPS-43)<br/>• 5x Paratroopers (SVT-40)<br/>• 1x DP-28 Gunner<br/>• 1x RPG-43 Anti-tank<br/>• 1x Radio Operator<br/>• Blue beret Guards<br/>(9 elite paratroopers)';
      case UnitType.CANADIAN_PARACHUTE_BATTALION:
        return '• 1x Section Leader (Sten Mk II)<br/>• 6x Paratroopers (Lee-Enfield No.4)<br/>• 1x Bren Gunner<br/>• 1x PIAT Gunner<br/>• First into Normandy<br/>(9 paratroopers)';
      case UnitType.MARINE_PARACHUTE_REGIMENT:
        return '• 1x Squad Leader (Thompson SMG)<br/>• 6x Paramarines (M1 Garand)<br/>• 1x BAR Gunner<br/>• 1x Demolitions Expert<br/>• Island assault specialists<br/>(9 Marines)';
      case UnitType.TEISHIN_SHUDAN:
        return '• 1x Squad Leader (Type 100 SMG)<br/>• 5x Paratroopers (Type 38 Arisaka)<br/>• 1x Type 99 LMG Gunner<br/>• 1x Demolitions Expert<br/>• "Giretsu" special attack unit<br/>(8 paratroopers)';
        
      // Medic Units
      case UnitType.MEDICAL_CORPS_SQUAD:
        return '• 1x Medical Officer (M1911 pistol)<br/>• 3x Combat Medics (unarmed)<br/>• 2x Stretcher Bearers<br/>• Medical supplies & morphine<br/>• Geneva Convention protected<br/>(6 medical personnel)';
      case UnitType.NAVY_CORPSMEN:
        return '• 1x Chief Corpsman (M1911 pistol)<br/>• 3x Corpsmen (unarmed)<br/>• 2x Stretcher Bearers<br/>• Plasma & surgical kit<br/>• "Doc" to the Marines<br/>(6 Navy medical)';
      case UnitType.FIELD_AMBULANCE_SECTION:
        return '• 1x Medical Officer (Webley revolver)<br/>• 3x Medics (unarmed)<br/>• 2x Stretcher Bearers<br/>• Field surgical equipment<br/>• RAMC personnel<br/>(6 medical corps)';
      case UnitType.MEDICAL_BATTALION:
        return '• 1x Doctor (TT-33 pistol)<br/>• 2x Medics (unarmed)<br/>• 3x Nurses (unarmed)<br/>• Basic medical supplies<br/>• Many are women<br/>(6 medical personnel)';
      case UnitType.SANITATER_SQUAD:
        return '• 1x Oberarzt (Luger P08)<br/>• 3x Sanitäter (unarmed)<br/>• 2x Krankenträger<br/>• Field dressings & morphine<br/>• Red Cross armbands<br/>(6 medical personnel)';
      case UnitType.AK_SANITATER:
        return '• 1x Medical NCO (Walther P38)<br/>• 3x Sanitäter (unarmed)<br/>• 2x Stretcher Bearers<br/>• Desert medical kit<br/>• Water conservation trained<br/>(6 Afrika Korps medics)';
      case UnitType.MEDICAL_SERVICE_CORPS:
        return '• 1x Medical Officer (Browning Hi-Power)<br/>• 3x Medics (unarmed)<br/>• 2x Stretcher Bearers<br/>• Advanced medical supplies<br/>• RCAMC trained<br/>(6 medical personnel)';
      case UnitType.SANITA_MILITARE:
        return '• 1x Medico (Beretta M1934)<br/>• 3x Infermieri (unarmed)<br/>• 2x Barellieri<br/>• Field hospital equipment<br/>• Red Cross protection<br/>(6 medical personnel)';
      case UnitType.IJA_MEDICAL_CORPS:
        return '• 1x Medical Officer (Type 14 Nambu)<br/>• 2x Medics (unarmed)<br/>• 2x Stretcher Bearers<br/>• Limited supplies<br/>• Dedicated despite shortages<br/>(5 medical personnel)';
        
      // Transport Aircraft
      case UnitType.C47_SKYTRAIN:
        return '• 1x C-47 Skytrain transport<br/>• 2x Pilots<br/>• 1x Navigator<br/>• 1x Radio Operator<br/>• 1x Crew Chief<br/>• Carrying: 11 Airborne Troopers<br/>• "Gooney Bird"';
      case UnitType.R4D_SKYTRAIN:
        return '• 1x R4D Skytrain (Navy C-47)<br/>• 2x Pilots<br/>• 1x Navigator<br/>• 1x Radio Operator<br/>• 1x Loadmaster<br/>• Carrying: 9 Marine Paratroopers<br/>• Naval variant';
      case UnitType.STIRLING_TRANSPORT:
        return '• 1x Short Stirling Mk V<br/>• 2x Pilots<br/>• 1x Navigator<br/>• 1x Flight Engineer<br/>• 1x Radio Operator<br/>• Carrying: 9 Red Devils Paratroopers';
      case UnitType.LI2_TRANSPORT:
        return '• 1x Lisunov Li-2<br/>• 2x Pilots<br/>• 1x Navigator<br/>• 1x Radio Operator<br/>• Carrying: 9 VDV Guards<br/>• Soviet-built DC-3';
      case UnitType.JU52_TRANSPORT:
        return '• 1x Junkers Ju 52/3m<br/>• 2x Pilots<br/>• 1x Radio Operator<br/>• Carrying: 10 Fallschirmjäger<br/>• "Tante Ju" (Aunt Ju)';
      case UnitType.JU52_AFRIKA:
        return '• 1x Ju 52/3m (Afrika)<br/>• 2x Pilots<br/>• 1x Radio Operator<br/>• Desert camouflage<br/>• Carrying: 10 Afrika Fallschirmjäger<br/>• Sand filters installed';
      case UnitType.DAKOTA_TRANSPORT:
        return '• 1x Dakota Mk III<br/>• 2x Pilots<br/>• 1x Navigator<br/>• 1x Radio Operator<br/>• 1x Dispatcher<br/>• Carrying: 9 Canadian Paratroopers<br/>• RCAF markings';
      case UnitType.SM82_MARSUPIALE:
        return '• 1x SM.82 Marsupiale<br/>• 2x Pilots<br/>• 1x Navigator<br/>• 1x Radio Operator<br/>• 1x Flight Engineer<br/>• Carrying: 9 Folgore Paratroopers<br/>• Three-engine transport';
      case UnitType.KI57_TRANSPORT:
        return '• 1x Mitsubishi Ki-57<br/>• 2x Pilots<br/>• 1x Navigator<br/>• Carrying: 8 Teishin Paratroopers<br/>• Type 100 transport<br/>• Light transport aircraft';
      
      // Fighter Aircraft
      case UnitType.P51_MUSTANG:
        return '• 1x P-51D Mustang<br/>• 6x .50 cal M2 Browning MGs<br/>• 1x Pilot<br/>• Bubble canopy<br/>• Long-range escort fighter';
      case UnitType.BF109_FIGHTER:
        return '• 1x Bf 109G-6<br/>• 1x 30mm MK 108 cannon<br/>• 2x 13mm MG 131<br/>• 1x Pilot<br/>• "Gustav" variant';
      case UnitType.SPITFIRE_MK_V:
        return '• 1x Spitfire Mk Vb<br/>• 2x 20mm Hispano cannons<br/>• 4x .303 Browning MGs<br/>• 1x Pilot<br/>• Battle of Britain veteran';
      case UnitType.YAK_3_FIGHTER:
        return '• 1x Yak-3<br/>• 1x 20mm ShVAK cannon<br/>• 2x 12.7mm UBS MGs<br/>• 1x Pilot<br/>• Lightweight dogfighter';
      case UnitType.A6M_ZERO:
        return '• 1x Mitsubishi A6M2 Zero<br/>• 2x 20mm Type 99 cannons<br/>• 2x 7.7mm Type 97 MGs<br/>• 1x Pilot<br/>• Exceptional maneuverability';
      
      // Bomber Aircraft  
      case UnitType.B24_LIBERATOR:
        return '• 1x B-24J Liberator<br/>• 10x .50 cal M2 Browning MGs<br/>• 10 crew members<br/>• 8,000 lb bomb load<br/>• Heavy bomber';
      case UnitType.JU88_BOMBER:
        return '• 1x Junkers Ju 88A-4<br/>• 3,000 kg bomb load<br/>• 4x 7.92mm MG 81<br/>• 4 crew members<br/>• Fast medium bomber';
      case UnitType.LANCASTER_BOMBER:
        return '• 1x Avro Lancaster Mk III<br/>• 8x .303 Browning MGs<br/>• 7 crew members<br/>• 14,000 lb bomb load<br/>• "Grand Slam" capability';
      case UnitType.IL2_STURMOVIK:
        return '• 1x Il-2M3 Sturmovik<br/>• 2x 23mm VYa cannons<br/>• 2x 7.62mm ShKAS MGs<br/>• 600 kg bomb/rocket load<br/>• 2 crew members<br/>• "Flying Tank"';
      case UnitType.G4M_BETTY:
        return '• 1x Mitsubishi G4M2<br/>• 1x 20mm Type 99 cannon<br/>• 4x 7.7mm Type 92 MGs<br/>• 1,000 kg bomb/torpedo<br/>• 7 crew members<br/>• Long-range bomber';
      
      // Reconnaissance Units
      case UnitType.M8_GREYHOUND:
        return '• 1x M8 Greyhound armored car<br/>• 37mm M6 gun<br/>• .50 cal M2 MG<br/>• 4 crew members<br/>• 6x6 wheeled scout';
      case UnitType.SDKFZ_222:
        return '• 1x Sd.Kfz. 222<br/>• 20mm KwK 30 autocannon<br/>• 7.92mm MG 34<br/>• 3 crew members<br/>• Light armored car';
      case UnitType.SDKFZ_250_9:
        return '• 1x Sd.Kfz. 250/9<br/>• 20mm KwK 38 autocannon<br/>• 7.92mm MG 34<br/>• 2 crew members<br/>• Half-track scout';
      case UnitType.DAIMLER_DINGO:
        return '• 1x Daimler Dingo Mk II<br/>• .303 Bren gun<br/>• 2 crew members<br/>• 4x4 scout car<br/>• Silent running gear';
      case UnitType.BA64_ARMORED_CAR:
        return '• 1x BA-64B<br/>• 7.62mm DT machine gun<br/>• 2 crew members<br/>• 4x4 light scout<br/>• All-wheel drive';
      case UnitType.TYPE_92_JYU_SOKOSHA:
        return '• 1x Type 92 Jyu-Sokosha<br/>• 6.5mm Type 91 MG<br/>• 3 crew members<br/>• Cavalry armored car<br/>• 6-wheeled scout';
      case UnitType.RECONNAISSANCE_PATROL:
        return '• 1x Universal Carrier<br/>• 1x Patrol Leader (Thompson SMG)<br/>• 2x Scouts (Lee-Enfield No.4 Mk I)<br/>• 1x Driver (Sten Mk II)<br/>• .303 Bren gun mount<br/>• Extended vision equipment<br/>(4 men + vehicle)';
      case UnitType.ARDITI_SCOUTS:
        return '• 1x Lancia 1ZM armored car<br/>• 1x Patrol Leader (Beretta MAB 38)<br/>• 2x Arditi scouts (Carcano M91 cavalry)<br/>• 1x Driver/Gunner<br/>• Twin 8mm Breda MGs<br/>• Elite reconnaissance unit<br/>(4 men + vehicle)';
      
      case UnitType.GRIZZLY_CRUISER:
        return '• 1x Grizzly I cruiser tank<br/>• 75mm M3 L/40 gun<br/>• .30 cal coaxial MG<br/>• .50 cal AA machine gun<br/>• 5 crew members<br/>• Canadian M4A1 Sherman variant';
      case UnitType.OBICE_149_19:
        return '• 2x Obice da 149/19 howitzers<br/>• 149mm heavy artillery<br/>• HE/Smoke shells<br/>• 8 men per gun (16 total)<br/>• Fiat-SPA TM40 tractors<br/>• Long-range bombardment';
        
      // Ground Transport Vehicles - US
      case UnitType.GMC_CCKW:
        return '• 1x GMC CCKW 2.5-ton truck<br/>• 1x Driver<br/>• Capacity: 2 squads<br/>• 6x6 drive<br/>• "Deuce and a Half"';
      case UnitType.M3_HALFTRACK:
        return '• 1x M3 Halftrack<br/>• 1x .50 cal M2 MG<br/>• 2 crew members<br/>• Capacity: 1 squad<br/>• Armored personnel carrier';
      case UnitType.DUKW_AMPHIBIOUS:
        return '• 1x DUKW amphibious truck<br/>• 1x Driver<br/>• Capacity: 2 squads<br/>• 6x6 amphibious<br/>• "Duck" boat-truck';
      case UnitType.LVT_4_AMTRAC:
        return '• 1x LVT(A)-4 Amtrac<br/>• 1x .50 cal M2 turret<br/>• 3 crew members<br/>• Capacity: 1 squad<br/>• Amphibious assault vehicle';
        
      // Ground Transport Vehicles - German
      case UnitType.OPEL_BLITZ:
        return '• 1x Opel Blitz 3-ton truck<br/>• 1x Driver<br/>• Capacity: 2 squads<br/>• 4x2 drive<br/>• Wehrmacht workhorse';
      case UnitType.SDKFZ_251:
        return '• 1x Sd.Kfz. 251 Ausf. D<br/>• 1x MG42<br/>• 2 crew members<br/>• Capacity: 1 squad<br/>• "Hanomag" halftrack';
      case UnitType.STEYR_1500A:
        return '• 1x Steyr 1500A truck<br/>• 1x Driver<br/>• Capacity: 2 squads<br/>• Desert modifications<br/>• Austrian-built';
      case UnitType.SDKFZ_251_AFRIKA:
        return '• 1x Sd.Kfz. 251 Afrika<br/>• 1x MG34<br/>• 2 crew members<br/>• Capacity: 1 squad<br/>• Desert filters and cooling';
        
      // Ground Transport Vehicles - Soviet
      case UnitType.ZIS_5:
        return '• 1x ZiS-5 3-ton truck<br/>• 1x Driver<br/>• Capacity: 2 squads<br/>• 4x2 drive<br/>• "Zakhar" - rugged and reliable';
      case UnitType.M3A1_SCOUT_CAR:
        return '• 1x M3A1 Scout Car<br/>• 1x DShK 12.7mm MG<br/>• 2 crew members<br/>• Capacity: 1 squad<br/>• Lend-lease armored car';
        
      // Ground Transport Vehicles - British
      case UnitType.BEDFORD_OYD:
        return '• 1x Bedford OYD 3-ton lorry<br/>• 1x Driver<br/>• Capacity: 2 squads<br/>• 4x4 drive<br/>• Desert-proven transport';
      case UnitType.LOYD_CARRIER:
        return '• 1x Loyd Carrier<br/>• 1x Bren gun<br/>• 2 crew members<br/>• Capacity: 1 squad<br/>• Tracked APC';
        
      // Ground Transport Vehicles - Japanese
      case UnitType.ISUZU_TYPE_94:
        return '• 1x Isuzu Type 94 truck<br/>• 1x Driver<br/>• Capacity: 2 squads<br/>• 6x4 drive<br/>• Jungle-adapted';
      case UnitType.TYPE_1_HO_HA:
        return '• 1x Type 1 Ho-Ha halftrack<br/>• 1x Type 96 MG<br/>• 2 crew members<br/>• Capacity: 1 squad<br/>• Rare late-war vehicle';
        
      // Ground Transport Vehicles - Canadian
      case UnitType.CMP_TRUCK:
        return '• 1x CMP 3-ton truck<br/>• 1x Driver<br/>• Capacity: 2 squads<br/>• 4x4 drive<br/>• Canadian Military Pattern';
      case UnitType.PRIEST_KANGAROO:
        return '• 1x Priest Kangaroo APC<br/>• 1x .50 cal MG<br/>• 3 crew members<br/>• Capacity: 1 squad<br/>• Converted SPG';
        
      // Ground Transport Vehicles - Italian
      case UnitType.FIAT_626:
        return '• 1x Fiat 626 truck<br/>• 1x Driver<br/>• Capacity: 2 squads<br/>• 4x2 drive<br/>• Colonial service veteran';
      case UnitType.FIAT_SPA_DOVUNQUE:
        return '• 1x Fiat SPA Dovunque<br/>• 1x Breda MG<br/>• 2 crew members<br/>• Capacity: 1 squad<br/>• All-terrain protected truck';
        
      // France 1940 Units
      case UnitType.FRENCH_INFANTRY_SQUAD:
        return '• 1x Squad Leader (MAS-38 SMG)<br/>• 6x Riflemen (MAS-36 rifles)<br/>• 1x FM 24/29 LMG Gunner<br/>• 1x Assistant Gunner<br/>• 1x Grenadier (VB launcher)<br/>(10 men)';
      case UnitType.CHASSEURS_ALPINS:
        return '• 1x Squad Leader (MAS-38 SMG)<br/>• 5x Alpine Troops (MAS-36 rifles)<br/>• 1x FM 24/29 LMG Gunner<br/>• 1x Assistant Gunner<br/>• 1x Mountain Guide<br/>• Elite mountain infantry<br/>(9 men)';
      case UnitType.FOREIGN_LEGION_SQUAD:
        return '• 1x Squad Leader (Thompson SMG)<br/>• 7x Legionnaires (Lebel/Berthier rifles)<br/>• 1x FM 24/29 LMG Gunner<br/>• 1x Assistant Gunner<br/>• Elite colonial troops<br/>(10 men)';
      case UnitType.CHAR_B1_BIS:
        return '• 1x Char B1 bis heavy tank<br/>• 75mm ABS SA 35 howitzer<br/>• 47mm SA 35 turret gun<br/>• 2x 7.5mm MAC 31 MGs<br/>• 4 crew members<br/>• Heaviest Allied tank 1940';
      case UnitType.SOMUA_S35:
        return '• 1x SOMUA S35 cavalry tank<br/>• 47mm SA 35 gun<br/>• 7.5mm MAC 31 coaxial MG<br/>• 3 crew members<br/>• Best French tank 1940';
      case UnitType.CANON_75MM:
        return '• 1x Canon de 75 modèle 1897<br/>• Famous "French 75"<br/>• Quick-firing field gun<br/>• 6 crew members<br/>• Revolutionary recoil system';
      case UnitType.MS_406_FIGHTER:
        return '• 1x Morane-Saulnier MS.406<br/>• 1x 20mm HS.404 cannon<br/>• 2x 7.5mm MAC 34 MGs<br/>• Single-seat fighter<br/>• Most numerous French fighter';
      case UnitType.POTEZ_63_BOMBER:
        return '• 1x Potez 63.11<br/>• 3x 7.5mm MAC 34 MGs<br/>• 600kg bomb load<br/>• 3 crew members<br/>• Reconnaissance bomber';
      case UnitType.COLONIAL_TROOPS:
        return '• 1x Squad Leader (MAS-38 SMG)<br/>• 7x Colonial Infantry (Lebel rifles)<br/>• 1x FM 24/29 LMG Gunner<br/>• 1x Assistant Gunner<br/>• Troops from French colonies<br/>(10 men)';
      case UnitType.FRENCH_ENGINEERS:
        return '• 1x Squad Leader (MAS-38 SMG)<br/>• 5x Engineers (MAS-36 rifles)<br/>• 1x Flamethrower Operator<br/>• 1x Demolitions Expert<br/>• 1x Mine Layer<br/>• Génie combat troops<br/>(9 men)';
      case UnitType.FRENCH_MEDICAL_CORPS:
        return '• 1x Medical Officer (Ruby pistol)<br/>• 3x Medics (unarmed)<br/>• 2x Stretcher Bearers<br/>• Medical supplies<br/>• Service de Santé<br/>(6 medical personnel)';
      case UnitType.HOTCHKISS_H39:
        return '• 1x Hotchkiss H39 light tank<br/>• 37mm SA38 gun<br/>• 7.5mm MAC 31 coaxial MG<br/>• 2 crew members<br/>• Improved cavalry tank';
      case UnitType.CANON_47MM_AT:
        return '• 1x Canon de 47mm modèle 1937<br/>• 47mm anti-tank gun<br/>• AP/HE shells<br/>• 6 crew members<br/>• Effective tank killer';
      case UnitType.FRENCH_RECON:
        return '• 1x Panhard 178 armored car<br/>• 25mm SA35 cannon<br/>• 7.5mm MAC 31 MG<br/>• 4 crew members<br/>• AMD reconnaissance vehicle';
      case UnitType.RENAULT_AHS:
        return '• 1x Renault AHS truck<br/>• 1x Driver<br/>• Capacity: 2 squads<br/>• 4x2 drive<br/>• Standard army transport';
      case UnitType.LORRAINE_38L:
        return '• 1x Lorraine 38L carrier<br/>• Tracked supply vehicle<br/>• 2 crew members<br/>• Capacity: 1 squad<br/>• Armored personnel carrier';
        
      // Wehrmacht 1940 Units
      case UnitType.WEHRMACHT_INFANTRY_1940:
        return '• 1x Squad Leader (MP38)<br/>• 1x MG34 Gunner<br/>• 1x Assistant Gunner<br/>• 7x Riflemen (Kar 98k)<br/>• Early war squad<br/>(10 men)';
      case UnitType.STOSSTRUPPEN_1940:
        return '• 1x Squad Leader (MP38)<br/>• 3x Assault Troops (MP38)<br/>• 1x MG34 Gunner<br/>• 1x Assistant Gunner<br/>• 4x Riflemen (Kar 98k)<br/>• Storm troopers<br/>(10 men)';
      case UnitType.GEBIRGSJAGER_1940:
        return '• 1x Squad Leader (MP38)<br/>• 1x MG34 Gunner<br/>• 1x Assistant Gunner<br/>• 6x Mountain Troops (Kar 98k)<br/>• 1x Mountain Guide<br/>• Alpine specialists<br/>(10 men)';
      case UnitType.MOTORIZED_INFANTRY_1940:
        return '• 1x Squad Leader (MP38)<br/>• 1x MG34 Gunner<br/>• 1x Assistant Gunner<br/>• 6x Riflemen (Kar 98k)<br/>• 1x Opel Blitz truck<br/>• Mobile infantry<br/>(9 men + vehicle)';
      case UnitType.PIONIERE_1940:
        return '• 1x Squad Leader (P08 Luger)<br/>• 5x Pioneers (Kar 98k)<br/>• 1x Flamethrower Operator<br/>• 1x Demolitions Expert<br/>• 1x Mine Layer<br/>• Combat engineers<br/>(9 men)';
      case UnitType.SANITATER_1940:
        return '• 1x Medical NCO (Walther P38)<br/>• 3x Sanitäter (unarmed)<br/>• 2x Stretcher Bearers<br/>• Medical supplies<br/>• Red Cross protection<br/>(6 medical personnel)';
      case UnitType.PANZER_III_E:
        return '• 1x Panzer III Ausf. E<br/>• 37mm KwK 36 gun<br/>• 2x 7.92mm MG34<br/>• 5 crew members<br/>• Main battle tank 1940';
      case UnitType.PANZER_II_C:
        return '• 1x Panzer II Ausf. C<br/>• 20mm KwK 30 autocannon<br/>• 7.92mm MG34 coaxial<br/>• 3 crew members<br/>• Light reconnaissance tank';
      case UnitType.PANZER_38T:
        return '• 1x Panzer 38(t)<br/>• 37mm Škoda A7 gun<br/>• 2x 7.92mm MG37(t)<br/>• 4 crew members<br/>• Czech-built tank';
      case UnitType.STUG_III_A:
        return '• 1x StuG III Ausf. A<br/>• 75mm StuK 37 L/24<br/>• Low profile design<br/>• 4 crew members<br/>• Infantry support gun';
      case UnitType.PAK_36_BATTERY:
        return '• 2x 3.7cm PaK 36<br/>• "Door knocker" AT guns<br/>• AP/APCR shells<br/>• 5 men per gun (10 total)<br/>• Horse-drawn transport';
      case UnitType.AUFKLARUNG_1940:
        return '• 1x SdKfz 222 armored car<br/>• 20mm KwK 30 autocannon<br/>• 7.92mm MG34<br/>• 3 crew members<br/>• Reconnaissance vehicle';
      case UnitType.BF_109E_FIGHTER:
        return '• 1x Messerschmitt Bf 109E<br/>• 2x 20mm MG FF cannons<br/>• 2x 7.92mm MG17<br/>• Single-seat fighter<br/>• "Emil" - Battle of Britain fighter';
      case UnitType.JU_87B_STUKA:
        return '• 1x Junkers Ju 87B Stuka<br/>• 2x 7.92mm MG17<br/>• 1x 7.92mm MG15 rear<br/>• 500kg bomb load<br/>• 2 crew members<br/>• Dive bomber with sirens';
      case UnitType.OPEL_BLITZ_1940:
        return '• 1x Opel Blitz 3-ton truck<br/>• 1x Driver<br/>• Capacity: 2 squads<br/>• 4x2 drive<br/>• Standard Wehrmacht transport';
      case UnitType.SDKFZ_251_AUSF_A:
        return '• 1x SdKfz 251/1 Ausf. A<br/>• 2x 7.92mm MG34<br/>• 2 crew + 10 passengers<br/>• Capacity: 1 squad<br/>• "Hanomag" halftrack';
        
      // Greek 1940 Units
      case UnitType.EVZONES_REGIMENT:
        return '• 1x Squad Leader (Mannlicher M1903/14)<br/>• 1x Hotchkiss M1926 LMG<br/>• 1x Assistant Gunner<br/>• 7x Elite Evzones (Mannlicher M1903/14)<br/>• Traditional elite mountain troops<br/>(10 men)';
      case UnitType.REGULAR_INFANTRY_DIVISION:
        return '• 1x Squad Leader (pistol)<br/>• 9x Infantry (Mannlicher M1903/14)<br/>• 1x Hotchkiss M1914 MG team<br/>• Well-motivated Greek conscripts<br/>(11 men)';
      case UnitType.CRETAN_DIVISION:
        return '• 1x Squad Leader (Mannlicher M1903/14)<br/>• 9x Cretan warriors (mixed rifles)<br/>• Legendary fighters<br/>• Known for knife fighting<br/>(10 men)';
      case UnitType.NATIONAL_GUARD_BATTALION:
        return '• 1x Reserve Officer (Mannlicher M1903/14)<br/>• 8x Reservists (Gras M1874/Lebel rifles)<br/>• Basic militia unit<br/>• Older WWI equipment<br/>(9 men)';
      case UnitType.ENGINEER_COMPANY:
        return '• 1x Engineer Officer (pistol)<br/>• 7x Combat Engineers<br/>• Demolition charges<br/>• Mine-laying equipment<br/>• Mountain fortification experts<br/>(8 men)';
      case UnitType.MEDICAL_SECTION:
        return '• 1x Doctor (pistol)<br/>• 4x Medics (unarmed)<br/>• 1x Field ambulance<br/>• Medical supplies<br/>• Small but dedicated<br/>(5 medical personnel)';
      case UnitType.VICKERS_6TON_TANK:
        return '• 1x Vickers 6-Ton Mark E<br/>• 47mm gun<br/>• 7.92mm Vickers coaxial MG<br/>• 3 crew members<br/>• British-supplied tank';
      case UnitType.FT_17_RENAULT:
        return '• 1x Renault FT-17<br/>• 37mm Puteaux SA 18 gun<br/>• 2 crew members<br/>• WWI veteran tank<br/>• Slow but functional';
      case UnitType.CARDEN_LOYD_TANKETTE:
        return '• 1x Carden Loyd Mk VI<br/>• 1x Hotchkiss MG<br/>• 2 crew members<br/>• British tankette<br/>• Reconnaissance vehicle';
      case UnitType.CAPTURED_L3_TANKETTE:
        return '• 1x L3/33 tankette (captured)<br/>• Twin 8mm Breda MGs<br/>• 2 crew members<br/>• Italian tankette<br/>• Captured from Albania';
      case UnitType.SCHNEIDER_75MM_MOUNTAIN_GUN:
        return '• 2x Schneider 75mm mountain guns<br/>• Pack artillery for mountains<br/>• HE/Shrapnel shells<br/>• 6 men per gun (12 total)<br/>• Mule transport';
      case UnitType.SKODA_75MM_FIELD_GUN:
        return '• 2x Škoda 75mm M1928 guns<br/>• Czech-made field artillery<br/>• HE shells<br/>• 5 men per gun (10 total)<br/>• Horse-drawn transport';
      case UnitType.PZL_P24_FIGHTER:
        return '• 1x PZL P.24 fighter<br/>• 2x 20mm Oerlikon cannons<br/>• 2x 7.92mm MGs<br/>• Single-seat fighter<br/>• Polish-made aircraft';
      case UnitType.FAIREY_BATTLE_BOMBER:
        return '• 1x Fairey Battle<br/>• 1,000 lb bomb load<br/>• 1x .303 Browning forward<br/>• 1x Vickers K rear gun<br/>• 3 crew members<br/>• British light bomber';
      case UnitType.FORD_V8_TRUCK:
        return '• 1x Ford V8 truck<br/>• 1x Driver<br/>• Capacity: 2 squads<br/>• American-made transport<br/>• Greek Army standard truck';
      case UnitType.DODGE_T110L_HALFTRACK:
        return '• 1x Dodge T-110L halftrack<br/>• 1x Hotchkiss M1914 MG<br/>• 2 crew + 10 passengers<br/>• Capacity: 1 squad<br/>• Armored transport';
        
      // Italian 1940 Units
      case UnitType.REGULAR_INFANTRY_1940:
        return '• 1x Squad Leader (Beretta M1934)<br/>• 8x Riflemen (Carcano M1891)<br/>• 1x Breda M30 LMG<br/>• Poorly trained conscripts<br/>(10 men)';
      case UnitType.ALPINI_1940:
        return '• 1x Squad Leader (Beretta M1938)<br/>• 7x Alpini (Carcano M1891/24)<br/>• 1x Breda M30 LMG<br/>• 1x Mountain guide<br/>• Elite mountain troops<br/>(10 men)';
      case UnitType.BLACKSHIRT_BATTALION:
        return '• 1x Centurion (Beretta M1938)<br/>• 8x Blackshirts (Carcano M1891)<br/>• 1x Breda M30 LMG<br/>• CCNN militia<br/>(10 men)';
      case UnitType.LIBYAN_COLONIAL_TROOPS:
        return '• 1x Italian NCO (Beretta M1934)<br/>• 9x Colonial soldiers (Carcano M1870/87)<br/>• Basic colonial unit<br/>• North African troops<br/>(10 men)';
      case UnitType.GENIO_SAPPERS_1940:
        return '• 1x Squad Leader (Beretta M1938)<br/>• 6x Sappers (Carcano rifles)<br/>• Demolition charges<br/>• Mine equipment<br/>• Combat engineers<br/>(7 men)';
      case UnitType.REGIO_MEDICI:
        return '• 1x Medical Officer (pistol)<br/>• 3x Medics (unarmed)<br/>• 2x Stretcher bearers<br/>• Royal Army medical corps<br/>(6 medical personnel)';
      case UnitType.L3_33_TANKETTE:
        return '• 1x L3/33 (CV33) tankette<br/>• Twin 8mm Breda SAFAT MGs<br/>• 2 crew members<br/>• Obsolete by 1940<br/>• Death trap';
      case UnitType.M11_39_TANK:
        return '• 1x M11/39 medium tank<br/>• 37mm Vickers-Terni gun (hull)<br/>• Twin 8mm MGs (turret)<br/>• 3 crew members<br/>• Poor design';
      case UnitType.L6_40_LIGHT_TANK:
        return '• 1x L6/40 light tank<br/>• 20mm Breda 35 autocannon<br/>• 8mm Breda 38 coaxial MG<br/>• 2 crew members<br/>• Light reconnaissance tank';
      case UnitType.CANNONE_47_32:
        return '• 1x Cannone da 47/32 Mod. 1935<br/>• 47mm anti-tank gun<br/>• AP/HE shells<br/>• 6 crew members<br/>• Best Italian AT gun';
      case UnitType.OBICE_100_17:
        return '• 2x Škoda 100mm Model 14 howitzers<br/>• Pack mountain artillery<br/>• HE shells<br/>• 6 men per gun (12 total)<br/>• Mule transport';
      case UnitType.AB_40_ARMORED_CAR:
        return '• 1x AB 40 armored car<br/>• Twin 8mm Breda 38 MGs<br/>• 4 crew members<br/>• Good mobility<br/>• Reconnaissance vehicle';
      case UnitType.CR_42_FALCO:
        return '• 1x Fiat CR.42 Falco<br/>• 2x 12.7mm Breda-SAFAT MGs<br/>• Single pilot<br/>• Last biplane fighter<br/>• Obsolete by 1940';
      case UnitType.SM_79_SPARVIERO:
        return '• 1x Savoia-Marchetti SM.79<br/>• 1,000kg bomb load<br/>• 3x 12.7mm Breda MGs<br/>• 5 crew members<br/>• "Sparviero" tri-motor bomber';
      case UnitType.FIAT_626:
        return '• 1x Fiat 626 truck<br/>• 3-ton capacity<br/>• 1x Driver<br/>• Capacity: 2 squads<br/>• Standard Italian truck';
      case UnitType.FIAT_SPA_AUTOPROTETTO:
        return '• 1x Fiat-SPA TL37 armored<br/>• 1x 8mm Breda MG<br/>• 2 crew + 8 passengers<br/>• Capacity: 1 squad<br/>• Armored truck';
        
      default:
        return 'Unknown unit composition';
    }
  }

  private buildUnit(unitType: UnitType, city: any): void {
    // Check if it's your turn in online games
    if (this.isOnlineGame && !this.isMyTurn) {
      this.soundManager.playError();
      this.showNotification('You can only build units on your turn!');
      document.getElementById('build-menu')?.remove();
      return;
    }
    
    // Check if city is upgrading
    if (city.upgradeInProgress) {
      this.soundManager.playError();
      this.showNotification('Cannot build units while city is upgrading!');
      return;
    }
    
    // Check if enemy units are in the city
    const unitsInCity = this.getUnitsInCity(city);
    const enemyUnits = unitsInCity.filter(u => u.faction !== this.currentFaction);
    
    if (enemyUnits.length > 0) {
      this.soundManager.playError();
      this.showNotification('Cannot build units while enemy forces occupy the city!');
      return;
    }
    
    const cost = UNIT_DEFINITIONS[unitType].cost;
    const trainingTime = UNIT_DEFINITIONS[unitType].trainingTime;
    const resources = this.gameState.resources.get(this.currentFaction) || 0;
    
    // Count actual spawned units (not placeholders) and units in training queue
    // Filter out any placeholder queueIds from unitsProduced
    const actualUnits = city.unitsProduced.filter((id: string) => !id.startsWith('queue_'));
    const unitsInTraining = city.trainingQueue ? city.trainingQueue.length : 0;
    const totalUnitsAndTraining = actualUnits.length + unitsInTraining;
    const slotsAvailable = city.maxUnits - totalUnitsAndTraining;
    
    if (resources >= cost && slotsAvailable > 0) {
      // Find empty tile in city for unit to spawn
      let spawnTile = null;
      for (const tile of city.tiles) {
        if (!this.unitManager.getUnitAt(tile)) {
          spawnTile = tile;
          break;
        }
      }
      
      if (spawnTile) {
        // Play build unit sound for successful unit creation
        this.soundManager.playBuildUnit();
        
        // Initialize training queue if it doesn't exist
        if (!city.trainingQueue) {
          city.trainingQueue = [];
        }
        
        // Add unit to training queue and reserve a slot
        const queueId = `queue_${Date.now()}_${Math.random()}`;
        const trainingUnit = {
          type: unitType,
          turnsRemaining: trainingTime,
          tile: spawnTile,
          queueId: queueId
        };
        city.trainingQueue.push(trainingUnit);
        
        debug.log(`Added ${UNIT_DEFINITIONS[unitType].name} to training queue in ${city.name}. Queue now has ${city.trainingQueue.length} units`);
        
        // Don't add placeholder to unitsProduced - only add actual spawned units
        
        // Deduct resources
        this.gameState.resources.set(this.currentFaction, resources - cost);
        this.updateResourceDisplay();
        this.updateCityCount();
        
        // Track unit production in analytics
        this.analytics.trackUnitProduction({
          unitType: unitType,
          faction: this.currentFaction,
          cityLevel: city.level || 1,
          cost: cost
        });
        
        // Update tile info immediately if this city is selected
        if (this.gameState.selectedTile) {
          const selectedTile = this.map.getTile(this.gameState.selectedTile.x, this.gameState.selectedTile.y);
          if (selectedTile && selectedTile.city === city) {
            this.updateTileInfo(selectedTile);
          }
        }
        
        // Save game state after building unit
        this.saveGameState();
        
        // Sync with Firebase in online games
        if (this.isOnlineGame && this.multiplayerService) {
          const completeGameState = this.buildCompleteGameState();
          this.multiplayerService.updateGameState(
            completeGameState,
            this.gameState.currentFaction,
            this.gameState.turn
          );
        }
      } else {
        this.soundManager.playError();
        this.showNotification('No space in city to spawn unit!');
      }
    } else {
      // Not enough resources or no slots available
      this.soundManager.playError();
      if (resources < cost) {
        this.showNotification(`Not enough resources! Need ${cost}, have ${resources}`);
      } else {
        const actualSpawnedUnits = city.unitsProduced.filter((id: string) => !id.startsWith('queue_')).length;
        const inTraining = city.trainingQueue ? city.trainingQueue.length : 0;
        this.showNotification(`City unit capacity reached! (${actualSpawnedUnits} units + ${inTraining} training = ${city.maxUnits} max)`);
      }
    }
    
    // Remove build menu
    document.getElementById('build-menu')?.remove();
  }

  private upgradeCity(city: any, toLevel: CityLevel): void {
    // Check if it's your turn in online games
    if (this.isOnlineGame && !this.isMyTurn) {
      this.soundManager.playError();
      this.showNotification('You can only upgrade cities on your turn!');
      return;
    }
    
    // Check if city is being captured
    if (city.captureProgress && city.captureProgress.faction !== this.currentFaction) {
      this.soundManager.playError();
      this.showNotification('Cannot upgrade city while it is being captured!');
      return;
    }
    
    const cost = getCityUpgradeCost(toLevel);
    const upgradeTime = getCityUpgradeTime(toLevel);
    const resources = this.gameState.resources.get(this.currentFaction) || 0;
    
    if (resources >= cost) {
      // Deduct resources and track spending
      this.gameState.resources.set(this.currentFaction, resources - cost);
      
      // Track resource spending in battle statistics
      const factionStats = this.gameState.battleStats?.get(this.currentFaction);
      if (factionStats) {
        factionStats.resourcesSpent += cost;
      }
      
      // Start upgrade
      city.upgradeInProgress = {
        toLevel: toLevel,
        turnsRemaining: upgradeTime
      };
      
      // Only show notification and play sound if it's the player's city
      // In PvE, only show for human player. In PvP/Online, show for current faction
      const shouldShowNotification = 
        this.gameState.gameMode === 'PvE' 
          ? city.faction === this.humanPlayerFaction
          : city.faction === this.currentFaction;
      
      if (shouldShowNotification) {
        // Play build sound
        this.soundManager.playBuildUnit();
        
        // Show notification
        const levelName = getCityLevelName(toLevel);
        this.showNotification(`${city.name} is upgrading to ${levelName}! (${upgradeTime} turns)`);
      }
      
      // Update displays
      this.updateResourceDisplay();
      this.updateCityCount();
      
      // Close build menu
      document.getElementById('build-menu')?.remove();
      
      // Update tile info if this city is selected
      const selectedTile = this.gameState.selectedTile;
      if (selectedTile) {
        const tile = this.map.getTile(selectedTile.x, selectedTile.y);
        if (tile && tile.city && tile.city.name === city.name) {
          this.updateTileInfo(tile);
        }
      }
      
      // Sync if online game
      if (this.isOnlineGame && this.multiplayerService) {
        const completeGameState = this.buildCompleteGameState();
        this.multiplayerService.updateGameState(
          completeGameState,
          this.gameState.currentFaction,
          this.gameState.turn
        );
      }
    } else {
      this.soundManager.playError();
      this.showNotification(`Not enough resources! Need ${cost}, have ${resources}`);
    }
  }

  private updateResourceDisplay(): void {
    const resourcesEl = document.getElementById('resources-value');
    const incomeEl = document.getElementById('income-value');
    
    if (resourcesEl) {
      // In PvE mode or online games, always show the human player's resources
      const displayFaction = (this.isOnlineGame || (this.gameState.gameMode === 'PvE' && this.humanPlayerFaction))
        ? (this.humanPlayerFaction || this.currentFaction)
        : this.currentFaction;
      resourcesEl.textContent = String(this.gameState.resources.get(displayFaction) || 0);
    }
    
    // Calculate and display income
    if (incomeEl) {
      const displayFaction = (this.isOnlineGame || (this.gameState.gameMode === 'PvE' && this.humanPlayerFaction))
        ? (this.humanPlayerFaction || this.currentFaction)
        : this.currentFaction;
      const income = this.calculateIncome(displayFaction);
      incomeEl.textContent = `+${income}/turn`;
    }
  }
  
  private calculateIncome(faction: FactionType): number {
    let income = 0;
    const cities = this.map.getCities();
    cities.forEach(city => {
      if (city.faction === faction) {
        const cityLevel = city.level || CityLevel.OUTPOST;
        income += getCityResourceGeneration(cityLevel);
      }
    });
    return income;
  }

  private updateTurnCounter(): void {
    const turnCounter = document.getElementById('turn-counter');
    if (turnCounter) {
      turnCounter.textContent = `Turn ${this.gameState.turn}`;
    }
  }
  
  private updateCityCount(): void {
    const citiesEl = document.getElementById('cities-controlled');
    if (citiesEl) {
      const cities = this.map.getCities();
      let controlled = 0;
      // In PvE mode or online games, always show human player's stats
      const displayFaction = (this.isOnlineGame || (this.gameState.gameMode === 'PvE' && this.humanPlayerFaction))
        ? (this.humanPlayerFaction || this.currentFaction)
        : this.currentFaction;
      cities.forEach(city => {
        if (city.faction === displayFaction) controlled++;
      });
      citiesEl.textContent = `Cities: ${controlled}/${cities.size}`;
    }
    
    const unitsEl = document.getElementById('units-count');
    if (unitsEl) {
      // In PvE mode or online games, always show human player's stats
      const displayFaction = (this.isOnlineGame || (this.gameState.gameMode === 'PvE' && this.humanPlayerFaction))
        ? (this.humanPlayerFaction || this.currentFaction)
        : this.currentFaction;
      const units = this.unitManager.getUnitsForFaction(displayFaction);
      unitsEl.textContent = `Units: ${units.length}`;
    }
  }
  
  private checkVictoryConditions(): void {
    const cities = this.map.getCities();
    const factionCities = new Map<FactionType, number>();
    
    // Count cities for each faction based on campaign
    if (this.selectedCampaign === CampaignType.NORMANDY) {
      factionCities.set('United States Of America', 0);
      factionCities.set('3rd Reich', 0);
    } else if (this.selectedCampaign === CampaignType.TUNISIA) {
      factionCities.set('United Kingdom', 0);
      factionCities.set('3rd Reich', 0);
    } else if (this.selectedCampaign === CampaignType.STALINGRAD) {
      factionCities.set('USSR', 0);
      factionCities.set('3rd Reich', 0);
    } else if (this.selectedCampaign === CampaignType.OKINAWA) {
      factionCities.set('United States Of America', 0);
      factionCities.set('Imperial Japan', 0);
    } else if (this.selectedCampaign === CampaignType.CAMPOBASSO) {
      factionCities.set('Canada', 0);
      factionCities.set('Fascist Italy', 0);
    } else if (this.selectedCampaign === CampaignType.FRANCE_1940) {
      factionCities.set('France', 0);
      factionCities.set('3rd Reich', 0);
    } else if (this.selectedCampaign === CampaignType.ALBANIA_1940) {
      factionCities.set('Greece', 0);
      factionCities.set('Fascist Italy', 0);
    }
    
    // Count cities for relevant factions
    for (const [, city] of cities) {
      if (city.faction && factionCities.has(city.faction)) {
        factionCities.set(city.faction, factionCities.get(city.faction)! + 1);
      }
    }
    
    // Determine winner based on campaign
    let winner: FactionType | null = null;
    
    if (this.selectedCampaign === CampaignType.NORMANDY) {
      const usaCities = factionCities.get('United States Of America')!;
      const germanCities = factionCities.get('3rd Reich')!;
      
      if (usaCities === 0 || germanCities === 0) {
        winner = usaCities > 0 ? 'United States Of America' : '3rd Reich';
      }
    } else if (this.selectedCampaign === CampaignType.TUNISIA) {
      const ukCities = factionCities.get('United Kingdom')!;
      const germanCities = factionCities.get('3rd Reich')!;
      
      if (ukCities === 0 || germanCities === 0) {
        winner = ukCities > 0 ? 'United Kingdom' : '3rd Reich';
      }
    } else if (this.selectedCampaign === CampaignType.STALINGRAD) {
      const ussrCities = factionCities.get('USSR')!;
      const germanCities = factionCities.get('3rd Reich')!;
      
      if (ussrCities === 0 || germanCities === 0) {
        winner = ussrCities > 0 ? 'USSR' : '3rd Reich';
      }
    } else if (this.selectedCampaign === CampaignType.OKINAWA) {
      const usaCities = factionCities.get('United States Of America')!;
      const japanCities = factionCities.get('Imperial Japan')!;
      
      if (usaCities === 0 || japanCities === 0) {
        winner = usaCities > 0 ? 'United States Of America' : 'Imperial Japan';
      }
    } else if (this.selectedCampaign === CampaignType.CAMPOBASSO) {
      const canadaCities = factionCities.get('Canada')!;
      const italyCities = factionCities.get('Fascist Italy')!;
      
      if (canadaCities === 0 || italyCities === 0) {
        winner = canadaCities > 0 ? 'Canada' : 'Fascist Italy';
      }
    } else if (this.selectedCampaign === CampaignType.FRANCE_1940) {
      const franceCities = factionCities.get('France')!;
      const germanCities = factionCities.get('3rd Reich')!;
      
      if (franceCities === 0 || germanCities === 0) {
        winner = franceCities > 0 ? 'France' : '3rd Reich';
      }
    } else if (this.selectedCampaign === CampaignType.ALBANIA_1940) {
      const greeceCities = factionCities.get('Greece')!;
      const italyCities = factionCities.get('Fascist Italy')!;
      
      if (greeceCities === 0 || italyCities === 0) {
        winner = greeceCities > 0 ? 'Greece' : 'Fascist Italy';
      }
    }
    
    // If there's a winner, handle game end
    if (winner) {
      if (this.isOnlineGame && this.multiplayerService) {
        // For online games, notify Firebase first
        this.multiplayerService.endGame(winner).then(() => {
          debug.log(`Online game ended. Winner: ${winner}`);
          // The handleOnlineGameEnd will be called via the room update listener
        }).catch(error => {
          debug.error('Failed to end online game:', error);
          // Show game over screen anyway
          this.showGameOverScreen(winner);
        });
      } else {
        // For offline games, show game over screen directly
        this.showGameOverScreen(winner);
      }
    }
  }
  
  private collectMinesFromMap(): Array<{x: number, y: number, mine: {owner: FactionType, id: string}}> {
    const mines: Array<{x: number, y: number, mine: {owner: FactionType, id: string}}> = [];
    const tiles = this.map.getTiles();
    for (let y = 0; y < tiles.length; y++) {
      for (let x = 0; x < tiles[y].length; x++) {
        const tile = tiles[y][x];
        if (tile.mine) {
          mines.push({
            x: tile.x,
            y: tile.y,
            mine: tile.mine
          });
        }
      }
    }
    return mines;
  }

  private restoreMinesOnMap(mines: Array<{x: number, y: number, mine: {owner: FactionType, id: string}}>): void {
    if (!mines || !Array.isArray(mines)) return;
    
    for (const mineData of mines) {
      const tile = this.map.getTile(mineData.x, mineData.y);
      if (tile) {
        tile.mine = mineData.mine;
      }
    }
  }

  private buildCompleteGameState(): any {
    const timestamp = Date.now();
    this.lastGameStateTimestamp = timestamp;
    
    // Get the latest city data from the cities Map
    const cities = this.map.getCities();
    
    // Build the complete map state including all tiles and cities
    const mapTiles: any[][] = [];
    for (let y = 0; y < this.map.getHeight(); y++) {
      const row: any[] = [];
      for (let x = 0; x < this.map.getWidth(); x++) {
        const tile = this.map.getTile(x, y);
        if (tile) {
          // If this tile has a city, get the latest city data from the cities Map
          let cityData = undefined;
          if (tile.city) {
            const latestCity = cities.get(tile.city.name);
            if (latestCity) {
              cityData = {
                name: latestCity.name,
                faction: latestCity.faction,
                captureProgress: latestCity.captureProgress,
                trainingQueue: latestCity.trainingQueue,
                unitsProduced: latestCity.unitsProduced,
                maxUnits: latestCity.maxUnits,
                tiles: latestCity.tiles,
                level: latestCity.level || CityLevel.OUTPOST,
                upgradeInProgress: latestCity.upgradeInProgress
              };
            }
          }
          
          row.push({
            x: tile.x,
            y: tile.y,
            terrain: tile.terrain,
            city: cityData,
            mine: tile.mine
          });
        }
      }
      mapTiles.push(row);
    }
    
    // CRITICAL: Always sync the actual units from UnitManager, not the potentially stale gameState.units
    // Include dead units WITH death animations so other clients can play the animation
    // Exclude dead units WITHOUT death animations (they should have been cleaned up already)
    const allUnits = this.unitManager.getAllUnits();
    const unitsMap = new Map();
    allUnits.forEach(unit => {
      // Include alive units
      if (unit.stats.health > 0) {
        unitsMap.set(unit.id, unit);
      } 
      // Also include dead units that have a death animation in progress
      else if (unit.stats.health <= 0 && unit.deathAnimation) {
        const elapsed = Date.now() - unit.deathAnimation.startTime;
        if (elapsed < unit.deathAnimation.duration + 100) { // Add 100ms buffer
          // Death animation still playing, include the unit
          unitsMap.set(unit.id, unit);
          console.log(`[BUILD STATE] Including dead unit ${unit.name} with death animation (elapsed: ${elapsed}ms)`);
        } else {
          console.log(`[BUILD STATE] Excluding dead unit ${unit.name} - animation complete`);
        }
      } else {
        console.log(`[BUILD STATE] Excluding dead unit ${unit.name} (health: ${unit.stats.health}, no animation)`);
      }
    });
    
    // Debug logging for movement values being sent
    debug.log(`[BUILD STATE] Timestamp: ${timestamp}, Units: ${unitsMap.size} from UnitManager`);
    const sampleUnits = Array.from(unitsMap.values()).slice(0, 3);
    sampleUnits.forEach(unit => {
      debug.log(`  ${unit.name}: movement ${unit.movementRemaining}/${unit.stats.movement}`);
    });
    
    // Check if our newly created unit is in the state
    const newUnitIds = Array.from(unitsMap.keys()).filter(id => id.includes('unit_'));
    debug.log(`[BUILD STATE] Unit IDs in state:`, newUnitIds);
    
    return {
      ...this.gameState,
      units: unitsMap,  // Override with actual units from UnitManager
      map: mapTiles,
      stateTimestamp: timestamp  // Add timestamp to track this state version
    };
  }

  private saveGameState(): void {
    // Don't save if game is over
    if (this.isGameOver) {
      debug.log('Game is over, not saving state');
      return;
    }
    
    try {
      // Get all units from unit manager
      const allUnits = this.unitManager.getAllUnits();
      // Create a deep copy of units to ensure all properties are preserved
      const unitsArray = allUnits.map(unit => [unit.id, {
        ...unit,
        // Explicitly preserve dynamic properties that might be lost
        hometown: unit.hometown,
        isHunkered: (unit as any).isHunkered,
        hunkerChangedThisTurn: (unit as any).hunkerChangedThisTurn,
        carryingUnit: unit.carryingUnit,
        carryingUnits: unit.carryingUnits,
        carryingUnitData: unit.carryingUnitData,
        carryingUnitsData: unit.carryingUnitsData
      }]);
      
      // Create a serializable version of the game state
      const serializableState = {
        ...this.gameState,
        // Convert Map objects to arrays for serialization
        units: unitsArray,
        resources: Array.from(this.gameState.resources.entries()),
        pendingNotifications: this.gameState.pendingNotifications ? 
          Array.from(this.gameState.pendingNotifications.entries()) : undefined,
        battleStats: this.gameState.battleStats ? 
          Array.from(this.gameState.battleStats.entries()) : undefined,
        // Explicitly include game mode fields
        gameMode: this.gameState.gameMode,
        aiControlledFaction: this.gameState.aiControlledFaction,
        // Add additional game context
        selectedCampaign: this.selectedCampaign,
        selectedGameMode: this.selectedGameMode,
        humanPlayerFaction: this.humanPlayerFaction,
        secondFaction: this.secondFaction,
        // Save city states
        cities: Array.from(this.map.getCities().entries()).map(([name, city]) => ({
          name,
          faction: city.faction,
          captureProgress: city.captureProgress,
          trainingQueue: city.trainingQueue,
          unitsProduced: city.unitsProduced,
          maxUnits: city.maxUnits,
          level: city.level || CityLevel.OUTPOST,
          upgradeInProgress: city.upgradeInProgress
        })),
        // Save last turn cities for capture detection
        lastTurnCities: Array.from(this.lastTurnCities.entries()),
        // Save AI turn state
        isAITurn: this.isAITurn,
        // Save AI controller state if it exists
        aiState: this.aiController ? this.aiController.getAIState() : null,
        // Save mines on the map
        mines: this.collectMinesFromMap()
      };
      
      // Add metadata for the save slot
      const enhancedState = {
        ...serializableState,
        saveDate: new Date().toISOString(),
        turn: this.gameState.turn,
        isOnlineGame: this.isOnlineGame,
        roomCode: this.isOnlineGame ? this.multiplayerService?.getCurrentRoom()?.id : undefined,
        currentFaction: this.currentFaction,
        playerFaction: this.isOnlineGame ? 
          (this.multiplayerService?.isHost() ? 
            this.multiplayerService?.getCurrentRoom()?.host.faction : 
            this.multiplayerService?.getCurrentRoom()?.guest?.faction) : 
          this.currentFaction
      };
      
      // Save to different slot for multiplayer games
      if (this.isOnlineGame) {
        localStorage.setItem('frontlines_multiplayer_save', JSON.stringify(enhancedState));
      } else {
        localStorage.setItem(`frontlines_gamestate_slot${this.currentSaveSlot}`, JSON.stringify(enhancedState));
      }
      // Commented out to reduce console spam
      // debug.log(`Game state saved to localStorage slot ${this.currentSaveSlot}`);
      // debug.log(`Saved ${allUnits.length} units to localStorage`);
      // debug.log(`Game mode: ${serializableState.gameMode}, AI faction: ${serializableState.aiControlledFaction}`);
    } catch (error) {
      debug.error('Failed to save game state:', error);
    }
  }
  
  private loadGameState(slotNumber?: number): boolean {
    try {
      const slot = slotNumber || this.currentSaveSlot;
      const savedState = localStorage.getItem(`frontlines_gamestate_slot${slot}`);
      if (!savedState) return false;
      
      // Set the current save slot
      this.currentSaveSlot = slot;
      
      const parsedState = JSON.parse(savedState);
      
      // Restore Maps from arrays (but don't restore units here, we'll do it via unitManager)
      this.gameState = {
        ...parsedState,
        units: new Map(), // Keep empty, units are managed by unitManager
        resources: new Map(parsedState.resources),
        pendingNotifications: parsedState.pendingNotifications ? 
          new Map(parsedState.pendingNotifications) : undefined,
        battleStats: parsedState.battleStats ? 
          new Map(parsedState.battleStats) : undefined,
        // Ensure game mode fields are preserved
        gameMode: parsedState.gameMode,
        aiControlledFaction: parsedState.aiControlledFaction
      };
      
      // Restore game context
      this.selectedCampaign = parsedState.selectedCampaign;
      this.selectedGameMode = parsedState.selectedGameMode;
      this.humanPlayerFaction = parsedState.humanPlayerFaction;
      this.secondFaction = parsedState.secondFaction;
      this.currentFaction = this.gameState.currentFaction;
      
      // Only restore city states if map is initialized
      if (this.map) {
        // Restore city states
        const cities = this.map.getCities();
        parsedState.cities.forEach((cityData: any) => {
          const city = cities.get(cityData.name);
          if (city) {
            city.faction = cityData.faction;
            city.captureProgress = cityData.captureProgress;
            city.trainingQueue = cityData.trainingQueue || [];
            // Clean up any placeholder queue IDs from old saves
            city.unitsProduced = (cityData.unitsProduced || []).filter((id: string) => !id.startsWith('queue_'));
            city.level = cityData.level || CityLevel.OUTPOST;
            city.maxUnits = getCityMaxUnits(city.level);
            city.upgradeInProgress = cityData.upgradeInProgress;
          }
        });
      }
      
      // Restore last turn cities
      this.lastTurnCities = new Map(parsedState.lastTurnCities);
      
      // Restore AI turn state
      if (parsedState.isAITurn !== undefined) {
        this.isAITurn = parsedState.isAITurn;
      }
      
      // Store AI state to be restored after AI controller initialization
      if (parsedState.aiState) {
        (this as any).pendingAIState = parsedState.aiState;
      }
      
      // Only restore units if unit manager is initialized
      if (this.unitManager) {
        // Clear existing units
        this.unitManager.clearAllUnits();
        
        // Restore units from saved state
        if (parsedState.units && Array.isArray(parsedState.units)) {
          debug.log(`Loading ${parsedState.units.length} units from saved state`);
          for (const [, unitData] of parsedState.units) {
            this.unitManager.restoreUnit(unitData);
          }
        } else {
          debug.log('No units found in saved state');
        }
      }
      
      // Restore mines on the map
      if (this.map && parsedState.mines) {
        this.restoreMinesOnMap(parsedState.mines);
        debug.log(`Restored ${parsedState.mines.length} mines on the map`);
      }
      
      debug.log('Game state loaded from localStorage');
      debug.log(`Loaded game mode: ${this.gameState.gameMode}, AI faction: ${this.gameState.aiControlledFaction}`);
      
      // Update turn counter display
      const turnCounter = document.getElementById('turn-counter');
      if (turnCounter) {
        turnCounter.textContent = `Turn ${this.gameState.turn}`;
      }
      
      // Initialize battle stats if not present (for old saves)
      if (!this.gameState.battleStats) {
        this.initializeBattleStats();
      }
      
      return true;
    } catch (error) {
      debug.error('Failed to load game state:', error);
      return false;
    }
  }
  
  private showSaveSlotSelectionForNewGame(startingFaction: FactionType, menuOverlay: HTMLElement): void {
    // Create save slot selection overlay
    const slotOverlay = document.createElement('div');
    slotOverlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.8);
      z-index: 31000;
      display: flex;
      align-items: center;
      justify-content: center;
    `;
    
    const slotContainer = document.createElement('div');
    slotContainer.style.cssText = `
      background: #2a2a2a;
      border: 2px solid #444;
      border-radius: 10px;
      padding: 30px;
      max-width: 600px;
      width: 90%;
    `;
    
    const title = document.createElement('h2');
    title.textContent = 'Select Save Slot for New Game';
    title.style.cssText = `
      color: white;
      margin-bottom: 20px;
      text-align: center;
    `;
    slotContainer.appendChild(title);
    
    const warning = document.createElement('p');
    warning.textContent = 'Warning: Selecting a slot with existing save data will overwrite it!';
    warning.style.cssText = `
      color: #ff6666;
      text-align: center;
      margin-bottom: 20px;
      font-style: italic;
    `;
    slotContainer.appendChild(warning);
    
    const slotsGrid = document.createElement('div');
    slotsGrid.style.cssText = `
      display: flex;
      flex-direction: column;
      gap: 10px;
    `;
    
    // Create 5 save slots
    for (let slot = 1; slot <= 5; slot++) {
      const slotButton = document.createElement('button');
      slotButton.style.cssText = `
        padding: 15px;
        background: #3a3a3a;
        color: white;
        border: 2px solid #555;
        border-radius: 5px;
        cursor: pointer;
        transition: all 0.3s ease;
        text-align: left;
        display: flex;
        justify-content: space-between;
        align-items: center;
      `;
      
      const savedState = localStorage.getItem(`frontlines_gamestate_slot${slot}`);
      
      if (savedState) {
        try {
          const parsedState = JSON.parse(savedState);
          const campaignMap = {
            'normandy': 'Normandy',
            'tunisia': 'Tunisia',
            'stalingrad': 'Stalingrad',
            'okinawa': 'Okinawa',
            'campobasso': 'Campobasso',
            'france_1940': 'Battle of France'
          };
          const campaignName = campaignMap[parsedState.selectedCampaign as keyof typeof campaignMap] || 'Unknown';
          
          const gameMode = parsedState.gameMode || 'PvP';
          const isPvE = gameMode === 'PvE';
          const displayFaction = isPvE ? parsedState.humanPlayerFaction : parsedState.currentFaction;
          const faction = displayFaction || parsedState.gameState?.currentFaction || 'Unknown';
          
          // Count units and cities
          const unitCount = parsedState.units ? parsedState.units.filter((u: any) => u[1].faction === displayFaction).length : 0;
          const cityData = parsedState.cities || [];
          const playerCities = cityData.filter((c: any) => c.faction === displayFaction).length;
          const totalCities = cityData.length;
          const resources = parsedState.resources ? Object.fromEntries(parsedState.resources) : {};
          const playerResources = resources[displayFaction] || 0;
          
          slotButton.innerHTML = `
            <div style="flex: 1;">
              <div style="font-weight: bold; margin-bottom: 5px;">Slot ${slot}: ${campaignName}</div>
              <div style="font-size: 14px; color: #ccc;">
                ${gameMode} • Turn ${parsedState.turn || 1} • ${isPvE ? 'Your Faction' : 'Current'}: ${faction}
              </div>
              <div style="font-size: 13px; color: #aaa;">
                Units: ${unitCount} • Cities: ${playerCities}/${totalCities} • Resources: ${playerResources}
              </div>
            </div>
            <div style="color: #ff9999; font-size: 12px;">⚠️ Will overwrite</div>
          `;
        } catch (e) {
          slotButton.innerHTML = `<div>Slot ${slot}: Corrupted Save</div>`;
        }
      } else {
        slotButton.innerHTML = `<div style="color: #88ff88;">Slot ${slot}: EMPTY</div>`;
      }
      
      slotButton.onmouseover = () => {
        this.soundManager.playMenuHover();
        slotButton.style.background = '#4a4a4a';
        slotButton.style.borderColor = '#777';
      };
      slotButton.onmouseout = () => {
        slotButton.style.background = '#3a3a3a';
        slotButton.style.borderColor = '#555';
      };
      slotButton.onclick = () => {
        this.soundManager.playMenuSelect();
        this.currentSaveSlot = slot;
        slotOverlay.remove();
        
        // Show faction briefing and start game
        this.showFactionBriefing(startingFaction, () => {
          // Only fade menu after briefing is shown
          menuOverlay.style.opacity = '0';
          setTimeout(() => {
            menuOverlay.remove();
          }, 500);
        });
      };
      
      slotsGrid.appendChild(slotButton);
    }
    
    slotContainer.appendChild(slotsGrid);
    
    // Cancel button
    const cancelButton = document.createElement('button');
    cancelButton.textContent = 'Cancel';
    cancelButton.style.cssText = `
      margin-top: 20px;
      padding: 10px 30px;
      background: #5a5a5a;
      color: white;
      border: none;
      border-radius: 5px;
      cursor: pointer;
      width: 100%;
    `;
    cancelButton.onclick = () => {
      this.soundManager.playMenuSelect();
      slotOverlay.remove();
    };
    slotContainer.appendChild(cancelButton);
    
    slotOverlay.appendChild(slotContainer);
    document.body.appendChild(slotOverlay);
  }
  
  private updateFactionButtonsForOnline(container: HTMLElement, _campaign: CampaignType, menuOverlay: HTMLElement): void {
    // Clear existing buttons
    container.innerHTML = '';
    
    // Add a "Play Online" button that opens the lobby
    const playOnlineButton = document.createElement('button');
    playOnlineButton.textContent = 'Enter Multiplayer Lobby';
    playOnlineButton.style.cssText = `
      padding: 20px 40px;
      font-size: 20px;
      background: #4CAF50;
      color: white;
      border: 2px solid #45a049;
      border-radius: 10px;
      cursor: pointer;
      transition: all 0.3s ease;
      margin: 20px;
    `;
    
    playOnlineButton.onmouseover = () => {
      this.soundManager.playMenuHover();
      playOnlineButton.style.background = '#45a049';
      playOnlineButton.style.borderColor = '#3d8b40';
      playOnlineButton.style.transform = 'scale(1.05)';
    };
    
    playOnlineButton.onmouseout = () => {
      playOnlineButton.style.background = '#4CAF50';
      playOnlineButton.style.borderColor = '#45a049';
      playOnlineButton.style.transform = 'scale(1)';
    };
    
    playOnlineButton.onclick = async () => {
      this.soundManager.playMenuSelect();
      menuOverlay.style.display = 'none';
      
      // Initialize multiplayer service if not already done
      if (!this.multiplayerService) {
        this.multiplayerService = new MultiplayerService();
      }
      
      // Check if there's an existing multiplayer save
      const existingMpSave = localStorage.getItem('frontlines_multiplayer_save');
      if (existingMpSave) {
        const confirmOverwrite = await this.popupManager.showConfirm(
          'Starting a new online game will overwrite your existing multiplayer save. ' +
          'This is equivalent to resigning from your current game. Continue?'
        );
        if (!confirmOverwrite) {
          // User cancelled, don't proceed
          return;
        }
        
        // Try to surrender the existing game
        // First, parse the save to get the room ID
        try {
          const savedGame = JSON.parse(existingMpSave);
          if (savedGame.roomCode) {
            debug.log('Found existing multiplayer game, attempting to surrender...');
            
            // Try to reconnect to the room to surrender it
            try {
              await this.multiplayerService.reconnectToRoom(
                savedGame.roomCode,
                savedGame.currentFaction || savedGame.playerFaction
              );
              
              // Now surrender the game
              await this.multiplayerService.surrenderGame();
              debug.log('Successfully surrendered previous multiplayer game');
            } catch (error) {
              debug.error('Failed to reconnect and surrender:', error);
              // Continue anyway - the room might not exist anymore
            }
          }
        } catch (parseError) {
          debug.error('Failed to parse existing save:', parseError);
        }
        
        // Clear the existing save
        localStorage.removeItem('frontlines_multiplayer_save');
      }
      
      // Show multiplayer lobby with the selected campaign
      const lobby = new MultiplayerLobby(
        this.multiplayerService,
        this.selectedCampaign, // Pass the selected campaign
        (room) => {
          // Game started from lobby
          this.isOnlineGame = true;
          this.selectedCampaign = room.campaign;
          
          // Determine factions
          if (this.multiplayerService!.isHost()) {
            this.currentFaction = room.host.faction; // Host always starts first
            this.humanPlayerFaction = room.host.faction; // Set our faction
            this.secondFaction = room.guest!.faction;
            this.isMyTurn = true;
          } else {
            // Guest: currentFaction should be whoever's turn it is (host starts first)
            this.currentFaction = room.host.faction; // Game starts with host's turn
            this.humanPlayerFaction = room.guest!.faction; // Set our faction (guest)
            this.secondFaction = room.host.faction; // The opponent's faction (host)
            this.isMyTurn = false; // Guest doesn't start first
          }
          
          // Initialize the game
          this.initOnlineGame(menuOverlay);
        },
        () => {
          // Cancelled - return to menu
          menuOverlay.style.display = 'flex';
        }
      );
      lobby.show();
    };
    
    container.appendChild(playOnlineButton);
  }
  
  private showSaveSlotSelection(menuOverlay: HTMLElement): void {
    // Create save slot selection overlay
    const slotOverlay = document.createElement('div');
    slotOverlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.8);
      z-index: 31000;
      display: flex;
      align-items: center;
      justify-content: center;
    `;
    
    const slotContainer = document.createElement('div');
    slotContainer.style.cssText = `
      background: #2a2a2a;
      border: 2px solid #444;
      border-radius: 10px;
      padding: 30px;
      max-width: 600px;
      width: 90%;
    `;
    
    const title = document.createElement('h2');
    title.textContent = 'Select Save Slot';
    title.style.cssText = `
      color: white;
      margin-bottom: 20px;
      text-align: center;
    `;
    slotContainer.appendChild(title);
    
    const slotsGrid = document.createElement('div');
    slotsGrid.style.cssText = `
      display: flex;
      flex-direction: column;
      gap: 10px;
    `;
    
    // Check for multiplayer save first
    const multiplayerSave = localStorage.getItem('frontlines_multiplayer_save');
    if (multiplayerSave) {
      const mpSlotButton = document.createElement('button');
      mpSlotButton.style.cssText = `
        padding: 15px;
        background: #4a3a5a;
        color: white;
        border: 2px solid #665577;
        border-radius: 5px;
        cursor: pointer;
        transition: all 0.3s ease;
        text-align: left;
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 10px;
      `;
      
      try {
        const parsedState = JSON.parse(multiplayerSave);
        const campaignMap = {
          'normandy': 'Normandy',
          'tunisia': 'Tunisia',
          'stalingrad': 'Stalingrad',
          'okinawa': 'Okinawa',
          'campobasso': 'Campobasso'
        };
        const campaignName = campaignMap[parsedState.selectedCampaign as keyof typeof campaignMap] || 'Unknown';
        
        const faction = parsedState.currentFaction || 'Unknown';
        const turn = parsedState.turn || 1;
        const saveDate = parsedState.saveDate ? new Date(parsedState.saveDate).toLocaleDateString() : 'Unknown date';
        
        mpSlotButton.innerHTML = `
          <div>
            <strong style="color: #ffcc00;">Multiplayer Save</strong><br/>
            <span style="font-size: 12px; color: #ddd;">
              ${campaignName} - ${faction} - Turn ${turn}
            </span>
          </div>
          <div style="text-align: right; font-size: 11px; color: #aaa;">
            ${saveDate}
          </div>
        `;
        
        mpSlotButton.onmouseover = () => {
          mpSlotButton.style.background = '#5a4a6a';
          mpSlotButton.style.transform = 'scale(1.02)';
        };
        
        mpSlotButton.onmouseout = () => {
          mpSlotButton.style.background = '#4a3a5a';
          mpSlotButton.style.transform = 'scale(1)';
        };
        
        mpSlotButton.onclick = async () => {
          // Warn about overwriting
          const confirmLoad = await this.popupManager.showConfirm('Loading this multiplayer save will reconnect to the online game. Continue?');
          if (!confirmLoad) return;
          
          // Load the multiplayer save
          await this.loadMultiplayerSave(parsedState);
          slotOverlay.remove();
        };
        
        slotsGrid.appendChild(mpSlotButton);
      } catch (error) {
        debug.error('Failed to parse multiplayer save:', error);
      }
    }
    
    // Create 5 save slots
    for (let slot = 1; slot <= 5; slot++) {
      const slotButton = document.createElement('button');
      slotButton.style.cssText = `
        padding: 15px;
        background: #3a3a3a;
        color: white;
        border: 2px solid #555;
        border-radius: 5px;
        cursor: pointer;
        transition: all 0.3s ease;
        text-align: left;
        display: flex;
        justify-content: space-between;
        align-items: center;
      `;
      
      const savedState = localStorage.getItem(`frontlines_gamestate_slot${slot}`);
      
      if (savedState) {
        try {
          const parsedState = JSON.parse(savedState);
          const campaignMap = {
            'normandy': 'Normandy',
            'tunisia': 'Tunisia',
            'stalingrad': 'Stalingrad',
            'okinawa': 'Okinawa',
            'campobasso': 'Campobasso',
            'france_1940': 'Battle of France'
          };
          const campaignName = campaignMap[parsedState.selectedCampaign as keyof typeof campaignMap] || 'Unknown';
          
          const gameMode = parsedState.gameMode || 'PvP';
          const isPvE = gameMode === 'PvE';
          const displayFaction = isPvE ? parsedState.humanPlayerFaction : parsedState.currentFaction;
          const faction = displayFaction || parsedState.gameState?.currentFaction || 'Unknown';
          
          // Count units and cities
          const unitCount = parsedState.units ? parsedState.units.filter((u: any) => u[1].faction === displayFaction).length : 0;
          const cityData = parsedState.cities || [];
          const playerCities = cityData.filter((c: any) => c.faction === displayFaction).length;
          const totalCities = cityData.length;
          const resources = parsedState.resources ? Object.fromEntries(parsedState.resources) : {};
          const playerResources = resources[displayFaction] || 0;
          
          slotButton.innerHTML = `
            <div>
              <div style="font-weight: bold; margin-bottom: 5px;">Slot ${slot}: ${campaignName}</div>
              <div style="font-size: 14px; color: #ccc;">
                ${gameMode} • Turn ${parsedState.turn || 1} • ${isPvE ? 'Your Faction' : 'Current'}: ${faction}
              </div>
              <div style="font-size: 13px; color: #aaa;">
                Units: ${unitCount} • Cities: ${playerCities}/${totalCities} • Resources: ${playerResources}
              </div>
            </div>
          `;
        } catch (e) {
          slotButton.innerHTML = `<div>Slot ${slot}: Corrupted Save</div>`;
        }
      } else {
        slotButton.innerHTML = `<div style="color: #888;">Slot ${slot}: EMPTY</div>`;
        slotButton.style.opacity = '0.5';
      }
      
      if (savedState) {
        slotButton.onmouseover = () => {
          this.soundManager.playMenuHover();
          slotButton.style.background = '#4a4a4a';
          slotButton.style.borderColor = '#777';
        };
        slotButton.onmouseout = () => {
          slotButton.style.background = '#3a3a3a';
          slotButton.style.borderColor = '#555';
        };
        slotButton.onclick = () => {
          this.soundManager.playMenuSelect();
          slotOverlay.remove();
          
          // Load the game directly
          try {
            const state = JSON.parse(savedState);
            this.selectedCampaign = state.selectedCampaign;
            this.selectedGameMode = state.selectedGameMode || state.gameMode;
            this.currentFaction = state.currentFaction;
            this.humanPlayerFaction = state.humanPlayerFaction;
            this.secondFaction = state.secondFaction;
            
            // Set the save slot
            this.currentSaveSlot = slot;
            
            // Mark that we're loading a saved game
            this.isLoadingSavedGame = true;
            
            menuOverlay.style.opacity = '0';
            setTimeout(() => {
              menuOverlay.remove();
              this.initializeGame();
            }, 500);
          } catch (error) {
            debug.error('Failed to load saved game:', error);
            this.popupManager.showAlert('Failed to load saved game');
          }
        };
      }
      
      slotsGrid.appendChild(slotButton);
    }
    
    slotContainer.appendChild(slotsGrid);
    
    // Cancel button
    const cancelButton = document.createElement('button');
    cancelButton.textContent = 'Cancel';
    cancelButton.style.cssText = `
      margin-top: 20px;
      padding: 10px 30px;
      background: #5a5a5a;
      color: white;
      border: none;
      border-radius: 5px;
      cursor: pointer;
      width: 100%;
    `;
    cancelButton.onclick = () => {
      this.soundManager.playMenuSelect();
      slotOverlay.remove();
    };
    slotContainer.appendChild(cancelButton);
    
    slotOverlay.appendChild(slotContainer);
    document.body.appendChild(slotOverlay);
  }
  
  
  private showBattleLog(): void {
    // Create battle log overlay
    const battleLogOverlay = document.createElement('div');
    battleLogOverlay.id = 'battle-log-overlay';
    battleLogOverlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.98);
      z-index: 50000;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      opacity: 0;
      transition: opacity 0.3s ease-in-out;
    `;
    
    // Create container
    const container = document.createElement('div');
    container.style.cssText = `
      width: 90%;
      max-width: 800px;
      height: 80%;
      max-height: 600px;
      background: linear-gradient(135deg, #2c2c2c 0%, #1a1a1a 100%);
      border: 3px solid #444;
      border-radius: 10px;
      padding: 20px;
      display: flex;
      flex-direction: column;
      box-shadow: 0 10px 30px rgba(0,0,0,0.8);
    `;
    
    // Title
    const title = document.createElement('h2');
    title.textContent = 'Battle Statistics';
    title.style.cssText = `
      text-align: center;
      color: white;
      font-size: 32px;
      margin-bottom: 20px;
      text-shadow: 2px 2px 4px rgba(0,0,0,0.8);
      font-family: 'Georgia', serif;
    `;
    container.appendChild(title);
    
    // Faction tabs
    const tabContainer = document.createElement('div');
    tabContainer.style.cssText = `
      display: flex;
      justify-content: center;
      margin-bottom: 20px;
      border-bottom: 2px solid #444;
    `;
    
    // Stats display area
    const statsDisplay = document.createElement('div');
    statsDisplay.style.cssText = `
      flex: 1;
      overflow-y: auto;
      padding: 20px;
      background: rgba(0,0,0,0.3);
      border-radius: 5px;
    `;
    
    // Get factions that participated in this game
    const participatingFactions: FactionType[] = [];
    if (this.gameState.battleStats) {
      this.gameState.battleStats.forEach((stats, faction) => {
        // Only show factions that actually participated (have some activity)
        if (stats.unitsCreated > 0 || stats.unitsLost > 0 || stats.enemiesKilled > 0) {
          participatingFactions.push(faction);
        }
      });
    }
    
    // If no stats or factions, show the two main factions for this campaign
    if (participatingFactions.length === 0) {
      if (this.selectedCampaign === CampaignType.NORMANDY) {
        participatingFactions.push('United States Of America', '3rd Reich');
      } else if (this.selectedCampaign === CampaignType.TUNISIA) {
        participatingFactions.push('United Kingdom', '3rd Reich');
      } else if (this.selectedCampaign === CampaignType.STALINGRAD) {
        participatingFactions.push('USSR', '3rd Reich');
      } else if (this.selectedCampaign === CampaignType.OKINAWA) {
        participatingFactions.push('United States Of America', 'Imperial Japan');
      } else if (this.selectedCampaign === CampaignType.CAMPOBASSO) {
        participatingFactions.push('Canada', 'Fascist Italy', '3rd Reich');
      }
    }
    
    let selectedFaction = participatingFactions[0];
    
    // Create tab buttons
    participatingFactions.forEach(faction => {
      const tab = document.createElement('button');
      tab.textContent = faction;
      tab.style.cssText = `
        padding: 10px 20px;
        margin: 0 5px;
        background: transparent;
        color: #aaa;
        border: none;
        border-bottom: 3px solid transparent;
        cursor: pointer;
        font-size: 16px;
        transition: all 0.3s ease;
      `;
      
      const updateTabs = () => {
        // Update all tabs
        tabContainer.querySelectorAll('button').forEach(btn => {
          if (btn.textContent === selectedFaction) {
            btn.style.color = 'white';
            btn.style.borderBottomColor = '#4CAF50';
          } else {
            btn.style.color = '#aaa';
            btn.style.borderBottomColor = 'transparent';
          }
        });
        
        // Update stats display
        displayStats(selectedFaction);
      };
      
      tab.onclick = () => {
        selectedFaction = faction;
        updateTabs();
      };
      
      tab.onmouseover = () => {
        if (faction !== selectedFaction) {
          tab.style.color = '#ddd';
        }
      };
      
      tab.onmouseout = () => {
        if (faction !== selectedFaction) {
          tab.style.color = '#aaa';
        }
      };
      
      tabContainer.appendChild(tab);
    });
    
    const displayStats = (faction: FactionType) => {
      const stats = this.gameState.battleStats?.get(faction);
      
      if (!stats) {
        statsDisplay.innerHTML = `
          <div style="color: #aaa; text-align: center; margin-top: 50px;">
            No statistics available for ${faction}
          </div>
        `;
        return;
      }
      
      // Calculate efficiency metrics
      const killDeathRatio = stats.unitsLost > 0 ? (stats.enemiesKilled / stats.unitsLost).toFixed(2) : stats.enemiesKilled.toString();
      
      statsDisplay.innerHTML = `
        <div style="color: white; font-family: 'Courier New', monospace;">
          <h3 style="color: #4CAF50; margin-bottom: 20px; font-size: 24px;">${faction}</h3>
          
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px;">
            <div style="background: rgba(0,0,0,0.5); padding: 15px; border-radius: 5px;">
              <h4 style="color: #4CAF50; margin-bottom: 10px;">Combat Statistics</h4>
              <div style="margin-bottom: 8px;">Units Created: <span style="color: #aaa;">${stats.unitsCreated}</span></div>
              <div style="margin-bottom: 8px;">Units Lost: <span style="color: #f44336;">${stats.unitsLost}</span></div>
              <div style="margin-bottom: 8px;">Enemies Destroyed: <span style="color: #4CAF50;">${stats.enemiesKilled}</span></div>
              <div style="margin-bottom: 8px;">K/D Ratio: <span style="color: #ffa726;">${killDeathRatio}</span></div>
              <div style="margin-bottom: 8px;">Damage Dealt: <span style="color: #aaa;">${stats.damageDealt}</span></div>
              <div style="margin-bottom: 8px;">Damage Taken: <span style="color: #aaa;">${stats.damageTaken}</span></div>
            </div>
            
            <div style="background: rgba(0,0,0,0.5); padding: 15px; border-radius: 5px;">
              <h4 style="color: #4CAF50; margin-bottom: 10px;">Strategic Statistics</h4>
              <div style="margin-bottom: 8px;">Cities Captured: <span style="color: #4CAF50;">${stats.citiesCaptured}</span></div>
              <div style="margin-bottom: 8px;">Cities Lost: <span style="color: #f44336;">${stats.citiesLost}</span></div>
              <div style="margin-bottom: 8px;">Resources Earned: <span style="color: #4CAF50;">${stats.resourcesEarned}</span></div>
              <div style="margin-bottom: 8px;">Resources Spent: <span style="color: #ffa726;">${stats.resourcesSpent}</span></div>
            </div>
            
            <div style="background: rgba(0,0,0,0.5); padding: 15px; border-radius: 5px;">
              <h4 style="color: #4CAF50; margin-bottom: 10px;">Support Statistics</h4>
              <div style="margin-bottom: 8px;">Infantry Healed: <span style="color: #4CAF50;">${stats.unitsHealed}</span></div>
              <div style="margin-bottom: 8px;">Vehicles Repaired: <span style="color: #4CAF50;">${stats.unitsRepaired}</span></div>
              <div style="margin-bottom: 8px;">Mines Placed: <span style="color: #aaa;">${stats.minesPlaced}</span></div>
              <div style="margin-bottom: 8px;">Mines Detonated: <span style="color: #ffa726;">${stats.minesDetonated}</span></div>
            </div>
            
            <div style="background: rgba(0,0,0,0.5); padding: 15px; border-radius: 5px;">
              <h4 style="color: #4CAF50; margin-bottom: 10px;">Overall Performance</h4>
              <div style="margin-bottom: 8px;">Turns Played: <span style="color: #aaa;">${this.gameState.turn}</span></div>
              <div style="margin-bottom: 8px;">Net Resources: <span style="color: ${stats.resourcesEarned - stats.resourcesSpent >= 0 ? '#4CAF50' : '#f44336'}">${stats.resourcesEarned - stats.resourcesSpent}</span></div>
            </div>
          </div>
        </div>
      `;
    };
    
    // Close button
    const closeBtn = document.createElement('button');
    closeBtn.textContent = 'Close';
    closeBtn.style.cssText = `
      margin-top: 20px;
      padding: 10px 30px;
      font-size: 18px;
      background: #4a4a4a;
      color: white;
      border: 2px solid #666;
      border-radius: 5px;
      cursor: pointer;
      transition: all 0.3s ease;
      align-self: center;
    `;
    
    closeBtn.onmouseover = () => {
      this.soundManager.playMenuHover();
      closeBtn.style.background = '#5a5a5a';
      closeBtn.style.borderColor = '#888';
    };
    
    closeBtn.onmouseout = () => {
      closeBtn.style.background = '#4a4a4a';
      closeBtn.style.borderColor = '#666';
    };
    
    closeBtn.onclick = () => {
      this.soundManager.playMenuSelect();
      battleLogOverlay.style.opacity = '0';
      setTimeout(() => {
        battleLogOverlay.remove();
      }, 300);
    };
    
    container.appendChild(tabContainer);
    container.appendChild(statsDisplay);
    container.appendChild(closeBtn);
    battleLogOverlay.appendChild(container);
    document.body.appendChild(battleLogOverlay);
    
    // Initialize display with first faction
    displayStats(selectedFaction);
    
    // Highlight first tab
    const firstTab = tabContainer.querySelector('button');
    if (firstTab) {
      firstTab.style.color = 'white';
      firstTab.style.borderBottomColor = '#4CAF50';
    }
    
    // Fade in
    requestAnimationFrame(() => {
      battleLogOverlay.style.opacity = '1';
    });
  }
  
  private showGameOverScreen(victor: FactionType): void {
    // Prevent showing multiple game over screens
    if (document.getElementById('game-over-overlay')) return;
    
    // Mark game as over to prevent any further saves
    this.isGameOver = true;
    
    // Track game end in analytics
    const endReason = victor === (this.humanPlayerFaction || this.currentFaction) ? 'victory' : 'defeat';
    this.analytics.trackGameEnd({
      winner: victor,
      endReason: endReason as 'victory' | 'defeat',
      turnsPlayed: this.gameState.turn,
      battleStats: this.gameState.battleStats || new Map()
    });
    
    // Track game end in statistics
    const duration = this.gameStartTime ? Date.now() - this.gameStartTime : 0;
    this.statistics.trackGameEnd({
      winner: victor,
      duration: duration,
      turnsPlayed: this.gameState.turn
    });
    
    // Remove saved game state since the game is over
    if (this.isOnlineGame) {
      // Clear multiplayer save
      localStorage.removeItem('frontlines_multiplayer_save');
      debug.log('Multiplayer save cleared');
    } else {
      // Clear regular save slot
      localStorage.removeItem(`frontlines_gamestate_slot${this.currentSaveSlot}`);
      debug.log(`Game over - saved game state removed from slot ${this.currentSaveSlot}`);
      
      // Also clear the save slot metadata to show it as empty
      const slotKey = `frontlines_slot${this.currentSaveSlot}_metadata`;
      localStorage.removeItem(slotKey);
      debug.log(`Save slot ${this.currentSaveSlot} metadata cleared`);
    }
    
    // Create game over overlay
    const gameOverOverlay = document.createElement('div');
    gameOverOverlay.id = 'game-over-overlay';
    gameOverOverlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.95);
      z-index: 40000;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      opacity: 0;
      transition: opacity 1s ease-in-out;
    `;
    
    // Create content
    const content = document.createElement('div');
    content.style.cssText = `
      text-align: center;
      color: white;
      font-family: 'Georgia', serif;
    `;
    
    // Victory or defeat - check against the human player's faction
    const checkFaction = this.humanPlayerFaction || this.currentFaction;
    const isVictory = victor === checkFaction;
    const resultText = isVictory ? 'VICTORY' : 'DEFEAT';
    const resultColor = isVictory ? '#4CAF50' : '#F44336';
    
    // Get the victor's flag
    const victorFlag = victor === 'United States Of America' ? 'flags/USA.jpg' : 
                      victor === 'United Kingdom' ? 'flags/Brits.jpg' : 
                      victor === 'USSR' ? 'flags/Soviet.jpg' :
                      victor === 'Imperial Japan' ? 'flags/Japs.jpg' :
                      victor === 'Canada' ? 'flags/Canadians.jpg' :
                      victor === 'Fascist Italy' ? 'flags/Italians.jpg' :
                      victor === 'France' ? 'flags/French.jpg' :
                      victor === 'Greece' ? 'flags/Greeks.jpg' :
                      victor === '3rd Reich' ? 'flags/Reich.jpg' :
                      'flags/Reich.jpg';
    
    content.innerHTML = `
      <img src="${victorFlag}" alt="${victor} Flag" 
           style="width: 300px; height: auto; margin-bottom: 30px; 
                  border: 3px solid ${resultColor}; 
                  box-shadow: 0 8px 16px rgba(0,0,0,0.8);">
      <h2 style="font-size: 48px; margin-bottom: 30px; color: ${resultColor}; 
                 letter-spacing: 6px; text-shadow: 3px 3px 6px rgba(0,0,0,0.8);">
        ${resultText}
      </h2>
      <div style="font-size: 20px; line-height: 1.8; max-width: 600px; margin: 0 auto;">
    `;
    
    // Add appropriate message based on victor and campaign
    if (this.selectedCampaign === CampaignType.NORMANDY) {
      if (victor === 'United States Of America') {
        content.innerHTML += `
          <h2 style="font-size: 32px; margin-bottom: 20px; color: #4B5320;">
            US Army Controls Normandy
          </h2>
          <p style="margin-bottom: 20px; color: #ccc;">
            On June 1944, Allied forces achieved complete control of the Normandy region. 
            All German resistance in the area was eliminated, opening the road to Paris and 
            beginning the liberation of France. The successful breakout from the beaches proved 
            that the Atlantic Wall could be breached, marking the beginning of the end for the 
            Third Reich in Western Europe.
          </p>
        `;
      } else {
        content.innerHTML += `
          <h2 style="font-size: 32px; margin-bottom: 20px; color: #708090;">
            Wehrmacht Holds Normandy
          </h2>
          <p style="margin-bottom: 20px; color: #ccc;">
            On June 1944, Wehrmacht forces successfully repelled the Allied invasion of Normandy. 
            The enemy beachheads were eliminated and the invasion force was driven back into the 
            English Channel. The Atlantic Wall held firm, preventing the opening of a second front 
            in Western Europe and allowing German forces to continue focusing on the Eastern Front.
          </p>
        `;
      }
    } else if (this.selectedCampaign === CampaignType.TUNISIA) {
      if (victor === 'United Kingdom') {
        content.innerHTML += `
          <h2 style="font-size: 32px; margin-bottom: 20px; color: #003F87;">
            8th Army Secures Tunisia
          </h2>
          <p style="margin-bottom: 20px; color: #ccc;">
            In May 1943, British and Commonwealth forces achieved total victory in Tunisia. 
            The Afrika Korps, trapped between Montgomery's 8th Army and American forces from 
            Operation Torch, surrendered en masse. Over 250,000 Axis troops were captured, 
            eliminating German presence in North Africa. The Desert Rats had completed their 
            long journey from El Alamein to final victory.
          </p>
        `;
      } else {
        content.innerHTML += `
          <h2 style="font-size: 32px; margin-bottom: 20px; color: #708090;">
            Afrika Korps Holds Tunisia
          </h2>
          <p style="margin-bottom: 20px; color: #ccc;">
            In May 1943, the Afrika Korps achieved the impossible, holding Tunisia against 
            overwhelming Allied forces. Rommel's tactical genius and the determination of his 
            desert veterans prevented the link-up of British and American forces. The retention 
            of North African ports allowed continued Axis operations in the Mediterranean, 
            changing the course of the war.
          </p>
        `;
      }
    } else if (this.selectedCampaign === CampaignType.STALINGRAD) {
      if (victor === 'USSR') {
        content.innerHTML += `
          <h2 style="font-size: 32px; margin-bottom: 20px; color: #CC0000;">
            Red Army Liberates Stalingrad
          </h2>
          <p style="margin-bottom: 20px; color: #ccc;">
            In February 1943, the Red Army achieved total victory at Stalingrad. The German 
            6th Army, surrounded and cut off from supplies, surrendered after months of brutal 
            urban warfare. Over 250,000 German troops were lost. This crushing defeat marked 
            the beginning of the Wehrmacht's long retreat westward. The heroic defense of 
            Stalingrad proved that the German army could be defeated.
          </p>
        `;
      } else {
        content.innerHTML += `
          <h2 style="font-size: 32px; margin-bottom: 20px; color: #708090;">
            Wehrmacht Captures Stalingrad
          </h2>
          <p style="margin-bottom: 20px; color: #ccc;">
            In February 1943, the German 6th Army achieved what seemed impossible - the complete 
            capture of Stalingrad. Despite fierce Soviet resistance and harsh winter conditions, 
            Wehrmacht forces secured all strategic positions along the Volga. Control of this 
            vital industrial center crippled Soviet war production and morale. The path to the 
            Caucasus oil fields now lay open.
          </p>
        `;
      }
    } else if (this.selectedCampaign === CampaignType.CAMPOBASSO) {
      if (victor === 'Canada') {
        content.innerHTML += `
          <h2 style="font-size: 32px; margin-bottom: 20px; color: #FF0000;">
            Canada Liberates Campobasso
          </h2>
          <p style="margin-bottom: 20px; color: #ccc;">
            In October 1943, Canadian forces achieved a decisive victory at Campobasso. 
            The 1st Canadian Infantry Division, fighting their way through the treacherous 
            Italian terrain, successfully drove out German defenders from this strategic 
            mountain stronghold. This victory opened the road to Rome and proved that 
            Canadian forces were among the finest fighting units in the Allied armies.
          </p>
        `;
      } else if (victor === 'Fascist Italy') {
        content.innerHTML += `
          <h2 style="font-size: 32px; margin-bottom: 20px; color: #008C45;">
            Italy Holds Campobasso
          </h2>
          <p style="margin-bottom: 20px; color: #ccc;">
            In October 1943, Italian forces successfully defended Campobasso against the 
            Allied advance. Despite internal turmoil following Mussolini's fall, the Italian 
            Army rallied to defend their homeland. The mountainous terrain and fierce 
            determination of Italian defenders stopped the Canadian advance cold, maintaining 
            control of this vital defensive position in central Italy.
          </p>
        `;
      } else {
        content.innerHTML += `
          <h2 style="font-size: 32px; margin-bottom: 20px; color: #708090;">
            Wehrmacht Controls Campobasso
          </h2>
          <p style="margin-bottom: 20px; color: #ccc;">
            In October 1943, German forces maintained their iron grip on Campobasso. 
            The Wehrmacht's defensive lines held firm against Allied attempts to advance 
            through central Italy. Control of this strategic mountain position allowed 
            German forces to continue their defensive campaign, turning the Italian 
            peninsula into a costly battlefield for the Allies.
          </p>
        `;
      }
    } else if (this.selectedCampaign === CampaignType.OKINAWA) {
      if (victor === 'United States Of America') {
        content.innerHTML += `
          <h2 style="font-size: 32px; margin-bottom: 20px; color: #4B5320;">
            Victory on Okinawa
          </h2>
          <p style="margin-bottom: 20px; color: #ccc;">
            On June 21, 1945, after 82 days of the largest Pacific campaign, 
            Okinawa was declared secure. The fall of Shuri Castle broke the back of Japanese 
            resistance. Though the cost was staggering - over 12,000 American and 110,000 Japanese 
            killed - the capture of Okinawa's airfields provided the final staging ground 
            and brought victory over Japan closer.
          </p>
        `;
      } else {
        content.innerHTML += `
          <h2 style="font-size: 32px; margin-bottom: 20px; color: #DC143C;">
            Okinawa Defended
          </h2>
          <p style="margin-bottom: 20px; color: #ccc;">
            Against overwhelming odds, the 32nd Army successfully defended Okinawa. 
            General Ushijima's strategic withdrawal to prepared positions, combined with the 
            fanatical resistance of Japanese defenders, inflicted such casualties that the US 
            forces were forced to withdraw. The sacred soil of the Ryukyu Kingdom remained 
            unconquered, buying precious time for the homeland's defense.
          </p>
        `;
      }
    } else if (this.selectedCampaign === CampaignType.FRANCE_1940) {
      if (victor === 'France') {
        content.innerHTML += `
          <h2 style="font-size: 32px; margin-bottom: 20px; color: #7A9BB8;">
            France Holds the Line
          </h2>
          <p style="margin-bottom: 20px; color: #ccc;">
            In June 1940, against all odds, the French Army successfully halted the German 
            blitzkrieg. The Maginot Line held, and coordinated counterattacks through the Ardennes 
            pushed back Wehrmacht forces. The Third Republic survived its greatest test, preventing 
            the fall of France and maintaining the Western Front. Churchill's Britain would not 
            stand alone, and the two-front war Germany feared became reality.
          </p>
        `;
      } else {
        content.innerHTML += `
          <h2 style="font-size: 32px; margin-bottom: 20px; color: #708090;">
            Fall of France Complete
          </h2>
          <p style="margin-bottom: 20px; color: #ccc;">
            In June 1940, Wehrmacht forces achieved total victory over France in just six weeks. 
            The revolutionary blitzkrieg tactics, spearheaded by panzer divisions through the 
            Ardennes, bypassed the Maginot Line and shattered Allied resistance. Paris fell without 
            a fight, and Marshal Pétain signed the armistice at Compiègne. The Third Republic 
            collapsed, and German dominance over Western Europe was established.
          </p>
        `;
      }
    } else if (this.selectedCampaign === CampaignType.ALBANIA_1940) {
      if (victor === 'Greece') {
        content.innerHTML += `
          <h2 style="font-size: 32px; margin-bottom: 20px; color: #0072CE;">
            Hellenic Victory in Albania
          </h2>
          <p style="margin-bottom: 20px; color: #ccc;">
            In March 1941, Greek forces achieved a stunning victory over Fascist Italy. The Evzones 
            and mountain divisions not only repelled Mussolini's invasion but pushed deep into 
            Albanian territory, capturing key cities including Korçë and Gjirokastër. This was the 
            first Allied land victory against the Axis powers, proving that the fascist war machine 
            could be defeated. Greece's heroic stand inspired resistance movements across occupied Europe.
          </p>
        `;
      } else {
        content.innerHTML += `
          <h2 style="font-size: 32px; margin-bottom: 20px; color: #009246;">
            Italy Conquers Greece
          </h2>
          <p style="margin-bottom: 20px; color: #ccc;">
            In March 1941, Italian forces finally achieved victory in Greece after months of bitter 
            fighting. Despite initial setbacks, reinforced Italian divisions, supported by improved 
            tactics and overwhelming numbers, broke through Greek mountain defenses. The fall of 
            Epirus opened the path to Athens. Mussolini's prestige was restored, and Italy secured 
            its position as a Mediterranean power without requiring German intervention.
          </p>
        `;
      }
    }
    
    content.innerHTML += `</div>`;
    
    // Add return to menu button
    const menuBtn = document.createElement('button');
    menuBtn.textContent = 'Return to Main Menu';
    menuBtn.style.cssText = `
      margin-top: 40px;
      padding: 15px 40px;
      font-size: 20px;
      background: #4a4a4a;
      color: white;
      border: 2px solid #666;
      border-radius: 5px;
      cursor: pointer;
      transition: all 0.3s ease;
    `;
    
    menuBtn.onmouseover = () => {
      this.soundManager.playMenuHover();
      menuBtn.style.background = '#5a5a5a';
      menuBtn.style.borderColor = '#888';
    };
    
    menuBtn.onmouseout = () => {
      menuBtn.style.background = '#4a4a4a';
      menuBtn.style.borderColor = '#666';
    };
    
    menuBtn.onclick = () => {
      this.soundManager.playMenuSelect();
      // Reload the page to return to main menu
      window.location.reload();
    };
    
    // Add battle log button
    const battleLogBtn = document.createElement('button');
    battleLogBtn.textContent = 'View Battle Log';
    battleLogBtn.style.cssText = `
      margin-top: 20px;
      margin-left: 10px;
      padding: 15px 40px;
      font-size: 20px;
      background: #3a3a3a;
      color: white;
      border: 2px solid #555;
      border-radius: 5px;
      cursor: pointer;
      transition: all 0.3s ease;
    `;
    
    battleLogBtn.onmouseover = () => {
      this.soundManager.playMenuHover();
      battleLogBtn.style.background = '#4a4a4a';
      battleLogBtn.style.borderColor = '#777';
    };
    
    battleLogBtn.onmouseout = () => {
      battleLogBtn.style.background = '#3a3a3a';
      battleLogBtn.style.borderColor = '#555';
    };
    
    battleLogBtn.onclick = () => {
      this.soundManager.playMenuSelect();
      this.showBattleLog();
    };
    
    content.appendChild(menuBtn);
    content.appendChild(battleLogBtn);
    gameOverOverlay.appendChild(content);
    document.body.appendChild(gameOverOverlay);
    
    // Play the victor's national anthem
    if (victor === 'United States Of America') {
      this.soundManager.playUSAnthem();
    } else if (victor === '3rd Reich') {
      this.soundManager.playGermanAnthem();
    } else if (victor === 'United Kingdom') {
      this.soundManager.playBritishAnthem();
    } else if (victor === 'USSR') {
      this.soundManager.playSovietAnthem();
    } else if (victor === 'Imperial Japan') {
      this.soundManager.playJapaneseAnthem();
    } else if (victor === 'Canada') {
      this.soundManager.playCanadianAnthem();
    } else if (victor === 'Fascist Italy') {
      this.soundManager.playItalianAnthem();
    } else if (victor === 'France') {
      this.soundManager.playFrenchAnthem();
    } else if (victor === 'Greece') {
      this.soundManager.playGreekAnthem();
    }
    
    // Fade in overlay
    requestAnimationFrame(() => {
      gameOverOverlay.style.opacity = '1';
    });
  }

  private checkCaptureInterruptions(): void {
    const cities = this.map.getCities();
    
    cities.forEach(city => {
      if (city.captureProgress) {
        const unitsInCity = this.getUnitsInCity(city);
        const capturingUnits = unitsInCity.filter(u => u.faction === city.captureProgress!.faction);
        const defendingUnits = unitsInCity.filter(u => u.faction !== city.captureProgress!.faction);
        
        // Check if capture should be interrupted
        if (capturingUnits.length === 0 || defendingUnits.length > 0) {
          debug.log(`❌ ${city.captureProgress.faction} capture of ${city.name} failed - conditions not maintained!`);
          city.captureProgress = undefined;
        }
      }
    });
  }
  
  private processCityCaptures(faction: FactionType): void {
    const cities = this.map.getCities();
    const currentTurn = this.gameState.turn;
    debug.log(`Processing city captures for ${faction} at turn ${currentTurn}`);
    
    cities.forEach(city => {
      const unitsInCity = this.getUnitsInCity(city);
      const friendlyUnits = unitsInCity.filter(u => u.faction === faction);
      const enemyUnits = unitsInCity.filter(u => u.faction !== faction);
      
      if (city.captureProgress) {
        debug.log(`City ${city.name}: captureProgress exists - faction: ${city.captureProgress.faction}, startedTurn: ${city.captureProgress.startedTurn}, currentOwner: ${city.faction}`);
      }
      
      // Check if we can complete a capture (requires holding until start of next turn)
      // City can be captured if it's neutral (null) or owned by another faction
      const canCapture = city.faction === null || city.faction !== faction;
      
      if (friendlyUnits.length > 0 && enemyUnits.length === 0 && canCapture) {
        // Double-check that units are really there by checking unit manager directly
        const verifiedUnits = friendlyUnits.filter(u => this.unitManager.getUnit(u.id));
        debug.log(`City ${city.name}: ${friendlyUnits.length} friendly units (${verifiedUnits.length} verified), 0 enemies, owned by ${city.faction || 'neutral'}`);
        
        // Only proceed if verified units exist
        if (verifiedUnits.length === 0) {
          debug.log(`WARNING: City ${city.name} appeared to have units but verification failed - clearing capture progress`);
          city.captureProgress = undefined;
          return;
        }
        
        if (city.captureProgress && city.captureProgress.faction === faction) {
          // Check if this is a different turn from when capture started
          // In a 2-player game, turns increment for each faction, so we need at least 2 turn increments
          // (Your turn N -> Enemy turn N+1 -> Your turn N+2)
          const turnsHeld = currentTurn - city.captureProgress.startedTurn;
          debug.log(`City ${city.name}: capture in progress, turnsHeld = ${turnsHeld} (need >= 2)`);
          
          // Capture completes after holding for a full round (2 turn increments minimum)
          if (turnsHeld >= 2) {
            // Store the previous owner before changing
            const previousOwner = city.faction;
            
            // Complete the capture
            debug.log(`🎯 ${faction} captured ${city.name} after holding for ${turnsHeld} turns!`);
            city.faction = faction;
            city.captureProgress = undefined;
            
            // Clear any enemy training queue and upgrade progress
            city.trainingQueue = [];
            city.upgradeInProgress = undefined;
            
            // Reset unit capacity by clearing the unitsProduced array
            city.unitsProduced = [];
            
            // Track city capture in battle statistics
            const captorStats = this.gameState.battleStats?.get(faction);
            if (captorStats) {
              captorStats.citiesCaptured++;
            }
            
            // Track city loss for previous owner if applicable
            if (previousOwner && previousOwner !== faction) {
              const loserStats = this.gameState.battleStats?.get(previousOwner);
              if (loserStats) {
                loserStats.citiesLost++;
              }
            }
            
            // Track city capture in analytics
            this.analytics.trackCityCapture({
              cityName: city.name,
              capturingFaction: faction,
              previousOwner: previousOwner,
              turn: this.gameState.turn
            });
            
            // Keep the city level when captured - infrastructure remains
            
            // CRITICAL: Sync city capture with Firebase BEFORE sending notification
            // This prevents the notification from triggering a sync that might have old state
            if (this.isOnlineGame && this.multiplayerService) {
              debug.log(`[CITY CAPTURE SYNC] Syncing state after capturing ${city.name}`);
              const completeGameState = this.buildCompleteGameState();
              this.multiplayerService.updateGameState(
                completeGameState,
                this.gameState.currentFaction,
                this.gameState.turn
              );
            }
            
            // Send capture notification AFTER syncing
            // In PvE mode, only send notifications for human player's actions
            if (!this.selectedGameMode || this.selectedGameMode !== 'PvE' || faction === this.humanPlayerFaction) {
              this.sendNotification({
                type: NotificationType.CITY_CAPTURED,
                faction: faction,
                targetFaction: previousOwner || undefined,
                data: { cityName: city.name },
                timestamp: Date.now()
              });
            }
            
            // Update city count display
            this.updateCityCount();
            
            // Update tutorial progress if in tutorial mode and human player captured a neutral city
            // DISABLED: Now handled by the guided tutorial system which waits for proper validation
            // if (this.isTutorial && faction === this.humanPlayerFaction && this.tutorialStage === 2 && previousOwner === null) {
            //   this.updateTutorialProgress();
            // }
            
            // Check victory conditions after capture
            this.checkVictoryConditions();
            
            // Add loss notification for the previous owner
            if (previousOwner && previousOwner !== faction) {
              if (!this.gameState.pendingNotifications) {
                this.gameState.pendingNotifications = new Map();
              }
              if (!this.gameState.pendingNotifications.has(previousOwner)) {
                this.gameState.pendingNotifications.set(previousOwner, []);
              }
              this.gameState.pendingNotifications.get(previousOwner)!.push(`You have lost ${city.name} to the enemy!`);
            }
          } else {
            // Capture in progress - log status
            debug.log(`⏳ ${faction} holding ${city.name} - ${turnsHeld}/2 turns completed`);
          }
        }
        // No else clause - captures now start immediately when units move into cities
      } else if (enemyUnits.length > 0 || friendlyUnits.length === 0) {
        // Cancel capture if conditions not met
        if (city.captureProgress && city.captureProgress.faction === faction) {
          debug.log(`❌ ${faction} capture of ${city.name} interrupted!`);
          city.captureProgress = undefined;
        }
      }
    });
    
    // Save game state after processing captures
    this.saveGameState();
  }
  
  private getUnitsInCity(city: City): Unit[] {
    const unitsInCity: Unit[] = [];
    const allUnits = this.unitManager.getAllUnits();
    
    for (const unit of allUnits) {
      if (city.tiles.some((tile: Position) => tile.x === unit.position.x && tile.y === unit.position.y)) {
        unitsInCity.push(unit);
      }
    }
    
    return unitsInCity;
  }
  
  private checkAndStartCapture(unit: Unit): void {
    // Find city at unit's position
    const tile = this.map.getTile(unit.position.x, unit.position.y);
    if (!tile?.city) return;
    
    const city = tile.city;
    
    // Check if city belongs to enemy or is neutral
    // Can capture if city is neutral (null) or belongs to another faction
    if (city.faction !== null && city.faction === unit.faction) return;
    
    // IMPORTANT: Only start captures on YOUR turn
    if (unit.faction !== this.currentFaction) return;
    
    // Aircraft cannot capture cities
    if (isAircraft(unit.type as UnitType)) return;
    
    // Check if there are enemy units in the city
    const unitsInCity = this.getUnitsInCity(city);
    const enemyUnits = unitsInCity.filter(u => u.faction !== unit.faction);
    
    if (enemyUnits.length === 0 && !city.captureProgress) {
      // Start capture immediately
      debug.log(`⏳ ${unit.faction} beginning capture of ${city.name} at turn ${this.gameState.turn} - hold for 2 turns`);
      city.captureProgress = {
        faction: unit.faction,
        startedTurn: this.gameState.turn
      };
      
      // Log the capture progress that was just set
      debug.log(`Set captureProgress for ${city.name}:`, city.captureProgress);
      
      // Save game state after starting capture
      this.saveGameState();
      
      // Sync with Firebase in online games
      if (this.isOnlineGame && this.multiplayerService) {
        const completeGameState = this.buildCompleteGameState();
        this.multiplayerService.updateGameState(
          completeGameState,
          this.gameState.currentFaction,
          this.gameState.turn
        );
      }
    }
  }
  
  private checkForNewCaptures(): void {
    const cities = this.map.getCities();
    
    // Check each city for potential new captures - ONLY for the current faction
    cities.forEach(city => {
      // Skip if city is already owned by current faction or already being captured
      if (city.faction === this.currentFaction || city.captureProgress) return;
      
      const unitsInCity = this.getUnitsInCity(city);
      const friendlyUnits = unitsInCity.filter(u => u.faction === this.currentFaction);
      const enemyUnits = unitsInCity.filter(u => u.faction !== this.currentFaction);
      
      // Only start capture if current faction has units and no enemies
      // Aircraft cannot capture cities
      const nonAircraftUnits = friendlyUnits.filter(u => !isAircraft(u.type as UnitType));
      if (nonAircraftUnits.length > 0 && enemyUnits.length === 0) {
        // Start capture
        debug.log(`⏳ ${this.currentFaction} beginning capture of ${city.name} at start of their turn`);
        city.captureProgress = {
          faction: this.currentFaction,
          startedTurn: this.gameState.turn
        };
      }
    });
    
    // Save game state if any new captures were started
    this.saveGameState();
  }
  
  private checkForNewCapturesInCity(city: any): void {
    // This method is called when a unit moves OUT of a city
    // We should clear capture progress if no units remain
    
    if (city.captureProgress) {
      const unitsInCity = this.getUnitsInCity(city);
      const capturingUnits = unitsInCity.filter(u => u.faction === city.captureProgress!.faction);
      
      // Cancel capture if no capturing units remain
      // Also cancel if enemy units are present
      const enemyUnits = unitsInCity.filter(u => 
        u.faction !== city.captureProgress!.faction && 
        u.faction !== city.faction
      );
      
      if (capturingUnits.length === 0) {
        debug.log(`❌ Capture of ${city.name} interrupted - no capturing units remain`);
        const capturingFaction = city.captureProgress.faction;
        city.captureProgress = undefined;
        
        // Send interruption notification
        this.sendNotification({
          type: NotificationType.CITY_CAPTURE_INTERRUPTED,
          faction: capturingFaction,
          data: { cityName: city.name },
          timestamp: Date.now()
        });
      } else if (enemyUnits.length > 0) {
        debug.log(`❌ Capture of ${city.name} interrupted - enemy units present`);
        const capturingFaction = city.captureProgress.faction;
        city.captureProgress = undefined;
        
        // Send interruption notification
        this.sendNotification({
          type: NotificationType.CITY_CAPTURE_INTERRUPTED,
          faction: capturingFaction,
          data: { cityName: city.name },
          timestamp: Date.now()
        });
      }
    }
  }
  
  private processedTrainingThisTurn = new Set<string>();  // Track which units we've notified about
  private pendingNotifications: any[] = [];  // Queue notifications to send after sync
  
  private processCityUpgrades(faction: FactionType): void {
    const cities = this.map.getCities();
    
    cities.forEach(city => {
      if (city.faction === faction && city.upgradeInProgress) {
        city.upgradeInProgress.turnsRemaining--;
        
        if (city.upgradeInProgress.turnsRemaining <= 0) {
          // Upgrade complete!
          const oldLevel = city.level || CityLevel.OUTPOST;
          const newLevel = city.upgradeInProgress.toLevel;
          city.level = newLevel;
          city.maxUnits = getCityMaxUnits(newLevel);
          city.upgradeInProgress = undefined;
          
          // Only show notification if it's the player's city
          // In PvE, only show for human player. In PvP/Online, show for current faction
          const shouldShowNotification = 
            this.gameState.gameMode === 'PvE' 
              ? city.faction === this.humanPlayerFaction
              : city.faction === this.currentFaction;
          
          if (shouldShowNotification) {
            const levelName = getCityLevelName(newLevel);
            this.showNotification(`${city.name} has been upgraded to ${levelName}!`);
          }
          
          // Update tile info if this city is selected
          const selectedTile = this.gameState.selectedTile;
          if (selectedTile) {
            const tile = this.map.getTile(selectedTile.x, selectedTile.y);
            if (tile && tile.city && tile.city.name === city.name) {
              this.updateTileInfo(tile);
            }
          }
          
          debug.log(`City ${city.name} upgraded from level ${oldLevel} to ${newLevel}`);
        }
      }
    });
  }
  
  private processTrainingQueues(faction: FactionType): void {
    const cities = this.map.getCities();
    
    // Reset tracking for new turn
    if (!this.processedTrainingThisTurn) {
      this.processedTrainingThisTurn = new Set();
    }
    
    // Clear pending notifications
    this.pendingNotifications = [];
    
    // Debug logging with stack trace
    debug.log(`[PROCESS TRAINING] Processing training queues for ${faction} at START of turn ${this.gameState.turn} (currentFaction: ${this.currentFaction}, isMyTurn: ${this.isMyTurn})`);
    debug.log(`[PROCESS TRAINING] processedTrainingThisTurn Set size: ${this.processedTrainingThisTurn ? this.processedTrainingThisTurn.size : 0}`);
    console.trace('processTrainingQueues called from:');
    
    cities.forEach(city => {
      if (city.faction === faction && city.trainingQueue && city.trainingQueue.length > 0) {
        debug.log(`  - ${city.name} has ${city.trainingQueue.length} units in training`);
        // Process all units in the queue
        const completedUnits: any[] = [];
        
        // Only process the first unit in the queue (sequential training)
        // Use a copy to avoid Firebase sync overwriting our changes
        const updatedQueue = [...city.trainingQueue];
        if (updatedQueue.length > 0) {
          // Only decrement the first unit in queue
          const firstUnit = updatedQueue[0];
          firstUnit.turnsRemaining--;
          debug.log(`    - ${UNIT_DEFINITIONS[firstUnit.type].name}: ${firstUnit.turnsRemaining} turns remaining`);
          
          if (firstUnit.turnsRemaining <= 0) {
            completedUnits.push(firstUnit);
          }
          
          // Log the rest of the queue (not being processed yet)
          for (let i = 1; i < updatedQueue.length; i++) {
            debug.log(`    - ${UNIT_DEFINITIONS[updatedQueue[i].type].name}: ${updatedQueue[i].turnsRemaining} turns remaining (waiting in queue)`);
          }
        }
        // Update the city's queue immediately
        city.trainingQueue = updatedQueue;
        
        // Remove completed units from queue and spawn them
        const spawnedUnits: any[] = [];
        completedUnits.forEach(trainingUnit => {
          // Check if spawn tile is still empty
          if (!this.unitManager.getUnitAt(trainingUnit.tile)) {
            // Check if this is an airborne unit
            let unit: Unit;
            if (isAirborneUnit(trainingUnit.type)) {
              // Create transport aircraft instead of airborne unit
              const transportType = getTransportForAirborne(trainingUnit.type);
              if (transportType) {
                unit = this.unitManager.createUnit(transportType, faction, trainingUnit.tile);
                unit.carryingUnit = trainingUnit.type; // Store what unit it's carrying
                unit.isTransport = true;
              } else {
                // Fallback to normal unit creation
                unit = this.unitManager.createUnit(trainingUnit.type, faction, trainingUnit.tile);
              }
            } else {
              // Create the unit normally
              unit = this.unitManager.createUnit(trainingUnit.type, faction, trainingUnit.tile);
            }
            unit.hometown = city.name;
            
            // Add to gameState
            this.gameState.units.set(unit.id, unit);
            
            // Track unit creation in battle statistics
            const factionStats = this.gameState.battleStats?.get(faction);
            if (factionStats) {
              factionStats.unitsCreated++;
            }
            
            // Add the actual unit ID to unitsProduced
            city.unitsProduced.push(unit.id);
            
            // Mark as successfully spawned
            spawnedUnits.push(trainingUnit);
            
            // Update tutorial progress if in tutorial mode and human player trained unit
            // DISABLED: Now handled by the guided tutorial system
            // if (this.isTutorial && faction === this.humanPlayerFaction && this.tutorialStage === 3) {
            //   this.updateTutorialProgress();
            // }
            
            // Notify player and log for debugging
            debug.log(`TRAINING COMPLETE: ${unit.name} in ${city.name} (id: ${unit.id}, faction: ${faction}, turn: ${this.gameState.turn})`);
            
            // Queue notification to send AFTER Firebase sync
            const notificationKey = `${city.name}_${unit.type}_${this.gameState.turn}`;
            debug.log(`[NOTIFICATION CHECK] Key: ${notificationKey}, Already sent: ${this.processedTrainingThisTurn.has(notificationKey)}`);
            // In PvE mode, only send notifications for human player's units
            const shouldSendNotification = faction === this.currentFaction && 
              (!this.selectedGameMode || this.selectedGameMode !== 'PvE' || faction === this.humanPlayerFaction);
            
            if (shouldSendNotification && !this.processedTrainingThisTurn.has(notificationKey)) {
              this.processedTrainingThisTurn.add(notificationKey);
              debug.log(`[NOTIFICATION QUEUED] Training complete for ${unit.name} in ${city.name}`);
              this.pendingNotifications.push({
                type: NotificationType.UNIT_TRAINING_COMPLETE,
                faction: faction,
                data: { unitName: unit.name, cityName: city.name },
                timestamp: Date.now()
              });
            } else {
              debug.log(`[NOTIFICATION SKIPPED] Already sent for ${unit.name} in ${city.name} this turn or is AI in PvE`);
            }
          } else {
            // Try to find alternative spawn tile
            let spawned = false;
            for (const tile of city.tiles) {
              if (!this.unitManager.getUnitAt(tile)) {
                // Check if this is an airborne unit
                let unit: Unit;
                if (isAirborneUnit(trainingUnit.type)) {
                  // Create transport aircraft instead of airborne unit
                  const transportType = getTransportForAirborne(trainingUnit.type);
                  if (transportType) {
                    unit = this.unitManager.createUnit(transportType, faction, tile);
                    unit.carryingUnit = trainingUnit.type; // Store what unit it's carrying
                    unit.isTransport = true;
                  } else {
                    // Fallback to normal unit creation
                    unit = this.unitManager.createUnit(trainingUnit.type, faction, tile);
                  }
                } else {
                  // Create the unit normally
                  unit = this.unitManager.createUnit(trainingUnit.type, faction, tile);
                }
                unit.hometown = city.name;
                
                // Add to gameState
                this.gameState.units.set(unit.id, unit);
                
                // Add the actual unit ID to unitsProduced
                city.unitsProduced.push(unit.id);
                
                // Mark as successfully spawned
                spawned = true;
                spawnedUnits.push(trainingUnit);
                debug.log(`${unit.name} completed training in ${city.name} (alternative spawn, unit id: ${unit.id})`);
                
                // Update tutorial progress if in tutorial mode and human player trained unit
                // DISABLED: Now handled by the guided tutorial system
                // if (this.isTutorial && faction === this.humanPlayerFaction && this.tutorialStage === 3) {
                //   this.updateTutorialProgress();
                // }
                
                // Queue notification for alternative spawn
                const altNotificationKey = `${city.name}_${unit.type}_${this.gameState.turn}`;
                debug.log(`[ALT NOTIFICATION CHECK] Key: ${altNotificationKey}, Already sent: ${this.processedTrainingThisTurn.has(altNotificationKey)}`);
                // In PvE mode, only send notifications for human player's units
                const shouldSendAltNotification = faction === this.currentFaction && 
                  (!this.selectedGameMode || this.selectedGameMode !== 'PvE' || faction === this.humanPlayerFaction);
                
                if (shouldSendAltNotification && !this.processedTrainingThisTurn.has(altNotificationKey)) {
                  this.processedTrainingThisTurn.add(altNotificationKey);
                  debug.log(`[ALT NOTIFICATION QUEUED] Training complete for ${unit.name} in ${city.name} (alternative spawn)`);
                  this.pendingNotifications.push({
                    type: NotificationType.UNIT_TRAINING_COMPLETE,
                    faction: faction,
                    data: { unitName: unit.name, cityName: city.name },
                    timestamp: Date.now()
                  });
                } else {
                  debug.log(`[ALT NOTIFICATION SKIPPED] Already sent for ${unit.name} in ${city.name} this turn or is AI in PvE`);
                }
                break;
              }
            }
            
            if (!spawned) {
              // Mark as waiting for spawn space (turnsRemaining = 0 means ready to spawn)
              trainingUnit.turnsRemaining = 0;
              debug.log(`Training completed in ${city.name} but no spawn space available - will retry next turn`);
            }
          }
        });
        
        // Remove successfully spawned units from the queue
        debug.log(`Before filtering: ${city.name} has ${city.trainingQueue.length} units in queue`);
        debug.log(`Spawned units count: ${spawnedUnits.length}`);
        
        // Create a set of spawned unit IDs for efficient lookup
        debug.log(`Spawned units details:`, spawnedUnits.map(u => ({
          type: u.type,
          queueId: u.queueId,
          tile: u.tile ? `${u.tile.x},${u.tile.y}` : 'no tile'
        })));
        const spawnedUnitIds = new Set(spawnedUnits.map(u => u.queueId || `${u.type}_${u.tile.x}_${u.tile.y}`));
        debug.log(`Spawned unit IDs set:`, Array.from(spawnedUnitIds));
        
        const newQueue = city.trainingQueue.filter((unit: any) => {
          // Create a unique identifier for this training unit
          const unitId = unit.queueId || `${unit.type}_${unit.tile.x}_${unit.tile.y}`;
          debug.log(`  Checking queue unit: type=${unit.type}, queueId=${unit.queueId}, unitId=${unitId}`);
          
          // If the unit was spawned, ALWAYS remove it from queue regardless of turnsRemaining
          const wasSpawned = spawnedUnitIds.has(unitId);
          if (wasSpawned) {
            debug.log(`  Unit ${unit.type}: REMOVING - was spawned successfully (matched ID: ${unitId})`);
            return false; // Remove from queue
          }
          
          // Keep units that are still training (turnsRemaining > 0)
          // OR units that completed but couldn't spawn (turnsRemaining = 0)
          const shouldKeep = unit.turnsRemaining > 0 || unit.turnsRemaining === 0;
          debug.log(`  Unit ${unit.type}: turnsRemaining=${unit.turnsRemaining}, wasSpawned=${wasSpawned}, keeping=${shouldKeep}`);
          return shouldKeep;
        });
        
        city.trainingQueue = newQueue;
        debug.log(`After filtering: ${city.name} has ${city.trainingQueue.length} units in queue`);
      }
    });
    
    // Save game state after processing training queues
    this.saveGameState();
  }
  
  private showTurnTransition(callback: () => void): void {
    // Create transition overlay
    const overlay = document.createElement('div');
    overlay.id = 'turn-transition';
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: black;
      z-index: 20000;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      opacity: 0;
      transition: opacity 0.5s ease-in-out;
    `;
    
    // Create content container
    const content = document.createElement('div');
    content.style.cssText = `
      text-align: center;
      opacity: 0;
      transition: opacity 0.5s ease-in-out;
    `;
    
    // Add current faction display
    const currentFactionDiv = document.createElement('div');
    currentFactionDiv.innerHTML = `
      <img src="${this.currentFaction === 'United States Of America' ? 'flags/USA.jpg' : 
                 this.currentFaction === 'United Kingdom' ? 'flags/Brits.jpg' : 
                 this.currentFaction === 'USSR' ? 'flags/Soviet.jpg' :
                 this.currentFaction === 'Imperial Japan' ? 'flags/Japs.jpg' :
                 this.currentFaction === 'Canada' ? 'flags/Canadians.jpg' :
                 this.currentFaction === 'Fascist Italy' ? 'flags/Italians.jpg' :
                 this.currentFaction === 'France' ? 'flags/French.jpg' :
                 this.currentFaction === 'Greece' ? 'flags/Greeks.jpg' :
                 'flags/Reich.jpg'}" 
           alt="${this.currentFaction} Flag" 
           style="width: 200px; height: auto; margin-bottom: 20px; border: 2px solid #666;">
      <h2 style="color: ${this.currentFaction === 'United States Of America' ? '#4B5320' : 
                         this.currentFaction === 'United Kingdom' ? '#003F87' :
                         this.currentFaction === 'USSR' ? '#CC0000' :
                         this.currentFaction === 'Imperial Japan' ? '#DC143C' :
                         this.currentFaction === 'Canada' ? '#D2691E' :
                         this.currentFaction === 'Fascist Italy' ? '#009246' :
                         this.currentFaction === 'France' ? '#7A9BB8' :
                         this.currentFaction === 'Greece' ? '#0072CE' :
                         '#708090'}; 
                 font-size: 32px; 
                 margin: 0;
                 text-shadow: 2px 2px 4px rgba(0,0,0,0.8);">
        ${this.currentFaction}
      </h2>
    `;
    content.appendChild(currentFactionDiv);
    
    overlay.appendChild(content);
    document.body.appendChild(overlay);
    
    // Fade in overlay
    requestAnimationFrame(() => {
      overlay.style.opacity = '1';
      setTimeout(() => {
        content.style.opacity = '1';
      }, 300);
    });
    
    // After 1.5 seconds, switch to next faction
    setTimeout(() => {
      // Fade out current faction
      content.style.opacity = '0';
      
      setTimeout(() => {
        // Execute the turn switch callback
        callback();
        
        // Update to show next faction
        const nextFaction = this.currentFaction; // This will be the new faction after callback
        currentFactionDiv.innerHTML = `
          <img src="${nextFaction === 'United States Of America' ? 'flags/USA.jpg' : 
                     nextFaction === 'United Kingdom' ? 'flags/Brits.jpg' : 
                     nextFaction === 'USSR' ? 'flags/Soviet.jpg' :
                     nextFaction === 'Imperial Japan' ? 'flags/Japs.jpg' :
                     nextFaction === 'Canada' ? 'flags/Canadians.jpg' :
                     nextFaction === 'Fascist Italy' ? 'flags/Italians.jpg' :
                     nextFaction === 'France' ? 'flags/French.jpg' :
                     nextFaction === 'Greece' ? 'flags/Greeks.jpg' :
                     'flags/Reich.jpg'}" 
               alt="${nextFaction} Flag" 
               style="width: 200px; height: auto; margin-bottom: 20px; border: 2px solid #666;">
          <h2 style="color: ${nextFaction === 'United States Of America' ? '#4B5320' : 
                             nextFaction === 'United Kingdom' ? '#003F87' :
                             nextFaction === 'USSR' ? '#CC0000' :
                             nextFaction === 'Imperial Japan' ? '#DC143C' :
                             nextFaction === 'Canada' ? '#D2691E' :
                             nextFaction === 'Fascist Italy' ? '#009246' :
                             nextFaction === 'France' ? '#7A9BB8' :
                             nextFaction === 'Greece' ? '#0072CE' :
                             '#708090'}; 
                     font-size: 32px; 
                     margin: 0;
                     text-shadow: 2px 2px 4px rgba(0,0,0,0.8);">
            ${nextFaction}
          </h2>
        `;
        
        // Fade in new content
        setTimeout(() => {
          content.style.opacity = '1';
        }, 300);
      }, 500);
      
      // Add continue button after faction switch
      setTimeout(() => {
        const continueBtn = document.createElement('button');
        continueBtn.textContent = 'Continue';
        continueBtn.style.cssText = `
          position: absolute;
          bottom: 80px;
          left: 50%;
          transform: translateX(-50%);
          padding: 15px 40px;
          font-size: 20px;
          background: #4a4a4a;
          color: white;
          border: 2px solid #666;
          border-radius: 5px;
          cursor: pointer;
          opacity: 0;
          transition: all 0.3s ease;
        `;
        continueBtn.onmouseover = () => {
          continueBtn.style.background = '#5a5a5a';
          continueBtn.style.borderColor = '#888';
        };
        continueBtn.onmouseout = () => {
          continueBtn.style.background = '#4a4a4a';
          continueBtn.style.borderColor = '#666';
        };
        continueBtn.onclick = () => {
          // Check if this is the second faction's first turn and show their briefing
          if (!this.hasShownSecondBriefing && this.currentFaction === this.secondFaction) {
            this.hasShownSecondBriefing = true;
            // Show second faction's briefing immediately, before removing overlay
            this.showFactionBriefing(this.secondFaction, () => {
              // Only remove overlay after briefing is shown
              overlay.style.opacity = '0';
              setTimeout(() => {
                overlay.remove();
                // Notifications are now handled by NotificationManager
              }, 500);
            });
          } else {
            // Normal continue
            overlay.style.opacity = '0';
            setTimeout(() => {
              overlay.remove();
              // Notifications are now handled by NotificationManager
            }, 500);
          }
        };
        
        content.appendChild(continueBtn);
        
        // Fade in button
        setTimeout(() => {
          continueBtn.style.opacity = '1';
        }, 300);
      }, 1300);
    }, 1500);
  }

  private endTurn(): void {
    debug.log('End turn clicked - isOnlineGame:', this.isOnlineGame, 'multiplayerService:', !!this.multiplayerService);
    
    // Check if end turn confirmation is enabled
    const endTurnConfirmation = localStorage.getItem('endTurnConfirmation') !== 'false';
    
    if (endTurnConfirmation) {
      // Show end turn confirmation dialog
      this.showEndTurnConfirmation();
    } else {
      // Skip confirmation and end turn directly
      this.executeEndTurn();
    }
  }

  private showEndTurnConfirmation(): void {
    const movableUnits = this.getMovableUnits();
    const buildableUnits = this.getBuildableUnits();
    
    const hasMovableUnits = movableUnits.length > 0;
    const hasBuildableUnits = buildableUnits.length > 0;
    
    const overlay = document.createElement('div');
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.7);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 10000;
    `;
    
    const dialog = document.createElement('div');
    dialog.style.cssText = `
      background: #2a2a2a;
      border: 2px solid #4a4a4a;
      border-radius: 10px;
      padding: 20px;
      max-width: 500px;
      max-height: 70vh;
      overflow-y: auto;
      color: white;
      font-family: Arial, sans-serif;
    `;
    
    let content = `
      <h3 style="margin: 0 0 15px 0; color: #ffdd44;">End Turn Confirmation</h3>
      <p style="margin: 0 0 15px 0;">Are you sure you would like to end your turn?</p>
    `;
    
    if (hasMovableUnits) {
      content += `
        <div style="margin-bottom: 15px;">
          <h4 style="margin: 0 0 10px 0; color: #ff6b6b;">Units that can still move:</h4>
          <ul style="margin: 0; padding-left: 20px;">
      `;
      movableUnits.forEach(unit => {
        const unitDef = UNIT_DEFINITIONS[unit.type];
        content += `<li>${unitDef.name} at (${unit.position.x}, ${unit.position.y}) - ${unit.movementRemaining} movement left</li>`;
      });
      content += `</ul></div>`;
    }
    
    if (hasBuildableUnits) {
      content += `
        <div style="margin-bottom: 15px;">
          <h4 style="margin: 0 0 10px 0; color: #4ecdc4;">Cities that can build units:</h4>
          <ul style="margin: 0; padding-left: 20px;">
      `;
      buildableUnits.forEach(city => {
        content += `<li>${city.name} - Can build ${city.availableUnits.length} unit type${city.availableUnits.length > 1 ? 's' : ''}</li>`;
      });
      content += `</ul></div>`;
    }
    
    content += `
      <div style="display: flex; gap: 10px; justify-content: flex-end; margin-top: 20px;">
        <button id="cancel-end-turn" style="
          background: #666;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 5px;
          cursor: pointer;
          font-size: 14px;
        ">Cancel</button>
        <button id="confirm-end-turn" style="
          background: #d32f2f;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 5px;
          cursor: pointer;
          font-size: 14px;
        ">End Turn</button>
      </div>
    `;
    
    dialog.innerHTML = content;
    overlay.appendChild(dialog);
    document.body.appendChild(overlay);
    
    // Add event listeners
    document.getElementById('cancel-end-turn')?.addEventListener('click', () => {
      document.body.removeChild(overlay);
    });
    
    document.getElementById('confirm-end-turn')?.addEventListener('click', () => {
      document.body.removeChild(overlay);
      this.executeEndTurn();
    });
  }

  private executeEndTurn(): void {
    debug.log('Turn ended - isOnlineGame:', this.isOnlineGame, 'isMyTurn:', this.isMyTurn);
    console.log(`executeEndTurn: isTutorial=${this.isTutorial}, gameMode=${this.gameState?.gameMode}, currentFaction=${this.currentFaction}`);
    
    // Check if it's my turn in online games
    if (this.isOnlineGame && !this.isMyTurn) {
      debug.log('Not my turn! currentFaction:', this.gameState.currentFaction, 'humanPlayerFaction:', this.humanPlayerFaction);
      this.soundManager.playError();
      this.showNotification("It's not your turn!");
      return;
    }
    
    // Play end turn sound
    this.soundManager.playEndTurn();
    
    // Update tutorial progress if in tutorial mode and human player ended turn
    // DISABLED: Now handled by the guided tutorial system
    // if (this.isTutorial && this.gameState.currentFaction === this.humanPlayerFaction && this.tutorialStage === 6) {
    //   this.updateTutorialProgress();
    // }
    
    // Handle online games
    console.log(`Checking conditions: isOnlineGame=${this.isOnlineGame}, gameMode=${this.gameState?.gameMode}, isTutorial=${this.isTutorial}`);
    if (this.isOnlineGame) {
      debug.log('Processing online end turn - multiplayerService exists:', !!this.multiplayerService);
      // Don't execute turn switch locally - let Firebase sync handle it
      
      // Submit end turn to Firebase
      if (this.multiplayerService) {
        debug.log('Sending end turn to Firebase');
        // Don't send individual endTurn moves - just update the full game state after processing
        
        // Determine next faction
        let nextFaction: FactionType;
        if (this.selectedCampaign === CampaignType.TUNISIA) {
          nextFaction = this.gameState.currentFaction === 'United Kingdom' ? '3rd Reich' : 'United Kingdom';
        } else if (this.selectedCampaign === CampaignType.STALINGRAD) {
          nextFaction = this.gameState.currentFaction === 'USSR' ? '3rd Reich' : 'USSR';
        } else if (this.selectedCampaign === CampaignType.OKINAWA) {
          nextFaction = this.gameState.currentFaction === 'United States Of America' ? 'Imperial Japan' : 'United States Of America';
        } else if (this.selectedCampaign === CampaignType.CAMPOBASSO) {
          nextFaction = this.gameState.currentFaction === 'Canada' ? 'Fascist Italy' : 'Canada';
        } else if (this.selectedCampaign === CampaignType.FRANCE_1940) {
          nextFaction = this.gameState.currentFaction === 'France' ? '3rd Reich' : 'France';
        } else if (this.selectedCampaign === CampaignType.ALBANIA_1940) {
          nextFaction = this.gameState.currentFaction === 'Greece' ? 'Fascist Italy' : 'Greece';
        } else {
          nextFaction = this.gameState.currentFaction === 'United States Of America' ? '3rd Reich' : 'United States Of America';
        }
        
        // Process turn-end actions before sending to Firebase
        // Movement reset now happens at the START of each faction's turn, not at the end
        // This ensures units have their movement when their turn actually begins
        
        // Don't process city captures here - they happen at the start of turn
        // Don't process training here - it happens at the start of turn
        
        // Add resources for cities owned by the faction ending their turn
        // Use calculateIncome to ensure consistent resource calculation
        const income = this.calculateIncome(this.currentFaction);
        if (income > 0) {
          const currentResources = this.gameState.resources.get(this.currentFaction) || 0;
          this.gameState.resources.set(this.currentFaction, currentResources + income);
          
          // Track resource earnings in battle statistics
          const factionStats = this.gameState.battleStats?.get(this.currentFaction);
          if (factionStats) {
            factionStats.resourcesEarned += income;
          }
        }
        
        // Update local state
        this.gameState.currentFaction = nextFaction;
        this.gameState.turn++;
        
        // Update game state in Firebase with the processed state
        const completeGameState = this.buildCompleteGameState();
        this.multiplayerService.updateGameState(
          completeGameState,
          nextFaction,
          this.gameState.turn
        );
      } else {
        debug.error('MultiplayerService is null! Cannot end turn.');
        this.showNotification('Error: Connection lost. Please reload the game.');
      }
      
      // Don't set isMyTurn here - let the Firebase sync handle it
      // The sync callback will properly set isMyTurn based on the new currentFaction
    }
    // Check if this is Tutorial mode FIRST (tutorials are technically PvE but need special handling)
    else if (this.isTutorial) {
      // In tutorial, check who's ending their turn
      const endingFaction = this.currentFaction;
      console.log(`Tutorial turn end - ending faction: ${endingFaction}`);
      
      // Execute turn switch without transition
      this.executeTurnSwitch();
      console.log(`Tutorial turn switched - new faction: ${this.currentFaction}`);
      
      // If US just ended their turn, we switched to 3rd Reich - execute AI
      if (endingFaction === 'United States Of America' && this.currentFaction === '3rd Reich') {
        console.log('Tutorial: Executing AI turn for 3rd Reich');
        this.executeAITurn();
      }
      // If 3rd Reich just ended their turn, we switched to US - show transition for player
      else if (endingFaction === '3rd Reich' && this.currentFaction === 'United States Of America') {
        console.log('Tutorial: Switched back to US player turn');
        // Don't execute AI, the player needs to play
        // The turn has already been switched, UI will update automatically via gameLoop
      }
    }
    // Check if this is PvE mode and next turn is AI (non-tutorial PvE)
    else if (this.gameState.gameMode === 'PvE' && this.currentFaction !== this.gameState.aiControlledFaction) {
      // Execute turn switch without transition
      this.executeTurnSwitch();

      // Start AI turn execution with small delay to ensure movement is reset
      // V3 AI: Execute even if aiController is null - executeAITurn handles the placeholder
      setTimeout(() => {
        this.executeAITurn();
      }, 100);
    } else {
      // Show transition screen for PvP or when switching back to player
      console.log('Falling through to ELSE branch - showing turn transition screen');
      this.showTurnTransition(() => {
        // All the turn switching logic happens while screen is black
        this.executeTurnSwitch();
      });
    }
  }

  private getMovableUnits(): Unit[] {
    const movableUnits: Unit[] = [];
    const currentFactionUnits = this.unitManager.getUnitsForFaction(this.currentFaction);
    
    for (const unit of currentFactionUnits) {
      if (unit.movementRemaining > 0) {
        // Check if unit has valid moves by calculating available moves
        const hasValidMoves = this.unitHasValidMoves(unit);
        if (hasValidMoves) {
          movableUnits.push(unit);
        }
      }
    }
    
    return movableUnits;
  }

  private unitHasValidMoves(unit: Unit): boolean {
    const visited = new Map<string, number>();
    const queue: { pos: Position, cost: number }[] = [];
    
    // Apply shellshocked movement penalty
    let effectiveMovement = unit.movementRemaining;
    if (unit.shellshocked && unit.shellshocked.turnsRemaining > 0) {
      effectiveMovement = Math.floor(effectiveMovement * 0.5); // 50% movement when shellshocked
    }
    
    // Start from unit's current position with 0 cost
    queue.push({ pos: unit.position, cost: 0 });
    visited.set(`${unit.position.x},${unit.position.y}`, 0);
    
    // Use Dijkstra's algorithm to find any valid move
    while (queue.length > 0) {
      queue.sort((a, b) => a.cost - b.cost);
      const { pos, cost } = queue.shift()!;
      
      const key = `${pos.x},${pos.y}`;
      if (visited.has(key) && visited.get(key)! < cost) {
        continue;
      }
      
      // Check all adjacent tiles (8 directions)
      const directions = [
        { dx: 0, dy: -1 }, { dx: 1, dy: -1 }, { dx: 1, dy: 0 }, { dx: 1, dy: 1 },
        { dx: 0, dy: 1 }, { dx: -1, dy: 1 }, { dx: -1, dy: 0 }, { dx: -1, dy: -1 }
      ];
      
      for (const { dx, dy } of directions) {
        const newX = pos.x + dx;
        const newY = pos.y + dy;
        const newKey = `${newX},${newY}`;
        
        const tile = this.map.getTile(newX, newY);
        if (tile && !this.unitManager.getUnitAt({ x: newX, y: newY })) {
          const terrainCost = this.getTerrainCost(tile.terrain, unit);
          const totalCost = cost + terrainCost;
          
          if (totalCost <= effectiveMovement && (!visited.has(newKey) || visited.get(newKey)! > totalCost)) {
            // If this is not the starting position, we found a valid move
            if (!(newX === unit.position.x && newY === unit.position.y)) {
              return true;
            }
            
            visited.set(newKey, totalCost);
            queue.push({ pos: { x: newX, y: newY }, cost: totalCost });
          }
        }
      }
    }
    
    return false;
  }

  private getBuildableUnits(): Array<{ name: string, availableUnits: UnitType[] }> {
    const buildableUnits: Array<{ name: string, availableUnits: UnitType[] }> = [];
    const cities = this.map.getCities();
    const resources = this.gameState.resources.get(this.currentFaction) || 0;
    
    for (const city of cities.values()) {
      if (city.faction === this.currentFaction) {
        // Check if city has capacity for more units
        const actualUnits = city.unitsProduced.filter((id: string) => !id.startsWith('queue_'));
        const unitsInTraining = (city.trainingQueue || []).length;
        const slotsAvailable = city.maxUnits - (actualUnits.length + unitsInTraining);
        if (slotsAvailable > 0) {
          // Check if there's space in the city for units to spawn
          const hasSpawnSpace = city.tiles.some(tile => !this.unitManager.getUnitAt(tile));
          if (hasSpawnSpace) {
            // Check if there are no enemy units in the city
            const unitsInCity = this.getUnitsInCity(city);
            const enemyUnits = unitsInCity.filter(u => u.faction !== this.currentFaction);
            if (enemyUnits.length === 0) {
              // Get available units for this faction that this city can train based on its level
              const allAvailableUnits = getUnitsForFaction(this.currentFaction, this.selectedCampaign);
              // Filter by city level first
              const cityLevelUnits = allAvailableUnits.filter(unitType => canCityTrainUnit(city, unitType));
              // Then filter by affordability
              const affordableUnits = cityLevelUnits.filter(unitType => {
                const cost = UNIT_DEFINITIONS[unitType].cost;
                return resources >= cost;
              });
              
              if (affordableUnits.length > 0) {
                buildableUnits.push({
                  name: city.name,
                  availableUnits: affordableUnits
                });
              }
            }
          }
        }
      }
    }
    
    return buildableUnits;
  }
  
  private async executeAITurn(): Promise<void> {
    // === V3 AI TURN EXECUTION ===
    this.isAITurn = true;

    // Show AI turn indicator
    const aiIndicator = document.createElement('div');
    aiIndicator.id = 'ai-turn-indicator';
    aiIndicator.style.cssText = `
      position: fixed;
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      background: rgba(180, 30, 30, 0.9);
      color: white;
      padding: 15px 30px;
      border-radius: 5px;
      font-size: 20px;
      font-weight: bold;
      z-index: 1000;
      box-shadow: 0 4px 6px rgba(0,0,0,0.3);
    `;
    aiIndicator.textContent = 'AI Turn - Planning';
    document.body.appendChild(aiIndicator);

    // V3 AI Execution
    if (this.aiController) {
      // Update AI with current game state
      this.aiController.update(this.gameState, this.unitManager, this.map);

      // Allow the AI turn indicator to render before planning
      await new Promise(resolve => setTimeout(resolve, 50));

      // Plan the turn (generates decision queue)
      // Use setTimeout to prevent browser from becoming unresponsive
      await new Promise<void>(resolve => {
        setTimeout(() => {
          this.aiController!.planTurn();
          resolve();
        }, 0);
      });

      // Update indicator to show we're now executing
      aiIndicator.textContent = 'AI Turn - Executing';
      await new Promise(resolve => setTimeout(resolve, 50));

      // Process AI decisions with animations
      while (this.aiController.hasMoreDecisions()) {
        const decision = this.aiController.getNextDecision();
        if (!decision) break;

        switch (decision.type) {
          case 'move':
            if (decision.unit && decision.target) {
              await this.moveAIUnit(decision.unit, decision.target);
            }
            break;

          case 'attack':
            if (decision.unit && decision.targetUnit) {
              await this.performAIAttack(decision.unit, decision.targetUnit.position);
            }
            break;

          case 'build':
            if (decision.city && decision.unitType) {
              await this.buildAIUnit(decision.unitType, decision.city);
            }
            break;

          case 'upgrade_city':
            if (decision.city && decision.upgradeToLevel) {
              await this.upgradeAICity(decision.city, decision.upgradeToLevel);
            }
            break;

          case 'hunker':
            if (decision.unit) {
              await this.performAIHunker(decision.unit);
            }
            break;

          case 'heal':
            if (decision.unit && decision.targetUnit) {
              await this.performAIHeal(decision.unit, decision.targetUnit);
            }
            break;

          case 'repair':
            if (decision.unit && decision.targetUnit) {
              await this.performAIRepair(decision.unit, decision.targetUnit);
            }
            break;

          case 'board_transport':
            if (decision.unit && decision.targetUnit) {
              await this.performAIBoardTransport(decision.unit, decision.targetUnit);
            }
            break;

          case 'disembark':
            if (decision.unit && decision.target) {
              await this.performAIDisembark(decision.unit, decision.target);
            }
            break;

          case 'tow_artillery':
            if (decision.unit && decision.targetUnit) {
              await this.performAITowArtillery(decision.unit, decision.targetUnit);
            }
            break;

          case 'untow_artillery':
            if (decision.unit) {
              await this.performAIUntowArtillery(decision.unit);
            }
            break;

          case 'end_turn':
            // End of AI actions
            break;
        }

        // Small delay between actions for visibility
        await this.delay(300);
      }

      console.log(`[V3 AI] ${this.aiController.getDebugInfo()}`);
    } else {
      // No AI controller - just wait briefly
      await this.delay(500);
      console.log('[V3 AI] No AI controller - skipping AI turn');
    }

    // Remove AI indicator
    aiIndicator.remove();
    this.isAITurn = false;

    // Switch back to player without transition screen
    this.executeTurnSwitch();

    // Notifications are now handled by NotificationManager
  }
  
  private async moveAIUnit(unit: Unit, target: Position): Promise<void> {
    // Get fresh unit data to ensure we have current movement points
    const freshUnit = this.unitManager.getUnit(unit.id);
    if (!freshUnit) return;

    // Don't use the UI selection system - move directly
    const targetTile = this.map.getTile(target.x, target.y);
    if (!targetTile) return;

    // Check if target is occupied
    const unitAtTarget = this.unitManager.getUnitAt(target);
    if (unitAtTarget) return;

    // Calculate path cost using fresh unit data
    const pathCost = this.calculatePathCost(freshUnit.position, target, freshUnit);

    if (pathCost <= 0 || pathCost > freshUnit.movementRemaining) return;

    // Move the unit directly without UI selection
    if (this.unitManager.moveUnit(freshUnit.id, target, pathCost)) {
      // Clear vision cache since unit positions changed
      this.visionSystem.clearCache();
      
      // Play movement sound
      this.soundManager.playMove();
      
      // Save game state after AI move
      this.saveGameState();
      
      // Wait for movement animation
      await this.delay(500);
      
      // Check for city capture
      const updatedUnit = this.unitManager.getUnit(unit.id);
      if (updatedUnit) {
        this.checkAndStartCapture(updatedUnit);
      }
    }
  }
  
  private async performAIAttack(attacker: Unit, targetPos: Position): Promise<void> {
    const defender = this.unitManager.getUnitAt(targetPos);
    if (!defender || defender.faction === attacker.faction || attacker.hasAttacked) return;
    
    // Check if defender is already dead
    if (defender.stats.health <= 0) {
      debug.log(`AI: Skipping attack on already dead unit`);
      return;
    }
    
    // Check range
    const distance = Math.max(
      Math.abs(targetPos.x - attacker.position.x),
      Math.abs(targetPos.y - attacker.position.y)
    );
    if (distance > attacker.stats.range) return;
    
    // Perform attack
    this.attackUnit(attacker, defender);
    
    // Wait longer if unit was killed to allow death animation
    if (defender.stats.health <= 0) {
      await this.delay(1500); // Wait for death animation
    } else {
      await this.delay(500); // Normal attack animation
    }
  }
  
  private async buildAIUnit(unitType: UnitType, city: any): Promise<void> {
    // Use existing buildUnit logic
    this.buildUnit(unitType, city);
    await this.delay(300);
  }
  
  private async upgradeAICity(city: any, toLevel: CityLevel): Promise<void> {
    // Use existing upgradeCity logic
    this.upgradeCity(city, toLevel);
    await this.delay(300);
  }
  
  private async performAIHunker(unit: Unit): Promise<void> {
    // Check if unit is already hunkered
    if ((unit as any).isHunkered) {
      debug.log(`AI: ${unit.name} is already hunkered`);
      return;
    }
    
    // Check if unit has already changed stance this turn
    if ((unit as any).hunkerChangedThisTurn) {
      debug.log(`AI: ${unit.name} already changed stance this turn`);
      return;
    }
    
    // Hunker the unit
    (unit as any).isHunkered = true;
    (unit as any).hunkerChangedThisTurn = true;
    
    // Add defense bonus
    unit.stats.defense += 2;
    
    // Remove all movement
    unit.movementRemaining = 0;
    
    // Log the action
    debug.log(`AI: ${unit.name} hunkered down for +2 defense and health regeneration`);
    
    // Save game state
    this.saveGameState();
    
    // Small delay for visual effect
    await this.delay(300);
  }

  private async performAIHeal(healer: Unit, target: Unit): Promise<void> {
    // Check if healer is a medic
    if (healer.category !== UnitCategory.MEDICS) {
      debug.log(`AI: ${healer.name} is not a medic, cannot heal`);
      return;
    }

    // Check if target is infantry (can be healed)
    if (target.category !== UnitCategory.INFANTRY) {
      debug.log(`AI: ${target.name} is not infantry, cannot be healed`);
      return;
    }

    // Check if healer has already healed this turn
    if ((healer as any).hasHealed) {
      debug.log(`AI: ${healer.name} has already healed this turn`);
      return;
    }

    // Calculate heal amount (typically 50% of max health)
    const healAmount = Math.floor(target.stats.maxHealth * 0.5);
    const healCost = Math.floor(healAmount * 0.5); // Cost is half the heal amount

    // Check if AI has enough resources
    const currentResources = this.gameState.resources.get(this.currentFaction) || 0;
    if (currentResources < healCost) {
      debug.log(`AI: Not enough resources to heal (need ${healCost}, have ${currentResources})`);
      return;
    }

    // Deduct resources
    this.gameState.resources.set(this.currentFaction, currentResources - healCost);

    // Heal the target
    target.stats.health = Math.min(target.stats.health + healAmount, target.stats.maxHealth);

    // Mark healer as having healed this turn
    (healer as any).hasHealed = true;

    // Track statistics
    const factionStats = this.gameState.battleStats?.get(this.currentFaction);
    if (factionStats) {
      factionStats.unitsHealed++;
      factionStats.resourcesSpent += healCost;
    }

    debug.log(`AI: ${healer.name} healed ${target.name} for ${healAmount} HP (cost: ${healCost})`);

    // Play sound
    this.soundManager.playClick();

    // Update displays
    this.updateResourceDisplay();

    // Save game state
    this.saveGameState();

    // Small delay for visual effect
    await this.delay(300);
  }

  private async performAIRepair(engineer: Unit, target: Unit): Promise<void> {
    // Check if engineer is an engineer unit
    if (engineer.category !== UnitCategory.ENGINEERS) {
      debug.log(`AI: ${engineer.name} is not an engineer, cannot repair`);
      return;
    }

    // Check if target is a vehicle (can be repaired)
    const isVehicle = target.category === UnitCategory.ARMOR ||
                      target.category === UnitCategory.RECONNAISSANCE ||
                      target.category === UnitCategory.ARTILLERY ||
                      target.category === UnitCategory.TRANSPORT;

    if (!isVehicle) {
      debug.log(`AI: ${target.name} is not a vehicle, cannot be repaired`);
      return;
    }

    // Check if engineer has already repaired this turn
    if ((engineer as any).hasHealed) {
      debug.log(`AI: ${engineer.name} has already repaired this turn`);
      return;
    }

    // Calculate repair amount (typically 50% of max health)
    const repairAmount = Math.floor(target.stats.maxHealth * 0.5);
    const repairCost = Math.floor(repairAmount * 0.5); // Cost is half the repair amount

    // Check if AI has enough resources
    const currentResources = this.gameState.resources.get(this.currentFaction) || 0;
    if (currentResources < repairCost) {
      debug.log(`AI: Not enough resources to repair (need ${repairCost}, have ${currentResources})`);
      return;
    }

    // Deduct resources
    this.gameState.resources.set(this.currentFaction, currentResources - repairCost);

    // Repair the target
    target.stats.health = Math.min(target.stats.health + repairAmount, target.stats.maxHealth);

    // Mark engineer as having repaired this turn
    (engineer as any).hasHealed = true;

    // Track statistics
    const factionStats = this.gameState.battleStats?.get(this.currentFaction);
    if (factionStats) {
      factionStats.unitsRepaired++;
      factionStats.resourcesSpent += repairCost;
    }

    debug.log(`AI: ${engineer.name} repaired ${target.name} for ${repairAmount} HP (cost: ${repairCost})`);

    // Play sound
    this.soundManager.playClick();

    // Update displays
    this.updateResourceDisplay();

    // Save game state
    this.saveGameState();

    // Small delay for visual effect
    await this.delay(300);
  }

  private async performAIBoardTransport(unit: Unit, transport: Unit): Promise<void> {
    // Check if unit can board
    if (!canBeTransported(unit.type)) {
      debug.log(`AI: ${unit.name} cannot be transported`);
      return;
    }
    
    // Check distance
    const distance = Math.max(
      Math.abs(unit.position.x - transport.position.x),
      Math.abs(unit.position.y - transport.position.y)
    );
    
    if (distance > 1.5) {
      debug.log(`AI: ${unit.name} too far from transport to board`);
      return;
    }
    
    // Check transport capacity
    if (isTruck(transport.type)) {
      // Trucks can carry 2 units
      if (!transport.carryingUnits) {
        transport.carryingUnits = [];
        transport.carryingUnitsData = [];
      }
      if (transport.carryingUnits.length >= 2) {
        debug.log(`AI: Transport is full`);
        return;
      }
      // Store both the type and the actual unit data
      transport.carryingUnits.push(unit.type);
      transport.carryingUnitsData!.push({...unit}); // Clone the unit data
      debug.log(`AI: ${unit.name} boarded truck (${transport.carryingUnits.length}/2)`);
    } else {
      // Other transports carry 1 unit
      if (transport.carryingUnit) {
        debug.log(`AI: Transport already carrying a unit`);
        return;
      }
      // Store both the type and the actual unit data
      transport.carryingUnit = unit.type;
      transport.carryingUnitData = {...unit}; // Clone the unit data
      debug.log(`AI: ${unit.name} boarded ${transport.name}`);
    }
    
    // Remove the unit from the map (but data is preserved in transport)
    this.unitManager.removeUnit(unit.id);
    
    // Clear vision cache
    this.visionSystem.clearCache();
    
    // Save game state
    this.saveGameState();
    
    // Small delay for visual effect
    await this.delay(300);
  }
  
  private async performAIDisembark(transport: Unit, target: Position): Promise<void> {
    // Check if transport has units to disembark
    if (!transport.carryingUnit && (!transport.carryingUnits || transport.carryingUnits.length === 0)) {
      debug.log(`AI: Transport has no units to disembark`);
      return;
    }
    
    // Check if target tile is valid
    const targetTile = this.map.getTile(target.x, target.y);
    if (!targetTile) {
      debug.log(`AI: Invalid disembark position`);
      return;
    }
    
    // Check if target is occupied
    const unitAtTarget = this.unitManager.getUnitAt(target);
    if (unitAtTarget) {
      debug.log(`AI: Cannot disembark - position occupied`);
      return;
    }
    
    // Disembark units
    if (transport.carryingUnitData) {
      // Single unit transport - restore the preserved unit data
      const restoredUnit = transport.carryingUnitData;
      const newUnit = this.unitManager.createUnit(
        restoredUnit.type,
        restoredUnit.faction,
        target
      );
      // Restore preserved attributes
      newUnit.name = restoredUnit.name;
      newUnit.stats = {...restoredUnit.stats};
      newUnit.hometown = restoredUnit.hometown;
      newUnit.level = restoredUnit.level;
      newUnit.movementRemaining = 0; // Can't move after disembarking
      newUnit.hasAttacked = true; // Can't attack after disembarking
      
      // Add to gameState
      this.gameState.units.set(newUnit.id, newUnit);
      
      transport.carryingUnit = undefined;
      transport.carryingUnitData = undefined;
      debug.log(`AI: Disembarked ${newUnit.name} at (${target.x},${target.y})`);
    } else if (transport.carryingUnitsData && transport.carryingUnitsData.length > 0) {
      // Truck with multiple units - disembark first one
      const restoredUnit = transport.carryingUnitsData.shift();
      transport.carryingUnits!.shift(); // Also remove from type array
      
      if (restoredUnit) {
        const newUnit = this.unitManager.createUnit(
          restoredUnit.type,
          restoredUnit.faction,
          target
        );
        // Restore preserved attributes
        newUnit.name = restoredUnit.name;
        newUnit.stats = {...restoredUnit.stats};
        newUnit.hometown = restoredUnit.hometown;
        newUnit.level = restoredUnit.level;
        newUnit.movementRemaining = 0;
        newUnit.hasAttacked = true; // Can't attack after disembarking
        
        // Add to gameState
        this.gameState.units.set(newUnit.id, newUnit);
        
        debug.log(`AI: Disembarked ${newUnit.name} at (${target.x},${target.y})`);
      }
    } else if (transport.carryingUnit) {
      // Fallback for old saves without carryingUnitData
      const newUnit = this.unitManager.createUnit(
        transport.carryingUnit,
        transport.faction,
        target
      );
      newUnit.movementRemaining = 0;
      newUnit.hasAttacked = true; // Can't attack after disembarking
      
      // Add to gameState
      this.gameState.units.set(newUnit.id, newUnit);
      
      transport.carryingUnit = undefined;
      debug.log(`AI: Disembarked ${newUnit.name} at (${target.x},${target.y})`);
    }
    
    // Clear vision cache
    this.visionSystem.clearCache();
    
    // Save game state
    this.saveGameState();
    
    // Small delay for visual effect
    await this.delay(300);
  }
  
  
  private async performAITowArtillery(transport: Unit, artillery: Unit): Promise<void> {
    // Check if transport can tow
    if (transport.towingUnit || transport.hasTowed) {
      debug.log(`AI: Transport cannot tow - already towing or has towed`);
      return;
    }
    
    // Check distance
    const distance = Math.abs(transport.position.x - artillery.position.x) + 
                    Math.abs(transport.position.y - artillery.position.y);
    if (distance > 1) {
      debug.log(`AI: Artillery too far to tow (distance: ${distance})`);
      return;
    }
    
    // Store artillery data in transport (like boarding)
    transport.towingUnit = artillery.type;
    transport.towingUnitData = {...artillery}; // Clone the artillery data
    transport.hasTowed = true;
    
    // Remove the artillery from the map
    this.unitManager.removeUnit(artillery.id);
    this.gameState.units.delete(artillery.id);
    
    debug.log(`AI: ${transport.name} started towing ${artillery.name}`);
    
    // Clear vision cache
    this.visionSystem.clearCache();
    
    // Save game state
    this.saveGameState();
    
    // Small delay for visual effect
    await this.delay(300);
  }
  
  private async performAIUntowArtillery(transport: Unit): Promise<void> {
    // Check if transport is towing
    if (!transport.towingUnit || !transport.towingUnitData) {
      debug.log(`AI: Transport is not towing any unit`);
      return;
    }
    
    // Find an adjacent tile to place the artillery
    const adjacentTiles = this.getAdjacentTiles(transport.position);
    let deployPosition: Position | null = null;
    
    for (const tile of adjacentTiles) {
      if (!this.unitManager.getUnitAt(tile) && this.map.getTile(tile.x, tile.y)) {
        deployPosition = tile;
        break;
      }
    }
    
    if (!deployPosition) {
      debug.log(`AI: No valid position to untow artillery`);
      return;
    }
    
    // Create the artillery unit at the deploy position
    const artillery = this.unitManager.createUnit(
      transport.towingUnit,
      transport.faction,
      deployPosition
    );
    
    // Restore preserved data
    if (transport.towingUnitData) {
      artillery.name = transport.towingUnitData.name;
      artillery.stats = {...transport.towingUnitData.stats};
      artillery.hometown = transport.towingUnitData.hometown;
      artillery.level = transport.towingUnitData.level;
      artillery.movementRemaining = 0; // Can't move after untowing
      artillery.hasAttacked = true; // Can't attack after untowing
    }
    
    // Add to gameState
    this.gameState.units.set(artillery.id, artillery);
    
    // Clear towing data from transport
    transport.towingUnit = undefined;
    transport.towingUnitData = undefined;
    transport.hasTowed = true; // Mark that untowing action was used this turn
    
    debug.log(`AI: ${transport.name} untowed ${artillery.name} at (${deployPosition.x},${deployPosition.y})`);
    
    // Clear vision cache
    this.visionSystem.clearCache();
    
    // Save game state
    this.saveGameState();
    
    // Small delay for visual effect
    await this.delay(300);
  }
  
  private getAdjacentTiles(position: Position): Position[] {
    const tiles: Position[] = [];
    const directions = [
      { x: 0, y: -1 }, // Up
      { x: 1, y: 0 },  // Right
      { x: 0, y: 1 },  // Down
      { x: -1, y: 0 }  // Left
    ];
    
    for (const dir of directions) {
      const newPos = {
        x: position.x + dir.x,
        y: position.y + dir.y
      };
      
      if (newPos.x >= 0 && newPos.x < this.map.getWidth() && 
          newPos.y >= 0 && newPos.y < this.map.getHeight()) {
        tiles.push(newPos);
      }
    }
    
    return tiles;
  }
  
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
  
  // Utility method for getting player faction - kept for future use
  // private getPlayerFaction(): FactionType {
  //   if (this.gameState.gameMode === 'PvE' && this.humanPlayerFaction) {
  //     return this.humanPlayerFaction;
  //   }
  //   return this.currentFaction;
  // }
  
  private executeTurnSwitch(): void {
    // Reset unit cycle index when turn switches
    this.unitCycleIndex = -1;
    
    // Track turn end in analytics
    this.analytics.trackTurnEnd(this.currentFaction, this.gameState.turn);
    
    // Notifications are now handled by NotificationManager
    
    // In online games, training is processed in the Firebase sync handler
    // when it becomes a player's turn
    if (this.isOnlineGame) {
      return; // Don't do camera switching or other local turn changes
    }
    
    // Update turn counter
    this.gameState.turn++;
    const turnCounter = document.getElementById('turn-counter');
    if (turnCounter) {
      turnCounter.textContent = `Turn ${this.gameState.turn}`;
    }
    
    // Switch factions first (only for local games)
    if (this.selectedCampaign === CampaignType.TUTORIAL) {
      // Tutorial mode - alternate between player and AI faction normally
      // AI will have no units/cities until spawned at the appropriate tutorial stage
      this.currentFaction = this.currentFaction === 'United States Of America' ? '3rd Reich' : 'United States Of America';
      console.log(`🔄 TUTORIAL TURN SWITCH: Now ${this.currentFaction} turn (stage ${this.tutorialStage})`);
      
      // If it's AI turn but before stage 4, they have no units - turn will be quick
      if (this.currentFaction === '3rd Reich' && this.tutorialStage < 4) {
        console.log(`  - AI has no units yet (waiting for stage 4)`);
      }
    } else if (this.selectedCampaign === CampaignType.TUNISIA) {
      // Tunisia has UK vs 3rd Reich
      this.currentFaction = this.currentFaction === 'United Kingdom' ? '3rd Reich' : 'United Kingdom';
    } else if (this.selectedCampaign === CampaignType.STALINGRAD) {
      // Stalingrad has USSR vs 3rd Reich
      this.currentFaction = this.currentFaction === 'USSR' ? '3rd Reich' : 'USSR';
    } else if (this.selectedCampaign === CampaignType.OKINAWA) {
      // Okinawa has US vs Imperial Japan
      this.currentFaction = this.currentFaction === 'United States Of America' ? 'Imperial Japan' : 'United States Of America';
    } else if (this.selectedCampaign === CampaignType.CAMPOBASSO) {
      // Campobasso has Canada vs Fascist Italy
      this.currentFaction = this.currentFaction === 'Canada' ? 'Fascist Italy' : 'Canada';
    } else if (this.selectedCampaign === CampaignType.FRANCE_1940) {
      // France 1940 has France vs 3rd Reich
      this.currentFaction = this.currentFaction === 'France' ? '3rd Reich' : 'France';
    } else if (this.selectedCampaign === CampaignType.ALBANIA_1940) {
      // Albania 1940 has Greece vs Fascist Italy
      this.currentFaction = this.currentFaction === 'Greece' ? 'Fascist Italy' : 'Greece';
    } else {
      // Normandy has US vs 3rd Reich
      this.currentFaction = this.currentFaction === 'United States Of America' ? '3rd Reich' : 'United States Of America';
    }
    this.gameState.currentFaction = this.currentFaction;
    
    // Reset unit movement for new faction FIRST
    this.unitManager.resetUnitMovement(this.currentFaction);
    
    // Process hunkered units (heal them)
    this.processHunkeredUnits();
    
    // Process shellshocked units - reduce duration for current faction's units
    this.processShellshockedUnits();
    
    // Process city captures at the START of the new faction's turn
    // For online games, this is handled in the Firebase sync callback instead
    if (!this.isOnlineGame) {
      this.processCityCaptures(this.currentFaction);
      
      // Check for any captures that can start at the beginning of the turn
      this.checkForNewCaptures();
      
      // Process city upgrades at the beginning of the turn
      this.processCityUpgrades(this.currentFaction);
    }
    
    // Update city ownership tracking for next turn
    const cities = this.map.getCities();
    cities.forEach((city, name) => {
      this.lastTurnCities.set(name, city.faction);
    });
    
    // Add pending notifications for the current faction
    // In PvE mode, only show notifications for the human player
    if (this.gameState.pendingNotifications) {
      const factionsToCheck: FactionType[] = [];
      
      // In PvE mode, only show notifications for human player
      if (this.selectedGameMode === 'PvE' && this.humanPlayerFaction) {
        // Only process human player notifications when it's their turn
        if (this.currentFaction === this.humanPlayerFaction) {
          factionsToCheck.push(this.humanPlayerFaction);
        }
        // Clear AI's pending notifications to prevent accumulation
        const aiFaction = this.getOpponentFaction(this.humanPlayerFaction);
        if (this.gameState.pendingNotifications.has(aiFaction)) {
          this.gameState.pendingNotifications.set(aiFaction, []);
        }
      } else {
        // In PvP or online, show notifications for current faction
        factionsToCheck.push(this.currentFaction);
      }
      
      for (const faction of factionsToCheck) {
        if (this.gameState.pendingNotifications.has(faction)) {
          const pendingForFaction = this.gameState.pendingNotifications.get(faction) || [];
          // Show each pending notification using the new system
          pendingForFaction.forEach(message => {
            this.showNotification(message);
          });
          this.gameState.pendingNotifications.set(faction, []); // Clear after showing
        }
      }
    }
    
    // Process training queues at the START of the faction's turn
    // This ensures units complete at the beginning of the faction's new turn
    // For online games, this is handled in the Firebase sync callback instead
    const shouldProcessTraining = !this.isOnlineGame;
    
    debug.log(`Turn switch: isOnlineGame=${this.isOnlineGame}, shouldProcess=${shouldProcessTraining}`);
    
    if (shouldProcessTraining) {
      this.processTrainingQueues(this.gameState.currentFaction);
      
      // In multiplayer, sync the newly created units immediately
      if (this.isOnlineGame && this.multiplayerService) {
        const completeGameState = this.buildCompleteGameState();
        this.multiplayerService.updateGameState(
          completeGameState,
          this.gameState.currentFaction,
          this.gameState.turn
        );
      }
      
      // Send pending training notifications (for singleplayer)
      if (this.pendingNotifications && this.pendingNotifications.length > 0 && !this.isOnlineGame) {
        debug.log(`[SINGLEPLAYER] Sending ${this.pendingNotifications.length} training notifications`);
        this.pendingNotifications.forEach((notification) => {
          this.sendNotification(notification);
        });
        this.pendingNotifications = [];
      }
    }
    
    // Generate resources from cities for the new faction
    // Skip resources on turn 2 (first turn for both factions)
    if (this.gameState.turn > 2) {
      const income = this.calculateIncome(this.currentFaction);
      const currentResources = this.gameState.resources.get(this.currentFaction) || 0;
      this.gameState.resources.set(this.currentFaction, currentResources + income);
      
      // Track resource earnings in battle statistics
      const factionStats = this.gameState.battleStats?.get(this.currentFaction);
      if (factionStats) {
        factionStats.resourcesEarned += income;
      }
    }
    
    // Track turn start in analytics
    this.analytics.trackTurnStart(this.currentFaction);
    
    this.gameState.selectedUnit = null;
    this.gameState.selectedTile = null;
    this.availableMoves = null;
    this.enemiesInRange = null;
    
    // Update displays to clear any selected unit/tile info
    this.updateUnitInfo(null);
    this.updateTileInfo(null);
    
    // Close any open build menu
    const buildMenu = document.getElementById('build-menu');
    if (buildMenu) {
      buildMenu.remove();
    }
    
    // Units have already been reset by resetUnitMovement above, no need to do it again
    // Just ensure gameState.units is synced
    const units = this.unitManager.getUnitsForFaction(this.currentFaction);
    for (const unit of units) {
      if (this.gameState.units.has(unit.id)) {
        const gameStateUnit = this.gameState.units.get(unit.id)!;
        gameStateUnit.movementRemaining = unit.movementRemaining;
        gameStateUnit.hasAttacked = unit.hasAttacked;
        (gameStateUnit as any).hunkerChangedThisTurn = (unit as any).hunkerChangedThisTurn;
      }
    }
    
    // Check for interrupted captures at start of new turn
    this.checkCaptureInterruptions();
    
    // Only pan camera in local PvP mode (helps players see opponent's last move)
    if (this.gameState.gameMode === 'PvP') {
      // Pan camera to last moved unit or first city of the new faction
      const lastMovedUnit = this.unitManager.getLastMovedUnit(this.currentFaction);
      if (lastMovedUnit) {
        this.cameraController.smoothCenterOn(
          lastMovedUnit.position.x,
          lastMovedUnit.position.y,
          64, // tileSize
          this.canvas.width,
          this.canvas.height
        );
      } else {
        // No last moved unit, find first city
        const cities = this.map.getCities();
        let firstCity = null;

        // Find the oldest city belonging to this faction
        for (const city of cities.values()) {
          if (city.faction === this.currentFaction) {
            firstCity = city;
            break; // Since Map maintains insertion order, first match is oldest
          }
        }

        if (firstCity && firstCity.tiles.length > 0) {
          const centerTile = firstCity.tiles[Math.floor(firstCity.tiles.length / 2)];
          this.cameraController.smoothCenterOn(
            centerTile.x,
            centerTile.y,
            64, // tileSize
            this.canvas.width,
            this.canvas.height
          );
        }
      }
    }
    
    this.updateFactionDisplay();
    this.updateResourceDisplay();
    this.updateCityCount();
    
    // Check for game over
    this.checkVictoryConditions();
    
    // Commented out to reduce console spam - uncomment for debugging
    // debug.log('=== Game State after Turn', this.gameState.turn, '===');
    // debug.log('Current Faction:', this.gameState.currentFaction);
    // debug.log('Resources:', Object.fromEntries(this.gameState.resources));
    // debug.log('Units:', Array.from(this.gameState.units.values()).map(u => ({
    //   id: u.id,
    //   type: u.type,
    //   faction: u.faction,
    //   position: u.position,
    //   health: u.stats.health,
    //   movementRemaining: u.movementRemaining,
    //   hasAttacked: u.hasAttacked
    // })));
    // debug.log('Cities:', Array.from(this.map.getCities().entries()).map(([name, city]) => ({
    //   name,
    //   faction: city.faction,
    //   captureProgress: city.captureProgress,
    //   trainingQueue: city.trainingQueue
    // })));
    // debug.log('Full Game State:', this.gameState);
    // debug.log('=====================================');
    
    // Also save game state after turn switch
    this.saveGameState();
  }

  private handleKeyUp(e: KeyboardEvent): void {
    if (e.key === 'ArrowUp' || e.key === 'w' || e.key === 'W' ||
        e.key === 'ArrowDown' || e.key === 's' || e.key === 'S' ||
        e.key === 'ArrowLeft' || e.key === 'a' || e.key === 'A' ||
        e.key === 'ArrowRight' || e.key === 'd' || e.key === 'D') {
      e.preventDefault();
      this.keysPressed.delete(e.key.toLowerCase());
      this.updateCameraControls();
    } else if (e.key === '+' || e.key === '=' || e.key === '-' || e.key === '_') {
      e.preventDefault();
      this.keysPressed.delete('zoom_in');
      this.keysPressed.delete('zoom_out');
      this.updateCameraControls();
    }
  }
  
  private updateCameraControls(): void {
    // Movement
    let dx = 0;
    let dy = 0;
    
    if (this.keysPressed.has('w') || this.keysPressed.has('arrowup')) dy = 1;
    if (this.keysPressed.has('s') || this.keysPressed.has('arrowdown')) dy = -1;
    if (this.keysPressed.has('a') || this.keysPressed.has('arrowleft')) dx = 1;
    if (this.keysPressed.has('d') || this.keysPressed.has('arrowright')) dx = -1;
    
    this.cameraController.setVelocity(dx, dy);
    
    // Zoom direction
    let zoomDir = 0;
    if (this.keysPressed.has('zoom_in')) zoomDir = -1; // Zoom in = fewer tiles
    if (this.keysPressed.has('zoom_out')) zoomDir = 1; // Zoom out = more tiles
    
    this.cameraController.setZoomDirection(zoomDir);
  }

  private notifiedDestroyedUnits = new Set<string>();
  
  private cleanupDeadUnits(): void {
    const units = this.unitManager.getAllUnits();
    for (const unit of units) {
      if (unit.stats.health <= 0) {
        // If unit has death animation, wait for it to complete
        if (unit.deathAnimation) {
          const elapsed = Date.now() - unit.deathAnimation.startTime;
          if (elapsed < unit.deathAnimation.duration) {
            continue; // Animation still playing, don't remove yet
          }
          // Animation complete, proceed with removal
          console.log(`[CLEANUP] Removing dead unit ${unit.name} after animation (health: ${unit.stats.health})`);
        } else {
          // No death animation but unit is dead - this happens when synced from Firebase
          // Give it a quick death animation so it doesn't just disappear
          console.log(`[CLEANUP] Dead unit ${unit.name} has no animation, adding one (health: ${unit.stats.health})`);
          unit.deathAnimation = {
            startTime: Date.now() - 900, // Almost complete animation
            duration: 1000,
            startY: unit.displayPosition?.y || unit.position.y
          };
          continue; // Let it play briefly
        }
        
        // Animation complete, remove the unit and update hometown capacity
        if (unit.hometown) {
            const cities = this.map.getCities();
            let hometown = null;
            // Cities is a Map, so we need to iterate through values
            for (const city of cities.values()) {
              if (city.name === unit.hometown) {
                hometown = city;
                break;
              }
            }
            
            if (hometown && hometown.unitsProduced) {
              const index = hometown.unitsProduced.indexOf(unit.id);
              if (index > -1) {
                hometown.unitsProduced.splice(index, 1);
                debug.log(`Unit removed from ${hometown.name}, capacity now: ${hometown.unitsProduced.length}/${hometown.maxUnits}`);
              }
            }
          }
        
        // Send unit destroyed notification only once per unit
        if (this.isOnlineGame && !this.notifiedDestroyedUnits.has(unit.id)) {
          this.notifiedDestroyedUnits.add(unit.id);
          this.sendNotification({
            type: NotificationType.UNIT_DESTROYED,
            faction: unit.faction,
            data: { 
              unitName: unit.name,
              unitType: unit.type,
              position: unit.position
            },
            timestamp: Date.now()
          });
        }
        
        debug.log(`[CLEANUP DEAD] Removing unit ${unit.name} (${unit.id}) - death animation completed`);
        this.unitManager.removeUnit(unit.id);
        
        // CRITICAL: Also remove from gameState.units to prevent ghost units
        this.gameState.units.delete(unit.id);
        
        // Clear vision cache since unit was removed
        this.visionSystem.clearCache();
        
        // Check if any friendly units can now start capturing cities
        // (since the destroyed unit may have been blocking captures)
        this.checkForNewCaptures();
      }
    }
  }


  private showSettingsMenu(): void {
    // Create settings overlay
    const settingsOverlay = document.createElement('div');
    settingsOverlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.85);
      z-index: 35000;
      display: flex;
      align-items: center;
      justify-content: center;
    `;
    
    const settingsContainer = document.createElement('div');
    settingsContainer.style.cssText = `
      background: linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%);
      border: 3px solid #5a5a5a;
      border-radius: 15px;
      width: 90%;
      max-width: 600px;
      padding: 30px;
      box-shadow: 0 15px 40px rgba(0, 0, 0, 0.9);
    `;
    
    // Header
    const header = document.createElement('div');
    header.style.cssText = `
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 30px;
      padding-bottom: 20px;
      border-bottom: 2px solid #5a5a5a;
    `;
    
    const title = document.createElement('h2');
    title.textContent = 'SETTINGS';
    title.style.cssText = `
      color: #f0f0f0;
      font-size: 28px;
      letter-spacing: 2px;
      margin: 0;
    `;
    header.appendChild(title);
    
    const closeButton = document.createElement('button');
    closeButton.textContent = '✕';
    closeButton.style.cssText = `
      background: #8b2222;
      border: 2px solid #6b1212;
      color: white;
      font-size: 20px;
      width: 40px;
      height: 40px;
      border-radius: 5px;
      cursor: pointer;
      transition: all 0.2s ease;
    `;
    closeButton.onmouseover = () => {
      closeButton.style.background = '#ab3232';
      closeButton.style.transform = 'scale(1.1)';
    };
    closeButton.onmouseout = () => {
      closeButton.style.background = '#8b2222';
      closeButton.style.transform = 'scale(1)';
    };
    closeButton.onclick = () => {
      this.soundManager.playMenuSelect();
      settingsOverlay.remove();
    };
    header.appendChild(closeButton);
    
    settingsContainer.appendChild(header);
    
    // Sprite Selection Section
    const spriteSection = document.createElement('div');
    spriteSection.style.cssText = `
      margin-bottom: 30px;
      padding: 20px;
      background: rgba(255, 255, 255, 0.05);
      border-radius: 8px;
    `;
    
    const spriteLabel = document.createElement('h3');
    spriteLabel.textContent = 'Unit Sprite Style';
    spriteLabel.style.cssText = `
      color: #f0f0f0;
      font-size: 18px;
      margin-bottom: 10px;
    `;
    spriteSection.appendChild(spriteLabel);
    
    const spriteDescription = document.createElement('p');
    spriteDescription.textContent = 'Choose how unit icons appear on the battlefield';
    spriteDescription.style.cssText = `
      color: #aaa;
      font-size: 14px;
      margin-bottom: 15px;
    `;
    spriteSection.appendChild(spriteDescription);
    
    const spriteSelect = document.createElement('select');
    spriteSelect.style.cssText = `
      width: 100%;
      padding: 10px;
      background: #333;
      color: white;
      border: 2px solid #666;
      border-radius: 5px;
      font-size: 16px;
      cursor: pointer;
    `;
    
    const savedSpriteMode = localStorage.getItem('spriteMode') || 'outline';
    
    const options = [
      { value: 'outline', text: 'Outline - Classic line art style' },
      { value: 'coloured', text: 'Coloured - Detailed with colors' },
      { value: 'filled', text: 'Filled - Solid silhouettes' },
      { value: 'silhouette', text: 'Silhouette - Minimal style' },
      { value: 'borderless', text: 'Borderless - Faction-colored icons, no borders' },
      { value: 'coloured-borderless', text: 'Coloured Borderless - Colored icons, no borders' },
      { value: 'coloured-faction', text: 'Coloured Faction - Colored icons with faction borders' }
    ];
    
    options.forEach(opt => {
      const option = document.createElement('option');
      option.value = opt.value;
      option.textContent = opt.text;
      if (opt.value === savedSpriteMode) {
        option.selected = true;
      }
      spriteSelect.appendChild(option);
    });
    
    spriteSelect.onchange = () => {
      const newMode = spriteSelect.value as 'outline' | 'coloured' | 'filled' | 'silhouette' | 'borderless' | 'coloured-borderless' | 'coloured-faction';
      localStorage.setItem('spriteMode', newMode);
      this.debugSpriteMode = newMode;
      if (this.renderer) {
        this.renderer.setSpriteMode(newMode);
      }
      debug.log(`Sprite mode changed to: ${newMode}`);
    };
    
    spriteSection.appendChild(spriteSelect);
    settingsContainer.appendChild(spriteSection);
    
    // Health Bars Setting
    const healthBarSection = document.createElement('div');
    healthBarSection.style.cssText = `
      margin-bottom: 30px;
      padding: 20px;
      background: rgba(255, 255, 255, 0.05);
      border-radius: 8px;
    `;
    
    const healthBarLabel = document.createElement('h3');
    healthBarLabel.textContent = 'Unit Health Bars';
    healthBarLabel.style.cssText = `
      color: #f0f0f0;
      font-size: 18px;
      margin-bottom: 10px;
    `;
    healthBarSection.appendChild(healthBarLabel);
    
    const healthBarSelect = document.createElement('select');
    healthBarSelect.style.cssText = `
      width: 100%;
      padding: 10px;
      background: #333;
      color: white;
      border: 2px solid #666;
      border-radius: 5px;
      font-size: 16px;
      cursor: pointer;
    `;
    
    const savedHealthBar = localStorage.getItem('healthBarMode') || 'select';
    const healthBarOptions = [
      { value: 'always', text: 'Always Show' },
      { value: 'select', text: 'Only When Selected' },
      { value: 'never', text: 'Never Show' }
    ];
    
    healthBarOptions.forEach(opt => {
      const option = document.createElement('option');
      option.value = opt.value;
      option.textContent = opt.text;
      if (opt.value === savedHealthBar) {
        option.selected = true;
      }
      healthBarSelect.appendChild(option);
    });
    
    healthBarSelect.onchange = () => {
      localStorage.setItem('healthBarMode', healthBarSelect.value);
    };
    
    healthBarSection.appendChild(healthBarSelect);
    settingsContainer.appendChild(healthBarSection);
    
    // Notifications Settings
    const notificationSection = document.createElement('div');
    notificationSection.style.cssText = `
      margin-bottom: 30px;
      padding: 20px;
      background: rgba(255, 255, 255, 0.05);
      border-radius: 8px;
    `;
    
    const notificationLabel = document.createElement('h3');
    notificationLabel.textContent = 'Notifications';
    notificationLabel.style.cssText = `
      color: #f0f0f0;
      font-size: 18px;
      margin-bottom: 15px;
    `;
    notificationSection.appendChild(notificationLabel);
    
    // Notifications On/Off
    const notificationToggleContainer = document.createElement('div');
    notificationToggleContainer.style.cssText = `
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 15px;
    `;
    
    const notificationToggleLabel = document.createElement('label');
    notificationToggleLabel.textContent = 'Show Notifications';
    notificationToggleLabel.style.cssText = `
      color: #e0e0e0;
      font-size: 14px;
    `;
    
    const notificationToggle = document.createElement('input');
    notificationToggle.type = 'checkbox';
    notificationToggle.checked = localStorage.getItem('notificationsEnabled') !== 'false';
    notificationToggle.style.cssText = `
      width: 20px;
      height: 20px;
      cursor: pointer;
    `;
    
    notificationToggleContainer.appendChild(notificationToggleLabel);
    notificationToggleContainer.appendChild(notificationToggle);
    notificationSection.appendChild(notificationToggleContainer);
    
    // Notification Duration
    const durationContainer = document.createElement('div');
    durationContainer.style.cssText = `
      margin-top: 10px;
    `;
    
    const durationLabel = document.createElement('label');
    durationLabel.textContent = 'Notification Duration';
    durationLabel.style.cssText = `
      color: ${notificationToggle.checked ? '#e0e0e0' : '#666'};
      font-size: 14px;
      display: block;
      margin-bottom: 8px;
    `;
    durationContainer.appendChild(durationLabel);
    
    const durationSelect = document.createElement('select');
    durationSelect.style.cssText = `
      width: 100%;
      padding: 10px;
      background: #333;
      color: white;
      border: 2px solid #666;
      border-radius: 5px;
      font-size: 16px;
      cursor: pointer;
      opacity: ${notificationToggle.checked ? '1' : '0.5'};
    `;
    durationSelect.disabled = !notificationToggle.checked;
    
    const savedDuration = localStorage.getItem('notificationDuration') || '10';
    const durationOptions = [
      { value: '5', text: '5 seconds' },
      { value: '10', text: '10 seconds' },
      { value: '15', text: '15 seconds' },
      { value: 'manual', text: 'Manual Only' }
    ];
    
    durationOptions.forEach(opt => {
      const option = document.createElement('option');
      option.value = opt.value;
      option.textContent = opt.text;
      if (opt.value === savedDuration) {
        option.selected = true;
      }
      durationSelect.appendChild(option);
    });
    
    durationSelect.onchange = () => {
      localStorage.setItem('notificationDuration', durationSelect.value);
    };
    
    durationContainer.appendChild(durationSelect);
    notificationSection.appendChild(durationContainer);
    
    // Update duration field when toggle changes
    notificationToggle.onchange = () => {
      const enabled = notificationToggle.checked;
      localStorage.setItem('notificationsEnabled', String(enabled));
      durationSelect.disabled = !enabled;
      durationSelect.style.opacity = enabled ? '1' : '0.5';
      durationLabel.style.color = enabled ? '#e0e0e0' : '#666';
    };
    
    settingsContainer.appendChild(notificationSection);
    
    // Gameplay Settings
    const gameplaySection = document.createElement('div');
    gameplaySection.style.cssText = `
      margin-bottom: 30px;
      padding: 20px;
      background: rgba(255, 255, 255, 0.05);
      border-radius: 8px;
    `;
    
    const gameplayLabel = document.createElement('h3');
    gameplayLabel.textContent = 'Gameplay';
    gameplayLabel.style.cssText = `
      color: #f0f0f0;
      font-size: 18px;
      margin-bottom: 15px;
    `;
    gameplaySection.appendChild(gameplayLabel);
    
    // End Turn Confirmation
    const endTurnContainer = document.createElement('div');
    endTurnContainer.style.cssText = `
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 15px;
    `;
    
    const endTurnLabel = document.createElement('label');
    endTurnLabel.textContent = 'End Turn Confirmation';
    endTurnLabel.style.cssText = `
      color: #e0e0e0;
      font-size: 14px;
    `;
    
    const endTurnToggle = document.createElement('input');
    endTurnToggle.type = 'checkbox';
    endTurnToggle.checked = localStorage.getItem('endTurnConfirmation') !== 'false';
    endTurnToggle.style.cssText = `
      width: 20px;
      height: 20px;
      cursor: pointer;
    `;
    
    endTurnToggle.onchange = () => {
      localStorage.setItem('endTurnConfirmation', String(endTurnToggle.checked));
    };
    
    endTurnContainer.appendChild(endTurnLabel);
    endTurnContainer.appendChild(endTurnToggle);
    gameplaySection.appendChild(endTurnContainer);
    
    settingsContainer.appendChild(gameplaySection);
    
    // Sound Settings
    const soundSection = document.createElement('div');
    soundSection.style.cssText = `
      margin-bottom: 20px;
      padding: 20px;
      background: rgba(255, 255, 255, 0.05);
      border-radius: 8px;
    `;
    
    const soundLabel = document.createElement('h3');
    soundLabel.textContent = 'Audio';
    soundLabel.style.cssText = `
      color: #f0f0f0;
      font-size: 18px;
      margin-bottom: 15px;
    `;
    soundSection.appendChild(soundLabel);
    
    // Game Sound On/Off
    const soundContainer = document.createElement('div');
    soundContainer.style.cssText = `
      display: flex;
      align-items: center;
      justify-content: space-between;
    `;
    
    const soundToggleLabel = document.createElement('label');
    soundToggleLabel.textContent = 'Game Sound';
    soundToggleLabel.style.cssText = `
      color: #e0e0e0;
      font-size: 14px;
    `;
    
    const soundToggle = document.createElement('input');
    soundToggle.type = 'checkbox';
    soundToggle.checked = localStorage.getItem('soundEnabled') !== 'false';
    soundToggle.style.cssText = `
      width: 20px;
      height: 20px;
      cursor: pointer;
    `;
    
    soundToggle.onchange = () => {
      const enabled = soundToggle.checked;
      localStorage.setItem('soundEnabled', String(enabled));
      // Sound enabling/disabling is handled within each SoundManager method
    };
    
    soundContainer.appendChild(soundToggleLabel);
    soundContainer.appendChild(soundToggle);
    soundSection.appendChild(soundContainer);
    
    settingsContainer.appendChild(soundSection);
    
    // Note about applying changes
    const note = document.createElement('p');
    note.textContent = 'Some settings will be applied to new games';
    note.style.cssText = `
      color: #888;
      font-size: 12px;
      text-align: center;
      margin-top: 20px;
    `;
    settingsContainer.appendChild(note);
    
    settingsOverlay.appendChild(settingsContainer);
    document.body.appendChild(settingsOverlay);
  }

  private gameLoop(currentTime: number = 0): void {
    // Calculate delta time
    const deltaTime = this.lastFrameTime ? (currentTime - this.lastFrameTime) / 1000 : 0;
    this.lastFrameTime = currentTime;
    
    // Clean up dead units after their death animation
    this.cleanupDeadUnits();
    
    // Update camera's selected tile for zoom centering
    this.cameraController.setSelectedTile(this.gameState.selectedTile);

    // Update camera position and zoom
    this.cameraController.update(deltaTime, this.canvas.width, this.canvas.height);

    // Update game state
    this.gameState.camera = this.cameraController.getCamera();
    
    // Calculate visibility for current faction
    // In online games, always show our own faction's view (not whoever's turn it is)
    // In PvE, always show human player's view (never show AI's fog of war)
    const viewFaction = this.isOnlineGame ? (this.humanPlayerFaction || this.currentFaction) :
                       (this.gameState.gameMode === 'PvE' && this.humanPlayerFaction) ? this.humanPlayerFaction :
                       this.currentFaction;

    this.visionSystem.calculateVisibility(this.map, this.unitManager, viewFaction);

    // DEV MODE: If fog is disabled for testing, pass undefined to show entire map
    // Note: AI still uses proper fog of war, this only affects player rendering
    const renderVision = this.devFogDisabled ? undefined : this.visionSystem;

    // Render
    this.renderer.render(
      this.map,
      this.gameState.camera,
      this.gameState.selectedTile,
      this.unitManager.getAllUnits(),
      this.gameState.selectedUnit,
      this.availableMoves,
      this.enemiesInRange,
      renderVision,
      viewFaction,
      this.minePlacementTiles,
      this.healTargetTiles,
      this.isBoardingMode ? this.validBoardTargets : null
    );
    
    // Continue loop
    requestAnimationFrame((time) => this.gameLoop(time));
  }
  
  // Battle Statistics Methods
  private initializeBattleStats(): void {
    // Ensure battleStats Map exists
    if (!this.gameState.battleStats) {
      this.gameState.battleStats = new Map();
    }
    
    const factions = this.getCampaignFactions();
    for (const faction of factions) {
      this.gameState.battleStats.set(faction, {
        unitsCreated: 0,
        unitsLost: 0,
        enemiesKilled: 0,
        citiesCaptured: 0,
        citiesLost: 0,
        resourcesSpent: 0,
        resourcesEarned: 0,
        damageDealt: 0,
        damageTaken: 0,
        unitsHealed: 0,
        unitsRepaired: 0,
        minesPlaced: 0,
        minesDetonated: 0
      });
    }
  }
  
  private getCampaignFactions(): FactionType[] {
    switch (this.selectedCampaign) {
      case CampaignType.TUNISIA:
        return ['United Kingdom', '3rd Reich'];
      case CampaignType.STALINGRAD:
        return ['USSR', '3rd Reich'];
      case CampaignType.OKINAWA:
        return ['United States Of America', 'Imperial Japan'];
      case CampaignType.CAMPOBASSO:
        return ['Canada', 'Fascist Italy'];
      case CampaignType.FRANCE_1940:
        return ['France', '3rd Reich'];
      case CampaignType.ALBANIA_1940:
        return ['Greece', 'Fascist Italy'];
      default: // NORMANDY
        return ['United States Of America', '3rd Reich'];
    }
  }

  private startTutorial(menuOverlay: HTMLElement): void {
    // Initialize tutorial mode
    this.isTutorial = true;
    this.tutorialStage = 0; // Start with UI introduction
    this.tutorialStep = 0;
    this.tutorialValidationLogged = false; // Reset logging flag for new tutorial
    // this.tutorialActionCount = 0; // Removed - variable no longer exists
    // this.tutorialStageData = {}; // Removed - unused
    
    // Set up tutorial on special TUTORIAL campaign
    this.selectedCampaign = CampaignType.TUTORIAL;
    this.currentFaction = 'United States Of America';
    this.secondFaction = '3rd Reich';
    this.selectedGameMode = 'PvE';
    this.humanPlayerFaction = 'United States Of America';
    
    // Hide main menu
    menuOverlay.style.display = 'none';
    
    // Initialize game in tutorial mode
    this.initializeGame();
    
    // Start guided tutorial system
    setTimeout(() => {
      this.startGuidedTutorial();
    }, 1000); // Small delay to let game initialize
  }

  // Removed unused method: createTutorialOverlay

  // Removed unused method: showTutorialStage1

  // Removed unused method: updateTutorialProgress

  // Keep spawnTutorialEnemyUnit and spawnTutorialEnemyAttackForce - they're used by new tutorial
  private spawnTutorialEnemyUnit(): void {
    // First, assign Enemy Stronghold to the AI faction
    this.assignEnemyStrongholdToAI();
    
    // Spawn enemy unit closer to be visible but not immediately threatening
    const spawnPosition = { x: 8, y: 8 }; // Visible position near Neutral City
    
    const unit = this.unitManager.createUnit(UnitType.PANZERGRENADIER_SQUAD, '3rd Reich', spawnPosition);
    unit.hometown = 'Enemy Stronghold';
    // Add to gameState
    this.gameState.units.set(unit.id, unit);
    
    // Track this as a tutorial enemy unit
    this.tutorialEnemyUnits.push(unit.id);
    
    // Set AI to attack player units immediately
    if (this.aiController) {
      this.aiController.setTutorialObjective(
        'Attack player units',
        [],
        'attack_player'
      );
    }
    
    // Clear vision cache so enemy unit appears immediately
    this.visionSystem.clearCache();
    
    this.showNotification(`Enemy unit spotted at position (${spawnPosition.x}, ${spawnPosition.y})!`);
  }
  
  // Spawn enemy attack force for defensive phase
  private spawnTutorialEnemyAttackForce(): void {
    // Spawn 2 infantry units to attack neutral city
    const positions = [
      { x: 13, y: 6 }, // East of neutral city
      { x: 10, y: 9 }  // South of neutral city
    ];
    
    positions.forEach(pos => {
      const unit = this.unitManager.createUnit(UnitType.PANZERGRENADIER_SQUAD, '3rd Reich', pos);
      if (unit) {
        unit.hometown = 'Enemy Stronghold';
        this.gameState.units.set(unit.id, unit);
        this.tutorialEnemyUnits.push(unit.id);
      }
    });
    
    // Set AI objective to attack player units (will also move toward city)
    if (this.aiController) {
      const neutralCity = Array.from(this.map.getCities().values()).find(c => c.name === 'Neutral City');
      if (neutralCity) {
        const centerTile = neutralCity.tiles[Math.floor(neutralCity.tiles.length / 2)];
        this.aiController.setTutorialObjective(
          'Attack player units and Neutral City', 
          [centerTile],
          'attack_player'  // Changed to attack_player so they will attack units
        );
      }
    }
    
    // this.tutorialEnemyAttackDefeated = false; // Removed - unused
    
    this.visionSystem.clearCache();
    this.showNotification('Enemy reinforcements spotted approaching the Neutral City!');
  }

  /* Removed old tutorial methods - showTutorialStage2 through showTutorialStage8
  private showTutorialStage2(): void {
    this.tutorialStage = 2;
    this.tutorialObjective = "Capture the Neutral City by moving your unit onto it";
    
    if (this.tutorialOverlay) {
      this.tutorialOverlay.innerHTML = `
        <h3 style="margin: 0 0 15px 0; color: #4a90e2;">Tutorial Stage 2: City Capture</h3>
        <p><strong>Objective:</strong> ${this.tutorialObjective}</p>
        <div style="margin: 10px 0; padding: 10px; background: rgba(74, 144, 226, 0.2); border-radius: 5px;">
          <strong>About Cities:</strong><br>
          • Cities generate resources each turn<br>
          • Move a unit onto a city to capture it<br>
          • Takes 2 turns to fully capture<br>
          • Cities can train new units and be upgraded
        </div>
        <p style="color: #4a90e2;">Find the "Neutral City" (gray) and move your unit onto it!</p>
        <button onclick="window.game.skipTutorialStage()" style="
          background: #666; 
          color: white; 
          border: 1px solid #888; 
          padding: 5px 10px; 
          border-radius: 3px; 
          cursor: pointer; 
          font-size: 12px;
          margin-top: 10px;
        ">Skip this stage</button>
      `;
    }
  }

  private showTutorialStage3(): void {
    this.tutorialStage = 3;
    this.tutorialObjective = "Train a new unit from your captured city";
    
    if (this.tutorialOverlay) {
      this.tutorialOverlay.innerHTML = `
        <h3 style="margin: 0 0 15px 0; color: #4a90e2;">Tutorial Stage 3: Unit Training</h3>
        <p><strong>Objective:</strong> ${this.tutorialObjective}</p>
        <div style="margin: 10px 0; padding: 10px; background: rgba(74, 144, 226, 0.2); border-radius: 5px;">
          <strong>Training Units:</strong><br>
          1. Click on one of your cities<br>
          2. Click the "Train Unit" button<br>
          3. Select a unit type to train<br>
          4. Units cost resources and take time to train
        </div>
        <p style="color: #4a90e2;">Click on your captured city and train your first unit!</p>
        <button onclick="window.game.skipTutorialStage()" style="
          background: #666; 
          color: white; 
          border: 1px solid #888; 
          padding: 5px 10px; 
          border-radius: 3px; 
          cursor: pointer; 
          font-size: 12px;
          margin-top: 10px;
        ">Skip this stage</button>
      `;
    }
  }

  private showTutorialStage4(): void {
    this.tutorialStage = 4;
    this.tutorialObjective = "Attack an enemy unit to learn combat";
    
    // Spawn enemy unit for combat tutorial
    this.spawnTutorialEnemyUnit();
    
    if (this.tutorialOverlay) {
      this.tutorialOverlay.innerHTML = `
        <h3 style="margin: 0 0 15px 0; color: #4a90e2;">Tutorial Stage 4: Combat</h3>
        <p><strong>Objective:</strong> ${this.tutorialObjective}</p>
        <div style="margin: 10px 0; padding: 10px; background: rgba(74, 144, 226, 0.2); border-radius: 5px;">
          <strong>Combat System:</strong><br>
          • Select your unit, then click an enemy<br>
          • Units have attack/defense values<br>
          • Terrain affects combat (hills give defense bonus)<br>
          • Units can only attack once per turn
        </div>
        <p style="color: #4a90e2;">An enemy unit is approaching! It will move toward your position. Attack it when it comes into range!</p>
        <button onclick="window.game.skipTutorialStage()" style="
          background: #666; 
          color: white; 
          border: 1px solid #888; 
          padding: 5px 10px; 
          border-radius: 3px; 
          cursor: pointer; 
          font-size: 12px;
          margin-top: 10px;
        ">Skip this stage</button>
      `;
    }
  }

  // duplicates removed - see active versions above
  */

  private assignEnemyStrongholdToAI(): void {
    if (!this.isTutorial) return;
    
    const enemyStronghold = Array.from(this.map.getCities().values()).find(city => city.name === 'Enemy Stronghold');
    if (enemyStronghold && enemyStronghold.faction === null) {
      enemyStronghold.faction = '3rd Reich';
      this.showNotification('Enemy forces have taken control of the Enemy Stronghold!');
    }
  }

  /* Continue removed old tutorial methods

  // Removed unused method: initializeTutorialAI (replaced by direct executeTutorialAI calls)

  private executeTutorialAI(): void {
    console.log('Tutorial AI: Called - isTutorial:', this.isTutorial, 'enemy units:', this.tutorialEnemyUnits.length, 'stage:', this.tutorialStage, 'aiEnabled:', this.tutorialAIEnabled);
    
    if (!this.isTutorial) {
      console.log('Tutorial AI: Not in tutorial mode');
      return;
    }
    
    // Check if AI should be active based on tutorial progression
    // AI is disabled before stage 4 unless explicitly enabled
    if (!this.tutorialAIEnabled) {
      console.log('Tutorial AI: AI not yet enabled (stage', this.tutorialStage, '), skipping turn');
      setTimeout(() => {
        this.isAITurn = false;
        this.endTurn();
      }, 100);
      return;
    }
    
    // Clean up destroyed units from tracking array
    this.tutorialEnemyUnits = this.tutorialEnemyUnits.filter(id => {
      const unit = this.unitManager.getUnit(id);
      return unit !== null && unit !== undefined && unit.faction === '3rd Reich';
    });
    
    // After stage 4, enemy can spawn new units from cities
    if (this.tutorialStage >= 4 && this.tutorialAIEnabled) {
      // Check if enemy has any cities to produce units from
      const enemyCities = Array.from(this.map.getCities().values()).filter(
        c => c.faction === '3rd Reich'
      );
      
      console.log('Tutorial AI: Enemy has', enemyCities.length, 'cities and', this.tutorialEnemyUnits.length, 'units');
      
      // If enemy has cities but no units, spawn some infantry
      if (enemyCities.length > 0 && this.tutorialEnemyUnits.length === 0) {
        const city = enemyCities[0];
        const unit = this.unitManager.createUnit(UnitType.PANZERGRENADIER_SQUAD, '3rd Reich', city.tiles[0]);
        if (unit) {
          this.gameState.units.set(unit.id, unit);
          this.tutorialEnemyUnits.push(unit.id);
          console.log('Tutorial AI: Spawned reinforcement unit at', city.name);
        }
      }
    }
    
    if (this.tutorialEnemyUnits.length === 0) {
      console.log('Tutorial AI: No enemy units to control - ending turn immediately');
      setTimeout(() => {
        this.isAITurn = false;
        this.endTurn();
      }, 100);
      return;
    }
    
    console.log('Tutorial AI: Executing for', this.tutorialEnemyUnits.length, 'units at stage', this.tutorialStage);
    
    // Execute scripted AI behavior based on tutorial stage
    this.executeTutorialAIScript();
    
    // After processing all units, end the AI turn
    setTimeout(() => {
      this.isAITurn = false;
      this.endTurn();
    }, 1500); // Delay to let movements complete visually
  }

  // New method to execute scripted AI behaviors based on tutorial stage
  private executeTutorialAIScript(): void {
    // Since tutorialAIScript is never set, always use default behavior
    // Default: move units toward player if stage >= 4
    if (this.tutorialStage >= 4 && this.tutorialAIEnabled) {
      for (const unitId of this.tutorialEnemyUnits) {
        this.tutorialAIMoveTowardPlayer(unitId);
      }
    }
  }

  // AI script: Attack the neutral city
  private tutorialAIAttackNeutralCity(): void {
    const neutralCity = Array.from(this.map.getCities().values()).find(c => c.name === 'Neutral City');
    if (!neutralCity) return;
    
    const targetPos = neutralCity.tiles[Math.floor(neutralCity.tiles.length / 2)];
    
    for (const unitId of this.tutorialEnemyUnits) {
      const unit = this.unitManager.getUnit(unitId);
      if (!unit || unit.movementRemaining <= 0) continue;
      
      // Move toward neutral city
      const dx = targetPos.x - unit.position.x;
      const dy = targetPos.y - unit.position.y;
      
      // Simple pathfinding - move one step closer
      let newX = unit.position.x;
      let newY = unit.position.y;
      
      if (Math.abs(dx) > Math.abs(dy)) {
        newX += dx > 0 ? 1 : -1;
      } else if (dy !== 0) {
        newY += dy > 0 ? 1 : -1;
      }
      
      const targetPosition = { x: newX, y: newY };
      
      // Check if position is valid and empty
      const tile = this.map.getTile(newX, newY);
      if (tile && !this.unitManager.getUnitAt(targetPosition)) {
        const pathCost = this.calculatePathCost(unit.position, targetPosition, unit);
        if (pathCost > 0 && pathCost <= unit.movementRemaining) {
          this.moveUnit(unit, targetPosition);
        }
      }
    }
  }
  
  // AI script: Defend Hill Fort
  private tutorialAIDefendHillFort(): void {
    const hillFort = Array.from(this.map.getCities().values()).find(c => c.name === 'Hill Fort');
    if (!hillFort) return;
    
    // Units should stay on or near the hill fort
    for (const unitId of this.tutorialEnemyUnits) {
      const unit = this.unitManager.getUnit(unitId);
      if (!unit) continue;
      
      // Check if already on the city
      const onCity = hillFort.tiles.some(t => t.x === unit.position.x && t.y === unit.position.y);
      if (onCity) continue; // Stay in defensive position
      
      // Move to city if not there
      const targetTile = hillFort.tiles.find(t => !this.unitManager.getUnitAt(t));
      if (targetTile && unit.movementRemaining > 0) {
        const pathCost = this.calculatePathCost(unit.position, targetTile, unit);
        if (pathCost > 0 && pathCost <= unit.movementRemaining) {
          this.moveUnit(unit, targetTile);
        }
      }
    }
  }
  
  // AI script: Defend stronghold with heavy forces
  private tutorialAIDefendStronghold(): void {
    // Units should patrol around stronghold and attack nearby enemies
    const stronghold = Array.from(this.map.getCities().values()).find(c => c.name === 'Enemy Stronghold');
    if (!stronghold) return;
    
    const playerUnits = this.humanPlayerFaction ? this.unitManager.getUnitsForFaction(this.humanPlayerFaction) : [];
    
    for (const unitId of this.tutorialEnemyUnits) {
      const unit = this.unitManager.getUnit(unitId);
      if (!unit || unit.movementRemaining <= 0) continue;
      
      // Find closest player unit within range
      let target = null;
      let minDist = 999;
      
      for (const player of playerUnits) {
        const dist = Math.abs(player.position.x - unit.position.x) + Math.abs(player.position.y - unit.position.y);
        if (dist < minDist && dist <= 5) { // Only engage if close
          minDist = dist;
          target = player;
        }
      }
      
      if (target) {
        // Move toward player unit
        const dx = target.position.x - unit.position.x;
        const dy = target.position.y - unit.position.y;
        
        let newX = unit.position.x;
        let newY = unit.position.y;
        
        if (Math.abs(dx) > Math.abs(dy)) {
          newX += dx > 0 ? 1 : -1;
        } else if (dy !== 0) {
          newY += dy > 0 ? 1 : -1;
        }
        
        const targetPosition = { x: newX, y: newY };
        const tile = this.map.getTile(newX, newY);
        
        if (tile && !this.unitManager.getUnitAt(targetPosition)) {
          const pathCost = this.calculatePathCost(unit.position, targetPosition, unit);
          if (pathCost > 0 && pathCost <= unit.movementRemaining) {
            this.moveUnit(unit, targetPosition);
          }
        }
      }
    }
  }

  private tutorialAIMoveTowardPlayer(unitId: string): void {
    const enemyUnit = this.unitManager.getUnit(unitId);
    if (!enemyUnit || enemyUnit.movementRemaining <= 0) {
      console.log('Tutorial AI: Cannot move unit', unitId, '- no movement remaining or unit not found');
      return;
    }
    
    // Find player units to move toward
    const playerUnits = this.unitManager.getUnitsForFaction('United States Of America');
    if (playerUnits.length === 0) {
      console.log('Tutorial AI: No player units found');
      return;
    }
    
    // Find closest player unit
    let closestPlayer = playerUnits[0];
    let minDistance = Math.abs(closestPlayer.position.x - enemyUnit.position.x) + Math.abs(closestPlayer.position.y - enemyUnit.position.y);
    
    for (const player of playerUnits) {
      const distance = Math.abs(player.position.x - enemyUnit.position.x) + Math.abs(player.position.y - enemyUnit.position.y);
      if (distance < minDistance) {
        minDistance = distance;
        closestPlayer = player;
      }
    }
    
    // Move one step toward the closest player
    const dx = closestPlayer.position.x - enemyUnit.position.x;
    const dy = closestPlayer.position.y - enemyUnit.position.y;
    
    let targetPosition = { x: enemyUnit.position.x, y: enemyUnit.position.y };
    
    // Move one step in the direction of the player
    if (Math.abs(dx) > Math.abs(dy)) {
      targetPosition.x += dx > 0 ? 1 : -1;
    } else {
      targetPosition.y += dy > 0 ? 1 : -1;
    }
    
    // Ensure target is within bounds
    targetPosition.x = Math.max(0, Math.min(19, targetPosition.x));
    targetPosition.y = Math.max(0, Math.min(19, targetPosition.y));
    
    // Check if target position is empty
    if (this.unitManager.getUnitAt(targetPosition)) {
      console.log('Tutorial AI: Target position occupied, skipping move');
      return;
    }
    
    // Calculate path and move if possible
    const pathCost = this.calculatePathCost(enemyUnit.position, targetPosition, enemyUnit);
    if (pathCost > 0 && pathCost <= enemyUnit.movementRemaining) {
      console.log('Tutorial AI: Moving unit from', enemyUnit.position, 'to', targetPosition, 'cost:', pathCost);
      this.unitManager.moveUnit(unitId, targetPosition, pathCost);
      
      // Update the unit in gameState
      if (this.gameState.units.has(unitId)) {
        const gameStateUnit = this.gameState.units.get(unitId)!;
        gameStateUnit.position = targetPosition;
        gameStateUnit.movementRemaining = enemyUnit.movementRemaining;
      }
      
      // Clear vision cache since unit positions changed
      this.visionSystem.clearCache();
    } else {
      console.log('Tutorial AI: Cannot move - path cost', pathCost, 'movement remaining', enemyUnit.movementRemaining);
    }
  }

  // Removed unused method: tutorialAISeekNeutralCity

  private showTutorialStage5(): void {
    this.tutorialStage = 5;
    this.tutorialObjective = "Learn about resources and management";
    
    if (this.tutorialOverlay) {
      this.tutorialOverlay.innerHTML = `
        <h3 style="margin: 0 0 15px 0; color: #4a90e2;">Tutorial Stage 5: Resources</h3>
        <p><strong>Objective:</strong> ${this.tutorialObjective}</p>
        <div style="margin: 10px 0; padding: 10px; background: rgba(74, 144, 226, 0.2); border-radius: 5px;">
          <strong>Resource Management:</strong><br>
          • Resources shown in top-left corner<br>
          • Generated by cities each turn<br>
          • Used to train units and upgrade cities<br>
          • Better cities = more resources
        </div>
        <p style="color: #4a90e2;">Check your resource count and plan your strategy!</p>
        <button onclick="window.game.skipTutorialStage()" style="
          background: #666; 
          color: white; 
          border: 1px solid #888; 
          padding: 5px 10px; 
          border-radius: 3px; 
          cursor: pointer; 
          font-size: 12px;
          margin-top: 10px;
        ">Skip this stage</button>
      `;
    }
    
    // Auto-advance after 5 seconds
    setTimeout(() => {
      if (this.tutorialStage === 5) {
        this.showTutorialStage6();
      }
    }, 5000);
  }

  private showTutorialStage6(): void {
    this.tutorialStage = 6;
    this.tutorialObjective = "End your turn to see enemy movement";
    
    if (this.tutorialOverlay) {
      this.tutorialOverlay.innerHTML = `
        <h3 style="margin: 0 0 15px 0; color: #4a90e2;">Tutorial Stage 6: Turn System</h3>
        <p><strong>Objective:</strong> ${this.tutorialObjective}</p>
        <div style="margin: 10px 0; padding: 10px; background: rgba(74, 144, 226, 0.2); border-radius: 5px;">
          <strong>Turn-Based System:</strong><br>
          • Each player takes turns alternately<br>
          • Click "End Turn" when you're done<br>
          • Units refresh each turn<br>
          • Resources are generated at turn start
        </div>
        <p style="color: #4a90e2;">Click the "End Turn" button to see how the AI responds!</p>
        <button onclick="window.game.skipTutorialStage()" style="
          background: #666; 
          color: white; 
          border: 1px solid #888; 
          padding: 5px 10px; 
          border-radius: 3px; 
          cursor: pointer; 
          font-size: 12px;
          margin-top: 10px;
        ">Skip this stage</button>
      `;
    }
  }

  private showTutorialStage7(): void {
    this.tutorialStage = 7;
    this.tutorialObjective = "Learn about victory conditions";
    
    if (this.tutorialOverlay) {
      this.tutorialOverlay.innerHTML = `
        <h3 style="margin: 0 0 15px 0; color: #4a90e2;">Tutorial Stage 7: Victory</h3>
        <p><strong>Objective:</strong> ${this.tutorialObjective}</p>
        <div style="margin: 10px 0; padding: 10px; background: rgba(74, 144, 226, 0.2); border-radius: 5px;">
          <strong>How to Win:</strong><br>
          • Capture all enemy cities, OR<br>
          • Destroy all enemy units<br>
          • Control key strategic positions<br>
          • Manage resources effectively
        </div>
        <p style="color: #4a90e2;">Your goal: Defeat the German forces!</p>
        <button onclick="window.game.skipTutorialStage()" style="
          background: #666; 
          color: white; 
          border: 1px solid #888; 
          padding: 5px 10px; 
          border-radius: 3px; 
          cursor: pointer; 
          font-size: 12px;
          margin-top: 10px;
        ">Skip this stage</button>
      `;
    }
    
    // Auto-advance after 5 seconds
    setTimeout(() => {
      if (this.tutorialStage === 7) {
        this.showTutorialStage8();
      }
    }, 5000);
  }

  private showTutorialStage8(): void {
    this.tutorialStage = 8;
    this.tutorialObjective = "Complete the tutorial";
    
    if (this.tutorialOverlay) {
      this.tutorialOverlay.innerHTML = `
        <h3 style="margin: 0 0 15px 0; color: #4a90e2;">Tutorial Stage 8: Advanced Tips</h3>
        <p><strong>Objective:</strong> ${this.tutorialObjective}</p>
        <div style="margin: 10px 0; padding: 10px; background: rgba(74, 144, 226, 0.2); border-radius: 5px;">
          <strong>Advanced Tactics:</strong><br>
          • Use terrain for defensive bonuses<br>
          • Different units have different roles<br>
          • Air units need airbases to operate<br>
          • Plan your unit composition carefully
        </div>
        <p style="color: #4a90e2;">You're ready to command! Continue playing or return to menu.</p>
        <button onclick="window.game.completeTutorial()" style="
          background: #4a90e2; 
          color: white; 
          border: 1px solid #6bb6ff; 
          padding: 8px 15px; 
          border-radius: 3px; 
          cursor: pointer; 
          font-size: 12px;
          margin-top: 10px;
          margin-right: 10px;
        ">Finish Tutorial</button>
        <button onclick="window.game.continuePlaying()" style="
          background: #666; 
          color: white; 
          border: 1px solid #888; 
          padding: 8px 15px; 
          border-radius: 3px; 
          cursor: pointer; 
          font-size: 12px;
          margin-top: 10px;
        ">Continue Playing</button>
      `;
    }
  }
  */ // End of removed old tutorial methods block

  // Removed unused method: skipTutorialStage

  // City upgrade functionality  
  private startCityUpgrade(city: City, toLevel: CityLevel): boolean {
    if (!city || city.upgradeInProgress) return false;
    
    const cost = getCityUpgradeCost(toLevel);
    const currentResources = this.gameState.resources.get(city.faction!) || 0;
    
    if (currentResources < cost) {
      this.showNotification(`Not enough resources! Need ${cost} to upgrade.`);
      return false;
    }
    
    // Deduct resources
    this.gameState.resources.set(city.faction!, currentResources - cost);
    
    // Start upgrade
    city.upgradeInProgress = {
      toLevel: toLevel,
      turnsRemaining: getCityUpgradeTime(toLevel)
    };
    
    this.showNotification(`${city.name} is upgrading to ${getCityLevelName(toLevel)}!`);
    // this.soundManager.playConstruction(); // Method doesn't exist
    
    return true;
  }
  

  private completeTutorial(): void {
    this.isTutorial = false;
    this.tutorialStage = 0;
    
    // Remove tutorial overlay
    if (this.tutorialOverlay) {
      document.body.removeChild(this.tutorialOverlay);
      this.tutorialOverlay = null;
    }
    
    // Show completion message
    const completionOverlay = document.createElement('div');
    completionOverlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background: rgba(0, 0, 0, 0.8);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 2000;
    `;
    
    const completionBox = document.createElement('div');
    completionBox.style.cssText = `
      background: #2a2a2a;
      color: white;
      padding: 40px;
      border-radius: 10px;
      border: 2px solid #4a90e2;
      text-align: center;
      font-family: 'Courier New', monospace;
      max-width: 400px;
    `;
    
    completionBox.innerHTML = `
      <h2 style="margin: 0 0 20px 0; color: #4a90e2;">Tutorial Complete!</h2>
      <p style="margin: 0 0 20px 0;">You've learned the basics of Frontlines combat. Good luck, Commander!</p>
      <button onclick="window.location.reload()" style="
        background: #4a90e2;
        color: white;
        border: 1px solid #6bb6ff;
        padding: 10px 20px;
        border-radius: 5px;
        cursor: pointer;
        font-size: 14px;
        margin-right: 10px;
      ">Return to Menu</button>
      <button onclick="this.parentElement.parentElement.remove()" style="
        background: #666;
        color: white;
        border: 1px solid #888;
        padding: 10px 20px;
        border-radius: 5px;
        cursor: pointer;
        font-size: 14px;
      ">Continue Playing</button>
    `;
    
    completionOverlay.appendChild(completionBox);
    document.body.appendChild(completionOverlay);
  }

  // Removed unused method: continuePlaying

  // ==================== GUIDED TUTORIAL SYSTEM ====================

  private createTutorialDimOverlay(): void {
    if (this.tutorialDimOverlay) return;
    
    this.tutorialDimOverlay = document.createElement('div');
    this.tutorialDimOverlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background: rgba(0, 0, 0, 0.8);
      z-index: 1500;
      pointer-events: none;
    `;
    document.body.appendChild(this.tutorialDimOverlay);
  }

  private removeTutorialDimOverlay(): void {
    if (this.tutorialDimOverlay) {
      document.body.removeChild(this.tutorialDimOverlay);
      this.tutorialDimOverlay = null;
    }
  }

  private highlightElement(selector: string, margin: number = 10): void {
    this.removeHighlight();
    
    const element = document.querySelector(selector) as HTMLElement;
    if (!element) {
      console.warn(`Tutorial: Element not found for selector: ${selector}`);
      return;
    }
    
    const rect = element.getBoundingClientRect();
    
    // Create a cutout effect by updating the dim overlay with a CSS clip-path
    if (this.tutorialDimOverlay) {
      const clipTop = Math.max(0, rect.top - margin);
      const clipLeft = Math.max(0, rect.left - margin);
      const clipWidth = rect.width + margin * 2;
      const clipHeight = rect.height + margin * 2;
      
      // Ensure we don't go beyond viewport boundaries
      const maxRight = Math.min(window.innerWidth, clipLeft + clipWidth);
      const maxBottom = Math.min(window.innerHeight, clipTop + clipHeight);
      
      // Use percentage-based clip-path for better responsiveness
      const leftPercent = (clipLeft / window.innerWidth) * 100;
      const topPercent = (clipTop / window.innerHeight) * 100;
      const rightPercent = (maxRight / window.innerWidth) * 100;
      const bottomPercent = (maxBottom / window.innerHeight) * 100;
      
      // Create complex clip-path to cut out the highlighted area
      this.tutorialDimOverlay.style.clipPath = `polygon(
        0% 0%,
        0% 100%,
        ${leftPercent}% 100%,
        ${leftPercent}% ${topPercent}%,
        ${rightPercent}% ${topPercent}%,
        ${rightPercent}% ${bottomPercent}%,
        ${leftPercent}% ${bottomPercent}%,
        ${leftPercent}% 100%,
        100% 100%,
        100% 0%
      )`;
    }
    
    // Create highlight border around the element
    this.tutorialHighlight = document.createElement('div');
    
    // Ensure highlight stays within viewport bounds
    const highlightTop = Math.max(0, rect.top - margin);
    const highlightLeft = Math.max(0, rect.left - margin);
    const highlightWidth = Math.min(rect.width + margin * 2, window.innerWidth - highlightLeft);
    const highlightHeight = Math.min(rect.height + margin * 2, window.innerHeight - highlightTop);
    
    this.tutorialHighlight.style.cssText = `
      position: fixed;
      top: ${highlightTop}px;
      left: ${highlightLeft}px;
      width: ${highlightWidth}px;
      height: ${highlightHeight}px;
      border: 4px solid #4a90e2;
      border-radius: 12px;
      z-index: 1600;
      pointer-events: none;
      box-shadow: 
        0 0 0 2px rgba(255, 255, 255, 0.8),
        0 0 30px rgba(74, 144, 226, 0.8),
        inset 0 0 0 2px rgba(74, 144, 226, 0.3);
      animation: tutorialPulse 2s ease-in-out infinite;
    `;
    
    // Add pulsing animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes tutorialPulse {
        0%, 100% { 
          opacity: 0.9; 
          transform: scale(1);
          box-shadow: 
            0 0 0 2px rgba(255, 255, 255, 0.8),
            0 0 30px rgba(74, 144, 226, 0.8),
            inset 0 0 0 2px rgba(74, 144, 226, 0.3);
        }
        50% { 
          opacity: 1; 
          transform: scale(1.02);
          box-shadow: 
            0 0 0 3px rgba(255, 255, 255, 1),
            0 0 40px rgba(74, 144, 226, 1),
            inset 0 0 0 3px rgba(74, 144, 226, 0.5);
        }
      }
    `;
    document.head.appendChild(style);
    
    document.body.appendChild(this.tutorialHighlight);
  }

  private removeHighlight(): void {
    if (this.tutorialHighlight) {
      document.body.removeChild(this.tutorialHighlight);
      this.tutorialHighlight = null;
    }
    
    // Reset clip-path on dim overlay
    if (this.tutorialDimOverlay) {
      this.tutorialDimOverlay.style.clipPath = 'none';
    }
  }

  private showTutorialDialog(title: string, content: string, position: 'center' | 'top' | 'bottom' | 'left' | 'right' = 'center', onContinue?: () => void): void {
    this.showDialog(title, content, position, 'information', onContinue);
  }

  private showTutorialOrderDialog(title: string, content: string, position: 'center' | 'top' | 'bottom' | 'left' | 'right' = 'top'): void {
    this.showDialog(title, content, position, 'order');
  }

  private showDialog(title: string, content: string, position: 'center' | 'top' | 'bottom' | 'left' | 'right', type: 'information' | 'order', onContinue?: () => void): void {
    this.removeTutorialDialog();
    
    this.tutorialDialog = document.createElement('div');
    
    let positionCSS = 'top: 50%; left: 50%; transform: translate(-50%, -50%);';
    switch (position) {
      case 'top':
        positionCSS = 'top: 10%; left: 50%; transform: translateX(-50%);';
        break;
      case 'bottom':
        positionCSS = 'bottom: 10%; left: 50%; transform: translateX(-50%);';
        break;
      case 'left':
        positionCSS = 'top: 50%; left: 10%; transform: translateY(-50%);';
        break;
      case 'right':
        positionCSS = 'top: 50%; right: 10%; transform: translateY(-50%);';
        break;
    }
    
    // Different styles for information vs order dialogs
    const isOrder = type === 'order';
    const borderColor = isOrder ? '#ff9500' : '#4a90e2';
    const titleColor = isOrder ? '#ff9500' : '#4a90e2';
    const buttonColor = isOrder ? '#ff9500' : '#4a90e2';
    const buttonHoverColor = isOrder ? '#ffb140' : '#6bb6ff';
    
    this.tutorialDialog.style.cssText = `
      position: fixed;
      ${positionCSS}
      background: ${isOrder ? '#2a2a1a' : '#2a2a2a'};
      color: white;
      padding: 25px;
      border-radius: 12px;
      border: 2px solid ${borderColor};
      z-index: 1700;
      max-width: 400px;
      min-width: 300px;
      font-family: 'Courier New', monospace;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.6);
      animation: tutorialSlideIn 0.3s ease-out;
    `;
    
    const buttonText = isOrder ? 'Waiting for action...' : 'Click to Continue';
    const buttonHTML = isOrder ? '' : `
      <button id="tutorialContinueBtn" style="
        background: ${buttonColor};
        color: white;
        border: 1px solid ${buttonHoverColor};
        padding: 10px 20px;
        border-radius: 6px;
        cursor: pointer;
        font-size: 14px;
        font-family: 'Courier New', monospace;
        float: right;
        transition: background-color 0.2s;
      " onmouseover="this.style.background='${buttonHoverColor}'" onmouseout="this.style.background='${buttonColor}'">
        ${buttonText}
      </button>
    `;
    
    this.tutorialDialog.innerHTML = `
      <h3 style="margin: 0 0 15px 0; color: ${titleColor}; font-size: 18px;">${title}</h3>
      <div style="margin-bottom: 20px; line-height: 1.6; font-size: 14px;">${content}</div>
      ${buttonHTML}
      <div style="clear: both;"></div>
    `;
    
    // Add slide-in animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes tutorialSlideIn {
        0% { opacity: 0; transform: translate(-50%, -50%) scale(0.8); }
        100% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
      }
    `;
    document.head.appendChild(style);
    
    // Add dialog to DOM first
    document.body.appendChild(this.tutorialDialog);
    
    // Then attach event handlers (after element is in DOM)
    if (!isOrder) {
      const continueBtn = this.tutorialDialog.querySelector('#tutorialContinueBtn') as HTMLButtonElement;
      if (continueBtn) {
        continueBtn.onclick = () => {
          this.removeTutorialDialog();
          if (onContinue) onContinue();
        };
      }
    }
  }

  private removeTutorialDialog(): void {
    if (this.tutorialDialog) {
      document.body.removeChild(this.tutorialDialog);
      this.tutorialDialog = null;
    }
  }


  // Comprehensive Tutorial Steps
  private startGuidedTutorial(): void {
    this.tutorialStage = 0;
    this.tutorialStep = 0;
    this.tutorialValidationLogged = false; // Reset logging flag for new tutorial
    // this.tutorialStageData = {}; // Removed - unused
    this.showGuidedTutorialStep();
  }

  private showGuidedTutorialStep(): void {
    switch (this.tutorialStage) {
      case 0: // UI Introduction
        this.showUIIntroductionSteps();
        break;
      case 1: // Movement Tutorial
        this.showMovementTutorialSteps();
        break;
      case 2: // City Capture Tutorial
        this.showCityCaptureSteps();
        break;
      case 3: // Unit Training Tutorial
        this.showUnitTrainingSteps();
        break;
      case 4: // Combat Tutorial
        this.showCombatTutorialSteps();
        break;
      case 5: // Advanced Unit Types
        this.showAdvancedUnitsSteps();
        break;
      case 6: // Transport and Logistics
        this.showTransportSteps();
        break;
      case 7: // Healing and Repairs
        this.showHealingSteps();
        break;
      case 8: // Defensive Tactics
        this.showDefensiveSteps();
        break;
      case 9: // Airborne Operations
        this.showAirborneSteps();
        break;
      case 10: // City Upgrades and Mines
        this.showUpgradesSteps();
        break;
      case 11: // Combined Arms and Advanced Strategy
        this.showAdvancedStrategySteps();
        break;
      default:
        this.completeTutorial();
        break;
    }
  }

  private showUIIntroductionSteps(): void {
    const steps = [
      {
        title: "Welcome to Frontlines!",
        content: "This is a comprehensive tutorial that will teach you everything about this turn-based strategy game. We'll start by exploring the user interface.",
        position: 'center'
      },
      {
        title: "Game Canvas",
        content: "This is the main game map where all the action happens. You can see terrain, units, and cities here. The map uses a grid system for movement and positioning.",
        highlight: "#game-canvas",
        position: 'right'
      },
      {
        title: "Unit Information Panel",
        content: "On the left side, you'll see detailed information about selected units including their stats, movement range, health, and special abilities.",
        highlight: "#left-panel",
        position: 'right'
      },
      {
        title: "Terrain Information Panel",
        content: "This panel shows information about the selected terrain tile, including movement costs and defensive bonuses.",
        highlight: "#side-panel",
        position: 'left'
      },
      {
        title: "Top Status Bar",
        content: "This shows your current faction, turn number, date, cities controlled, unit count, and other important status information.",
        highlight: "#top-bar",
        position: 'bottom'
      },
      {
        title: "Resources Display",
        content: "Keep track of your resources and income. Resources are crucial for training units and upgrading cities.",
        highlight: "#resources",
        position: 'bottom'
      },
      {
        title: "End Turn Button",
        content: "Use this button to end your turn and let the AI play. This advances the game and processes unit training, healing, and resource generation.",
        highlight: "#end-turn-btn",
        position: 'top'
      }
    ];

    this.processTutorialSteps(steps, () => {
      this.removeHighlight();
      this.removeTutorialDimOverlay();
      this.tutorialStage++;
      this.tutorialStep = 0;
      this.tutorialValidationLogged = false; // Reset logging flag for new stage
      this.showGuidedTutorialStep();
    });
  }

  private showMovementTutorialSteps(): void {
    const steps = [
      {
        title: "Movement Basics",
        content: "Let's learn how to move units. First, you need to select a unit. Look for your infantry unit (shown as a soldier icon) near the Tutorial Base.",
        position: 'center',
        type: 'information'
      },
      {
        title: "Select Your Unit",
        content: "Click on your starting infantry unit to select it. You'll see it highlighted and its movement range displayed as lighter colored squares.",
        requiresAction: true,
        validation: () => {
          if (!this.gameState.selectedUnit) return false;
          const selectedUnit = this.unitManager.getUnit(this.gameState.selectedUnit);
          return selectedUnit !== null && selectedUnit !== undefined && selectedUnit.faction === 'United States Of America';
        },
        position: 'top',
        type: 'order'
      },
      {
        title: "Movement Range",
        content: "Excellent! Notice the highlighted squares around your unit. These show where the unit can move this turn. The number of squares depends on the unit's movement points and terrain costs.",
        position: 'top',
        type: 'information'
      },
      {
        title: "Terrain Effects",
        content: "Different terrain types affect movement costs:<br/>• <strong>Easy</strong> terrain: 1 movement point<br/>• <strong>Rough</strong> terrain: 2 movement points<br/>• <strong>Challenging</strong> terrain: 3 movement points<br/><br/>You can see a tile's terrain type by clicking on it and checking the right panel. Cities provide defensive bonuses!",
        highlight: "#game-canvas",
        position: 'right',
        type: 'information'
      },
      {
        title: "Move Your Unit",
        content: "Now click on a valid movement square (within the highlighted area) to move your unit. Try moving towards the Neutral City to the east.",
        requiresAction: true,
        validation: () => {
          // Check if the selected unit has been moved
          if (!this.gameState.selectedUnit) return false;
          const selectedUnit = this.unitManager.getUnit(this.gameState.selectedUnit);
          if (!selectedUnit || selectedUnit.faction !== 'United States Of America') return false;
          
          // Check if unit has moved from initial position (2,2)
          return selectedUnit.position.x !== 2 || selectedUnit.position.y !== 2;
        },
        position: 'top',
        type: 'order'
      },
      {
        title: "Movement Complete!",
        content: "Great! You've learned the basics of unit movement. Now let's learn about capturing cities, which are essential for expanding your territory and resources.",
        position: 'center',
        type: 'information'
      }
    ];

    this.processTutorialSteps(steps, () => {
      console.log(`🎯 STAGE COMPLETION: Moving from stage ${this.tutorialStage} to ${this.tutorialStage + 1}`);
      this.tutorialStage++;
      this.tutorialStep = 0;
      this.tutorialValidationLogged = false; // Reset logging flag for new stage
      console.log(`🎯 NEW STAGE: Starting stage ${this.tutorialStage}`);
      this.showGuidedTutorialStep(); // Ensure we continue to the next stage
    });
  }

  private processTutorialSteps(steps: any[], onComplete: () => void): void {
    // Only log when step first starts or when logging hasn't been done yet
    if (!this.tutorialValidationLogged) {
      console.log(`📝 TUTORIAL STEP PROCESSING: Stage ${this.tutorialStage}, Step ${this.tutorialStep}/${steps.length}`);
    }
    
    if (this.tutorialStep < steps.length) {
      const step = steps[this.tutorialStep];
      
      // Only log step details when step first starts
      if (!this.tutorialValidationLogged) {
        console.log(`  - Current step: "${step.title}" (${step.type})`);
        console.log(`  - Requires action: ${step.requiresAction}`);
      }
      
      if (step.requiresAction && step.validation) {
        const validationResult = step.validation();
        
        // Only log validation check when step first starts or when validation passes
        if (!this.tutorialValidationLogged) {
          console.log(`  - Checking validation...`);
          console.log(`  - Validation result: ${validationResult}`);
          this.tutorialValidationLogged = true; // Mark that we've logged for this step
        }
        
        // Check if action is completed
        if (validationResult) {
          console.log(`  ✅ VALIDATION PASSED - Advancing to next step`);
          
          // Clear polling interval
          if (this.tutorialPollingInterval) {
            clearInterval(this.tutorialPollingInterval);
            this.tutorialPollingInterval = null;
          }
          
          this.removeHighlight();
          this.removeTutorialDimOverlay();
          this.removeTutorialDialog(); // Remove the order dialog
          this.tutorialStep++;
          this.tutorialValidationLogged = false; // Reset logging flag for next step
          console.log(`  - Advanced to step ${this.tutorialStep}`);
          this.showGuidedTutorialStep();
        } else {
          // Show order dialog and wait for action (only if not already shown)
          if (!this.tutorialDialog) {
            if (step.highlight) {
              this.createTutorialDimOverlay();
              this.highlightElement(step.highlight);
            }
            
            if (step.type === 'order') {
              this.showTutorialOrderDialog(step.title, step.content, step.position || 'top');
              
              // Start polling to check if action is completed
              if (!this.tutorialPollingInterval) {
                this.tutorialPollingInterval = window.setInterval(() => {
                  this.showGuidedTutorialStep(); // This will check validation again
                }, 500); // Check every 500ms
              }
            } else {
              this.showTutorialDialog(step.title, step.content, step.position || 'top');
            }
          }
        }
      } else {
        // Regular information dialog step
        if (step.action) step.action();
        
        // Handle highlighting and dimming
        if (step.highlight) {
          this.createTutorialDimOverlay();
          this.highlightElement(step.highlight);
        }
        
        const position = step.position || 'center';
        this.showTutorialDialog(step.title, step.content, position, () => {
          this.removeHighlight();
          this.removeTutorialDimOverlay();
          this.tutorialStep++;
          if (this.tutorialStep >= steps.length) {
            onComplete();
          } else {
            this.showGuidedTutorialStep();
          }
        });
      }
    }
  }

  // Removed unused method: highlightTerrain

  private showCityCaptureSteps(): void {
    const steps = [
      {
        title: "City Capture Tutorial",
        content: "Cities are vital strategic points that generate resources and allow unit training. Let's learn how to capture them.",
        position: 'center',
        type: 'information'
      },
      {
        title: "Capture the Neutral City",
        content: "Look for the gray-colored city labeled 'Neutral City'. Move your unit onto one of its tiles to begin capturing it. Cities take 2 turns to fully capture - you'll need to end your turn and wait.",
        requiresAction: true,
        validation: () => {
          const neutralCity = Array.from(this.map.getCities().values()).find(city => city.name === 'Neutral City');
          if (!this.tutorialValidationLogged) {
            console.log('🏰 CITY CAPTURE VALIDATION:');
            console.log('  - Neutral City found:', !!neutralCity);
            if (neutralCity) {
              console.log('  - City faction:', neutralCity.faction);
              console.log('  - Player faction:', this.humanPlayerFaction);
              console.log('  - Capture progress:', neutralCity.captureProgress);
              console.log('  - Faction match:', neutralCity.faction === this.humanPlayerFaction);
              console.log('  - Not capturing:', !neutralCity.captureProgress);
              console.log('  - FINAL RESULT:', neutralCity && neutralCity.faction === this.humanPlayerFaction && !neutralCity.captureProgress);
            }
            this.tutorialValidationLogged = true;
          }
          return neutralCity && neutralCity.faction === this.humanPlayerFaction && !neutralCity.captureProgress;
        },
        position: 'top',
        type: 'order'
      },
      {
        title: "City Captured!",
        content: "Excellent! You've captured your first city. Notice how it's now your color and generates resources each turn. Captured cities can train units and be upgraded.",
        position: 'center',
        type: 'information'
      }
    ];

    this.processTutorialSteps(steps, () => {
      this.tutorialStage++;
      this.tutorialStep = 0;
      this.tutorialValidationLogged = false; // Reset logging flag for new stage
      this.showGuidedTutorialStep(); // Continue to next stage
    });
  }

  private showUnitTrainingSteps(): void {
    const steps = [
      {
        title: "Unit Training",
        content: "Now that you have a city, you can train new units. Cities are the only way to produce new forces.",
        position: 'center',
        type: 'information'
      },
      {
        title: "Select Your City",
        content: "Click on your captured city to select it. You'll see training options in the left panel.",
        position: 'center',
        type: 'information'
      },
      {
        title: "Train an Engineer Unit",
        content: "With your city selected, click the 'Train Unit' button in the city panel, then select 'Engineer Squad' from the list.",
        action: () => this.highlightElement('[onclick*=\"trainUnit\"]'),
        requiresAction: true,
        validation: () => {
          // Check if Engineer training has started (specific unit type in queue)
          const cities = Array.from(this.map.getCities().values()).filter(city => city.faction === this.humanPlayerFaction);
          console.log('🏗️ ENGINEER TRAINING VALIDATION:');
          console.log('  - Player cities found:', cities.length);
          cities.forEach((city, index) => {
            console.log(`  - City ${index + 1} (${city.name}):`, {
              faction: city.faction,
              hasQueue: !!city.trainingQueue,
              queueLength: city.trainingQueue ? city.trainingQueue.length : 0,
              queueTypes: city.trainingQueue ? city.trainingQueue.map(u => u.type) : []
            });
          });
          const hasEngineer = cities.some(city => 
            city.trainingQueue && 
            city.trainingQueue.some(unit => unit.type === 'engineer_squad')
          );
          console.log('  - FINAL RESULT (has engineer in queue):', hasEngineer);
          return hasEngineer;
        },
        position: 'top',
        type: 'order'
      },
      {
        title: "Engineer Training Started",
        content: "Perfect! Your Engineer Squad is now in the training queue. Engineers are versatile units that can build defenses and repair vehicles.",
        position: 'center',
        type: 'information'
      },
      {
        title: "Wait for Engineer Training",
        content: "Training takes time. End your turn using the 'Next Turn' button to advance time and complete the Engineer training.",
        requiresAction: true,
        validation: () => {
          // Check if an Engineer unit has been trained by this player
          const playerUnits = this.humanPlayerFaction ? this.unitManager.getUnitsForFaction(this.humanPlayerFaction) : [];
          console.log('⚒️ ENGINEER COMPLETION VALIDATION:');
          console.log('  - Total player units:', playerUnits.length);
          console.log('  - Unit types:', playerUnits.map(u => ({ type: u.type, name: u.name, id: u.id })));
          const hasEngineerUnit = playerUnits.some(unit => unit.type === 'engineer_squad');
          console.log('  - FINAL RESULT (has engineer unit):', hasEngineerUnit);
          return hasEngineerUnit;
        },
        position: 'top',
        type: 'order'
      },
      {
        title: "Training Complete!",
        content: "Excellent! You now have multiple units. Let's learn about combat by engaging an enemy unit.",
        position: 'center',
        type: 'information'
      }
    ];

    this.processTutorialSteps(steps, () => {
      this.tutorialStage++;
      this.tutorialStep = 0;
      this.tutorialValidationLogged = false; // Reset logging flag for new stage
      this.showGuidedTutorialStep(); // Continue to next stage
    });
  }

  private showCombatTutorialSteps(): void {
    const steps = [
      {
        title: "Combat System",
        content: "Time to learn combat! An enemy unit will appear that you need to defeat.",
        position: 'center',
        type: 'information',
        action: () => {
          // Spawn enemy unit now that training is complete
          this.spawnTutorialEnemyUnit();
        }
      },
      {
        title: "Enemy Unit Spotted!",
        content: "An enemy unit has appeared! Enemy units have a different color from your forces. You can see their type by their icon and get information by clicking on them.",
        position: 'center',
        type: 'information'
      },
      {
        title: "Select Your Engineer",
        content: "First, select your newly trained Engineer unit. Engineers use flamethrowers and shotguns, giving them devastating damage at close range (1 tile) but they need to get up close to the enemy.",
        requiresAction: true,
        validation: () => {
          if (!this.gameState.selectedUnit) return false;
          const selectedUnit = this.unitManager.getUnit(this.gameState.selectedUnit);
          return selectedUnit !== null && selectedUnit !== undefined && selectedUnit.faction === this.humanPlayerFaction && selectedUnit.type === 'engineer_squad';
        },
        position: 'top',
        type: 'order'
      },
      {
        title: "Attack the Enemy",
        content: "Now click on the enemy unit to attack it. Combat is automatic and considers unit stats, terrain, and distance.",
        requiresAction: true,
        validation: () => {
          // Track if combat has occurred by checking if tutorial enemy unit exists and has taken damage or been destroyed
          if (this.tutorialEnemyUnits.length === 0) return true; // Enemy destroyed
          const enemyUnit = this.unitManager.getUnit(this.tutorialEnemyUnits[0]);
          if (!enemyUnit) return true; // Enemy destroyed
          
          // Check if enemy has taken damage (health < max health)
          return enemyUnit.stats.health < enemyUnit.stats.maxHealth;
        },
        position: 'top',
        type: 'order'
      },
      {
        title: "Combat Complete!",
        content: "Excellent! You've learned the basics of combat. The enemy AI is now active and will continue playing. Build up your forces and capture the enemy cities to win!",
        position: 'center',
        type: 'information',
        action: () => {
          // Enable full enemy AI from this point forward
          this.tutorialAIEnabled = true;
          
          // Set AI to attack player units
          if (this.aiController) {
            this.aiController.setTutorialObjective(
              'Attack player units',
              [],
              'attack_player'
            );
          }
          
          // Give player more resources to continue
          if (this.humanPlayerFaction) {
            const currentResources = this.gameState.resources.get(this.humanPlayerFaction) || 0;
            this.gameState.resources.set(this.humanPlayerFaction, currentResources + 50);
            this.showNotification('You received 50 resources to continue your campaign!');
          }
        }
      }
    ];

    this.processTutorialSteps(steps, () => {
      // Continue to next tutorial stage
      this.tutorialStage++;
      this.tutorialStep = 0;
      this.tutorialValidationLogged = false;
      this.showGuidedTutorialStep();
    });
  }

  private showAdvancedUnitsSteps(): void {
    // Stage 5: Defensive combat and enemy counter-attack
    const steps = [
      {
        title: "Enemy Counter-Attack!",
        content: "Enemy forces are moving to retake the Neutral City! Prepare your defenses.",
        position: 'center',
        type: 'information',
        action: () => {
          // Spawn enemy attack force
          this.spawnTutorialEnemyAttackForce();
        }
      },
      {
        title: "Defensive Combat",
        content: "Defend the Neutral City! Units in cities get defensive bonuses. Defeat the attacking enemies.",
        requiresAction: true,
        validation: () => {
          // Check if ALL enemy attack units are defeated
          const neutralCity = Array.from(this.map.getCities().values()).find(c => c.name === 'Neutral City');
          const activeEnemies = this.tutorialEnemyUnits.filter(id => {
            const unit = this.unitManager.getUnit(id);
            return unit !== null && unit !== undefined;
          });
          // Must defeat ALL enemies and still control the city
          return activeEnemies.length === 0 && neutralCity?.faction === this.humanPlayerFaction;
        },
        position: 'top',
        type: 'order'
      },
      {
        title: "Defense Successful!",
        content: "Great work! You held the city. Now let's learn about more advanced tactics.",
        position: 'center'
      }
    ];

    this.processTutorialSteps(steps, () => {
      // this.tutorialEnemyAttackDefeated = true; // Removed - unused
      this.tutorialStage++;
      this.tutorialStep = 0;
      this.showGuidedTutorialStep();
    });
  }

  private showTransportSteps(): void {
    // Stage 6: City upgrades and tank production
    const steps = [
      {
        title: "City Upgrades",
        content: "Cities can be upgraded to produce better units. Upgrade your Neutral City to a Factory (Level 2) to build tanks.",
        position: 'center',
        type: 'information'
      },
      {
        title: "Start Upgrade",
        content: "Select the Neutral City and click the 'Upgrade City' button to upgrade it to Factory. This costs 25 resources and takes 2 turns.",
        requiresAction: true,
        validation: () => {
          const neutralCity = Array.from(this.map.getCities().values()).find(c => c.name === 'Neutral City');
          // Check if upgrade has been started
          return neutralCity?.upgradeInProgress !== undefined || (neutralCity?.level !== undefined && neutralCity.level >= CityLevel.FACTORY);
        },
        position: 'top',
        type: 'order'
      },
      {
        title: "Build a Tank",
        content: "Once upgraded to Factory, build a Sherman tank. Tanks have high attack and armor!",
        requiresAction: true,
        validation: () => {
          // Only return true when tank actually exists on the map (not just in queue)
          const hasTank = Array.from(this.gameState.units.values()).some(
            u => u.type === UnitType.SHERMAN_SECTION && u.faction === this.humanPlayerFaction
          );
          return hasTank;
        },
        position: 'top',
        type: 'order'
      },
      {
        title: "Tank Built!",
        content: "Excellent! Your Sherman tank is a powerful unit. Now let's learn about transporting units across the battlefield.",
        position: 'center',
        type: 'information'
      }
    ];

    this.processTutorialSteps(steps, () => {
      this.tutorialStage++;
      this.tutorialStep = 0;
      this.showGuidedTutorialStep();
    });
  }

  private showHealingSteps(): void {
    // Stage 7: Transport units and Hill Fort assault
    const steps = [
      {
        title: "Transport Units",
        content: "Build a transport truck (GMC CCKW) from your Factory. Transports can carry infantry quickly across the map.",
        requiresAction: true,
        validation: () => {
          // Only return true when truck actually exists (not just in queue)
          const hasTruck = Array.from(this.gameState.units.values()).some(
            u => (u.type === UnitType.GMC_CCKW || u.type === UnitType.M3_HALFTRACK) && 
                 u.faction === this.humanPlayerFaction
          );
          return hasTruck;
        },
        position: 'top',
        type: 'order'
      },
      {
        title: "Load Infantry",
        content: "Move your infantry units next to the transport truck. Select an infantry unit and click 'Board Transport' to load them onto the truck.",
        requiresAction: true,
        validation: () => {
          // Check if any transport has units loaded
          return Array.from(this.gameState.units.values()).some(
            u => u.faction === this.humanPlayerFaction && 
                 u.type === UnitType.GMC_CCKW &&
                 ((u.carryingUnits?.length ?? 0) > 0 || (u.carryingUnitsData?.length ?? 0) > 0)
          );
        },
        position: 'top',
        type: 'order'
      },
      {
        title: "Enemy Takes Hill Fort!",
        content: "The enemy has captured Hill Fort! Drive your loaded transport there with your Sherman tank for a combined assault.",
        position: 'center',
        type: 'information',
        action: () => {
          // Make Hill Fort enemy-controlled when this step is shown
          const hillFort = Array.from(this.map.getCities().values()).find(c => c.name === 'Hill Fort');
          if (hillFort && hillFort.faction !== '3rd Reich') {
            hillFort.faction = '3rd Reich';
            
            // Spawn 2 infantry defenders at Hill Fort
            const infantry1 = this.unitManager.createUnit(UnitType.PANZERGRENADIER_SQUAD, '3rd Reich', hillFort.tiles[0]);
            if (infantry1) {
              this.gameState.units.set(infantry1.id, infantry1);
              this.tutorialEnemyUnits.push(infantry1.id);
            }
            
            const infantry2 = this.unitManager.createUnit(UnitType.PANZERGRENADIER_SQUAD, '3rd Reich', hillFort.tiles[2]);
            if (infantry2) {
              this.gameState.units.set(infantry2.id, infantry2);
              this.tutorialEnemyUnits.push(infantry2.id);
            }
            
            // Spawn 1 tank defender at Hill Fort
            const tank = this.unitManager.createUnit(UnitType.PANZER_IV_ZUG, '3rd Reich', hillFort.tiles[4]);
            if (tank) {
              this.gameState.units.set(tank.id, tank);
              this.tutorialEnemyUnits.push(tank.id);
            }
            
            // Set AI to actively attack player units from Hill Fort
            if (this.aiController) {
              const centerTile = hillFort.tiles[Math.floor(hillFort.tiles.length / 2)];
              this.aiController.setTutorialObjective(
                'Attack player units from Hill Fort',
                [centerTile],
                'attack_player'
              );
            }
          }
        }
      },
      {
        title: "Unload and Attack",
        content: "Once at Hill Fort, unload your infantry by selecting the truck and clicking 'Unload Units'. Use your combined forces to capture it!",
        requiresAction: true,
        validation: () => {
          const hillFort = Array.from(this.map.getCities().values()).find(c => c.name === 'Hill Fort');
          return hillFort?.faction === this.humanPlayerFaction;
        },
        position: 'top',
        type: 'order'
      },
      {
        title: "Hill Fort Captured!",
        content: "Excellent work! You've successfully used combined arms - tanks and transported infantry - to capture an enemy position. Now let's learn about healing and reinforcements.",
        position: 'center',
        type: 'information'
      }
    ];

    this.processTutorialSteps(steps, () => {
      this.tutorialStage++;
      this.tutorialStep = 0;
      this.showGuidedTutorialStep();
    });
  }

  private showDefensiveSteps(): void {
    // Stage 8: Healing and defensive positioning
    const steps = [
      {
        title: "Unit Healing",
        content: "Infantry can be healed by Medics or by using 'Hunker Down' to recover. Vehicles need Engineers to repair them. All units fully heal when they become veterans after gaining enough experience.",
        position: 'center',
        type: 'information'
      },
      {
        title: "Capture Resource Town",
        content: "Let's expand your territory. Capture Resource Town to increase your income. More cities mean more resources per turn!",
        requiresAction: true,
        validation: () => {
          const resourceTown = Array.from(this.map.getCities().values()).find(c => c.name === 'Resource Town');
          return resourceTown?.faction === this.humanPlayerFaction;
        },
        position: 'top',
        type: 'order'
      },
      {
        title: "Territory Secured!",
        content: "Great! You now control most of the map. Next, let's learn about air power and advanced tactics.",
        position: 'center',
        type: 'information'
      }
    ];

    this.processTutorialSteps(steps, () => {
      this.tutorialStage++;
      this.tutorialStep = 0;
      this.showGuidedTutorialStep();
    });
  }

  private showAirborneSteps(): void {
    // Stage 9: Airbase upgrade
    const steps = [
      {
        title: "Enemy Airbase!",
        content: "The enemy has upgraded their stronghold to an Airbase! You need air power too.",
        position: 'center',
        type: 'information',
        action: () => {
          // Upgrade enemy stronghold and add strong defenses
          const stronghold = Array.from(this.map.getCities().values()).find(c => c.name === 'Enemy Stronghold');
          if (stronghold && stronghold.level < CityLevel.AIRBASE) {
            stronghold.faction = '3rd Reich';
            stronghold.level = CityLevel.AIRBASE;
            
            // Spawn strong defensive force
            // 3 Infantry units
            const infantryPositions = [{x: 2, y: 14}, {x: 4, y: 14}, {x: 3, y: 15}];
            infantryPositions.forEach(pos => {
              const unit = this.unitManager.createUnit(UnitType.PANZERGRENADIER_SQUAD, '3rd Reich', pos);
              if (unit) {
                this.gameState.units.set(unit.id, unit);
                this.tutorialEnemyUnits.push(unit.id);
              }
            });
            
            // 1 Tank unit
            const tank = this.unitManager.createUnit(UnitType.PANZER_IV_ZUG, '3rd Reich', {x: 3, y: 16});
            if (tank) {
              this.gameState.units.set(tank.id, tank);
              this.tutorialEnemyUnits.push(tank.id);
            }
            
            // 1 Artillery unit for support
            const artillery = this.unitManager.createUnit(UnitType.GER_ARTILLERY_SECTION, '3rd Reich', {x: 3, y: 17});
            if (artillery) {
              this.gameState.units.set(artillery.id, artillery);
              this.tutorialEnemyUnits.push(artillery.id);
            }
            
            // Set AI to attack player units aggressively
            if (this.aiController) {
              const centerTile = stronghold.tiles[Math.floor(stronghold.tiles.length / 2)];
              this.aiController.setTutorialObjective(
                'Attack player forces',
                [centerTile],
                'attack_player'
              );
            }
          }
        }
      },
      {
        title: "Upgrade to Airbase",
        content: "Upgrade one of your cities to an Airbase (Level 3). This costs 50 resources and takes 3 turns.",
        requiresAction: true,
        validation: () => {
          const playerCities = Array.from(this.map.getCities().values()).filter(
            c => c.faction === this.humanPlayerFaction
          );
          // Check if player has started upgrade or completed it
          return playerCities.some(c => 
            c.level === CityLevel.AIRBASE || 
            c.upgradeInProgress?.toLevel === CityLevel.AIRBASE
          );
        },
        position: 'top',
        type: 'order'
      },
      {
        title: "Build Paratroopers",
        content: "Once your airbase is ready, build Airborne Squad paratroopers. They spawn as a C-47 transport aircraft with paratroopers loaded.",
        requiresAction: true,
        validation: () => {
          // Check for C-47 transport with airborne unit loaded (carryingUnit property)
          const hasC47WithAirborne = Array.from(this.gameState.units.values()).some(
            u => u.type === UnitType.C47_SKYTRAIN && u.faction === this.humanPlayerFaction &&
                 u.carryingUnit === UnitType.AIRBORNE_SQUAD
          );
          return hasC47WithAirborne;
        },
        position: 'top',
        type: 'order'
      },
      {
        title: "Airborne Deployment",
        content: "Your paratroopers are ready! Move your C-47 transport aircraft over the Enemy Stronghold and click 'Deploy Paratroopers' to drop them behind enemy lines.",
        requiresAction: true,
        validation: () => {
          // Check if C-47 no longer has paratroopers (meaning they were deployed)
          const c47WithoutParatroopers = Array.from(this.gameState.units.values()).some(
            u => u.type === UnitType.C47_SKYTRAIN && u.faction === this.humanPlayerFaction &&
                 !u.carryingUnit  // No longer carrying the airborne unit
          );
          
          // Also check if there are ground airborne units (deployed paratroopers)
          const hasDeployedAirborne = Array.from(this.gameState.units.values()).some(
            u => u.type === UnitType.AIRBORNE_SQUAD && u.faction === this.humanPlayerFaction
          );
          
          return c47WithoutParatroopers || hasDeployedAirborne;
        },
        position: 'top',
        type: 'order'
      },
      {
        title: "Paratroopers Deployed!",
        content: "Excellent! Your paratroopers are behind enemy lines. The enemy will now start producing reinforcements. Prepare for the final assault!",
        position: 'center',
        type: 'information',
        action: () => {
          // NOW enable AI production after paratroopers are deployed
          if (this.aiController) {
            // Enable production while keeping aggressive
            if ('enableTutorialProduction' in this.aiController) {
              (this.aiController as any).enableTutorialProduction();
            }
            
            // Give AI resources to produce units
            const currentResources = this.gameState.resources.get('3rd Reich') || 0;
            this.gameState.resources.set('3rd Reich', currentResources + 100);
            
            // Keep AI aggressive
            this.aiController.setTutorialObjective(
              'Final defense - attack all player units',
              [],
              'attack_player'
            );
            
            this.showNotification('Enemy is now producing reinforcements from their Airbase!');
            debug.log('Tutorial: Enabled AI production after paratrooper deployment');
          }
        }
      }
    ];

    this.processTutorialSteps(steps, () => {
      this.tutorialStage++;
      this.tutorialStep = 0;
      this.showGuidedTutorialStep();
    });
  }

  private showUpgradesSteps(): void {
    // Stage 10: Final preparations
    const steps = [
      {
        title: "Prepare for Final Assault",
        content: "With your paratroopers behind enemy lines, it's time for the final push. Gather all your remaining forces - tanks, infantry, and any surviving units.",
        position: 'center',
        type: 'information'
      },
      {
        title: "Coordinate Your Forces",
        content: "Move all your units toward the Enemy Stronghold. Use your Sherman tank to lead the assault, with infantry support. Your paratroopers can attack from behind!",
        position: 'center',
        type: 'information'
      }
    ];

    this.processTutorialSteps(steps, () => {
      this.tutorialStage++;
      this.tutorialStep = 0;
      this.showGuidedTutorialStep();
    });
  }

  private showAdvancedStrategySteps(): void {
    // Stage 11: Final assault on Enemy Stronghold
    const steps = [
      {
        title: "Final Assault!",
        content: "It's time to end this! Use all your forces - infantry, tanks, paratroopers - to capture the Enemy Stronghold.",
        position: 'center',
        type: 'information'
      },
      {
        title: "Capture Enemy Stronghold",
        content: "Attack and capture the Enemy Stronghold to complete the tutorial. Use combined arms tactics!",
        requiresAction: true,
        validation: () => {
          const stronghold = Array.from(this.map.getCities().values()).find(c => c.name === 'Enemy Stronghold');
          return stronghold?.faction === this.humanPlayerFaction;
        },
        position: 'top',
        type: 'order'
      },
      {
        title: "Victory!",
        content: "Congratulations! You've mastered all the mechanics: movement, combat, city capture, upgrades, tanks, transport, and air power. You're ready for real campaigns!",
        position: 'center',
        type: 'information'
      }
    ];

    this.processTutorialSteps(steps, () => {
      this.completeTutorial();
    });
  }
  
}

// Start the game when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new Game();
});